import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient, Session } from '@supabase/supabase-js';

// ─── SUPABASE ──────────────────────────────────────────────────────────────────
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Email notification helper — queues email via Supabase
async function sendEmail(toEmail: string, toName: string, subject: string, body: string) {
  if (!supabase || !toEmail) return;
  try {
    await supabase.from('email_queue').insert({
      to_email: toEmail,
      to_name: toName,
      subject: subject,
      body_text: body,
      body_html: `<div style="font-family:sans-serif;max-width:560px;margin:0 auto;padding:24px"><div style="background:#e8650a;padding:16px 20px;border-radius:10px 10px 0 0"><h2 style="color:#fff;margin:0;font-size:18px">🚗 PoolRide</h2></div><div style="background:#fff;padding:24px;border:1px solid #eee;border-radius:0 0 10px 10px"><h3 style="margin:0 0 12px">${subject}</h3><p style="color:#555;line-height:1.6;margin:0 0 16px">${body}</p><a href="https://poolride-parnikallc.netlify.app" style="background:#e8650a;color:#fff;padding:10px 20px;border-radius:8px;text-decoration:none;font-weight:600;display:inline-block">Open PoolRide →</a><p style="color:#aaa;font-size:11px;margin:20px 0 0">PoolRide by Parnika Systems · <a href="https://poolride-parnikallc.netlify.app" style="color:#aaa">Manage notifications</a></p></div></div>`,
    });
  } catch(e) { console.log('Email queue not available'); }
}

// Get user profile for email sending
async function getUserForNotify(userId: string): Promise<any | null> {
  if (!supabase) return null;
  const { data } = await supabase.from('profiles')
    .select('full_name, email, email_notifications, notif_push, phone')
    .eq('id', userId).maybeSingle();
  if (!data) return null;
  // If email missing from profiles, try getting it via email_queue history or auth
  if (!data.email) {
    // Use the add_email_to_profiles approach - check auth.users via edge function
    try {
      const res = await supabase.rpc('get_auth_email', { user_id: userId });
      if (res.data) data.email = res.data;
    } catch {}
  }
  return data;
}

// Scroll to a field by id or name and focus it
function scrollToField(fieldId: string, errorMsg: string, setError: (e: string) => void) {
  setError(errorMsg);
  setTimeout(() => {
    const el = document.getElementById(fieldId) || document.querySelector(`[placeholder*="${fieldId}"]`) as HTMLElement;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus();
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, 50);
}

// Validate address via Nominatim (OpenStreetMap) through Edge Function
async function validateAddress(street: string, city: string, state: string): Promise<{ valid: boolean; message?: string; warn?: boolean }> {
  try {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const res = await fetch(`${supabaseUrl}/functions/v1/zip-lookup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
      body: JSON.stringify({ street, city, state }),
    });
    if (!res.ok) { console.log('Address validation service error:', res.status); return { valid: true }; }
    const data = await res.json();
    console.log('Address validation response:', JSON.stringify(data));
    if (data.error) return { valid: true };
    return data;
  } catch(e) {
    console.log('Address validation failed:', e);
    return { valid: true };
  }
}
// Reverse geocode lat/lng to address using Nominatim
async function reverseGeocode(lat: number, lon: number): Promise<{ street: string; city: string; state: string } | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`,
      { headers: { 'Accept-Language': 'en-US', 'User-Agent': 'PoolRide/1.0' } }
    );
    const data = await res.json();
    const a = data.address || {};
    const street = [a.house_number, a.road].filter(Boolean).join(' ');
    const city = a.city || a.town || a.village || a.suburb || '';
    const stateMap: Record<string,string> = {
      'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA','Colorado':'CO','Connecticut':'CT',
      'Delaware':'DE','Florida':'FL','Georgia':'GA','Hawaii':'HI','Idaho':'ID','Illinois':'IL','Indiana':'IN','Iowa':'IA',
      'Kansas':'KS','Kentucky':'KY','Louisiana':'LA','Maine':'ME','Maryland':'MD','Massachusetts':'MA','Michigan':'MI',
      'Minnesota':'MN','Mississippi':'MS','Missouri':'MO','Montana':'MT','Nebraska':'NE','Nevada':'NV','New Hampshire':'NH',
      'New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND','Ohio':'OH','Oklahoma':'OK',
      'Oregon':'OR','Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD','Tennessee':'TN',
      'Texas':'TX','Utah':'UT','Vermont':'VT','Virginia':'VA','Washington':'WA','West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY'
    };
    const rawState = a.state_code || a.ISO3166_2_lvl4?.split('-')[1] || a['ISO3166-2-lvl4']?.split('-')[1] || '';
    const state = rawState.length === 2 ? rawState : (stateMap[a.state] || rawState.substring(0,2));
    return street ? { street, city, state: state.toUpperCase().substring(0,2) } : null;
  } catch(e) { return null; }
}

// Search places by name near a location using Nominatim
async function searchPlaces(query: string, lat?: number, lon?: number): Promise<Array<{ name: string; street: string; city: string; state: string; display: string }>> {
  try {
    // Use tight 0.3° viewbox (~20mi) with bounded=1 first; fall back to 1° unbounded if no results
    const tightBox = lat && lon ? `&viewbox=${lon-0.3},${lat-0.3},${lon+0.3},${lat+0.3}&bounded=1` : '';
    const wideBox  = lat && lon ? `&viewbox=${lon-1},${lat-1},${lon+1},${lat+1}&bounded=0` : '';
    const fetchNominatim = async (box: string) => {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=6&addressdetails=1&countrycodes=us${box}`,
        { headers: { 'Accept-Language': 'en-US', 'User-Agent': 'PoolRide/1.0' } }
      );
      return res.json();
    };
    let data = lat && lon ? await fetchNominatim(tightBox) : [];
    // If tight search returns < 3 results, try wider but still biased area
    if (data.length < 3 && lat && lon) {
      const extra = await fetchNominatim(wideBox);
      // Merge, dedup by display_name, keep tight results first
      const seen = new Set(data.map((r: any) => r.display_name));
      data = [...data, ...extra.filter((r: any) => !seen.has(r.display_name))];
    }
    // If still no results with location, try US-wide
    if (data.length === 0) {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1&countrycodes=us`,
        { headers: { 'Accept-Language': 'en-US', 'User-Agent': 'PoolRide/1.0' } }
      );
      data = await res.json();
    }
    // Sort by distance to user if location available
    if (lat && lon) {
      data = (data || []).sort((a: any, b: any) => {
        const distA = Math.pow(parseFloat(a.lat) - lat, 2) + Math.pow(parseFloat(a.lon) - lon, 2);
        const distB = Math.pow(parseFloat(b.lat) - lat, 2) + Math.pow(parseFloat(b.lon) - lon, 2);
        return distA - distB;
      });
    }
    // Sort by distance to user if location available
    if (lat && lon && data?.length) {
      data = data.sort((a: any, b: any) => {
        const distA = Math.pow(parseFloat(a.lat) - lat, 2) + Math.pow(parseFloat(a.lon) - lon, 2);
        const distB = Math.pow(parseFloat(b.lat) - lat, 2) + Math.pow(parseFloat(b.lon) - lon, 2);
        return distA - distB;
      });
    }
    return (data || []).map((r: any) => {
      const a = r.address || {};
      const street = [a.house_number, a.road].filter(Boolean).join(' ');
      const city = a.city || a.town || a.village || a.suburb || '';
      const stateMapS: Record<string,string> = {
        'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA','Colorado':'CO','Connecticut':'CT',
        'Delaware':'DE','Florida':'FL','Georgia':'GA','Hawaii':'HI','Idaho':'ID','Illinois':'IL','Indiana':'IN','Iowa':'IA',
        'Kansas':'KS','Kentucky':'KY','Louisiana':'LA','Maine':'ME','Maryland':'MD','Massachusetts':'MA','Michigan':'MI',
        'Minnesota':'MN','Mississippi':'MS','Missouri':'MO','Montana':'MT','Nebraska':'NE','Nevada':'NV','New Hampshire':'NH',
        'New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND','Ohio':'OH','Oklahoma':'OK',
        'Oregon':'OR','Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD','Tennessee':'TN',
        'Texas':'TX','Utah':'UT','Vermont':'VT','Virginia':'VA','Washington':'WA','West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY'
      };
      const rawStateS = a.state_code || a.ISO3166_2_lvl4?.split('-')[1] || a['ISO3166-2-lvl4']?.split('-')[1] || '';
      const state = (rawStateS.length === 2 ? rawStateS : (stateMapS[a.state] || rawStateS.substring(0,2))).toUpperCase().substring(0,2);
      const name = r.name || r.display_name?.split(',')[0] || query;
      return { name, street, city, state, display: [name, street, city, state].filter(Boolean).join(', ') };
    }).filter((r: any) => r.street);
  } catch(e) { return []; }
}

async function sendPushToUser(userId: string, title: string, body: string, data: any = {}) {
  if (!supabase) return;
  try {
    const { data: tokens, error: tokenErr } = await supabase.from('push_tokens').select('token').eq('user_id', userId);
    if (tokenErr) { console.error('Push token fetch error:', tokenErr.message); return; }
    if (!tokens || tokens.length === 0) { console.log('No push tokens for user:', userId); return; }
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    if (!supabaseUrl) return;
    console.log('📤 Sending push to', tokens.length, 'tokens for user', userId);
    const res = await fetch(`${supabaseUrl}/functions/v1/send-push`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
      body: JSON.stringify({ tokens: tokens.map((t: any) => t.token), title, body, data }),
    });
    const result = await res.json();
    console.log('📤 Push result:', result);
  } catch(e) { console.error('Push send error:', e); }
}

// Send in-app + push + email notification
async function notifyUser(userId: string, type: string, title: string, body: string, data: any = {}) {
  if (!supabase) return;
  // 1. In-app bell notification (always)
  try { await supabase.from('notifications').insert({ user_id: userId, type, title, body, data }); } catch(e) {}
  // 2. Get user preferences
  const user = await getUserForNotify(userId);
  if (!user) return;
  // 3. Push notification
  if (user.notif_push !== false) {
    await sendPushToUser(userId, title, body, { type, ...data });
  }
  // 4. Email
  if (user.email_notifications !== false && user.email) {
    await sendEmail(user.email, user.full_name, title, body);
  }
}


// ─── PUSH NOTIFICATION REGISTRATION ──────────────────────────────────────────
async function registerPushToken(userId: string) {
  if (!supabase) return;
  try {
    const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    const FB_API_KEY = process.env.REACT_APP_FIREBASE_API_KEY;
    const FB_PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID;
    const FB_SENDER_ID = process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID;
    const FB_APP_ID = process.env.REACT_APP_FIREBASE_APP_ID;

    if (!VAPID_KEY || !FB_API_KEY) { console.log('Firebase not configured'); return; }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') { console.log('Notification permission denied'); return; }

    // Lazy-load Firebase to avoid build issues
    const firebase = await import('firebase/app');
    const { getMessaging, getToken } = await import('firebase/messaging');

    const firebaseConfig = {
      apiKey: FB_API_KEY,
      authDomain: `${FB_PROJECT_ID}.firebaseapp.com`,
      projectId: FB_PROJECT_ID,
      messagingSenderId: FB_SENDER_ID,
      appId: FB_APP_ID,
    };

    const apps = firebase.getApps();
    const app = apps.length === 0 ? firebase.initializeApp(firebaseConfig) : apps[0];
    const messaging = getMessaging(app);

    // Use the main service worker for FCM (already registered)
    let swReg = await navigator.serviceWorker.getRegistration('/');
    if (!swReg) {
      swReg = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
    }
    console.log('Using SW for FCM:', swReg.scope, swReg.active?.state);

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });

    if (!token) { console.log('No FCM token received'); return; }
    console.log('FCM token received:', token.substring(0, 30) + '...');

    await supabase.from('push_tokens').upsert({
      user_id: userId, token, platform: 'web',
    }, { onConflict: 'user_id,token' });
    console.log('✅ Push token saved');

    // Handle foreground messages (app is open/focused)
    const { onMessage } = await import('firebase/messaging');
    onMessage(messaging, (payload) => {
      console.log('📬 Foreground message:', payload);
      const title = payload.notification?.title || 'PoolRide';
      const body = payload.notification?.body || '';
      // Show browser notification even when app is open
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: '/logo192.png',
          badge: '/logo192.png',
        });
      }
    });
  } catch (e) {
    console.log('Push registration error:', e);
  }
}

export const supabase = SUPABASE_URL ? createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  }
}) : null;

// ─── LEAFLET MAP ─────────────────────────────────────────────────────────────
// Dynamically inject Leaflet CSS so no separate import needed
if (!document.getElementById('leaflet-css')) {
  const link = document.createElement('link');
  link.id = 'leaflet-css';
  link.rel = 'stylesheet';
  link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  document.head.appendChild(link);
}

// Fix default marker icons broken by webpack
function fixLeafletIcons(L: any) {
  delete (L.Icon.Default.prototype as any)._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  });
}

// MapView component — renders an OpenStreetMap with markers + route
function MapView({ center, zoom = 13, markers = [], routePoints = [], height = 280 }: {
  center: [number, number];
  zoom?: number;
  markers?: { lat: number; lng: number; label: string; color?: string }[];
  routePoints?: [number, number][];
  height?: number;
}) {
  const mapRef = React.useRef<HTMLDivElement>(null);
  const mapInstanceRef = React.useRef<any>(null);
  const markerRefs = React.useRef<any[]>([]);
  const routeRef = React.useRef<any>(null);
  const initializedRef = React.useRef(false);

  // Initialize map once
  React.useEffect(() => {
    if (initializedRef.current) return;
    if (!mapRef.current) return;

    initializedRef.current = true;

    import('leaflet').then(({ default: L }) => {
      // Guard: if leaflet already attached to this container, bail out
      if ((mapRef.current as any)._leaflet_id) return;

      fixLeafletIcons(L);
      const map = L.map(mapRef.current!, { zoomControl: true }).setView(center, zoom);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map);
      mapInstanceRef.current = map;

      // Initial markers
      markers.forEach(m => {
        const icon = L.divIcon({
          html: `<div style="background:${m.color||'#1a7a5e'};color:#fff;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">${m.label.slice(0,2).toUpperCase()}</div>`,
          className: '', iconSize: [32, 32] as any, iconAnchor: [16, 16] as any,
        });
        markerRefs.current.push(L.marker([m.lat, m.lng], { icon }).addTo(map).bindPopup(m.label));
      });

      if (routePoints.length > 1) {
        routeRef.current = L.polyline(routePoints, { color: '#1a7a5e', weight: 4, opacity: .8 }).addTo(map);
      }
    });

    // Cleanup on unmount
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        initializedRef.current = false;
        markerRefs.current = [];
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update markers + route when props change (after map is ready)
  React.useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    import('leaflet').then(({ default: L }) => {
      markerRefs.current.forEach(m => m.remove());
      markerRefs.current = [];

      markers.forEach(m => {
        const icon = L.divIcon({
          html: `<div style="background:${m.color||'#1a7a5e'};color:#fff;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">${m.label.slice(0,2).toUpperCase()}</div>`,
          className: '', iconSize: [32, 32] as any, iconAnchor: [16, 16] as any,
        });
        markerRefs.current.push(L.marker([m.lat, m.lng], { icon }).addTo(map).bindPopup(m.label));
      });

      if (markers.length > 0) {
        map.panTo([markers[0].lat, markers[0].lng], { animate: true, duration: 0.8 });
      }

      if (routeRef.current) { routeRef.current.remove(); routeRef.current = null; }
      if (routePoints.length > 1) {
        routeRef.current = L.polyline(routePoints, { color: '#1a7a5e', weight: 4, opacity: .8 }).addTo(map);
      }
    });
  }, [markers, routePoints]);

  return (
    <div
      ref={mapRef}
      style={{ height, borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.bdr}`, zIndex: 0, position: 'relative' }}
    />
  );
}

// ─── GEO HOOKS ───────────────────────────────────────────────────────────────
function useWindowWidth() {
  const [width, setWidth] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}

function useGeo() {
  const [states] = React.useState([
    {code:'FL',name:'Florida'},{code:'TX',name:'Texas'},{code:'OH',name:'Ohio'},
    {code:'GA',name:'Georgia'},{code:'NJ',name:'New Jersey'},
    {code:'NC',name:'North Carolina'},{code:'CA',name:'California'},
  ]);
  const [counties, setCounties] = React.useState<any[]>([]);
  const [cities, setCities] = React.useState<any[]>([]);
  const [schools, setSchools] = React.useState<any[]>([]);

  const loadCounties = async (stateCode: string) => {
    if (!supabase || !stateCode) return;
    const { data } = await supabase.from('counties').select('*').eq('state_code', stateCode).order('name');
    setCounties(data || []); setCities([]); setSchools([]);
  };
  const loadCities = async (countyId: string) => {
    if (!supabase || !countyId) return;
    const { data } = await supabase.from('cities').select('*').eq('county_id', countyId).order('name');
    setCities(data || []); setSchools([]);
  };
  const loadSchools = async (countyId: string, stateCode?: string, cityName?: string) => {
    if (!supabase) return;
    // First try by county_id
    if (countyId) {
      const { data } = await supabase.from('schools').select('*').eq('county_id', countyId).eq('active', true).order('name');
      if (data && data.length > 0) { setSchools(data); return; }
    }
    // Fallback: match by state + city
    if (stateCode) {
      let q = supabase.from('schools').select('*').eq('state_code', stateCode).eq('active', true);
      if (cityName) q = q.eq('city', cityName);
      const { data } = await q.order('name');
      setSchools(data || []);
    }
  };
  return { states, counties, cities, schools, loadCounties, loadCities, loadSchools };
}

// ─── THEME ────────────────────────────────────────────────────────────────────
// ── PARNIKA SYSTEMS BRAND ─────────────────────────────────────
const LOGO_URL = '/logo192.png';
// Module-level chat target — persists across component renders/unmounts
let pendingChatTarget: { id: string; name: string } | null = null;
let pendingGroupChatId: string | null = null; // group_id to open group chat for
let boardTrackingRole: 'driver' | 'rider' | null = null; // set before navigating to tracking from board
let boardRideContext: { destination: string; destAddress: string; pickupAddresses: string[]; acceptedNames: string[] } | null = null;

const COPYRIGHT = `© ${new Date().getFullYear()} Parnika, LLC. All rights reserved.`;

const C = {
  // School mode — Parnika orange
  school: '#e8650a', schoolL: '#fff0e6', schoolD: '#a8480a',
  // College mode — same orange as school
  college: '#e8650a', collegeL: '#fff0e6', collegeD: '#a8480a',
  activity: '#e8650a', activityL: '#fff0e6', activityD: '#a8480a',
  amber: '#b45309', amberL: '#fef3c7', amberD: '#78350f',
  coral: '#c2410c', coralL: '#fff7ed', coralD: '#7c2d12',
  blue: '#1d4ed8', blueL: '#eff6ff', blueD: '#1e3a8a',
  ink: '#1a1a1a', ink2: '#4a4a4a', ink3: '#9a9a9a',
  bg: '#faf8f5', sur: '#ffffff', sur2: '#f5f1ec', bdr: '#e8e0d8',
};

const avColors = [
  [C.schoolL, C.schoolD], [C.collegeL, C.collegeD],
  [C.amberL, C.amberD], [C.blueL, C.blueD], [C.coralL, C.coralD],
];
function avColor(name: string) { return avColors[name.charCodeAt(0) % avColors.length]; }
function initials(name: string) { return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2); }

// ─── CONTEXT ──────────────────────────────────────────────────────────────────
type Mode = 'school' | 'college' | 'activity';
interface Ctx {
  session: Session | null;
  mode: Mode;
  setMode: (m: Mode) => void;
  loading: boolean;
  profile: any;
  setProfile: (p: any) => void;
  pendingGroupMembers: any[];
  setPendingGroupMembers: (m: any[]) => void;
  activeGroupId: string | null;
  setActiveGroupId: (id: string | null) => void;
  activeChildSchool: string;
  setActiveChildSchool: (s: string) => void;
  activeChildGrade: string;
  setActiveChildGrade: (s: string) => void;
  activeChatUser: { id: string; name: string } | null;
  setActiveChatUser: (u: { id: string; name: string } | null) => void;
}
const AppCtx = createContext<Ctx>({ session: null, mode: 'school', setMode: () => {}, loading: true, profile: null, setProfile: () => {}, pendingGroupMembers: [], setPendingGroupMembers: () => {}, activeGroupId: null, setActiveGroupId: () => {}, activeChildSchool: '', setActiveChildSchool: () => {}, activeChildGrade: '', setActiveChildGrade: () => {}, activeChatUser: null, setActiveChatUser: () => {} });
const useApp = () => useContext(AppCtx);

// ─── MINI UI COMPONENTS ───────────────────────────────────────────────────────
const s: any = {
  btn: (bg: string, color = '#fff') => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 15px',
    borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    border: 'none', fontFamily: 'inherit', background: bg, color, transition: 'opacity .15s',
  } as React.CSSProperties),
  btnSm: (bg: string, color = '#fff') => ({
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px',
    borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    border: 'none', fontFamily: 'inherit', background: bg, color,
  } as React.CSSProperties),
  outlineBtn: (color: string) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 15px',
    borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    border: `1.5px solid ${color}`, fontFamily: 'inherit', background: '#fff', color,
  } as React.CSSProperties),
  card: { background: C.sur, border: `1px solid ${C.bdr}`, borderRadius: 14, padding: 18, marginBottom: 12 } as React.CSSProperties,
  cardSm: { background: C.sur, border: `1px solid ${C.bdr}`, borderRadius: 14, padding: 13, marginBottom: 12 } as React.CSSProperties,
  input: { border: `1px solid ${C.bdr}`, borderRadius: 9, padding: '8px 11px', fontSize: 13, fontFamily: 'inherit', color: C.ink, background: C.sur, outline: 'none', width: '100%' } as React.CSSProperties,
  label: { fontSize: 11, fontWeight: 600, color: C.ink2, textTransform: 'uppercase' as const, letterSpacing: '.4px', display: 'block', marginBottom: 4 },
  tag: (bg: string, color: string) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: bg, color } as React.CSSProperties),
  alert: (bg: string, color: string, bdr: string) => ({ borderRadius: 9, padding: '9px 12px', fontSize: 12, marginBottom: 11, borderLeft: `3px solid ${bdr}`, background: bg, color } as React.CSSProperties),
  row: { display: 'flex', alignItems: 'center', gap: 11, padding: '9px 0', borderBottom: `1px solid ${C.sur2}` } as React.CSSProperties,
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 } as React.CSSProperties,
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 8 } as React.CSSProperties,
};

function Av({ name, size = 36 }: { name: string; size?: number }) {
  const [bg, color] = avColor(name);
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: size * 0.34, flexShrink: 0 }}>
      {initials(name)}
    </div>
  );
}

function Tag({ bg, color, children }: { bg: string; color: string; children: React.ReactNode }) {
  return <span style={s.tag(bg, color)}>{children}</span>;
}

function Alert({ bg, color, bdr, children }: { bg: string; color: string; bdr: string; children: React.ReactNode }) {
  return <div style={s.alert(bg, color, bdr)}>{children}</div>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={s.label}>{label}</label>
      {children}
    </div>
  );
}

function Card({ children, border, shadow, style, padding }: { children: React.ReactNode; border?: string; shadow?: string; style?: React.CSSProperties; padding?: 'sm'|'md' }) {
  const padStyle = padding === 'sm' ? { padding: 13 } : {};
  return (
    <div style={{ ...s.card, ...padStyle, ...(border ? { borderColor: border, boxShadow: shadow } : {}), ...style }}>
      {children}
    </div>
  );
}

function Divider() { return <div style={{ height: 1, background: C.sur2, margin: '12px 0' }} />; }

function SLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: C.ink3, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>{children}</div>;
}

// ─── AUTH SCREEN ──────────────────────────────────────────────────────────────
function AuthScreen() {
  const [tab, setTab] = useState<'login' | 'signup' | 'forgot' | 'reset'>(() => {
    // Check if this is a password recovery link
    const hash = window.location.hash;
    if (hash.includes('type=recovery') || hash.includes('#reset-password')) return 'reset';
    // Also check URL params
    const params = new URLSearchParams(window.location.search);
    if (params.get('type') === 'recovery') return 'reset';
    return 'login';
  });
  const [email, setEmail] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Listen for hash change (PASSWORD_RECOVERY event sets #reset-password)
  React.useEffect(() => {
    const onHash = () => {
      if (window.location.hash === '#reset-password') setTab('reset');
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  async function handle() {
    setError(''); setSuccess('');
    if (tab === 'reset') {
      if (!password) { setError('Please enter a new password.'); return; }
      if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
      if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
      setLoading(true);
      const { error } = await supabase!.auth.updateUser({ password });
      if (error) setError(error.message);
      else {
        setSuccess('Password updated! You can now log in.');
        window.location.hash = '';
        setTimeout(() => setTab('login'), 2000);
      }
      setLoading(false);
      return;
    }
    if (tab === 'forgot') {
      if (!email) { setError('Please enter your email address.'); return; }
      setLoading(true);
      if (!supabase) { setError('Supabase not configured.'); setLoading(false); return; }
      const redirectTo = process.env.REACT_APP_SITE_URL || 'https://poolride-parnikallc.netlify.app';
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (error) setError(error.message);
      else setSuccess('Password reset link sent! Check your email.');
      setLoading(false);
      return;
    }
    if (!email || !password) { setError('Please fill in all fields.'); return; }
    setLoading(true);
    if (!supabase) { setError('Supabase not configured. Add .env.local file.'); setLoading(false); return; }
    if (tab === 'login') {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setError(error.message);
    } else {
      if (!name) { setError('Name is required.'); setLoading(false); return; }
      const redirectTo = process.env.REACT_APP_SITE_URL || 'https://poolride-parnikallc.netlify.app';
      const { error } = await supabase.auth.signUp({
        email, password,
        options: { data: { full_name: name }, emailRedirectTo: redirectTo }
      });
      if (error) setError(error.message);
      else setSuccess('Check your email to confirm your account, then log in!');
    }
    setLoading(false);
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 400 }}>

        {/* Parnika logo + app name */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img
            src={LOGO_URL}
            alt="Parnika Systems"
            style={{ height: 100, objectFit: 'contain', marginBottom: 14 }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <div style={{ fontWeight: 800, fontSize: 30, letterSpacing: -1, lineHeight: 1.1 }}>
            <span style={{ color: C.school }}>Pool</span><span style={{ color: C.college }}>Ride</span>
          </div>
          <div style={{ fontSize: 15, color: C.ink2, marginTop: 6, fontWeight: 600, letterSpacing: 0.3 }}>School · College · Activities Carpooling</div>
        </div>

        {/* Tab switcher — hide for forgot */}
        {tab !== 'forgot' && (
          <div style={{ display: 'flex', background: C.sur2, borderRadius: 20, padding: 3, gap: 2, marginBottom: 16 }}>
            {(['login', 'signup'] as const).map(t => (
              <button key={t} onClick={() => { setTab(t); setError(''); setSuccess(''); }} style={{
                flex: 1, padding: '8px 0', borderRadius: 18, border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                background: tab === t ? C.sur : 'transparent',
                color: tab === t ? C.ink : C.ink2,
                boxShadow: tab === t ? '0 1px 3px rgba(0,0,0,.1)' : undefined, transition: 'all .15s'
              }}>
                {t === 'login' ? 'Log in' : 'Sign up'}
              </button>
            ))}
          </div>
        )}

        {/* Form card */}
        <div style={{ background: C.sur, border: `1px solid ${C.bdr}`, borderRadius: 14, padding: 20 }}>
          {tab === 'reset' && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Set new password</div>
              <div style={{ fontSize: 12, color: C.ink3 }}>Enter and confirm your new password below.</div>
            </div>
          )}
          {tab === 'forgot' && (
            <div style={{ marginBottom: 14 }}>
              <button onClick={() => { setTab('login'); setError(''); setSuccess(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.school, fontSize: 13, fontWeight: 600, padding: 0, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                ← Back to login
              </button>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Reset your password</div>
              <div style={{ fontSize: 12, color: C.ink3 }}>Enter your email and we'll send you a reset link.</div>
            </div>
          )}

          {error   && <Alert bg={C.coralL} color={C.coralD} bdr={C.coral}>{error}</Alert>}
          {success && <Alert bg={C.schoolL} color={C.schoolD} bdr={C.school}>{success}</Alert>}

          {tab === 'signup' && (
            <Field label="Full name">
              <input style={s.input} value={name} onChange={e => setName(e.target.value)} placeholder="Sarah Mitchell"
                onKeyDown={e => e.key === 'Enter' && handle()} />
            </Field>
          )}
          {tab !== 'forgot' && tab !== 'reset' && <Field label="Email">
            <input style={s.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com"
              onKeyDown={e => e.key === 'Enter' && handle()} />
          </Field>}
          {tab === 'forgot' && <Field label="Email">
            <input style={s.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com"
              onKeyDown={e => e.key === 'Enter' && handle()} />
          </Field>}
          {tab === 'reset' && (
            <>
              <Field label="New password">
                <input style={s.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters"
                  onKeyDown={e => e.key === 'Enter' && handle()} />
              </Field>
              <Field label="Confirm new password">
                <input style={s.input} type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat new password"
                  onKeyDown={e => e.key === 'Enter' && handle()} />
              </Field>
            </>
          )}
          {tab !== 'forgot' && tab !== 'reset' && (
            <Field label="Password">
              <input style={s.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••"
                onKeyDown={e => e.key === 'Enter' && handle()} />
            </Field>
          )}

          {tab === 'signup' && (
            <div style={{ marginTop: 12, marginBottom: 4 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" id="terms-check" style={{ marginTop: 3, accentColor: C.school, width: 16, height: 16, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: C.ink3, lineHeight: 1.5 }}>
                  I have read and agree to the{' '}
                  <span onClick={e => { e.preventDefault(); setShowTerms(true); }}
                    style={{ color: C.school, textDecoration: 'underline', cursor: 'pointer' }}>
                    Carpool Participation & Liability Waiver
                  </span>
                  {' '}and{' '}
                  <span onClick={e => { e.preventDefault(); setShowPrivacy(true); }}
                    style={{ color: C.school, textDecoration: 'underline', cursor: 'pointer' }}>
                    Privacy Policy
                  </span>.
                  I understand PoolRide is a coordination platform only and assumes no liability for transportation activities.
                </span>
              </label>
            </div>
          )}
          <button onClick={() => {
            if (tab === 'signup') {
              const cb = document.getElementById('terms-check') as HTMLInputElement;
              if (!cb?.checked) { setError('Please accept the Terms & Liability Waiver to continue.'); return; }
            }
            handle();
          }} disabled={loading} style={{
            ...s.btn(tab === 'signup' ? C.college : C.school),
            width: '100%', justifyContent: 'center', marginTop: 8, opacity: loading ? 0.7 : 1
          }}>
            {loading ? 'Please wait…' : tab === 'login' ? 'Log in →' : tab === 'signup' ? 'Create account →' : tab === 'reset' ? 'Set new password →' : 'Send reset link →'}
          </button>

          {tab === 'login' && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button onClick={() => { setTab('forgot'); setError(''); setSuccess(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: C.ink3, textDecoration: 'underline' }}>
                Forgot your password?
              </button>
            </div>
          )}
        </div>

      {/* Terms & Liability Waiver Modal */}
      {showPrivacy && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 640, maxHeight: '90vh', overflow: 'auto', padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ flex: 1, fontWeight: 800, fontSize: 17 }}>Privacy Policy</div>
              <button onClick={() => setShowPrivacy(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: C.ink3 }}>✕</button>
            </div>
            <div style={{ fontSize: 12, color: C.ink2, lineHeight: 1.7 }}>
              {[
                ['1. Information We Collect', 'PoolRide collects information you provide during registration, including your name, email address, phone number, home address, children\'s names and grades, school name, and vehicle details. We also collect usage data such as group memberships, schedule entries, and in-app messages to operate the service.'],
                ['2. How We Use Your Information', 'Your information is used solely to operate the PoolRide platform — to match families at the same school, coordinate carpool groups, generate driving schedules, and enable in-app communication. We do not sell, rent, or share your personal information with third parties for marketing purposes.'],
                ['3. Information Shared With Other Users', 'When you join or create a carpool group, your name, children\'s details, vehicle information, and preferred driving days are visible to other members of your group. Your phone number is visible to group members to facilitate coordination. Your home address may be shown to group members for route planning purposes.'],
                ['4. Background Check Data', 'If you submit a background check through the app, results are stored securely and only a verification status badge (Verified / Pending / Unverified) is displayed to other users. Raw background check data is not shared with other users.'],
                ['5. Push Notifications & Communications', 'With your consent, PoolRide may send push notifications and emails about carpool activity, group invitations, schedule updates, and drive swap requests. You may manage notification preferences in your account settings at any time.'],
                ['6. Data Storage & Security', 'Your data is stored on secured cloud infrastructure (Supabase). We implement reasonable technical and organizational safeguards. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.'],
                ['7. Children\'s Privacy', 'PoolRide is designed for use by parents and guardians. We collect children\'s names and grades solely to facilitate school carpool matching. We do not knowingly collect data directly from children. Parents are responsible for all decisions made on behalf of their children within the app.'],
                ['8. Data Retention', 'Your account data is retained for as long as your account is active. You may request deletion of your account and associated data by contacting support. Group history and schedule data may be retained in anonymized form for platform improvement purposes.'],
                ['9. Your Rights', 'You have the right to access, correct, or request deletion of your personal data. To exercise these rights, contact us through the in-app Support screen. We will respond to valid requests within 30 days.'],
                ['10. Changes to This Policy', 'We may update this Privacy Policy from time to time. Continued use of PoolRide after changes are posted constitutes your acceptance of the updated policy. Last updated: May 2026.'],
              ].map(([title, body]) => (
                <div key={title as string} style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.ink, marginBottom: 4 }}>{title}</div>
                  <div style={{ color: C.ink3 }}>{body}</div>
                </div>
              ))}
              <div style={{ background: C.schoolL, borderRadius: 8, padding: 12, marginTop: 8, fontSize: 12, color: C.schoolD, fontWeight: 600 }}>
                By creating an account, you agree to this Privacy Policy. For questions, contact us via the Support screen in the app.
              </div>
            </div>
            <button onClick={() => setShowPrivacy(false)} style={{ ...s.btn(C.school), width: '100%', justifyContent: 'center', marginTop: 16 }}>
              Got it — Close
            </button>
          </div>
        </div>
      )}

      {showTerms && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 640, maxHeight: '90vh', overflow: 'auto', padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ flex: 1, fontWeight: 800, fontSize: 17 }}>Carpool Participation & Liability Waiver</div>
              <button onClick={() => setShowTerms(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: C.ink3 }}>✕</button>
            </div>
            <div style={{ fontSize: 12, color: C.ink2, lineHeight: 1.7 }}>
              {[
                ['1. Platform Role Only', 'PoolRide by Parnika Systems ("the App") is a technology platform that enables parents, guardians, and authorized users to coordinate voluntary carpool and transportation arrangements. The App does not provide transportation services, operate as a transportation carrier, employ drivers, own vehicles, or supervise any ride or transportation activity. PoolRide strictly facilitates communication and coordination between users and assumes no responsibility for the execution, safety, or outcome of any transportation arrangement.'],
                ['2. User Responsibility & Voluntary Participation', 'All carpooling activities are voluntary and undertaken entirely at your own risk. Transportation arrangements are made solely between users at their own discretion. You are solely responsible for: evaluating and verifying the identity and trustworthiness of other participants; validating driver licenses, insurance coverage, and vehicle condition; determining the appropriateness of transporting or allowing minors to be transported; and complying with all applicable laws, school policies, traffic regulations, child safety requirements, and insurance obligations.'],
                ['3. No Guarantees or Verification Warranty', 'PoolRide does not guarantee, verify, certify, endorse, or warrant the safety, suitability, legality, or qualifications of any user, driver, passenger, vehicle, or transportation arrangement. Any background checks, identity verification, ratings, or screening features are offered solely for convenience and informational purposes and do not constitute a warranty or assurance of safety.'],
                ['4. Assumption of Risk', 'By using PoolRide, you voluntarily assume all risks associated with participation in carpools, including transporting or permitting transportation of yourself or minors. Risks include but are not limited to vehicle accidents, interactions with other users, and participation in informal transportation arrangements.'],
                ['5. Liability Disclaimer', 'To the maximum extent permitted by law, Parnika Systems LLC, its owners, affiliates, employees, contractors, officers, and partners shall not be liable for any claims, damages, losses, injuries, or expenses arising from: vehicle accidents or transportation incidents; bodily injury, death, or property damage; criminal acts or misconduct by users; child safety incidents or unauthorized pickups; missed pickups, delays, or miscommunications; inaccuracies in background checks or verification; technical failures, GPS errors, or notification failures; or any act or omission before, during, or after any ride.'],
                ['6. Indemnification', 'You agree to indemnify, defend, and hold harmless Parnika Systems LLC and its affiliates from and against any claims, damages, losses, liabilities, costs, or expenses (including attorneys\' fees) arising from: your use of the App; participation in any carpool activity; violation of these Terms; your negligence or misconduct; or any claims involving minors you transported or permitted to be transported.'],
                ['7. Platform Rights', 'Parnika Systems LLC reserves the right to suspend or terminate user accounts, request verification documents, restrict access to features, or remove users from the platform at its sole discretion without notice.'],
                ['8. Children & Minor Safety', 'Parents and guardians are solely responsible for decisions regarding the transportation of minors. By registering a child in the App, you represent that you are the legal parent or guardian of that child and have full authority to make transportation decisions on their behalf. You accept full responsibility for any transportation arrangement involving your child.'],
                ['9. Insurance Requirements', 'By using PoolRide as a driver, you represent that you maintain valid auto insurance as required by your state. PoolRide strongly recommends that drivers obtain a rideshare endorsement or additional coverage. Personal auto insurance policies may not cover transportation arranged through apps. PoolRide assumes no liability for insurance gaps or coverage disputes.'],
                ['10. Governing Law', 'These Terms are governed by the laws of the State of Florida. Any disputes shall be resolved in the courts of Hillsborough County, Florida. If any provision is found unenforceable, the remaining provisions remain in full effect.'],
              ].map(([title, body]) => (
                <div key={title as string} style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.ink, marginBottom: 4 }}>{title}</div>
                  <div style={{ color: C.ink3 }}>{body}</div>
                </div>
              ))}
              <div style={{ background: C.schoolL, borderRadius: 8, padding: 12, marginTop: 8, fontSize: 12, color: C.schoolD, fontWeight: 600 }}>
                By creating an account or using PoolRide, you confirm that you have read, understood, and agree to be bound by this Carpool Participation & Liability Waiver and the full Terms & Conditions. Last updated: May 2026.
              </div>
            </div>
            <button onClick={() => setShowTerms(false)} style={{ ...s.btn(C.school), width: '100%', justifyContent: 'center', marginTop: 16 }}>
              I understand — Close
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({ nav, refreshKey, pendingInvitations = 0 }: { nav: (p: string) => void; refreshKey?: number; pendingInvitations?: number }) {
  const { mode, setMode, profile, session, setActiveGroupId, setActiveChildSchool, setActiveChildGrade } = useApp();
  const name = profile?.full_name?.split(' ')[0] || 'there';
  const isSchool   = mode === 'school';
  const isActivity = mode === 'activity';
  const mc = isSchool ? C.school : isActivity ? C.activity : C.college;

  // Load all children (primary from profile + extras from children table)
  const [allChildren, setAllChildren] = React.useState<any[]>([]);
  const [userGroups, setUserGroups] = React.useState<any[]>([]);

  // Load all children from all sources
  React.useEffect(() => {
    if (!session || !supabase) return;
    (async () => {
      // Always start with profile's primary child (guaranteed to exist)
      const primary: any[] = [];
      if (profile?.child_name) {
        primary.push({ id: 'primary', name: profile.child_name, grade: profile.child_grade || '', school: profile.school_name || '' });
      }

      // Load extra children from children table
      const { data: childrenData } = await supabase.from('children')
        .select('*').eq('parent_id', session.user.id);
      const extras = (childrenData || [])
        .filter((ch: any) => ch.name !== profile?.child_name)
        .map((ch: any) => ({ id: ch.id, name: ch.name, grade: ch.grade || '', school: ch.school_name || ch.school || '' }));

      const all = [...primary, ...extras];

      // Load groups and match to children via children_included
      const { data: gmData } = await supabase.from('group_members')
        .select('group_id, children_included')
        .eq('user_id', session.user.id);
      if (gmData && gmData.length > 0) {
        setUserGroups(gmData);
        const groups: Record<string, any> = {};
        all.forEach((child: any) => {
          // Match by children_included array (most accurate)
          const byName = gmData.find((gm: any) =>
            Array.isArray(gm.children_included) && gm.children_included.includes(child.name)
          );
          if (byName) { groups[child.id] = byName; return; }
          // Fallback: primary child gets first group if children_included is empty
          if (child.id === 'primary' && !byName) {
            const unmatched = gmData.find((gm: any) =>
              !Object.values(groups).includes(gm) &&
              (!gm.children_included || gm.children_included.length === 0)
            );
            if (unmatched) groups[child.id] = unmatched;
          }
        });
        setChildGroups(groups);
      }

      setAllChildren(all);
    })();
  }, [profile?.child_name, profile?.school_name, session?.user?.id, refreshKey]);


  // childGroups is set directly in the async effect above
  const [childGroups, setChildGroups] = React.useState<Record<string, any>>({});

  // Rides in next 10 days + next ride date
  const [ridesNext10, setRidesNext10] = React.useState<number>(0);
  const [nextRideDate, setNextRideDate] = React.useState<string>('—');

  React.useEffect(() => {
    if (!session || !supabase) return;
    (async () => {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const future = new Date(); future.setDate(today.getDate() + 10);
      const futureStr = future.toISOString().split('T')[0];
      const { data: entries } = await supabase.from('schedule_entries')
        .select('date, ride_type, duty')
        .eq('user_id', session.user.id)
        .gte('date', todayStr)
        .lte('date', futureStr)
        .order('date');
      if (entries && entries.length > 0) {
        setRidesNext10(entries.length);
        const first = entries[0];
        const d = new Date(first.date + 'T00:00:00');
        const dateStr = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        const rt = first.ride_type || first.duty || '';
        const rideLabel = rt === 'pickup' ? '🌆 Pickup' : rt === 'dropoff' ? '🌅 Drop-off' : rt === 'both' ? '🌅🌆 Both' : '';
        setNextRideDate(rideLabel ? `${dateStr} ${rideLabel}` : dateStr);
      } else {
        setRidesNext10(0);
        setNextRideDate('None');
      }
    })();
  }, [session?.user?.id, refreshKey]);


  const stats = isActivity
    ? [
        ['0', 'Groups joined', mc],
        ['0', 'Activities', mc],
        ['0', 'Drives done', mc],
        [profile?.has_car ? 'Driver' : 'Rider', 'Your role', C.amber],
      ]
    : isSchool
    ? [
        [String(allChildren.length || 0), 'Children', mc],
        [Object.keys(childGroups).length + '/' + allChildren.length, 'In groups', mc],
        [String(ridesNext10), 'Rides next 10 days', mc],
        [nextRideDate, 'Next ride', mc],
      ]
    : [
        ['0', 'Trips posted', mc],
        ['0', 'Rides taken', mc],
        [profile?.rating ? profile.rating + '★' : '—', 'Rating', C.amber],
        [profile?.university ? profile.university.split(' ')[1] || profile.university.split(' ')[0] : '—', 'University', mc],
      ];

  return (
    <div>
      <div style={{ background: mc, borderRadius: 14, padding: 20, marginBottom: 13, position: 'relative', overflow: 'hidden', color: '#fff' }}>
        <div style={{ position: 'absolute', right: -18, top: -18, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,.1)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, opacity: .8, marginBottom: 5 }}>{isSchool ? 'School Carpooling' : isActivity ? 'Activity Carpooling' : 'College Rides'}</div>
          <div style={{ fontSize: 21, fontWeight: 800, marginBottom: 4 }}>Good morning, {name}! 👋</div>
          <div style={{ fontSize: 13, opacity: .85 }}>
            {isActivity
              ? <span>Coordinate carpool groups for <strong>kids' activities</strong> — dance, swimming, soccer & more.</span>
              : isSchool
                ? (allChildren.length > 0
                    ? <span>{allChildren.length} {allChildren.length === 1 ? 'child' : 'children'} · {allChildren.map(c => c.school).filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(', ')}</span>
                    : <span>Complete your profile to get started.</span>)
                : (profile?.university ? <span>University: <strong>{profile.university}</strong></span> : <span>Complete your profile to get started.</span>)
            }
          </div>
          <div style={{ marginTop: 11, display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
            <button onClick={() => nav(isActivity ? 'activities' : isSchool ? 'schedule' : 'browse')} style={{ background: 'rgba(255,255,255,.2)', color: '#fff', border: '1px solid rgba(255,255,255,.3)', padding: '7px 14px', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: 12 }}>
              {isActivity ? 'Browse activities →' : isSchool ? 'View schedule →' : 'Browse rides →'}
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 9, marginBottom: 13 }}>
        {stats.map(([n, l, c]) => (
          <div key={l} style={{ background: C.sur, border: `1px solid ${C.bdr}`, borderRadius: 9, padding: 11, textAlign: 'center', display: 'flex', flexDirection: 'column' as const, justifyContent: 'space-between', minHeight: 64 }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ fontSize: (n as string).length > 12 ? 10 : (n as string).length > 6 ? 12 : 20, fontWeight: 800, color: c as string, lineHeight: 1.3 }}>{n}</div>
            </div>
            <div style={{ fontSize: 11, color: C.ink3, marginTop: 4 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Pending invitations banner */}
      {pendingInvitations > 0 && (
        <div onClick={() => nav('invitations')}
          style={{ background: C.schoolL, border: `1.5px solid ${C.school}`, borderRadius: 12, padding: '12px 16px', marginBottom: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 24 }}>✉️</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.schoolD }}>
              {pendingInvitations} pending carpool invitation{pendingInvitations > 1 ? 's' : ''}
            </div>
            <div style={{ fontSize: 12, color: C.schoolD }}>Tap to view and accept or decline</div>
          </div>
          <div style={{ color: C.school, fontWeight: 700 }}>→</div>
        </div>
      )}

      {/* Neighborhood Board — visible on all modes */}
      <div onClick={() => nav('board')} style={{ background: C.sur, border: `1px solid ${C.bdr}`, borderRadius: 12, padding: '12px 16px', marginBottom: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 28 }}>📋</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Neighborhood Board</div>
          <div style={{ fontSize: 12, color: C.ink3 }}>Post or find one-off rides, errands & local help</div>
        </div>
        <div style={{ color: C.school, fontSize: 16, fontWeight: 700 }}>→</div>
      </div>

      {/* School mode: show one card per child */}
      {isSchool && (
        <>
          {allChildren.length === 0 ? (
            <Card>
              <div style={{ textAlign: 'center', padding: 16 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>👨‍👧</div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>No children added yet</div>
                <div style={{ fontSize: 13, color: C.ink3, marginBottom: 12 }}>Add your children's details to find carpool groups.</div>
                <button onClick={() => nav('editprofile')} style={s.btn(C.school)}>Set up profile →</button>
              </div>
            </Card>
          ) : (
            allChildren.map((child) => {
              const group = childGroups[child.id];
              return (
                <div key={child.id} style={{ marginBottom: 12 }}>
                  <SLabel>{child.name} — {child.school || 'School not set'}</SLabel>
                  <div style={s.cardSm}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 38, height: 38, borderRadius: '50%', background: C.schoolL, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>👦</div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{child.name}</div>
                        <div style={{ fontSize: 11, color: C.ink3 }}>{child.grade}{child.school ? ` · ${child.school}` : ''}</div>
                      </div>
                      {group && <span style={{ ...s.tag(C.schoolL, C.schoolD), marginLeft: 'auto' }}>In group ✓</span>}
                    </div>
                    {group ? (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const }}>
                        <button onClick={() => nav('schedule')} style={{ ...s.btnSm(C.schoolL, C.schoolD), flex: 1, justifyContent: 'center' }}>📅 Schedule</button>
                        <button onClick={() => { setActiveGroupId(group.group_id); nav('group'); }} style={{ ...s.btnSm(C.schoolL, C.schoolD), flex: 1, justifyContent: 'center' }}>👨‍👩‍👧 Group</button>
                        <button onClick={() => { boardTrackingRole = null; nav('tracking'); }} style={{ ...s.btnSm(C.school), flex: 1, justifyContent: 'center' }}>🚗 Track</button>
                        <button onClick={async () => {
                          if (!window.confirm(`Remove ${child.name} from their carpool group?\n\nOther group members will be notified.\nYou can rejoin later from the Match screen.`)) return;
                          if (!supabase || !session) return;
                          const gid = group.group_id;
                          console.log('Leaving group:', gid, 'user:', session.user.id);
                          // Get other members before deleting
                          const { data: otherMembers, error: fetchErr } = await supabase.from('group_members')
                            .select('user_id').eq('group_id', gid).neq('user_id', session.user.id);
                          if (fetchErr) console.error('Fetch members error:', fetchErr.message);
                          console.log('Other members to notify:', otherMembers?.length);
                          // Delete membership
                          const { error } = await supabase.from('group_members')
                            .delete()
                            .eq('user_id', session.user.id)
                            .eq('group_id', gid);
                          if (error) { alert('Error leaving group: ' + error.message); console.error('Leave error:', error); return; }
                          console.log('✅ Left group successfully');
                          // Notify remaining members
                          for (const om of (otherMembers || [])) {
                            await notifyUser(om.user_id, 'member_left', '👋 Family left the group',
                              `${profile?.full_name} (${child.name}) has left your carpool group. Check your schedule for any changes needed.`,
                              { group_id: gid, child_name: child.name });
                          }
                          setUserGroups(prev => prev.filter((g: any) => g.group_id !== gid));
                          setChildGroups(prev => { const n = { ...prev }; delete n[child.id]; return n; });
                        }} style={{ ...s.btnSm('#fff5f5', '#b91c1c'), border: '1px solid #fca5a5', fontSize: 11 }}>
                          ✕ Leave
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => {
                        setActiveChildSchool(child.school || '');
                        setActiveChildGrade(child.grade || '');
                        nav('match');
                      }} style={{ ...s.btn(C.school), width: '100%', justifyContent: 'center' }}>
                        Find carpool group →
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
          {/* College rides — only show on college/activity mode, not school */}
          {!isSchool && (
          <div style={{ marginBottom: 12 }}>
            <SLabel>College rides</SLabel>
            <div style={s.cardSm}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 28 }}>🎓</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>College mode</div>
                  <div style={{ fontSize: 11, color: C.ink3 }}>For longer trips home</div>
                </div>
              </div>
              <button onClick={() => { setMode('college'); nav('browse'); }} style={{ ...s.btn(C.college), width: '100%', justifyContent: 'center' }}>Browse college rides →</button>
            </div>
          </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── REGISTER SCREEN ──────────────────────────────────────────────────────────
function RegisterScreen({ nav }: { nav: (p: string) => void }) {
  const { mode, session, setProfile } = useApp();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const geo = useGeo();

  // Pre-populate from auth session
  const signupEmail = session?.user?.email || '';
  const signupName = session?.user?.user_metadata?.full_name || '';

  // Multiple children support
  const [children, setChildren] = useState([
    { name: '', grade: 'Grade 3', state: '', county: '', city: '', school: '' }
  ]);

  const addChild = () => setChildren(c => [...c, { name: '', grade: 'Grade 3', state: '', county: '', city: '', school: '' }]);
  const removeChild = (i: number) => setChildren(c => c.filter((_, idx) => idx !== i));
  const updChild = (i: number, k: string, v: string) => setChildren(c => c.map((ch, idx) => idx === i ? { ...ch, [k]: v } : ch));

  const [f, setF] = useState({
    name: signupName,
    phone: '',
    address: '',
    addrStreet: '',
    addrCity: '',
    addrState: '',
    addrZip: '',
    // College fields
    uni: 'University of Florida', sid: '', major: '', hometown: '',
    // Vehicle
    hasCar: 'yes', seats: '5', make: '', color: '', plate: '',
    days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    dropoffDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    pickupDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    notes: '',
    bgCheck: 'none', // 'none', 'requested', 'skipped'
  });
  const [addrWarned, setAddrWarned] = React.useState(false); // track if user already confirmed warning
  const upd = (k: string, v: any) => setF(x => ({ ...x, [k]: v }));
  const toggleDay = (d: string) => upd('days', f.days.includes(d) ? f.days.filter((x: string) => x !== d) : [...f.days, d]);
  const mc = mode === 'school' ? C.school : C.college;

  const steps = mode === 'school' ? ['Profile', 'Children', 'Vehicle'] : ['Profile', 'Vehicle', 'Preferences'];

  const StepBar = () => (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
      {steps.map((st, i) => {
        const n = i + 1; const done = n < step; const act = n === step;
        return (
          <React.Fragment key={st}>
            {i > 0 && <div style={{ flex: 1, height: 1, background: C.bdr, margin: '0 8px' }} />}
            <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, background: done ? mc : act ? mc : C.sur2, color: done || act ? '#fff' : C.ink3, boxShadow: act ? `0 0 0 4px ${mode === 'school' ? C.schoolL : C.collegeL}` : undefined }}>{done ? '✓' : n}</div>
            <span style={{ fontSize: 11, fontWeight: 600, marginLeft: 5, color: act ? (mode === 'school' ? C.schoolD : C.collegeD) : C.ink3 }}>{st}</span>
          </React.Fragment>
        );
      })}
    </div>
  );

  async function save() {
    if (!session || !supabase) { nav('dashboard'); return; }
    setError('');

    // ── VALIDATION ────────────────────────────────────────────────
    if (!f.name.trim()) { scrollToField('field-name', 'Full name is required.', setError); return; }
    if (!f.phone.trim()) { scrollToField('field-phone', 'Phone number is required.', setError); return; }
    if (!f.addrStreet.trim()) { scrollToField('field-addrStreet', 'Street address is required.', setError); return; }
    if (!f.addrCity.trim()) { scrollToField('field-addrCity', 'City is required.', setError); return; }
    if (!f.addrState.trim() || f.addrState.length !== 2) { scrollToField('field-addrState', 'State is required (2 letters, e.g. FL).', setError); return; }
    if (!f.addrZip.trim() || f.addrZip.length !== 5) { scrollToField('field-addrZip', 'ZIP code is required (5 digits).', setError); return; }

    // Address already validated on step 1 Next button

    // Validate children
    if (mode === 'school') {
      for (let i = 0; i < children.length; i++) {
        const ch = children[i];
        if (!ch.name.trim()) { scrollToField(`child-name-${i}`, `Child ${i + 1}: name is required.`, setError); return; }
        if (!ch.school) { scrollToField(`child-school-${i}`, `Child ${i + 1} (${ch.name || 'unnamed'}): school is required.`, setError); return; }
        if (!ch.grade) { scrollToField(`child-grade-${i}`, `Child ${i + 1} (${ch.name || 'unnamed'}): grade is required.`, setError); return; }
      }
    }

    if (f.bgCheck === 'none') { scrollToField('bg-check-section', 'Please choose the background verification option.', setError); return; }

    // Vehicle validation if driver
    if (f.hasCar === 'yes') {
      if (!f.make.trim()) { scrollToField('field-make', 'Car make & model is required for drivers.', setError); return; }
      if (!f.color.trim()) { scrollToField('field-color', 'Car color is required for drivers.', setError); return; }
      if (!f.plate.trim()) { scrollToField('field-plate', 'License plate is required for drivers.', setError); return; }
      // Basic plate format validation: letters and numbers, 2-8 chars
      const plateClean = f.plate.replace(/[-\s]/g, '');
      if (!/^[A-Z0-9]{2,8}$/.test(plateClean)) {
        scrollToField('field-plate', 'Invalid license plate format. Use letters and numbers only (e.g. ABC-1234).', setError); return;
      }
    }

    setSaving(true);

    // Primary child (first in list) goes to main profile for matching
    const primaryChild = children[0];

    const { data, error } = await supabase.from('profiles').upsert({
      id: session.user.id,
      full_name: f.name || signupName,
      phone: f.phone,
      address: [f.addrStreet, f.addrCity, f.addrState, f.addrZip].filter(Boolean).join(', '),
      mode,
      avatar_initials: (f.name || signupName).split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2),
      school_name: primaryChild?.school || '',
      child_name: primaryChild?.name || '',
      child_grade: primaryChild?.grade || '',
      university: f.uni, student_id: f.sid, major_year: f.major, hometown: f.hometown,
      has_car: f.hasCar !== 'no',
      car_make_model: f.make, car_color: f.color,
      license_plate: f.plate, total_seats: parseInt(f.seats),
      preferred_days: f.days, notes: f.notes,
      dropoff_days: f.dropoffDays,
      pickup_days: f.pickupDays,
      bg_check_status: f.bgCheck === 'requested' ? 'pending' : 'not_started',
      academic_year: '2025-2026',
    }).select().single();

    if (error) { setError(error.message); setSaving(false); return; }

    // Save additional children to a separate table if more than 1
    if (children.length > 1) {
      for (let i = 1; i < children.length; i++) {
        const ch = children[i];
        if (!ch.name) continue;
        try {
          await supabase.from('children').upsert({
            parent_id: session.user.id,
            name: ch.name,
            grade: ch.grade,
            school_name: ch.school,
          });
        } catch (_) {}
      }
    }

    setProfile(data);
    setSaving(false);
    setStep('done' as any);
  }

  if (step === ('done' as any)) return (
    <div style={{ textAlign: 'center', padding: '34px 16px' }}>
      <div style={{ fontSize: 50, marginBottom: 14 }}>🎉</div>
      <div style={{ fontSize: 21, fontWeight: 800, marginBottom: 8 }}>You're registered!</div>
      <div style={{ fontSize: 13, color: C.ink3, marginBottom: 20 }}>{mode === 'school' ? 'Head to Match to find carpool families near your school.' : 'Browse rides or post your first trip!'}</div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' as const }}>
        <button onClick={() => nav(mode === 'school' ? 'match' : 'browse')} style={s.btn(mc)}>{mode === 'school' ? 'Find carpool families →' : 'Browse rides →'}</button>
      </div>
    </div>
  );

  return (
    <div>
      <StepBar />
      {error && <Alert bg={C.coralL} color={C.coralD} bdr={C.coral}>{error}</Alert>}

      {step === 1 && (
        <Card>
          {/* Read-only from signup */}
          <div style={{ marginBottom: 12 }}>
            <label style={s.label}>Email (from sign up)</label>
            <div style={{ ...s.input, background: C.sur2, color: C.ink3 }}>{signupEmail}</div>
          </div>
          <div style={s.grid2}>
            <div style={{ marginBottom: 12 }}>
              <label style={s.label}>Full name <span style={{ color: C.coral }}>*</span></label>
              <input id="field-name" style={{ ...s.input, borderColor: !f.name ? C.coral : undefined }}
                value={f.name} onChange={e => upd('name', e.target.value)} placeholder="Your full name" />
              {signupName && f.name === signupName &&
                <div style={{ fontSize: 11, color: C.ink3, marginTop: 3 }}>Auto-filled from sign up</div>}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={s.label}>Phone number <span style={{ color: C.coral }}>*</span></label>
              <input id="field-phone" style={{ ...s.input, borderColor: !f.phone ? C.coral : undefined }}
                value={f.phone} onChange={e => upd('phone', e.target.value)} placeholder="(813) 555-0198" type="tel" />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={s.label}>Home address <span style={{ color: C.coral }}>*</span></label>
            <input id="field-addrStreet" style={{ ...s.input, marginBottom: 6, borderColor: !f.addrStreet ? C.coral : undefined }}
              value={f.addrStreet} onChange={e => upd('addrStreet', e.target.value)} placeholder="Street address e.g. 123 Maple Grove Dr" />
            <div style={{ display: 'flex', gap: 6 }}>
              <input id="field-addrZip" style={{ ...s.input, width: 90, borderColor: !f.addrZip ? C.coral : undefined }}
                value={f.addrZip} onChange={e => {
                  const zip = e.target.value.replace(/\D/g,'').slice(0,5);
                  upd('addrZip', zip);
                  if (zip.length === 5) {
                    upd('addrCity', '⏳'); upd('addrState', '');
                    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
                    fetch(`${supabaseUrl}/functions/v1/zip-lookup`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
                      body: JSON.stringify({ zip }),
                    }).then(r => r.ok ? r.json() : null).then(data => {
                      if (data?.city) { upd('addrCity', data.city); upd('addrState', data.state); }
                      else { upd('addrCity', ''); }
                    }).catch(() => { upd('addrCity', ''); });
                  }
                }} placeholder="ZIP" maxLength={5} />
              <input id="field-addrCity" style={{ ...s.input, flex: 2, borderColor: !f.addrCity ? C.coral : undefined }}
                value={f.addrCity} onChange={e => upd('addrCity', e.target.value)} placeholder="City" />
              <input id="field-addrState" style={{ ...s.input, width: 55, borderColor: !f.addrState ? C.coral : undefined, textTransform: 'uppercase' as const }}
                value={f.addrState} onChange={e => upd('addrState', e.target.value.toUpperCase())} placeholder="ST" maxLength={2} />
            </div>
            <div style={{ fontSize: 11, color: C.ink3, marginTop: 4 }}>Type your ZIP — city and state auto-fill instantly</div>
          </div>

          {mode === 'college' && (
            <div style={s.grid2}>
              <Field label="University"><input style={s.input} value={f.uni} onChange={e => upd('uni', e.target.value)} /></Field>
              <Field label="Hometown"><input style={s.input} value={f.hometown} onChange={e => upd('hometown', e.target.value)} placeholder="Tampa, FL" /></Field>
            </div>
          )}

          <button onClick={async () => {
            // Basic field checks first
            if (!f.name.trim()) { scrollToField('field-name', 'Full name is required.', setError); return; }
            if (!f.phone.trim()) { scrollToField('field-phone', 'Phone number is required.', setError); return; }
            if (!f.addrStreet.trim() || f.addrStreet.trim().length < 5 || !/\d/.test(f.addrStreet)) {
              scrollToField('field-addrStreet', 'Please enter a complete street address with house number.', setError); return;
            }
            if (!f.addrZip.trim() || f.addrZip.length !== 5) { scrollToField('field-addrZip', 'ZIP code is required (5 digits).', setError); return; }
            if (!f.addrCity.trim()) { scrollToField('field-addrCity', 'City is required.', setError); return; }
            if (!f.addrState.trim()) { scrollToField('field-addrState', 'State is required.', setError); return; }

            // Address validation — skip if user already confirmed warning
            if (!addrWarned) {
              setError('Validating address…');
              const addrCheck = await validateAddress(f.addrStreet, f.addrCity, f.addrState);
              console.log('Address validation response:', JSON.stringify(addrCheck));
              if (!addrCheck.valid) {
                setAddrWarned(true); // next click will bypass
                scrollToField('field-addrStreet',
                  (addrCheck.message || 'Address not verified.') + ' If correct, click Next again to continue.',
                  setError);
                return;
              }
            }
            setError('');
            setStep(2);
          }} style={{ ...s.btn(mc), width: '100%', justifyContent: 'center', marginTop: 8 }}>
            Next →
          </button>
        </Card>
      )}

      {step === 2 && mode === 'school' && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Your children</div>
          <div style={{ fontSize: 12, color: C.ink3, marginBottom: 16 }}>
            Add each child separately — they may go to different schools and you can set up a carpool for each.
          </div>

          {children.map((child, i) => (
            <ChildCard
              key={i}
              child={child}
              index={i}
              onUpdate={(k, v) => updChild(i, k, v)}
              onRemove={children.length > 1 ? () => removeChild(i) : undefined}
            />
          ))}

          <button onClick={addChild} style={{ ...s.outlineBtn(mc), width: '100%', justifyContent: 'center', marginBottom: 12 }}>
            + Add another child
          </button>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setStep(1)} style={s.btn(C.sur2, C.ink2)}>← Back</button>
            <button onClick={() => setStep(3)} style={{ ...s.btn(mc), flex: 1, justifyContent: 'center' }}>Next →</button>
          </div>
        </div>
      )}

      {(step === (mode === 'school' ? 3 : 2)) && (
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Your vehicle</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {[['yes', '🚗 I have a car'], ['no', '👤 No car — rider only']].map(([v, l]) => (
              <div key={v} onClick={() => upd('hasCar', v)} style={{ flex: 1, padding: 12, borderRadius: 10, border: `1.5px solid ${f.hasCar === v ? mc : C.bdr}`, background: f.hasCar === v ? (mode === 'school' ? C.schoolL : C.collegeL) : C.sur, cursor: 'pointer', textAlign: 'center' as const, fontSize: 13, fontWeight: 600 }}>
                {l}
              </div>
            ))}
          </div>
          {f.hasCar === 'yes' && (
            <>
              <div style={s.grid2}>
                <div style={{ marginBottom: 10 }}>
                  <label style={s.label}>Make & model <span style={{ color: C.coral }}>*</span></label>
                  <input id="field-make" style={{ ...s.input, borderColor: !f.make ? C.coral : undefined }}
                    value={f.make} onChange={e => upd('make', e.target.value)} placeholder="Toyota Sienna" />
                </div>
                <Field label="Seats available"><select style={s.input} value={f.seats} onChange={e => upd('seats', e.target.value)}>{['2','3','4','5','6','7','8'].map(n => <option key={n}>{n}</option>)}</select></Field>
              </div>
              <div style={s.grid2}>
              <div style={{ marginBottom: 10 }}>
                <label style={s.label}>Car color <span style={{ color: C.coral }}>*</span></label>
                <input id="field-color" style={{ ...s.input, borderColor: !f.color ? C.coral : undefined }}
                  value={f.color} onChange={e => upd('color', e.target.value)} placeholder="Silver" />
              </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={s.label}>License plate <span style={{ color: C.coral }}>*</span></label>
                  <input id="field-plate" style={{ ...s.input, borderColor: !f.plate ? C.coral : undefined, textTransform: 'uppercase' as const }}
                    value={f.plate} onChange={e => upd('plate', e.target.value.toUpperCase())} placeholder="ABC-1234" />
                  <div style={{ fontSize: 11, color: C.ink3, marginTop: 3 }}>Format: ABC-1234 or ABC1234</div>
                </div>
              </div>
                <Field label="Availability">
                  <div style={{ fontSize: 11, color: C.ink3, marginBottom: 8 }}>Select separately for drop-off and pickup — some parents can only do one.</div>
                  {(['dropoff', 'pickup'] as const).map(type => (
                    <div key={type} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 5, color: type === 'dropoff' ? C.schoolD : C.amber }}>
                        {type === 'dropoff' ? '🌅 Drop-off days (morning)' : '🌆 Pick-up days (afternoon)'}
                      </div>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' as const }}>
                        {['Mon','Tue','Wed','Thu','Fri'].map(d => {
                          const arr = type === 'dropoff' ? f.dropoffDays : f.pickupDays;
                          const sel = arr.includes(d);
                          return (
                            <span key={d} onClick={() => upd(type === 'dropoff' ? 'dropoffDays' : 'pickupDays',
                              sel ? arr.filter((x: string) => x !== d) : [...arr, d]
                            )} style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', userSelect: 'none' as const,
                              border: `1.5px solid ${sel ? (type === 'dropoff' ? C.school : C.amber) : C.bdr}`,
                              background: sel ? (type === 'dropoff' ? C.school : C.amber) : C.sur,
                              color: sel ? '#fff' : C.ink2 }}>{d}</span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </Field>
            </>
          )}
          {/* Background check option */}
          <div id="bg-check-section" style={{ background: C.amberL, borderRadius: 10, padding: 14, marginTop: 12, border: `1px solid ${C.amber}` }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ fontSize: 24 }}>🛡️</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.amberD, marginBottom: 4 }}>Background verification</div>
                <div style={{ fontSize: 12, color: C.amberD, lineHeight: 1.5, marginBottom: 10 }}>
                  Verified parents get a <strong>✓ Verified badge</strong> and are <strong>2x more likely</strong> to find carpool matches. Other parents can see your verification status before accepting you into their group.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { upd('bgCheck', 'requested'); }} style={{
                    ...s.btn(C.amber), fontSize: 12, padding: '8px 14px',
                    background: f.bgCheck === 'skipped' ? C.sur2 : f.bgCheck === 'requested' ? C.amberD : C.amber,
                    color: f.bgCheck === 'skipped' ? C.ink3 : '#fff',
                    border: f.bgCheck === 'skipped' ? `1px solid ${C.bdr}` : 'none',
                  }}>
                    ✅ Yes, verify me
                  </button>
                  <button onClick={() => upd('bgCheck', 'skipped')} style={{
                    ...s.btnSm(f.bgCheck === 'skipped' ? C.amberD : C.sur2, f.bgCheck === 'skipped' ? '#fff' : C.ink3), fontSize: 12,
                    border: `1px solid ${f.bgCheck === 'skipped' ? C.amberD : C.bdr}`,
                  }}>
                    Skip for now
                  </button>
                </div>
                {f.bgCheck === 'skipped' && (
                  <div style={{ fontSize: 11, color: C.amberD, marginTop: 8 }}>
                    You can complete verification later from your Profile → Verification.
                  </div>
                )}
                {f.bgCheck === 'requested' && (
                  <div style={{ fontSize: 11, color: C.amberD, marginTop: 8 }}>
                    ✓ Great! You'll receive instructions after registration.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button onClick={() => setStep(mode === 'school' ? 2 : 1)} style={s.btn(C.sur2, C.ink2)}>← Back</button>
            <button onClick={() => {
              if (f.bgCheck === 'none') {
                setError('Please choose whether to complete background verification or skip for now.');
                return;
              }
              save();
            }} disabled={saving} style={{ ...s.btn(mc), flex: 1, justifyContent: 'center', opacity: saving ? .7 : 1 }}>
              {saving ? 'Saving…' : '✅ Complete setup'}
            </button>
          </div>
        </Card>
      )}
    </div>
  );
}


// ─── MATCH SCREEN ─────────────────────────────────────────────────────────────
function MatchScreen({ nav }: { nav: (p: string) => void }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const { profile, session, setPendingGroupMembers, setActiveChildSchool, setActiveChildGrade, activeChildSchool } = useApp();
  const [matches, setMatches] = React.useState<any[]>([]);
  const [loadingMatches, setLoadingMatches] = React.useState(false);
  const [allChildren, setAllChildren] = React.useState<any[]>([]);
  const [selectedChild, setSelectedChild] = React.useState<any>(null);

  // Load all children
  React.useEffect(() => {
    if (!session || !supabase) return;
    const kids: any[] = [];
    if (profile?.child_name) kids.push({ name: profile.child_name, grade: profile.child_grade, school: profile.school_name });
    supabase.from('children').select('name, grade, school_name').eq('parent_id', session.user.id)
      .then(({ data }) => {
        (data || []).filter((c: any) => c.name !== profile?.child_name)
          .forEach((c: any) => kids.push({ name: c.name, grade: c.grade, school: c.school_name }));
        setAllChildren(kids);
        // If activeChildSchool is set (from dashboard), select that child
        const targetSchool = activeChildSchool;
        const targetChild = targetSchool
          ? kids.find((k: any) => k.school?.toLowerCase() === targetSchool.toLowerCase()) || kids[0]
          : kids[0];
        if (targetChild) {
          setSelectedChild(targetChild);
          setActiveChildSchool(targetChild.school || '');
          setActiveChildGrade(targetChild.grade || '');
        }
      });
  }, [profile?.child_name, session]);

  // Load matches for selected child's school
  React.useEffect(() => {
    const school = selectedChild?.school || profile?.school_name;
    if (!supabase || !school) return;
    setLoadingMatches(true);
    setMatches([]);
    // Match against profiles.school_name AND children table
    // This ensures second/third children's schools are also matched
    Promise.all([
      // Match 1: primary child school in profiles
      supabase.from('profiles')
        .select('id, full_name, phone, address, child_name, child_grade, school_name, car_make_model, has_car, preferred_days, total_seats, bg_check_status, verification_status')
        .ilike('school_name', school)
        .neq('id', session?.user?.id || ''),
      // Match 2: additional children in children table
      supabase.from('children')
        .select('parent_id, school_name')
        .ilike('school_name', school),
    ]).then(async ([profileRes, childrenRes]) => {
      const profileMatches = profileRes.data || [];
      const childParentIds = (childrenRes.data || [])
        .map((c: any) => c.parent_id)
        .filter((id: string) => id !== session?.user?.id);

      // Get profiles for parents found via children table
      let extraProfiles: any[] = [];
      if (childParentIds.length > 0) {
        const { data: extra } = await supabase!.from('profiles')
          .select('id, full_name, phone, address, child_name, child_grade, school_name, car_make_model, has_car, preferred_days, total_seats, bg_check_status, verification_status')
          .in('id', childParentIds);
        extraProfiles = extra || [];
      }

      // Merge and deduplicate
      const allIds = new Set(profileMatches.map((p: any) => p.id));
      const merged = [...profileMatches, ...extraProfiles.filter((p: any) => !allIds.has(p.id))];
      setMatches(merged);
      setLoadingMatches(false);
    });
  }, [selectedChild, profile?.school_name]);

  const toggle = (id: string) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const [existingMemberIds, setExistingMemberIds] = React.useState<string[]>([]);
  const [fullGroupMemberIds, setFullGroupMemberIds] = React.useState<string[]>([]); // families in full-capacity groups

  // Load existing group members to exclude from matches — only for same school
  React.useEffect(() => {
    if (!supabase || !session) return;
    supabase.from('group_members')
      .select('user_id, group_id, carpool_groups(school_name)')
      .eq('user_id', session.user.id)
      .then(async ({ data: myGms }) => {
        if (!myGms?.length) return;
        // Only exclude members from groups matching the current child's school
        const currentSchool = (selectedChild?.school || profile?.school_name || '').toLowerCase();
        const sameSchoolGids = myGms
          .filter((g: any) => {
            const gs = (g.carpool_groups?.school_name || '').toLowerCase();
            return !currentSchool || !gs || gs.includes(currentSchool) || currentSchool.includes(gs);
          })
          .map((g: any) => g.group_id).filter(Boolean);
        if (!sameSchoolGids.length) { setExistingMemberIds([]); return; }
        const { data: allMembers } = await supabase!.from('group_members')
          .select('user_id').in('group_id', sameSchoolGids);
        const ids = (allMembers || []).map((m: any) => m.user_id).filter((id: string) => id !== session.user.id);
        setExistingMemberIds(ids);
      });
  }, [session, selectedChild]);

  // Load full-capacity group info for all matched families
  React.useEffect(() => {
    if (!supabase || matches.length === 0) return;
    const SENIOR_GRADES_M = ['Grade 8','Grade 9','Grade 10','Grade 11','Grade 12'];
    const matchIds = matches.map((m: any) => m.id);
    // Get group memberships for all matched families
    supabase.from('group_members')
      .select('user_id, group_id, children_included, carpool_groups(school_name)')
      .in('user_id', matchIds)
      .then(async ({ data: memberships }) => {
        if (!memberships?.length) { setFullGroupMemberIds([]); return; }
        const groupIds = Array.from(new Set(memberships.map((m: any) => m.group_id)));
        // For each group, load all members with their profiles to compute capacity
        const { data: allGroupMembers } = await supabase!.from('group_members')
          .select('user_id, group_id, children_included, profiles(has_car, total_seats, child_grade)')
          .in('group_id', groupIds);
        // Also load children grades for senior check
        const fullIds: string[] = [];
        for (const gid of groupIds) {
          const gMembers = (allGroupMembers || []).filter((m: any) => m.group_id === gid);
          const totalKids = gMembers.reduce((sum: number, m: any) => sum + (m.children_included?.length || 1), 0);
          // Check for Grade 8+ in group
          const gradeVals = gMembers.map((m: any) => m.profiles?.child_grade || '');
          const hasSenior = gradeVals.some((g: string) => SENIOR_GRADES_M.includes(g));
          const frontUsable = hasSenior ? 1 : 0;
          const drivers = gMembers.filter((m: any) => m.profiles?.has_car);
          if (drivers.length === 0) continue;
          const minCap = Math.min(...drivers.map((m: any) => {
            const seats = m.profiles?.total_seats || 5;
            return (seats - 1) - (1 - frontUsable);
          }));
          if (minCap <= totalKids) {
            // This group is full — mark all its members
            gMembers.forEach((m: any) => { if (!fullIds.includes(m.user_id)) fullIds.push(m.user_id); });
          }
        }
        setFullGroupMemberIds(fullIds);
      });
  }, [matches]);

  const filtered = matches.filter(m => {
    if (existingMemberIds.includes(m.id)) return false; // hide already-added families
    if (filter === 'drivers') return m.has_car;
    if (filter === 'near') return true;
    return true;
  }).filter(m => !search || (m.full_name || '').toLowerCase().includes(search.toLowerCase()));



  return (
    <div>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>Find your carpool group</div>

      {/* Child selector — show when multiple children */}
      {allChildren.length > 1 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: C.ink3, marginBottom: 6 }}>Matching for which child?</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
            {allChildren.map((child: any) => (
              <div key={child.name} onClick={() => {
                setSelectedChild(child);
                setSelected([]);
                setActiveChildSchool(child.school || '');
                setActiveChildGrade(child.grade || '');
              }}
                style={{ padding: '8px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  background: selectedChild?.name === child.name ? C.school : C.sur2,
                  color: selectedChild?.name === child.name ? '#fff' : C.ink2,
                  border: `1.5px solid ${selectedChild?.name === child.name ? C.school : C.bdr}` }}>
                {child.name} · {child.school || 'No school set'}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ fontSize: 12, color: C.ink3, marginBottom: 16 }}>
        Families at {selectedChild?.school || profile?.school_name || 'your school'} · {selectedChild?.grade || profile?.child_grade || ''}
      </div>
      {!profile?.school_name && (
        <div style={{ ...s.card, textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🏫</div>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Complete your profile first</div>
          <div style={{ fontSize: 13, color: C.ink3, marginBottom: 16 }}>Add your school and child's details so we can find matching families.</div>
          <button onClick={() => nav('register')} style={s.btn(C.school)}>Complete profile →</button>
        </div>
      )}
      {loadingMatches && <div style={{ textAlign: 'center', padding: 20, color: C.ink3, fontSize: 13 }}>Finding families near you…</div>}
      {!loadingMatches && matches.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' as const, padding: '8px 12px', background: C.sur2, borderRadius: 9, marginBottom: 10, fontSize: 11 }}>
          <span><Tag bg={C.schoolL} color={C.schoolD}>✓ Verified</Tag> <span style={{ color: C.ink3 }}>Background check approved</span></span>
          <span><Tag bg={C.amberL} color={C.amberD}>⏳ Check pending</Tag> <span style={{ color: C.ink3 }}>In review</span></span>
          <span><Tag bg={C.coralL} color={C.coralD}>⚠ Unverified</Tag> <span style={{ color: C.ink3 }}>Driver, no check yet</span></span>
        </div>
      )}
      {!loadingMatches && profile?.school_name && matches.length === 0 && (
        <div style={{ ...s.card, textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🔍</div>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>No families found yet</div>
          <div style={{ fontSize: 13, color: C.ink3 }}>You're the first from your school to sign up! Share the app with other parents to get matches.</div>
        </div>
      )}
      <div style={{ ...s.cardSm, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const, marginBottom: 8 }}>
          {[['all','All matches'],['friends','Known friends'],['drivers','Drivers only'],['near','Nearby <0.5mi']].map(([k, l]) => (
            <span key={k} onClick={() => setFilter(k)} style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: filter === k ? C.schoolL : C.sur2, color: filter === k ? C.schoolD : C.ink2 }}>{l}</span>
          ))}
        </div>
        <input style={s.input} placeholder="Search by name or street…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {filtered.map((m: any) => {
        const isVerified = m.bg_check_status === 'approved';
        const isPending  = m.bg_check_status === 'pending';
        const isGroupFull = fullGroupMemberIds.includes(m.id);
        return (
        <div key={m.id} style={{ border: `1.5px solid ${selected.includes(m.id) ? C.school : isGroupFull ? C.coralL : C.bdr}`, background: selected.includes(m.id) ? C.schoolL : isGroupFull ? '#fff8f7' : C.sur, borderRadius: 14, padding: 12, marginBottom: 9, opacity: isGroupFull ? 0.85 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <Av name={m.full_name || 'User'} />
              {isVerified && (
                <div style={{ position: 'absolute', bottom: -2, right: -2, width: 16, height: 16, borderRadius: '50%', background: C.school, border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8 }}>✓</div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' as const }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{m.full_name}</span>
                {m.has_car ? <Tag bg={C.schoolL} color={C.schoolD}>Driver</Tag> : <Tag bg={C.sur2} color={C.ink2}>Passenger</Tag>}
                {isVerified && <Tag bg={C.schoolL} color={C.schoolD}>✓ Verified</Tag>}
                {isPending  && <Tag bg={C.amberL}  color={C.amberD}>⏳ Check pending</Tag>}
                {m.has_car && !isVerified && !isPending && <Tag bg={C.coralL} color={C.coralD}>⚠ Unverified</Tag>}
                {isGroupFull && <Tag bg={C.coralL} color={C.coralD}>🚫 Group Full</Tag>}
              </div>
              <div style={{ fontSize: 11, color: C.ink3, marginTop: 2 }}>{m.address || ''}</div>
              <div style={{ fontSize: 11, color: C.ink3 }}>{m.child_name ? `${m.child_name} (${m.child_grade})` : ''} {m.car_make_model ? '· ' + m.car_make_model : ''}</div>
              {m.notes && <div style={{ fontSize: 11, color: C.ink3, marginTop: 2, fontStyle: 'italic' }}>"{m.notes}"</div>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 5, alignItems: 'flex-end', flexShrink: 0 }}>
              {m.phone && (
                <a href={`tel:${m.phone}`} onClick={e => e.stopPropagation()} style={{ ...s.btnSm(C.schoolL, C.schoolD), textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>📞 Call</a>
              )}
              {isGroupFull ? (
                <div style={{ padding: '4px 10px', borderRadius: 8, background: C.coralL, color: C.coralD, fontSize: 11, fontWeight: 700, textAlign: 'center' as const }}>
                  🚫 Group Full
                </div>
              ) : (
                <button
                  onClick={() => toggle(m.id)}
                  style={selected.includes(m.id) ? s.btnSm(C.school) : s.btnSm(C.sur2, C.ink2)}
                >
                  {selected.includes(m.id) ? '✓ Selected' : '+ Select'}
                </button>
              )}
            </div>
          </div>
        </div>
        );
      })}

      {selected.length > 0 && (
        <Card border={C.school} shadow={`0 0 0 3px ${C.schoolL}`}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Selected families ({selected.length})</span>
          </div>
          {selected.map((id: string) => {
            const m = matches.find((x: any) => x.id === id);
            if (!m) return null;
            return (
              <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '8px', background: C.sur2, borderRadius: 9 }}>
                <Av name={m.full_name || 'User'} size={30} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{m.full_name}</div>
                  <div style={{ fontSize: 11, color: C.ink3 }}>{m.phone || 'Phone not shared'}</div>
                </div>
                <button onClick={e => { e.stopPropagation(); toggle(id); }} style={s.btnSm('#fff', '#b91c1c')}>✕</button>
              </div>
            );
          })}
          <div style={{ background: C.schoolL, borderRadius: 9, padding: 10, marginBottom: 10, fontSize: 12, color: C.schoolD }}>
            📱 Contact these families to introduce yourself, then form your group below.
          </div>
          <button onClick={() => {
            const selectedMembers = matches.filter((m: any) => selected.includes(m.id));
            setPendingGroupMembers(selectedMembers);
            setActiveChildSchool(selectedChild?.school || profile?.school_name || '');
            setActiveChildGrade(selectedChild?.grade || profile?.child_grade || '');
            nav('group');
          }} style={{ ...s.btn(C.school), width: '100%', justifyContent: 'center' }}>
            Create carpool group with these families →
          </button>
        </Card>
      )}
    </div>
  );
}

// ─── PICKUP ORDER CARD ───────────────────────────────────────────────────────
function PickupOrderCard({ members, groupId: propGroupId }: { members?: any[]; groupId?: string | null }) {
  const { profile, session, activeGroupId } = useApp();
  const [groupMembers, setGroupMembers] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [routeType, setRouteType] = React.useState<'dropoff'|'pickup'>('dropoff');
  const gid = propGroupId || activeGroupId;

  React.useEffect(() => {
    if (members) { setGroupMembers(members); setLoading(false); return; }
    if (!supabase || !session || !gid) { setLoading(false); return; }
    supabase.from('group_members')
      .select('user_id, role, profiles(full_name, address, school_name, has_car)')
      .eq('group_id', gid)
      .then(({ data }) => { setGroupMembers(data || []); setLoading(false); });
  }, [session, gid]);

  if (loading) return null;

  // Build stops — driver first, then passengers
  const allStops = groupMembers
    .map((m: any) => {
      const p = m.profiles || {};
      return {
        name: p.full_name || 'Member',
        address: p.address || '',
        school: p.school_name || '',
        isYou: m.user_id === session?.user?.id,
        isDriver: p.has_car || m.role === 'driver',
        userId: m.user_id,
      };
    })
    .filter((s: any) => s.address);

  if (allStops.length === 0) return (
    <div style={{ fontSize: 12, color: C.ink3, padding: '8px 0' }}>
      Add home addresses in profiles to see pickup order.
    </div>
  );

  // Put today's driver first (the current user if they're driving, otherwise first driver)
  const driver = allStops.find((s: any) => s.isYou && s.isDriver)
    || allStops.find((s: any) => s.isDriver)
    || allStops[0];
  const passengers = allStops.filter((s: any) => s.userId !== driver.userId);
  const stops = [driver, ...passengers];

  const schoolName = driver.school || profile?.school_name || 'School';

  const mapsUrl = routeType === 'dropoff'
    ? `https://www.google.com/maps/dir/${encodeURIComponent(driver.address)}/${passengers.map((s: any) => encodeURIComponent(s.address)).join('/')}/${encodeURIComponent(schoolName + ', FL')}`
    : `https://www.google.com/maps/dir/${encodeURIComponent(schoolName + ', FL')}/${[...passengers].reverse().map((s: any) => encodeURIComponent(s.address)).join('/')}/${encodeURIComponent(driver.address)}`;

  return (
    <div style={{ marginTop: 12 }}>
      {/* Toggle */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {(['dropoff', 'pickup'] as const).map(t => (
          <button key={t} onClick={() => setRouteType(t)}
            style={{ flex: 1, padding: '6px 0', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${routeType === t ? C.school : C.bdr}`, background: routeType === t ? C.school : C.sur2, color: routeType === t ? '#fff' : C.ink2 }}>
            {t === 'dropoff' ? '🌅 Drop-off' : '🌆 Pick-up'}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 11, color: C.ink3, marginBottom: 8 }}>
        {routeType === 'dropoff' ? 'Morning: driver → pick up families → school' : 'Afternoon: school → drop off families → driver home'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 6 }}>
        {/* Start */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 22, height: 22, borderRadius: '50%', background: C.schoolD, color: '#fff', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {routeType === 'dropoff' ? '🚗' : '🏫'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{routeType === 'dropoff' ? `${driver.name}${driver.isYou ? ' (You)' : ''} — Start` : `${schoolName} — Start`}</div>
            <div style={{ fontSize: 11, color: C.ink3 }}>{routeType === 'dropoff' ? driver.address : 'Pickup from school'}</div>
          </div>
        </div>
        {/* Stops */}
        {(routeType === 'dropoff' ? passengers : [...passengers].reverse()).map((stop: any, i: number) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: C.school, color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{stop.name}{stop.isYou ? ' (You)' : ''}</div>
              <div style={{ fontSize: 11, color: C.ink3 }}>{stop.address}</div>
            </div>
          </div>
        ))}
        {/* End */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 22, height: 22, borderRadius: '50%', background: C.amber, color: '#fff', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {routeType === 'dropoff' ? '🏫' : '🏠'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{routeType === 'dropoff' ? `${schoolName} — End` : `${driver.name}${driver.isYou ? ' (You)' : ''} — End`}</div>
            <div style={{ fontSize: 11, color: C.ink3 }}>{routeType === 'dropoff' ? 'Drop off at school' : driver.address}</div>
          </div>
        </div>
      </div>
      <a href={mapsUrl} target="_blank" rel="noreferrer"
        style={{ display: 'block', marginTop: 10, textAlign: 'center' as const, background: C.school, color: '#fff', borderRadius: 8, padding: '8px 0', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
        🗺️ Open {routeType === 'dropoff' ? 'drop-off' : 'pick-up'} route in Google Maps →
      </a>
    </div>
  );
}

// ─── GROUP SCREEN ─────────────────────────────────────────────────────────────
function GroupScreen({ nav }: { nav: (p: string) => void }) {
  const [showInvite, setShowInvite] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [showChildManager, setShowChildManager] = useState(false);
  const [driveDays, setDriveDays] = useState<string[]>([]);
  const [dropoffDays, setDropoffDays] = useState<string[]>([]);
  const [pickupDays, setPickupDays] = useState<string[]>([]);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [groupCreated, setGroupCreated] = useState(false);
  const [groupMembers, setGroupMembers] = useState<any[]>([]);
  const [groupInfo, setGroupInfo] = useState<any>(null);
  const [allGroups, setAllGroups] = useState<any[]>([]); // all groups user belongs to
  const [groupSchool, setGroupSchool] = useState<string>('');
  const [pendingOutgoingInvitation, setPendingOutgoingInvitation] = useState(false);
  const [myChildren, setMyChildren] = useState<any[]>([]);
  const [selectedChildren, setSelectedChildren] = useState<string[]>([]);
  const { profile, session, pendingGroupMembers, setPendingGroupMembers, activeGroupId, setActiveGroupId, activeChildSchool, activeChildGrade, setActiveChildSchool, setActiveChildGrade, setActiveChatUser } = useApp();

  const myName = profile?.full_name || 'You';
  const myCar = profile?.car_make_model || (profile?.has_car ? 'Vehicle not set' : 'Passenger only');

  // Load my children for group creation
  React.useEffect(() => {
    if (!session || !supabase) return;
    const kids: any[] = [];
    if (profile?.child_name) kids.push({ name: profile.child_name, grade: profile.child_grade, school: profile.school_name });
    supabase.from('children').select('name, grade, school_name').eq('parent_id', session.user.id)
      .then(async ({ data }) => {
        (data || []).filter((c: any) => c.name !== profile?.child_name)
          .forEach((c: any) => kids.push({ name: c.name, grade: c.grade, school: c.school_name }));
        setMyChildren(kids);
        // Load existing children_included from group_members if already in a group
        if (kids.length > 0) {
          const { data: gm } = await supabase!.from('group_members')
            .select('children_included')
            .eq('user_id', session.user.id)
            .limit(1).maybeSingle();
          if (gm?.children_included && gm.children_included.length > 0) {
            setSelectedChildren(gm.children_included);
          } else {
            // Pre-select children matching the target school
            const targetSchool = activeChildSchool || profile?.school_name || '';
            const sameSchool = kids.filter((k: any) => {
              if (!k.school) return false; // skip if no school info
              if (!targetSchool) return true;
              return k.school.toLowerCase().includes(targetSchool.toLowerCase()) ||
                targetSchool.toLowerCase().includes(k.school.toLowerCase());
            });
            setSelectedChildren(sameSchool.length > 0 ? sameSchool.map((k: any) => k.name) : []);
          }
        }
      });
  }, [profile?.child_name, session]);

  // When myChildren loads and we have a groupSchool, re-select correct children
  React.useEffect(() => {
    if (myChildren.length === 0 || !groupSchool || pendingGroupMembers.length === 0) return;
    const gs = groupSchool.toLowerCase().trim();
    const matching = myChildren.filter((k: any) => {
      const ks = (k.school || '').toLowerCase().trim();
      return ks && gs && ks === gs;
    });
    // Always reset to matching children — clears stale selections from previous group
    setSelectedChildren(matching.length > 0 ? matching.map((k: any) => k.name) : []);
  }, [myChildren, groupSchool]);
  React.useEffect(() => {
    if (!supabase || !session) return;
    // If no pending members (not coming from Match), clear activeChildSchool
    // so the loaded group's school takes over
    if (pendingGroupMembers.length === 0) {
      setActiveChildSchool('');
      setActiveChildGrade('');
    } else {
      // Coming from Match — lock in the school
      console.log('🏫 GroupScreen: pendingMembers present, activeChildSchool=', activeChildSchool, 'profile.school=', profile?.school_name);
      const school = activeChildSchool || profile?.school_name || '';
      console.log('🏫 GroupScreen: setting groupSchool=', school);
      setGroupSchool(school);
    }
    // Check for pending outgoing join requests
    supabase!.from('group_invitations')
      .select('id').eq('inviter_id', session.user.id).eq('status', 'pending')
      .then(({ data }) => setPendingOutgoingInvitation((data?.length || 0) > 0));
    supabase!.from('group_members')
      .select('group_id, carpool_groups(id, school_name, grade)')
      .eq('user_id', session.user.id)
      .then(({ data: allGms }) => {
        if (allGms && allGms.length > 0) {
          setAllGroups(allGms.map((g: any) => ({
            id: g.group_id,
            school_name: g.carpool_groups?.school_name || '',
            grade: g.carpool_groups?.grade || '',
          })));
        }
      });

    // Load group memberships without join to avoid RLS issues
    const targetGroupId = activeGroupId;

    // If coming from Match with pending members, don't load existing group
    // The group will be created fresh by createGroup()
    if (pendingGroupMembers.length > 0) return;
    supabase.from('group_members')
      .select('group_id, role, preferred_days')
      .eq('user_id', session.user.id)
      .then(async ({ data: gms }) => {
        if (!gms || gms.length === 0) return;
        
        // If coming from Match with a specific child school, find the matching group
        let gm: any = null;
        if (targetGroupId) {
          gm = gms.find((d: any) => d.group_id === targetGroupId) || gms[0];
        } else if (activeChildSchool && pendingGroupMembers.length > 0) {
          // Coming from Match — find group matching the child's school
          for (const g of gms) {
            const { data: grpData } = await supabase!.from('carpool_groups')
              .select('school_name').eq('id', g.group_id).maybeSingle();
            const gs = (grpData?.school_name || '').toLowerCase().trim();
            const cs = activeChildSchool.toLowerCase().trim();
            if (gs && cs && gs === cs) { gm = g; break; }
          }
          if (!gm) gm = gms[0]; // fallback
        } else {
          gm = gms[0];
        }
        if (!gm?.group_id) return;
        setGroupId(gm.group_id);
        // Now load all members of this group
        const { data: members } = await supabase!.from('group_members')
          .select('*, profiles(full_name, phone, car_make_model, has_car, preferred_days, total_seats, child_name, child_grade)')
          .eq('group_id', gm.group_id);
        if (!members) return;
        // Load own dropoff/pickup days
        const myMembership = members.find((m: any) => m.user_id === session.user.id);
        if (myMembership) {
          setDropoffDays(myMembership.dropoff_days || myMembership.preferred_days || []);
          setPickupDays(myMembership.pickup_days || myMembership.preferred_days || []);
        }
        const enriched = await Promise.all(members.map(async (m: any) => {
          // Only show children that are included in THIS group
          const includedNames: string[] = m.children_included || [];
          const children: any[] = [];
          // Add primary child if included
          if (m.profiles?.child_name && (includedNames.length === 0 || includedNames.includes(m.profiles.child_name))) {
            children.push({ name: m.profiles.child_name, grade: m.profiles.child_grade });
          }
          // Add additional children if included
          if (includedNames.length > 0) {
            const { data: extraKids } = await supabase!.from('children').select('name, grade').eq('parent_id', m.user_id);
            (extraKids || [])
              .filter((c: any) => c.name !== m.profiles?.child_name && includedNames.includes(c.name))
              .forEach((c: any) => children.push(c));
          }
          return { ...m, children };
        }));
        setGroupMembers(enriched);
        // Load school name from carpool_groups
        const { data: cg } = await supabase!.from('carpool_groups').select('school_name, grade, created_by').eq('id', gm.group_id).maybeSingle();
        if (cg) {
          setGroupInfo(cg);
          setGroupSchool(cg.school_name || '');
          setActiveChildSchool(cg.school_name || '');
          setActiveChildGrade(cg.grade || '');
          // When loading an existing group (not creating), select children from children_included
          if (pendingGroupMembers.length === 0) {
            const myMem = members.find((m: any) => m.user_id === session.user.id);
            const included = myMem?.children_included || [];
            if (included.length > 0) setSelectedChildren(included);
          }
        }
      });
  }, [session, activeGroupId]);

  async function createGroup() {
    if (!supabase || !session || !profile) return;
    setCreating(true);

    // Check if user already has a group — don't create duplicates
    // Find the school for the selected child (passed from MatchScreen via context)
    const childSchool = activeChildSchool || profile?.school_name || '';
    const childGrade = activeChildGrade || profile?.child_grade || '';
    console.log('🏫 createGroup: childSchool=', childSchool, 'activeChildSchool=', activeChildSchool);
    // Clear after use so it doesn't persist to next navigation
    setActiveChildSchool('');
    setActiveChildGrade('');

    // Load current user's groups first (needed for both checks below)
    const { data: existingGms } = await supabase.from('group_members')
      .select('group_id, carpool_groups(id, school_name, grade)')
      .eq('user_id', session.user.id);

    // Check ALL pending members — if any already have a group for this school
    for (const m of pendingGroupMembers) {
      // First get their group IDs
      const { data: theirMemberships, error: memErr } = await supabase
        .from('group_members').select('group_id').eq('user_id', m.id);
      console.log('🔍 theirMemberships for', m.full_name, ':', theirMemberships, 'error:', memErr?.message);

      const theirGroupIds = theirMemberships?.map((g:any) => g.group_id) || [];

      // Then find if any of those groups match this school
      const { data: theirGroups, error: grpErr } = theirGroupIds.length > 0
        ? await supabase.from('carpool_groups').select('id, school_name').eq('school_name', childSchool).in('id', theirGroupIds)
        : { data: [], error: null };

      console.log('🔍 theirGroups for school', childSchool, ':', theirGroups, 'error:', grpErr?.message);

      const theirGroupForSchool = theirGroups && theirGroups.length > 0 ? theirGroups[0] : null;
      // Skip if it's a group Rama is already in (shared group)
      const isSharedGroup = theirGroupForSchool && (existingGms || []).some((myGm: any) => myGm.group_id === theirGroupForSchool.id);

      console.log('🔍 theirGroupForSchool:', theirGroupForSchool, 'isShared:', isSharedGroup);
      if (theirGroupForSchool && !isSharedGroup) {
        const sendRequest = window.confirm(
          `${m.full_name} already has a carpool group for ${childSchool}.\n\nEach child can only be in one carpool group.\n\nWould you like to send a request to join their existing group?\n\nOK = Send join request\nCancel = Go back`
        );
        if (sendRequest) {
          const { data: theirGroup } = await supabase.from('carpool_groups')
            .select('created_by').eq('id', theirGroupForSchool.id).maybeSingle();
          await supabase.from('group_invitations').insert({
            group_id: theirGroupForSchool.id,
            inviter_id: session.user.id,
            invitee_id: theirGroup?.created_by || m.id,
            message: `${profile?.full_name} wants to join your carpool group at ${childSchool}`,
          });
          await notifyUser(theirGroup?.created_by || m.id, 'group_invite', '🚗 Carpool join request',
            `${profile?.full_name} wants to join your carpool group at ${childSchool}.`,
            { group_id: theirGroupForSchool.id });
          alert(`✅ Join request sent to ${m.full_name}'s group! You'll be notified when accepted.`);
        }
        setCreating(false);
        setPendingGroupMembers([]); // Clear so GroupScreen shows pending state
        return;
      }
    }

    // Find a group whose school matches this child's school
    console.log('🔍 existingGms:', JSON.stringify(existingGms?.map((g:any) => g.carpool_groups?.school_name)));
    const matchingGm = (existingGms || []).find((gm: any) => {
      const gs = (gm.carpool_groups?.school_name || '').toLowerCase().trim();
      const cs = childSchool.toLowerCase().trim();
      console.log('🔍 comparing:', gs, 'vs', cs, '→', gs === cs);
      return gs && cs && gs === cs;
    });

    let grp: any = null;

    if (matchingGm?.group_id) {
      // Child already in a group for this school
      const confirmed = window.confirm(
        `Your child is already in a carpool group for ${childSchool}.\n\nWould you like to add the selected ${pendingGroupMembers.length} family${pendingGroupMembers.length > 1 ? 'ies' : ''} to your existing group instead?`
      );
      if (!confirmed) { setCreating(false); return; }
      // Use existing group
      grp = { id: matchingGm.group_id };
      setGroupId(matchingGm.group_id);
      console.log('Using existing group for school:', childSchool, matchingGm.group_id);
    } else {
      // Create new group for this child's school
      const { data: newGrp, error } = await supabase.from('carpool_groups').insert({
        name: `${childSchool || 'Carpool'} Group`,
        school_name: childSchool,
        grade: childGrade,
        academic_year: '2025-2026',
        created_by: session.user.id,
        status: 'active',
      }).select().single();

      if (error || !newGrp) {
        alert('Error creating group: ' + error?.message);
        setCreating(false);
        return;
      }
      grp = newGrp;
      setGroupId(grp.id);
      console.log('Created new group:', grp.id);

      // Add self as first member only for new group
      // Only include children whose school matches this group's school
      const groupSchool = childSchool.toLowerCase();
      
      // Extract key words from school name (ignore common words)
      // Exact school name match — reliable for any school name
      const schoolsMatch = (s1: string, s2: string) => {
        if (!s1 || !s2) return false;
        return s1.toLowerCase().trim() === s2.toLowerCase().trim();
      };

      // Re-compute valid selected children right now using current myChildren + childSchool
      // Don't trust selectedChildren state which may be stale from previous group
      const freshSelected = myChildren
        .filter((k: any) => schoolsMatch(k.school || '', childSchool))
        .map((k: any) => k.name);
      
      if (freshSelected.length === 0) {
        alert(`No children found for ${childSchool}. Please go back to Match and select the correct child.`);
        setCreating(false);
        return;
      }

      const validChildren = freshSelected.filter((name: string) => {
        if (name === profile.child_name) {
          return schoolsMatch(profile.school_name || '', childSchool);
        }
        const child = myChildren.find((c: any) => c.name === name);
        if (!child?.school) return false;
        return schoolsMatch(child.school, childSchool);
      });
      console.log('School matching:', groupSchool, '| freshSelected:', freshSelected, '| validChildren:', validChildren);
      const excluded = freshSelected.filter((n: string) => !validChildren.includes(n));
      if (excluded.length > 0 && validChildren.length === 0) {
        setCreating(false);
        alert(`⚠️ No children found for ${childSchool}.\n\nGo back to Match and select the correct child's school first.`);
        return;
      }
      console.log('freshSelected:', freshSelected, 'validChildren:', validChildren, 'groupSchool:', groupSchool);

      const { error: selfErr } = await supabase.from('group_members').insert({
        group_id: grp.id,
        user_id: session.user.id,
        role: profile.has_car ? 'driver' : 'passenger',
        preferred_days: profile.preferred_days || [],
        children_included: validChildren.length > 0 ? validChildren : [profile?.child_name].filter(Boolean),
      });
      if (selfErr) console.error('Error adding self:', selfErr.message);
    } // end else (new group)

    // Send invitations to pending members — check if they already have a group for this school
    console.log('Sending invitations to:', pendingGroupMembers.length, 'members', pendingGroupMembers.map((m:any) => m.full_name));
    for (const m of pendingGroupMembers) {
      // Check if this member already has a group for the same school
      const { data: theirGms } = await supabase.from('group_members')
        .select('group_id, carpool_groups(school_name)')
        .eq('user_id', m.id);
      const theirGroupForSchool = (theirGms || []).find((gm: any) =>
        (gm.carpool_groups?.school_name || '').toLowerCase().trim() === childSchool.toLowerCase().trim()
      );

      if (theirGroupForSchool && theirGroupForSchool.group_id !== grp.id) {
        const joinTheirGroup = window.confirm(
          `${m.full_name} is already in a carpool group for ${childSchool}.\n\nWould you like to send a request to join their existing group instead?\n\nOK = Send request to join their group\nCancel = Skip ${m.full_name}`
        );
        if (joinTheirGroup) {
          const { error: invErr } = await supabase.from('group_invitations').insert({
            group_id: theirGroupForSchool.group_id,
            inviter_id: session.user.id,
            invitee_id: (await supabase.from('group_members')
              .select('user_id').eq('group_id', theirGroupForSchool.group_id).limit(1).maybeSingle()).data?.user_id || m.id,
            message: `${profile?.full_name} wants to join your carpool group at ${childSchool}`,
          });
          if (!invErr) {
            await notifyUser(m.id, 'group_invite', '🚗 Carpool group join request',
              `${profile?.full_name} wants to join your carpool group. Tap to accept or decline.`,
              { group_id: theirGroupForSchool.group_id, inviter_name: profile?.full_name });
          }
        }
        continue;
      }

      console.log('Inviting:', m.full_name, m.id);
      const { error: invErr } = await supabase.from('group_invitations').insert({
        group_id: grp.id,
        inviter_id: session.user.id,
        invitee_id: m.id,
        message: `${profile?.full_name} invited you to join their carpool group at ${childSchool}`,
      });
      if (invErr) console.error('Invitation error:', invErr.message);
      await notifyUser(m.id, 'group_invite', '🚗 Carpool group invitation',
        `${profile?.full_name} invited you to join a carpool group. Tap to accept or decline.`,
        { group_id: grp.id, inviter_name: profile?.full_name });
      console.log('✅ Invited', m.full_name);
    }

    // Clear pending members
    setPendingGroupMembers([]);

    // Reload members
    const { data: members } = await supabase.from('group_members')
      .select('*, profiles(full_name, phone, car_make_model, has_car, preferred_days)')
      .eq('group_id', grp.id);
    console.log('Final group members:', members?.length);
    setGroupMembers(members || []);
    setCreating(false);
    setGroupCreated(true);

    // If adding to existing group (not creating new), check if schedule exists and prompt to regenerate
    if (matchingGm?.group_id) {
      const { data: existingSchedule } = await supabase.from('schedule_entries')
        .select('id').eq('group_id', grp.id).limit(1);
      if (existingSchedule && existingSchedule.length > 0) {
        const regen = window.confirm(
          `New members added to an existing group!\n\nWould you like to regenerate the schedule to include the new members? This will replace the existing schedule.`
        );
        if (regen) {
          // Delete existing schedule and navigate to auto-schedule
          await supabase.from('schedule_entries').delete().eq('group_id', grp.id);
          setActiveGroupId(grp.id);
          nav('autosched');
        }
      }
    }
  }

  // Subscribe to realtime group_members changes (new members joining)
  React.useEffect(() => {
    if (!supabase || !groupId) return;
    const channel = supabase.channel('group-members-' + groupId)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'group_members',
        filter: `group_id=eq.${groupId}`,
      }, async (payload) => {
        console.log('New member joined:', payload.new);
        // Reload all members
        const { data } = await supabase!.from('group_members')
          .select('*, profiles(full_name, phone, car_make_model, has_car, preferred_days, total_seats)')
          .eq('group_id', groupId);
        if (data) {
          const enriched = await Promise.all(data.map(async (m: any) => {
            const children: any[] = [];
            if (m.profiles?.child_name) children.push({ name: m.profiles.child_name, grade: m.profiles.child_grade });
            const { data: extraKids } = await supabase!.from('children').select('name, grade').eq('parent_id', m.user_id);
            (extraKids || []).filter((c: any) => c.name !== m.profiles?.child_name).forEach((c: any) => children.push(c));
            return { ...m, children };
          }));
          setGroupMembers(enriched);
        }
      })
      .subscribe();
    return () => { supabase!.removeChannel(channel); };
  }, [groupId]);

  // All members to display — existing DB members + pending (before group created)
  const displayMembers = groupMembers.length > 0 ? groupMembers : [
    { id: 'me', profiles: { full_name: myName, car_make_model: myCar, has_car: profile?.has_car, preferred_days: profile?.preferred_days }, isYou: true },
    ...pendingGroupMembers.map(m => ({ id: m.id, profiles: m, isYou: false })),
  ];

  // Compute group capacity surplus (used to gate "Find more families" button)
  const _groupTotalKids = displayMembers.reduce((sum: number, m: any) => sum + (m.children_included?.length || 1), 0);
  const _SENIOR_GRADES = ['Grade 8','Grade 9','Grade 10','Grade 11','Grade 12'];
  const _hasSeniorKid = displayMembers.some((m: any) => (m.children || []).some((c: any) => _SENIOR_GRADES.includes(c.grade)));
  const _frontUsable = _hasSeniorKid ? 1 : 0;
  const _drivers = displayMembers.filter((m: any) => m.profiles?.has_car);
  const _frontReserved = _hasSeniorKid ? 0 : 1;
  const _smallestCar = _drivers.length > 0 ? Math.min(..._drivers.map((m: any) => m.profiles?.total_seats || 5)) : 0;
  const _smallestCarKidSeats = Math.max(0, _smallestCar - 1 - _frontReserved);
  const groupSurplus = _smallestCarKidSeats - _groupTotalKids; // available seats for new kids

  const hasPending = pendingGroupMembers.length > 0 && !groupCreated;

  return (
    <div>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>My carpool group</div>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: allGroups.length > 1 ? 8 : 16 }}>
        {groupInfo?.school_name || groupSchool || profile?.school_name || 'School not set'} · {groupInfo?.grade || activeChildGrade || profile?.child_grade || ''}
      </div>

      {/* Group selector tabs when user has multiple groups */}
      {allGroups.length > 1 && !hasPending && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' as const }}>
          {allGroups.map((g: any) => (
            <button key={g.id || g.school_name} onClick={() => {
              setActiveGroupId(g.id);
              setGroupSchool(g.school_name);
              setGroupInfo(null);
              setGroupMembers([]);
            }} style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${groupId === g.id ? C.school : C.bdr}`, background: groupId === g.id ? C.school : C.sur2, color: groupId === g.id ? '#fff' : C.ink2 }}>
              {g.school_name} · {g.grade || 'All grades'}
            </button>
          ))}
        </div>
      )}

      {/* Group created success */}
      {groupCreated && (
        <Alert bg={C.schoolL} color={C.schoolD} bdr={C.school}>
          🎉 Group created! All {displayMembers.length} families have been added. Set your driving preferences below.
        </Alert>
      )}

      {/* Pending members — ready to create */}
      {hasPending && (
        <div style={{ background: C.amberL, borderRadius: 12, padding: 16, marginBottom: 14, border: `1.5px solid ${C.amber}` }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: C.amberD, marginBottom: 8 }}>
            Ready to form your group
          </div>
          <div style={{ fontSize: 13, color: C.amberD, marginBottom: 12 }}>
            {pendingGroupMembers.length + 1} families selected — you + {pendingGroupMembers.length} from Match.
          </div>

          {/* Child selection — only same school as group */}
          {myChildren.length > 1 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.amberD, marginBottom: 6 }}>Which children are joining this group?</div>
              <div style={{ fontSize: 11, color: C.amberD, marginBottom: 8, opacity: .8 }}>
                Children at the same school can join this group.
              </div>
              {myChildren.map((child: any) => {
                const targetSch = (groupSchool || groupInfo?.school_name || activeChildSchool || profile?.school_name || '').toLowerCase().trim();
                const childSch = (child.school || '').toLowerCase().trim();
                const sameSchool = !childSch || !targetSch || childSch === targetSch;
                const isSelected = selectedChildren.includes(child.name);
                return (
                  <div key={child.name}
                    onClick={() => sameSchool && setSelectedChildren(prev =>
                      prev.includes(child.name) ? prev.filter(n => n !== child.name) : [...prev, child.name]
                    )}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, marginBottom: 4,
                      background: !sameSchool ? C.sur2 : isSelected ? C.schoolL : '#fff',
                      cursor: sameSchool ? 'pointer' : 'not-allowed',
                      border: `1px solid ${!sameSchool ? C.bdr : isSelected ? C.school : C.bdr}`,
                      opacity: sameSchool ? 1 : 0.5 }}>
                    <span style={{ fontSize: 16 }}>{!sameSchool ? '🚫' : isSelected ? '✅' : '⬜'}</span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{child.name}</span>
                    <span style={{ fontSize: 11, color: C.ink3 }}>{child.grade}</span>
                    {!sameSchool && <span style={{ fontSize: 10, color: C.coralD, marginLeft: 'auto' }}>{child.school} — different school</span>}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            {[{ full_name: myName, has_car: profile?.has_car, isYou: true }, ...pendingGroupMembers].map((m: any) => (
              <div key={m.full_name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: `1px solid ${C.amberL}` }}>
                <Av name={m.full_name} size={28} />
                <span style={{ fontSize: 13, flex: 1, fontWeight: 600 }}>{m.full_name}{m.isYou ? ' (You)' : ''}</span>
                {m.has_car ? <Tag bg={C.schoolL} color={C.schoolD}>Driver</Tag> : <Tag bg={C.sur2} color={C.ink2}>Passenger</Tag>}
              </div>
            ))}
          </div>
          <button onClick={createGroup} disabled={creating} style={{ ...s.btn(C.school), width: '100%', justifyContent: 'center', opacity: creating ? .7 : 1 }}>
            {creating ? '⚙️ Creating group…' : '✅ Create carpool group →'}
          </button>
        </div>
      )}

      {/* No group, no pending */}
      {!hasPending && !groupId && !groupCreated && pendingOutgoingInvitation && (
        <Alert bg={C.amberL} color={C.amber} bdr={C.amber}>
          ⏳ Your request to join a carpool group is pending approval. You'll be notified when accepted.
        </Alert>
      )}
      {!hasPending && !groupId && !groupCreated && !pendingOutgoingInvitation && (
        <Alert bg={C.schoolL} color={C.schoolD} bdr={C.school}>
          Go to Match to find and select families, then come back here to form your group.
        </Alert>
      )}

      {/* Members list */}
      <Card>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>Group members</div>
        <div style={{ fontSize: 12, color: C.ink3, marginBottom: 8 }}>
          {displayMembers.length} {displayMembers.length === 1 ? 'family' : 'families'} · {displayMembers.filter((m: any) => m.profiles?.has_car).length} drivers
        </div>

        {/* Seat capacity summary */}
        {(() => {
          // Total kids across all members
          const totalKids = displayMembers.reduce((sum: number, m: any) =>
            sum + (m.children_included?.length || 1), 0);

          const SENIOR_GRADES_G = ['Grade 8','Grade 9','Grade 10','Grade 11','Grade 12'];
          const hasSeniorKid = displayMembers.some((m: any) =>
            (m.children || []).some((c: any) => SENIOR_GRADES_G.includes(c.grade)));
          const frontReserved = hasSeniorKid ? 0 : 1;

          const driversWithCars = displayMembers.filter((m: any) => m.profiles?.has_car);

          // Smallest car kid capacity = min(total_seats - 1 driver - frontReserved) across drivers
          const smallestCar = driversWithCars.length > 0
            ? Math.min(...driversWithCars.map((m: any) => m.profiles?.total_seats || 5))
            : 0;
          const smallestCarKidSeats = Math.max(0, smallestCar - 1 - frontReserved);

          // Available seats for NEW kids = smallest car capacity - kids already in group
          const availableSeats = smallestCarKidSeats - totalKids;

          return (
            <div style={{ background: availableSeats >= 0 ? C.schoolL : C.coralL, borderRadius: 9, padding: 10, marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' as const, marginBottom: 8 }}>
                <div style={{ textAlign: 'center' as const }}>
                  <div style={{ fontWeight: 800, fontSize: 18, color: C.schoolD }}>{totalKids}</div>
                  <div style={{ fontSize: 11, color: C.ink3 }}>Children</div>
                </div>
                <div style={{ textAlign: 'center' as const }}>
                  <div style={{ fontWeight: 800, fontSize: 18, color: C.schoolD }}>{smallestCarKidSeats}</div>
                  <div style={{ fontSize: 11, color: C.ink3 }}>Seats (smallest car)</div>
                </div>
                <div style={{ textAlign: 'center' as const }}>
                  <div style={{ fontWeight: 800, fontSize: 18, color: availableSeats > 0 ? C.schoolD : C.coralD }}>
                    {availableSeats > 0 ? availableSeats : 0}
                  </div>
                  <div style={{ fontSize: 11, color: C.ink3 }}>Available</div>
                </div>
              </div>
              {/* Per-driver breakdown with children names */}
              {driversWithCars.map((m: any) => {
                const totalCar = m.profiles?.total_seats || 5;
                const kidNames = (m.children_included || []).map((c: any) => c.name || c).join(', ');
                const ownKids = m.children_included?.length || 1;
                const driverKidSeats = totalCar - 1 - frontReserved;
                const spare = driverKidSeats - ownKids;
                const frontNote = frontReserved ? ` − 1 front` : '';
                return (
                  <div key={m.user_id} style={{ fontSize: 11, color: C.schoolD, marginBottom: 2 }}>
                    🚗 {m.profiles?.full_name?.split(' ')[0]}: {totalCar} seats − 1 driver{frontNote} − {ownKids} kid{ownKids > 1 ? 's' : ''}
                    {kidNames ? <span style={{ color: C.ink3 }}> ({kidNames})</span> : null}
                    {' '}= <b>{spare >= 0 ? spare : 0} spare</b>
                  </div>
                );
              })}
              {!hasSeniorKid && (
                <div style={{ fontSize: 10, color: C.ink3, marginTop: 4, fontStyle: 'italic' }}>
                  🚫 Front seat excluded — no kids Grade 8+ in this group
                </div>
              )}
            </div>
          );
        })()}

        {displayMembers.map((m: any) => {
          const p = m.profiles || m;
          const name = p.full_name || 'Member';
          const isYou = m.isYou || m.user_id === session?.user?.id;
          const childCount = (p.child_count || 1);
          return (
            <div key={m.id || name} style={{ ...s.row, alignItems: 'flex-start' }}>
              <Av name={name} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{name}{isYou ? ' (You)' : ''}</div>
                <div style={{ fontSize: 11, color: C.ink3 }}>
                  {p.has_car ? 'Driver' : 'Passenger'}
                  {p.car_make_model ? ' · ' + p.car_make_model : ''}
                  {p.total_seats ? ` · ${p.total_seats} seats` : ''}
                  {p.preferred_days?.length ? ' · drives ' + p.preferred_days.join(', ') : ''}
                </div>
                {/* Children in this group */}
                {m.children && m.children.length > 0 && (
                  <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' as const }}>
                    {m.children.map((ch: any, i: number) => (
                      <span key={i} style={s.tag(C.schoolL, C.schoolD)}>{ch.name} · {ch.grade}</span>
                    ))}
                  </div>
                )}
              </div>
              {isYou
                ? <Tag bg={C.schoolL} color={C.schoolD}>You</Tag>
                : <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {p.phone && <a href={`tel:${p.phone}`} style={{ ...s.btnSm(C.schoolL, C.schoolD), textDecoration: 'none' }}>📞</a>}
                    <button onClick={() => { pendingChatTarget = { id: m.user_id, name }; nav('messages'); }} style={s.btnSm(C.sur2, C.ink2)}>Chat</button>
                    {groupInfo?.created_by === session?.user?.id && (
                      <button onClick={async () => {
                        if (!window.confirm(`Remove ${name} from the group?`)) return;
                        console.log('🗑️ Removing member:', m.user_id, 'from group:', groupId);
                        const { error, count } = await supabase!.from('group_members')
                          .delete({ count: 'exact' }).eq('user_id', m.user_id).eq('group_id', groupId!);
                        console.log('🗑️ Delete result: error=', error?.message, 'count=', count);
                        if (error) { alert('Error removing member: ' + error.message); return; }
                        if (count === 0) { alert('Could not remove member — you may not have permission.'); return; }
                        setGroupMembers(prev => prev.filter((gm: any) => gm.user_id !== m.user_id));
                        await notifyUser(m.user_id, 'removed_from_group', '❌ Removed from group',
                          `${profile?.full_name} has removed you from the carpool group.`, { group_id: groupId });
                      }} style={{ ...s.btnSm('#fff5f5', '#b91c1c'), border: '1px solid #fca5a5', fontSize: 11 }}>
                        ✕ Remove
                      </button>
                    )}
                  </div>
              }
            </div>
          );
        })}
        <Divider />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
          {groupSurplus > 0 ? (
            <button onClick={() => nav('match')} style={s.btn(C.school)}>🔍 Find more families</button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 9, background: C.coralL, color: C.coralD, fontSize: 12, fontWeight: 600 }}>
              🚫 Group is at full capacity
            </div>
          )}
          <button onClick={() => { pendingGroupChatId = groupId || null; nav('messages'); }} style={s.outlineBtn(C.school)}>
            💬 Group Chat
          </button>
          <button onClick={() => setShowInvite(!showInvite)} style={s.outlineBtn(C.school)}>+ Invite by link</button>
          {/* Manage my children in this group */}
          {myChildren.length > 1 && groupId && (
            <button onClick={() => setShowChildManager(!showChildManager)} style={s.outlineBtn(C.school)}>
              👧 Manage my children
            </button>
          )}
        </div>
      </Card>
      {showInvite && (
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Invite a family</div>
          {inviteSent ? <Alert bg={C.schoolL} color={C.schoolD} bdr={C.school}>Invite sent! They'll receive a link to join.</Alert> : (
            <>
              <div style={s.grid2}>
                <Field label="Parent name"><input style={s.input} placeholder="Jane Smith" /></Field>
                <Field label="Phone or email"><input style={s.input} placeholder="jane@email.com" /></Field>
              </div>
              <button onClick={() => setInviteSent(true)} style={s.btn(C.school)}>Send invite link</button>
            </>
          )}
        </Card>
      )}

      {/* Child manager — add/remove children from this group */}
      {showChildManager && groupId && myChildren.length > 1 && (
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>My children in this group</div>
          <div style={{ fontSize: 12, color: C.ink3, marginBottom: 12 }}>
            Select which of your children are part of this carpool group. This affects scheduling — more children = more driving days.
          </div>
          {myChildren.map((child: any) => {
            const isIncluded = selectedChildren.includes(child.name);
            // Get school from groupInfo or profile
            const groupSchool = (groupInfo?.school_name || profile?.school_name || '').toLowerCase();
            const childSchool = (child.school || '').toLowerCase();
            const sameSchool = !childSchool || !groupSchool ||
              childSchool.includes(groupSchool) || groupSchool.includes(childSchool);
            return (
              <div key={child.name}
                onClick={() => sameSchool && setSelectedChildren(prev =>
                  prev.includes(child.name) ? prev.filter(n => n !== child.name) : [...prev, child.name]
                )}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, marginBottom: 6,
                  background: !sameSchool ? C.sur2 : isIncluded ? C.schoolL : C.sur2,
                  cursor: sameSchool ? 'pointer' : 'not-allowed',
                  border: `1.5px solid ${!sameSchool ? C.bdr : isIncluded ? C.school : C.bdr}`,
                  opacity: sameSchool ? 1 : 0.5 }}>
                <span style={{ fontSize: 18 }}>{!sameSchool ? '🚫' : isIncluded ? '✅' : '⬜'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{child.name}</div>
                  <div style={{ fontSize: 11, color: C.ink3 }}>{child.grade}{!sameSchool ? ` · ${child.school} (different school)` : ''}</div>
                </div>
                {sameSchool && (
                  <span style={s.tag(isIncluded ? C.school : C.sur2, isIncluded ? '#fff' : C.ink3)}>
                    {isIncluded ? 'In group' : 'Not included'}
                  </span>
                )}
              </div>
            );
          })}
          <button onClick={async () => {
            if (!supabase || !session || !groupId) return;
            // Validate — only same school children
            const gSchool = (groupInfo?.school_name || profile?.school_name || '').toLowerCase();
            const validSel = selectedChildren.filter((name: string) => {
              const child = myChildren.find((c: any) => c.name === name);
              if (!child?.school) return true;
              const cs = child.school.toLowerCase();
              return cs.includes(gSchool) || gSchool.includes(cs);
            });
            if (validSel.length === 0) { alert('Please select at least one child from ' + (groupInfo?.school_name || profile?.school_name)); return; }
            const { error } = await supabase.from('group_members')
              .update({ children_included: validSel })
              .eq('group_id', groupId)
              .eq('user_id', session.user.id);
            if (error) { alert('Error: ' + error.message); return; }
            setShowChildManager(false);
            alert(`✅ Updated! ${selectedChildren.length} ${selectedChildren.length === 1 ? 'child' : 'children'} now in this group.`);
          }} style={{ ...s.btn(C.school), width: '100%', justifyContent: 'center', marginTop: 8 }}>
            Save changes
          </button>
        </Card>
      )}
      <Card>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>Your driving availability</div>
        <div style={{ fontSize: 12, color: C.ink3, marginBottom: 12 }}>
          Set separately for drop-off and pick-up. Only available days will be scheduled.
        </div>
        {(['dropoff', 'pickup'] as const).map(type => (
          <div key={type} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: type === 'dropoff' ? C.schoolD : C.amberD }}>
              {type === 'dropoff' ? '🌅 Drop-off (morning)' : '🌆 Pick-up (afternoon)'}
            </div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' as const }}>
              {['Mon','Tue','Wed','Thu','Fri'].map(d => {
                const arr = type === 'dropoff' ? dropoffDays : pickupDays;
                const sel = arr.includes(d);
                const setter = type === 'dropoff' ? setDropoffDays : setPickupDays;
                return (
                  <span key={d} onClick={() => setter((prev: string[]) => prev.includes(d) ? prev.filter((x: string) => x !== d) : [...prev, d])}
                    style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', userSelect: 'none' as const,
                      border: `1.5px solid ${sel ? (type === 'dropoff' ? C.school : C.amber) : C.bdr}`,
                      background: sel ? (type === 'dropoff' ? C.school : C.amber) : C.sur,
                      color: sel ? '#fff' : C.ink2 }}>{d}</span>
                );
              })}
            </div>
          </div>
        ))}
        <div style={{display:'flex', gap:8, flexWrap:'wrap' as const}}>
          <button onClick={async () => {
            if (supabase && session && groupId) {
              await supabase.from('group_members').update({
                preferred_days: Array.from(new Set([...dropoffDays, ...pickupDays])),
                dropoff_days: dropoffDays,
                pickup_days: pickupDays,
              }).eq('group_id', groupId).eq('user_id', session.user.id);
            }
            nav('autosched');
          }} style={{ ...s.btn(C.school), flex:1, justifyContent:'center' }}>✨ Auto-generate schedule →</button>
          <button onClick={() => nav('schedule')} style={s.outlineBtn(C.school)}>Manual schedule</button>
        </div>
      </Card>

      <Card>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Route preview</div>
        <div style={{ fontSize: 12, color: C.ink3, marginBottom: 10 }}>Optimal route based on member addresses</div>
        <PickupOrderCard groupId={groupId} />
      </Card>
    </div>
  );
}

// ─── SCHEDULE SCREEN ──────────────────────────────────────────────────────────
function ScheduleScreen({ nav }: { nav: (p: string) => void }) {
  const { profile, session, activeGroupId, setActiveGroupId } = useApp();
  const [week, setWeek] = useState(0);
  const [groupName, setGroupName] = useState('');
  const [allGroups, setAllGroups] = useState<any[]>([]);
  const [showSwap, setShowSwap] = useState(false);
  const [swaps, setSwaps] = useState<any[]>([]);
  const [swapDate, setSwapDate] = useState('');
  const [swapTarget, setSwapTarget] = useState('');
  const [swapReason, setSwapReason] = useState('');
  const [swapRideType, setSwapRideType] = useState<'dropoff'|'pickup'|'both'>('both');
  const [groupMembers, setGroupMembers] = useState<any[]>([]);
  const [mySchedule, setMySchedule] = useState<any[]>([]);
  const [allSchedule, setAllSchedule] = useState<any[]>([]);
  const [groupId, setGroupId] = useState<string|null>(null);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [schoolCalendar, setSchoolCalendar] = useState<any[]>([]);

  React.useEffect(() => {
    if (!supabase || !session) return;
    setLoadingSchedule(true);

    (async () => {
    // Load all groups for tab selector
    supabase!.from('group_members')
      .select('group_id, carpool_groups(id, school_name, grade)')
      .eq('user_id', session.user.id)
      .then(({ data: gms }) => {
        if (gms) setAllGroups(gms.map((g: any) => ({
          id: g.group_id,
          school_name: g.carpool_groups?.school_name || '',
          grade: g.carpool_groups?.grade || '',
        })));
      });

    // Find user's group — prefer activeGroupId if set
    const gmQuery = activeGroupId
      ? supabase.from('group_members').select('group_id').eq('user_id', session.user.id).eq('group_id', activeGroupId).maybeSingle()
      : supabase.from('group_members').select('group_id').eq('user_id', session.user.id).limit(1).maybeSingle();

    const { data: myGm } = await gmQuery;

    if (!myGm?.group_id) { setLoadingSchedule(false); return; }
    const gid = myGm.group_id;
    setGroupId(gid);

    // Load group name
    const { data: grpInfo } = await supabase.from('carpool_groups')
      .select('school_name, grade').eq('id', gid).maybeSingle();
    if (grpInfo) setGroupName(`${grpInfo.school_name}${grpInfo.grade ? ' · ' + grpInfo.grade : ''}`);

    const { data: gm } = await supabase.from('group_members')
      .select('*, profiles(full_name, preferred_days)')
      .eq('group_id', gid);

    if (!gm || gm.length === 0) { setLoadingSchedule(false); return; }
    const seen = new Set<string>();
    const unique = gm.filter((m: any) => { if(seen.has(m.user_id)) return false; seen.add(m.user_id); return true; });
    setGroupMembers(unique);
    const todayDate = new Date();
    const monday = new Date(todayDate);
    monday.setDate(todayDate.getDate() - todayDate.getDay() + 1);
    const toLocalDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const today = toLocalDate(monday);
    const futureD = new Date(); futureD.setDate(futureD.getDate() + 90);
    const futureStr = toLocalDate(futureD);
    const { data: entries } = await supabase.from('schedule_entries')
      .select('*, profiles(full_name)')
      .eq('group_id', gid)
      .gte('date', today).lte('date', futureStr)
      .order('date');
    setAllSchedule(entries || []);
    setMySchedule((entries || []).filter((e: any) => e.user_id === session.user.id));
    const { data: swapData } = await supabase.from('swap_requests')
      .select('*, req:profiles!requester_id(full_name), tgt:profiles!target_user_id(full_name)')
      .or(`requester_id.eq.${session.user.id},target_user_id.eq.${session.user.id}`)
      .order('created_at', { ascending: false });
    setSwaps(swapData || []);

    // Load holidays for this date range
    const { data: calData } = await supabase.from('school_calendar')
      .select('event_date, title, event_type')
      .gte('event_date', today).lte('event_date', futureStr)
      .in('event_type', ['holiday', 'no_school']);
    setSchoolCalendar((calData || []).map((h: any) => ({ ...h, date: h.event_date })));

    setLoadingSchedule(false);
    })();
  }, [session, activeGroupId]);

  // Build week date range — use local date to avoid UTC timezone shift
  const getWeekDates = (weekOffset: number) => {
    const dates: string[] = [];
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1 + weekOffset * 7);
    for (let i = 0; i < 5; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      // Use local date string to avoid UTC offset shifting the date
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
    }
    return dates;
  };
  const weekDates = getWeekDates(week);
  const weekLabel = weekDates.length > 0
    ? `${new Date(weekDates[0]+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})} – ${new Date(weekDates[4]+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})}`
    : '';

  const schedGrid = groupMembers.map((gm: any) => {
    const name = gm.profiles?.full_name || 'Member';
    const isMe = gm.user_id === session?.user?.id;
    return { name: isMe ? name + ' (You)' : name, userId: gm.user_id, isMe };
  });

  const hasSchedule = allSchedule.length > 0;
  const DUTIES = mySchedule.slice(0, 7);
  const dc = (d: string) => d === 'DRIVE' || d === 'DROP' ? [C.schoolL, C.schoolD] : d === 'PICKUP' ? [C.blueL, C.blueD] : [C.sur2, C.ink3];

  return (
    <div>
      {!profile?.school_name && (
        <div style={{ ...s.card, textAlign: 'center', padding: 32, marginBottom: 12 }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📅</div>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>No schedule yet</div>
          <div style={{ fontSize: 13, color: C.ink3, marginBottom: 16 }}>Complete your profile and form a carpool group to generate a schedule.</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button onClick={() => nav('register')} style={s.btn(C.school)}>Complete profile</button>
            <button onClick={() => nav('match')} style={s.outlineBtn(C.school)}>Find group</button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' as const }}>
        <div><div style={{ fontSize: 19, fontWeight: 800 }}>Schedule</div><div style={{ fontSize: 12, color: C.ink3 }}>Week of {weekLabel}</div>{groupName && <div style={{ fontSize: 12, color: C.school, fontWeight: 600, marginTop: 2 }}>📍 {groupName}</div>}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {allGroups.length > 1 && allGroups.map((g: any) => (
            <button key={g.id} onClick={() => { setActiveGroupId(g.id); setWeek(0); }}
              style={{ padding: '4px 10px', borderRadius: 14, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${groupId === g.id ? C.school : C.bdr}`, background: groupId === g.id ? C.school : C.sur2, color: groupId === g.id ? '#fff' : C.ink2 }}>
              {g.school_name.split(' ').slice(0, 2).join(' ')}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={() => setWeek(w => Math.max(0, w - 1))} disabled={week === 0} style={{ ...s.btnSm(C.sur2, C.ink2), opacity: week === 0 ? .4 : 1 }}>← Prev</button>
          <button onClick={() => setWeek(w => w + 1)} style={s.btnSm(C.sur2, C.ink2)}>Next →</button>
          <button onClick={() => setShowSwap(!showSwap)} style={s.outlineBtn(C.school)}>⇄ Swap day</button>
          <button onClick={() => { boardTrackingRole = null; nav('tracking'); }} style={s.btn(C.school)}>🚗 Start drive</button>
          <button onClick={() => nav('calendar')} style={s.btnSm(C.sur2, C.ink2)}>📅 Holidays</button>
        </div>
      </div>

      {loadingSchedule && <div style={{ textAlign:'center', padding:20, color:C.ink3 }}>Loading schedule…</div>}

      {!loadingSchedule && !hasSchedule ? (
        <div style={{ ...s.card, textAlign: 'center', padding: 28, marginBottom: 12 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📅</div>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>No schedule yet</div>
          <div style={{ fontSize: 13, color: C.ink3, marginBottom: 16 }}>
            Use Auto-generate to create your carpool schedule, or ask your group admin to generate one.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' as const }}>
            <button onClick={() => nav('autosched')} style={s.btn(C.school)}>✨ Auto-generate</button>
            <button onClick={() => nav('match')} style={s.outlineBtn(C.school)}>Find families</button>
          </div>
        </div>
      ) : !loadingSchedule && hasSchedule && schedGrid.length > 0 ? (
        <div style={{ ...s.cardSm, overflowX: 'auto', marginBottom: 12 }}>
          <div style={{ display: 'grid', gap: 1, background: C.bdr, borderRadius: 9, overflow: 'hidden', minWidth: 460 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '110px repeat(5,1fr)', background: C.sur2 }}>
              <div style={{ padding: '6px 10px', fontSize: 10, fontWeight: 700, color: C.ink2, textTransform: 'uppercase' as const }}>Member</div>
              {['Mon','Tue','Wed','Thu','Fri'].map((d,i) => {
                const isToday = new Date(weekDates[i]+'T12:00:00').toDateString() === new Date().toDateString();
                const holiday = schoolCalendar.find((h: any) => h.date === weekDates[i]);
                return (
                  <div key={d} style={{ padding: '4px 4px', textAlign: 'center', background: holiday ? '#fff5f5' : isToday ? C.schoolL : undefined }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: holiday ? C.coral : C.ink2, textTransform: 'uppercase' as const }}>{d}</div>
                    {holiday && <div style={{ fontSize: 8, color: C.coralD, fontWeight: 600, lineHeight: 1.2 }}>{holiday.title?.length > 12 ? holiday.title.slice(0,12)+'…' : holiday.title}</div>}
                  </div>
                );
              })}
            </div>
            {schedGrid.map((row: any) => (
              <div key={row.name} style={{ display: 'grid', gridTemplateColumns: '110px repeat(5,1fr)', background: row.isMe ? C.schoolL + '40' : C.sur }}>
                <div style={{ padding: '6px 10px', fontWeight: row.isMe ? 700 : 600, background: C.sur2, color: C.ink2, fontSize: 11 }}>{row.name}</div>
                {weekDates.map((date: string, i: number) => {
                  const dayEntries = allSchedule.filter((e: any) => e.user_id === row.userId && e.date === date);
                  const hasDropoff = dayEntries.some((e: any) => e.duty === 'drive' || e.ride_type === 'dropoff');
                  const hasPickup = dayEntries.some((e: any) => e.duty === 'pickup' || e.ride_type === 'pickup');
                  const hasBoth = dayEntries.some((e: any) => e.ride_type === 'both');
                  const holiday = schoolCalendar.find((h: any) => h.date === date);
                  return (
                    <div key={i} style={{ padding: '4px 2px', textAlign: 'center', background: holiday ? '#fff5f5' : C.sur }}>
                      {holiday
                        ? <span style={{ color: C.coral, fontSize: 10 }}>🏖️</span>
                        : hasBoth
                          ? <div><span style={{ background: C.schoolL, color: C.schoolD, borderRadius: 4, fontWeight: 700, fontSize: 9, padding: '2px 4px', display: 'block', marginBottom: 2 }}>DROP</span>
                              <span style={{ background: C.blueL, color: C.blueD, borderRadius: 4, fontWeight: 700, fontSize: 9, padding: '2px 4px', display: 'block' }}>PICKUP</span></div>
                        : hasDropoff && hasPickup
                          ? <div><span style={{ background: C.schoolL, color: C.schoolD, borderRadius: 4, fontWeight: 700, fontSize: 9, padding: '2px 4px', display: 'block', marginBottom: 2 }}>DROP</span>
                              <span style={{ background: C.blueL, color: C.blueD, borderRadius: 4, fontWeight: 700, fontSize: 9, padding: '2px 4px', display: 'block' }}>PICKUP</span></div>
                        : hasDropoff
                          ? <span style={{ background: C.schoolL, color: C.schoolD, borderRadius: 4, fontWeight: 700, fontSize: 10, padding: '3px 5px', display: 'inline-block' }}>DROP</span>
                        : hasPickup
                          ? <span style={{ background: C.blueL, color: C.blueD, borderRadius: 4, fontWeight: 700, fontSize: 10, padding: '3px 5px', display: 'inline-block' }}>PICKUP</span>
                        : <span style={{ color: C.ink3, fontSize: 10 }}>—</span>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <Card>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>My upcoming duties</div>
        {DUTIES.length === 0
          ? <div style={{ fontSize: 13, color: C.ink3 }}>
              {hasSchedule ? 'No upcoming duties for you.' : 'No schedule yet — '}
              {!hasSchedule && <button onClick={() => nav('autosched')} style={{ background:'none',border:'none',color:C.school,fontWeight:600,cursor:'pointer',fontSize:13,padding:0 }}>auto-generate one →</button>}
            </div>
          : DUTIES.map((d: any) => {
              const duty = (d.duty || '').toUpperCase();
              const [bg, color] = dc(duty);
              const dateDisplay = new Date(d.date+'T12:00:00').toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'});
              return (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: `1px solid ${C.sur2}` }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.ink2, minWidth: 90 }}>{dateDisplay}</div>
                  <span style={{ background: bg, color, borderRadius: 4, fontWeight: 700, fontSize: 10, padding: '3px 5px' }}>{duty}</span>
                  {(duty === 'DRIVE' || duty === 'DROP') && <span style={{ fontSize: 12, color: C.ink3 }}>You are driving today</span>}
                  {duty === 'PICKUP' && <span style={{ fontSize: 12, color: C.ink3 }}>Someone else is driving</span>}
                </div>
              );
            })
        }
      </Card>

      <Card>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Swap requests</div>
        {swaps.length === 0
          ? <div style={{ fontSize: 13, color: C.ink3 }}>No swap requests yet.</div>
          : swaps.map((sw: any) => {
              const isRequester = sw.requester_id === session?.user?.id;
              const fromName = sw.req?.full_name || sw.requester_id?.slice(0,8) || 'Someone';
              const toName   = sw.tgt?.full_name || sw.target_user_id?.slice(0,8) || 'Someone';
              return (
                <div key={sw.id} style={{ padding:'7px 0', borderBottom:`1px solid ${C.sur2}` }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ flex:1, fontSize:13 }}>
                      <strong>{fromName}</strong> → <strong>{toName}</strong>
                      <div style={{ fontSize:11, color:C.ink3 }}>
                        {sw.requester_date}
                        {sw.ride_type && sw.ride_type !== 'both' && <span style={{ marginLeft:6, color: sw.ride_type==='dropoff' ? C.schoolD : C.amberD }}>{sw.ride_type==='dropoff' ? ' 🌅 Drop-off only' : ' 🌆 Pick-up only'}</span>}
                        {sw.reason ? ' · '+sw.reason : ''}
                      </div>
                    </div>
                    <Tag bg={sw.status==='accepted'?C.schoolL:sw.status==='pending'?C.amberL:C.coralL} color={sw.status==='accepted'?C.schoolD:sw.status==='pending'?C.amberD:C.coralD}>{sw.status}</Tag>
                  </div>
                  {!isRequester && sw.status==='pending' && (
                    <div style={{ display:'flex',gap:6,marginTop:6 }}>
                      <button onClick={async()=>{
                        if(!supabase || !session) return;
                        await supabase.from('swap_requests').update({status:'accepted'}).eq('id',sw.id);
                        setSwaps(prev=>prev.map((s:any)=>s.id===sw.id?{...s,status:'accepted'}:s));

                        // Swap the actual schedule entries
                        const rideTypeFilter = sw.ride_type && sw.ride_type !== 'both' ? sw.ride_type : null;
                        console.log('🔄 Swap:', { requester: sw.requester_id, date: sw.requester_date, target: session.user.id, rideType: rideTypeFilter, groupId });

                        // Get requester's entry
                        let reqQuery = supabase.from('schedule_entries')
                          .select('id, ride_type, duty').eq('user_id', sw.requester_id).eq('date', sw.requester_date).eq('group_id', groupId!);
                        if (rideTypeFilter) reqQuery = reqQuery.eq('ride_type', rideTypeFilter);
                        const { data: reqEntries, error: reqErr } = await reqQuery;
                        console.log('🔄 reqEntries:', reqEntries, reqErr?.message);

                        // Get target's entry  
                        let tgtQuery = supabase.from('schedule_entries')
                          .select('id, ride_type, duty').eq('user_id', session.user.id).eq('date', sw.target_date || sw.requester_date).eq('group_id', groupId!);
                        if (rideTypeFilter) tgtQuery = tgtQuery.eq('ride_type', rideTypeFilter);
                        const { data: tgtEntries, error: tgtErr } = await tgtQuery;
                        console.log('🔄 tgtEntries:', tgtEntries, tgtErr?.message);

                        // Swap user_ids
                        if (reqEntries && reqEntries.length > 0) {
                          for (const e of reqEntries) {
                            const { error: upErr } = await supabase.from('schedule_entries').update({ user_id: session.user.id }).eq('id', e.id);
                            console.log('🔄 Updated req entry', e.id, '→', session.user.id, upErr?.message);
                          }
                        }
                        if (tgtEntries && tgtEntries.length > 0) {
                          for (const e of tgtEntries) {
                            const { error: upErr } = await supabase.from('schedule_entries').update({ user_id: sw.requester_id }).eq('id', e.id);
                            console.log('🔄 Updated tgt entry', e.id, '→', sw.requester_id, upErr?.message);
                          }
                        }

                        // Reload schedule
                        const { data: newEntries } = await supabase.from('schedule_entries')
                          .select('*').eq('group_id', groupId!).order('date');
                        if (newEntries) {
                          setAllSchedule(newEntries);
                          setMySchedule(newEntries.filter((e:any) => e.user_id === session.user.id));
                        }

                        // Notify requester
                        await notifyUser(sw.requester_id, 'swap_accepted', '✅ Swap accepted!',
                          `${profile?.full_name} accepted your drive swap request for ${new Date(sw.requester_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}.`,
                          { swap_id: sw.id });
                      }} style={s.btnSm(C.school)}>Accept</button>
                      <button onClick={async()=>{
                        if(!supabase || !session) return;
                        await supabase.from('swap_requests').update({status:'declined'}).eq('id',sw.id);
                        setSwaps(prev=>prev.map((s:any)=>s.id===sw.id?{...s,status:'declined'}:s));
                        // Notify requester of decline
                        await notifyUser(sw.requester_id, 'swap_declined', '❌ Swap declined',
                          `${profile?.full_name} declined your drive swap request. You can try requesting with another member.`,
                          { swap_id: sw.id });
                      }} style={{...s.btnSm('#fff','#b91c1c'),border:'1px solid #fca5a5'}}>Decline</button>
                    </div>
                  )}
                </div>
              );
            })
        }
      </Card>

      {showSwap && (
        <Card border={C.school} shadow={`0 0 0 3px ${C.schoolL}`}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Request a swap</div>

          {groupMembers.filter((m: any) => m.user_id !== session?.user?.id).length === 0 && (
            <Alert bg={C.amberL} color={C.amberD} bdr={C.amber}>
              No other members in your group yet. Add families from the Match screen first.
            </Alert>
          )}

          <Field label="What do you need to swap?">
            <div style={{ display: 'flex', gap: 6 }}>
              {([['dropoff', '🌅 Drop-off only'], ['pickup', '🌆 Pick-up only'], ['both', '🔄 Full day']] as const).map(([val, label]) => (
                <span key={val} onClick={() => setSwapRideType(val)}
                  style={{ flex: 1, textAlign: 'center' as const, padding: '8px 4px', borderRadius: 9, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    border: `1.5px solid ${swapRideType === val ? C.school : C.bdr}`,
                    background: swapRideType === val ? C.schoolL : C.sur,
                    color: swapRideType === val ? C.schoolD : C.ink3 }}>
                  {label}
                </span>
              ))}
            </div>
          </Field>

          <Field label="Date to swap">
            <input type="date" style={s.input} value={swapDate}
              onChange={e => setSwapDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]} />
          </Field>

          <Field label="Swap with">
            <select style={s.input} value={swapTarget} onChange={e => setSwapTarget(e.target.value)}>
              <option value="">— Select a member —</option>
              {groupMembers
                .filter((m: any) => m.user_id !== session?.user?.id)
                .map((m: any) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.profiles?.full_name || m.user_id}
                  </option>
                ))}
            </select>
          </Field>

          <Field label="Reason (optional)">
            <input style={s.input} value={swapReason}
              onChange={e => setSwapReason(e.target.value)}
              placeholder="Doctor appointment, travel…" />
          </Field>

          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={() => { setShowSwap(false); setSwapDate(''); setSwapTarget(''); setSwapReason(''); setSwapRideType('both'); }}
              style={s.btn(C.sur2, C.ink2)}>Cancel</button>
            <button onClick={async () => {
              if (!swapDate) { alert('Please select a date.'); return; }
              if (!swapTarget) { alert('Please select a member to swap with.'); return; }
              if (!supabase || !session) return;

              const targetMember = groupMembers.find((m: any) => m.user_id === swapTarget);
              const targetName = targetMember?.profiles?.full_name || 'Member';
              const rideLabel = swapRideType === 'dropoff' ? 'drop-off' : swapRideType === 'pickup' ? 'pick-up' : 'full day';

              const { data, error } = await supabase.from('swap_requests').insert({
                requester_id: session.user.id,
                target_user_id: swapTarget,
                requester_date: swapDate,
                target_date: swapDate,
                reason: swapReason || null,
                ride_type: swapRideType,
                status: 'pending',
              }).select().single();

              if (error) {
                alert('Error sending swap request: ' + error.message);
                return;
              }

              // Add to local state so it shows immediately
              setSwaps((prev: any[]) => [{
                ...data,
                req: { full_name: profile?.full_name || 'You' },
                tgt: { full_name: targetName },
              }, ...prev]);

              // Notify target member
              await notifyUser(swapTarget, 'swap_request', '🔄 Drive swap request',
                `${profile?.full_name} wants to swap their ${rideLabel} on ${new Date(swapDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}${swapReason ? ` — ${swapReason}` : ''}. Tap to accept or decline.`,
                { swap_id: data.id, requester_name: profile?.full_name, ride_type: swapRideType });

              setShowSwap(false); setSwapDate(''); setSwapTarget(''); setSwapReason(''); setSwapRideType('both');
              alert(`✅ Swap request sent to ${targetName} for ${rideLabel}!`);
            }} style={{ ...s.btn(C.school), flex: 1, justifyContent: 'center' }}>
              Send swap request
            </button>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── BROWSE SCREEN ────────────────────────────────────────────────────────────
function BrowseScreen({ nav }: { nav: (p: string) => void }) {
  const [filter, setFilter] = useState('all');
  const [booking, setBooking] = useState<any>(null);
  const [fromCity, setFromCity] = React.useState('');
  const [toCity, setToCity] = React.useState('');
  const [searchDate, setSearchDate] = React.useState('');
  const [bookConfirmed, setBookConfirmed] = useState(false);
  const [offerTarget, setOfferTarget] = useState<any>(null);
  const [tripType, setTripType] = useState('one-way');
  const [seats, setSeats] = useState(1);
  const [RIDES, setRIDES] = React.useState<any[]>([]);
  const [ridesLoading, setRidesLoading] = React.useState(false);

  async function searchRides() {
    if (!supabase) return;
    setRidesLoading(true);
    const { data: drivers } = await supabase.from('college_rides')
      .select('*, profiles(full_name, car_make_model, rating, university)')
      .eq('status', 'active')
      .order('departure_date', { ascending: true });
    const { data: requests } = await supabase.from('ride_requests')
      .select('*, profiles(full_name, university)')
      .eq('status', 'open')
      .order('created_at', { ascending: false });
    const driverRides = (drivers || []).map((r: any) => ({
      id: r.id, type: 'driver',
      from: r.from_city, to: r.to_city,
      date: r.departure_date, time: r.departure_time,
      name: r.profiles?.full_name || 'Driver',
      college: r.profiles?.university || '',
      rating: r.profiles?.rating || null,
      car: r.profiles?.car_make_model || r.vehicle || '',
      total: r.total_seats, taken: r.total_seats - r.seats_available,
      ow: r.price_one_way, rt: r.price_round_trip,
      pays: r.payment_methods || [],
      notes: r.notes || '',
      rt_trip: !!r.price_round_trip,
      driver_id: r.driver_id,
    }));
    const riderRequests = (requests || []).map((r: any) => ({
      id: r.id, type: 'request',
      from: r.from_city, to: r.to_city,
      date: r.trip_date, time: r.preferred_time || 'Flexible',
      name: r.profiles?.full_name || 'Rider',
      college: r.profiles?.university || '',
      ow: r.offer_one_way, rt: r.offer_round_trip,
      pays: r.payment_methods || [],
      notes: r.notes || '',
    }));
    setRIDES([...driverRides, ...riderRequests]);
    setRidesLoading(false);
  }

  React.useEffect(() => { searchRides(); }, []);
  const list = RIDES.filter((r: any) => {
    if (filter === 'drivers') return r.type === 'driver';
    if (filter === 'requests') return r.type === 'request';
    if (filter === 'seats') return r.type === 'driver' && r.total - r.taken > 0;
    return true;
  });

  return (
    <div>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>Find a college ride</div>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: 16 }}>Weekend trips · Long distance · Seats or full cars</div>
      <div style={s.cardSm}>
        <div style={s.grid3}><Field label="From"><input style={s.input} placeholder="Departure city" /></Field><Field label="To"><input style={s.input} placeholder="Destination city" /></Field><Field label="Date"><input type="date" style={s.input} /></Field></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
          {[['all', 'All'], ['drivers', 'Drivers only'], ['requests', 'Rider requests'], ['seats', 'Seats open']].map(([k, l]) => (
            <span key={k} onClick={() => setFilter(k)} style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: filter === k ? C.collegeL : C.sur2, color: filter === k ? C.collegeD : C.ink2 }}>{l}</span>
          ))}
          <button onClick={searchRides} style={{ ...s.btnSm(C.college), marginLeft: 'auto' }}>Search</button>
        </div>
      </div>

      {booking && (
        <Card border={C.college} shadow={`0 0 0 3px ${C.collegeL}`}>
          {bookConfirmed ? (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 5 }}>Request sent to {booking.name}!</div>
              <div style={{ fontSize: 12, color: C.ink3, marginBottom: 14 }}>{booking.from} → {booking.to} · {booking.date}</div>
              <button onClick={() => { nav('myrides'); setBooking(null); setBookConfirmed(false); }} style={s.btn(C.college)}>View in my rides →</button>
            </div>
          ) : (
            <>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 3 }}>Book a seat with {booking.name}</div>
              <div style={{ fontSize: 12, color: C.ink3, marginBottom: 10 }}>{booking.from} → {booking.to} · {booking.date} · {booking.time}</div>
              <div style={{ background: C.sur2, borderRadius: 9, padding: 10, marginBottom: 8, fontSize: 12, color: C.ink3, textAlign: 'center' }}>
                🗺️ {booking.from} → {booking.to}
              </div>
              <div style={{ marginBottom: 12 }} />
              <div style={s.grid2}>
                <Field label="Trip type">
                  <select style={s.input} value={tripType} onChange={e => setTripType(e.target.value)}>
                    <option value="one-way">One-way — ${booking.ow}</option>
                    {booking.rt && <option value="round-trip">Round trip — ${booking.rt}</option>}
                  </select>
                </Field>
                <Field label="Seats">
                  <select style={s.input} value={seats} onChange={e => setSeats(Number(e.target.value))}>
                    {Array.from({ length: booking.total - booking.taken }, (_, i) => <option key={i+1} value={i+1}>{i+1}</option>)}
                  </select>
                </Field>
              </div>
              <div style={{ background: C.sur2, borderRadius: 9, padding: 11, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}><span>Seat(s)</span><span>${tripType === 'round-trip' ? booking.rt : booking.ow} × {seats}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, borderTop: `1px solid ${C.bdr}`, paddingTop: 5 }}><span>Total to pay driver</span><span>${(tripType === 'round-trip' ? booking.rt : booking.ow) * seats}</span></div>
              </div>
              <Alert bg={C.amberL} color={C.amberD} bdr={C.amber}>Pay driver directly — before departure or via their method.</Alert>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setBooking(null)} style={s.btn(C.sur2, C.ink2)}>Cancel</button>
                <button onClick={() => setBookConfirmed(true)} style={{ ...s.btn(C.college), flex: 1, justifyContent: 'center' }}>Request to join ✓</button>
              </div>
            </>
          )}
        </Card>
      )}

      {offerTarget && (
        <Card border={C.amber} shadow={`0 0 0 3px ${C.amberL}`}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 3 }}>Offer {offerTarget.name} a seat</div>
          <Alert bg={C.blueL} color={C.blueD} bdr={C.blue}>Offering ${offerTarget.ow} OW{offerTarget.rt ? ` or $${offerTarget.rt} RT` : ''}. Accepts: {offerTarget.pays.join(', ')}</Alert>
          <Field label="Your message"><textarea style={{ ...s.input, minHeight: 54, resize: 'vertical' }} placeholder="Hi! I have a seat available — happy to give you a ride if the timing works…" /></Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setOfferTarget(null)} style={s.btn(C.sur2, C.ink2)}>Cancel</button>
            <button onClick={() => setOfferTarget(null)} style={{ ...s.btn(C.coral), flex: 1, justifyContent: 'center' }}>Send seat offer →</button>
          </div>
        </Card>
      )}

      {ridesLoading && <div style={{ textAlign:'center', padding:20, color:C.ink3 }}>Loading rides…</div>}
      {!ridesLoading && list.length === 0 && (
        <div style={{ textAlign:'center', padding:'32px 16px' }}>
          <div style={{ fontSize:40, marginBottom:10 }}>🎓</div>
          <div style={{ fontWeight:700, fontSize:16, marginBottom:8 }}>No rides posted yet</div>
          <div style={{ fontSize:13, color:C.ink3, marginBottom:16 }}>Be the first to post a ride or request a seat!</div>
          <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
            <button onClick={() => nav('postride')} style={s.btn(C.college)}>Post a ride</button>
            <button onClick={() => nav('request')} style={s.outlineBtn(C.college)}>Request a seat</button>
          </div>
        </div>
      )}
      {list.map(r => r.type === 'driver' ? (
        <div key={r.id} onClick={() => { setOfferTarget(null); setBookConfirmed(false); setBooking(r); window.scrollTo(0, 0); }} style={{ border: `1.5px solid ${C.bdr}`, borderRadius: 14, padding: 13, marginBottom: 9, cursor: 'pointer' }}
          onMouseOver={e => (e.currentTarget.style.borderColor = C.college)} onMouseOut={e => (e.currentTarget.style.borderColor = C.bdr)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const, marginBottom: 8 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: C.sur2, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}><strong>{r.from}</strong><span style={{ color: C.ink3 }}>→</span><strong>{r.to}</strong></span>
            <Tag bg={C.blueL} color={C.blueD}>{r.date}</Tag><Tag bg={C.sur2} color={C.ink2}>{r.time}</Tag>
            {r.rt_trip && <Tag bg={C.amberL} color={C.amberD}>Round trip</Tag>}
            <span style={{ marginLeft: 'auto' }}><Tag bg={C.collegeL} color={C.collegeD}>Driver</Tag></span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Av name={r.name} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name} <span style={{ fontSize: 11, color: C.ink3 }}>{r.college} · {r.rating}★</span></div>
              <div style={{ fontSize: 11, color: C.ink3 }}>{r.car}</div>
              <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
                {Array.from({ length: r.total! }, (_, i) => <div key={i} style={{ width: 17, height: 17, borderRadius: 3, background: i < r.taken! ? C.coralL : C.schoolL, border: i >= r.taken! ? `1px dashed ${C.school}` : undefined, fontSize: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: i < r.taken! ? C.coralD : C.schoolD, fontWeight: 700 }}>{i < r.taken! ? '●' : '+'}</div>)}
                <span style={{ fontSize: 11, color: C.ink3, alignSelf: 'center', marginLeft: 4 }}>{r.total! - r.taken!} open</span>
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.school }}>${r.ow}</div>
              <div style={{ fontSize: 11, color: C.ink3 }}>one-way</div>
              {r.rt && <div style={{ fontSize: 12, fontWeight: 600, color: C.collegeD, marginTop: 2 }}>${r.rt} RT</div>}
            </div>
          </div>
          <div style={{ fontSize: 11, color: C.ink3, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.sur2}` }}>
            {r.pays.map(p => <span key={p} style={{ background: C.sur2, borderRadius: 8, padding: '1px 7px', fontSize: 10, fontWeight: 700, marginRight: 3 }}>{p}</span>)}
            &ldquo;{r.notes?.substring(0, 48)}&hellip;&rdquo;
          </div>
        </div>
      ) : (
        <div key={r.id} onClick={() => { setBooking(null); setOfferTarget(r); window.scrollTo(0, 0); }} style={{ border: `1.5px solid ${C.bdr}`, borderRadius: 14, padding: 13, marginBottom: 9, cursor: 'pointer' }}
          onMouseOver={e => (e.currentTarget.style.borderColor = C.amber)} onMouseOut={e => (e.currentTarget.style.borderColor = C.bdr)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const, marginBottom: 8 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: C.sur2, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}><strong>{r.from}</strong><span style={{ color: C.ink3 }}>→</span><strong>{r.to}</strong></span>
            <Tag bg={C.blueL} color={C.blueD}>{r.date}</Tag><Tag bg={C.sur2} color={C.ink2}>{r.time}</Tag>
            <span style={{ marginLeft: 'auto' }}><Tag bg={C.amberL} color={C.amberD}>Rider looking</Tag></span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Av name={r.name} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name} <span style={{ fontSize: 11, color: C.ink3 }}>{r.college}</span></div>
              <div style={{ fontSize: 11, color: C.ink3 }}>{r.notes}</div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.amber }}>${r.ow}</div>
              <div style={{ fontSize: 11, color: C.ink3 }}>offering OW</div>
              {r.rt && <div style={{ fontSize: 12, fontWeight: 600, color: C.amberD, marginTop: 2 }}>${r.rt} RT</div>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── POST RIDE SCREEN ─────────────────────────────────────────────────────────
function PostRideScreen({ nav }: { nav: (p: string) => void }) {
  const { session } = useApp();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [pays, setPays] = useState(['cash']);
  const { profile } = useApp();
  const [f, setF] = useState({ from: '', to: '', date: '', time: '16:00', ttype: 'one-way', retDate: '', retTime: '18:00', pickup: '', car: profile?.car_make_model || '', seats: '2', ow: '', rt: '', note: '' });
  const upd = (k: string, v: string) => setF(x => ({ ...x, [k]: v }));
  const togglePay = (p: string) => setPays(ps => ps.includes(p) ? ps.filter(x => x !== p) : [...ps, p]);

  async function publish() {
    setSaving(true);
    if (supabase && session) {
      await supabase.from('college_rides').insert({ driver_id: session.user.id, from_city: f.from, to_city: f.to, departure_date: f.date, departure_time: f.time, trip_type: f.ttype, vehicle: f.car, total_seats: parseInt(f.seats), seats_available: parseInt(f.seats), price_one_way: parseFloat(f.ow), price_round_trip: parseFloat(f.rt), pickup_location: f.pickup, payment_methods: pays, notes: f.note });
    }
    setSaving(false);
    setStep(4);
  }

  const StepBar = () => (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
      {['Trip details', 'Payment', 'Publish'].map((st, i) => {
        const n = i + 1; const done = n < step; const act = n === step;
        return (
          <React.Fragment key={st}>
            {i > 0 && <div style={{ flex: 1, height: 1, background: C.bdr, margin: '0 8px' }} />}
            <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, background: done || act ? C.college : C.sur2, color: done || act ? '#fff' : C.ink3, boxShadow: act ? `0 0 0 4px ${C.collegeL}` : undefined }}>{done ? '✓' : n}</div>
            <span style={{ fontSize: 11, fontWeight: 600, marginLeft: 5, color: act ? C.collegeD : C.ink3 }}>{st}</span>
          </React.Fragment>
        );
      })}
    </div>
  );

  if (step === 4) return (
    <div style={{ textAlign: 'center', padding: '30px 16px' }}>
      <div style={{ fontSize: 46, marginBottom: 12 }}>🚗</div>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 8 }}>Ride posted!</div>
      <div style={{ fontSize: 13, color: C.ink3, marginBottom: 20 }}>Students will see your listing and send join requests.</div>
      <button onClick={() => nav('myrides')} style={s.btn(C.college)}>View in my rides →</button>
    </div>
  );

  return (
    <div>
      <StepBar />
      {step === 1 && (
        <>
          <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 16 }}>Post a ride</div>
          <Card>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Route & timing</div>
            <div style={s.grid2}><Field label="From"><input style={s.input} value={f.from} onChange={e => upd('from', e.target.value)} /></Field><Field label="To"><input style={s.input} value={f.to} onChange={e => upd('to', e.target.value)} /></Field></div>
            <div style={s.grid3}><Field label="Date"><input type="date" style={s.input} value={f.date} onChange={e => upd('date', e.target.value)} /></Field><Field label="Departs"><input type="time" style={s.input} value={f.time} onChange={e => upd('time', e.target.value)} /></Field><Field label="Trip type"><select style={s.input} value={f.ttype} onChange={e => upd('ttype', e.target.value)}><option value="one-way">One-way</option><option value="round-trip">Round trip</option></select></Field></div>
            {f.ttype === 'round-trip' && <div style={s.grid2}><Field label="Return date"><input type="date" style={s.input} value={f.retDate} onChange={e => upd('retDate', e.target.value)} /></Field><Field label="Return time"><input type="time" style={s.input} value={f.retTime} onChange={e => upd('retTime', e.target.value)} /></Field></div>}
            <Field label="Pickup location"><input style={s.input} value={f.pickup} onChange={e => upd('pickup', e.target.value)} /></Field>
          </Card>
          <Card>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Vehicle & seats</div>
            <div style={s.grid3}><Field label="Vehicle"><input style={s.input} value={f.car} onChange={e => upd('car', e.target.value)} placeholder="Honda Civic" /></Field><Field label="Seats for riders"><select style={s.input} value={f.seats} onChange={e => upd('seats', e.target.value)}><option>1</option><option>2</option><option>3</option><option>4</option></select></Field><Field label="Luggage"><select style={s.input}><option>Small bags only</option><option>1 bag/rider</option><option>Large bags ok</option></select></Field></div>
            <Field label="Notes for riders"><textarea style={{ ...s.input, minHeight: 54, resize: 'vertical' }} value={f.note} onChange={e => upd('note', e.target.value)} placeholder="Non-smoker, music on. Happy to stop at a gas station halfway." /></Field>
          </Card>
          <div style={{ display: 'flex', gap: 8 }}><button onClick={() => nav('browse')} style={s.btn(C.sur2, C.ink2)}>Cancel</button><button onClick={() => setStep(2)} style={{ ...s.btn(C.college), flex: 1, justifyContent: 'center' }}>Set payment →</button></div>
        </>
      )}
      {step === 2 && (
        <>
          <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 16 }}>Payment setup</div>
          <Card>
            <div style={s.grid2}><Field label="One-way ($)"><input type="number" style={s.input} value={f.ow} onChange={e => upd('ow', e.target.value)} /></Field><Field label="Round trip ($)"><input type="number" style={s.input} value={f.rt} onChange={e => upd('rt', e.target.value)} /></Field></div>
            <div style={{ background: C.sur2, borderRadius: 9, padding: 11, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.ink2, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6 }}>Estimated earnings ({f.seats} seats)</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}><span>One-way × {f.seats}</span><span>${parseFloat(f.ow) * parseInt(f.seats)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}><span>Round trip × {f.seats}</span><span>${parseFloat(f.rt) * parseInt(f.seats)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, borderTop: `1px solid ${C.bdr}`, marginTop: 5, paddingTop: 5 }}><span>Max total</span><span>${Math.max(parseFloat(f.ow), parseFloat(f.rt)) * parseInt(f.seats)}</span></div>
            </div>
            {[['cash', 'Cash', 'Collect before/after ride'], ['venmo', 'Venmo', '@handle'], ['zelle', 'Zelle', 'Phone/email'], ['cashapp', 'CashApp', '$cashtag']].map(([k, l, d]) => (
              <div key={k} onClick={() => togglePay(k)} style={{ border: `1.5px solid ${pays.includes(k) ? C.college : C.bdr}`, background: pays.includes(k) ? C.collegeL : C.sur, borderRadius: 9, padding: '10px 12px', marginBottom: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9 }}>
                <div style={{ width: 13, height: 13, borderRadius: '50%', border: `2px solid ${pays.includes(k) ? C.college : C.bdr}`, background: pays.includes(k) ? C.college : undefined, flexShrink: 0 }} />
                <div><div style={{ fontWeight: 600, fontSize: 13 }}>{l}</div><div style={{ fontSize: 11, color: C.ink3 }}>{d}</div></div>
              </div>
            ))}
            <Alert bg={C.amberL} color={C.amberD} bdr={C.amber}>SchoolPool does not process payments. All transactions are directly between you and your riders.</Alert>
          </Card>
          <div style={{ display: 'flex', gap: 8 }}><button onClick={() => setStep(1)} style={s.btn(C.sur2, C.ink2)}>← Back</button><button onClick={() => setStep(3)} style={{ ...s.btn(C.college), flex: 1, justifyContent: 'center' }}>Preview listing →</button></div>
        </>
      )}
      {step === 3 && (
        <>
          <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 16 }}>Preview & publish</div>
          <Card border={C.college} shadow={`0 0 0 3px ${C.collegeL}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}><Av name="You" size={46} /><div><div style={{ fontWeight: 600, fontSize: 14 }}>Your listing</div><div style={{ fontSize: 11, color: C.ink3 }}>New driver</div></div><div style={{ marginLeft: 'auto' }}><Tag bg={C.collegeL} color={C.collegeD}>Driver</Tag></div></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const, marginBottom: 10 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: C.sur2, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}><strong>{f.from.split(',')[0]}</strong><span style={{ color: C.ink3 }}>→</span><strong>{f.to.split(',')[0]}</strong></span>
              {f.date && <Tag bg={C.blueL} color={C.blueD}>{f.date}</Tag>}
              <Tag bg={C.sur2} color={C.ink2}>{f.time}</Tag>
              {f.ttype === 'round-trip' && <Tag bg={C.amberL} color={C.amberD}>Round trip</Tag>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
              {[['$' + f.ow, 'one-way', C.school], ['$' + f.rt, 'round trip', C.college], [f.seats, 'seats open', C.ink]].map(([n, l, c]) => (
                <div key={l} style={{ textAlign: 'center', padding: 9, background: C.sur2, borderRadius: 9 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: c as string }}>{n}</div>
                  <div style={{ fontSize: 11, color: C.ink3 }}>{l}</div>
                </div>
              ))}
            </div>
            <Divider />
            <div style={{ fontSize: 11, color: C.ink2, marginBottom: 4 }}>{f.car || 'Vehicle TBD'} · {f.pickup}</div>
            <Divider />
            <div style={{ display: 'flex', gap: 6 }}>{pays.map(p => <Tag key={p} bg={C.sur2} color={C.ink2}>{p}</Tag>)}</div>
          </Card>
          <Alert bg={C.collegeL} color={C.collegeD} bdr={C.college}>Visible to students at nearby colleges searching this route.</Alert>
          <div style={{ display: 'flex', gap: 8 }}><button onClick={() => setStep(2)} style={s.btn(C.sur2, C.ink2)}>← Edit</button><button onClick={publish} disabled={saving} style={{ ...s.btn(C.college), flex: 1, justifyContent: 'center', opacity: saving ? .7 : 1 }}>{saving ? 'Publishing…' : 'Publish ride listing ✓'}</button></div>
        </>
      )}
    </div>
  );
}

// ─── REQUEST RIDE SCREEN ──────────────────────────────────────────────────────
function RequestRideScreen({ nav }: { nav: (p: string) => void }) {
  const [submitted, setSubmitted] = useState(false);
  const [to, setTo] = useState('Orlando, FL');
  const [ow, setOw] = useState('15');
  const [rt, setRt] = useState('25');
  const [retTrip, setRetTrip] = useState('yes');

  if (submitted) return (
    <div style={{ textAlign: 'center', padding: '28px 16px' }}>
      <div style={{ fontSize: 42, marginBottom: 12 }}>🙋</div>
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Request posted!</div>
      <div style={{ fontSize: 13, color: C.ink3, marginBottom: 20 }}>Drivers heading to {to} will see your request and can offer you a seat.</div>
      <button onClick={() => nav('myrides')} style={s.btn(C.college)}>View in my rides →</button>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>Request a ride</div>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: 16 }}>No driver found? Post a request — drivers heading your way will reach out</div>
      <Card>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Trip details</div>
        <div style={s.grid2}><Field label="From"><input style={s.input} placeholder="Your city, State" onChange={e => {}} /></Field><Field label="To"><input style={s.input} value={to} onChange={e => setTo(e.target.value)} placeholder="Destination city" /></Field></div>
        <div style={s.grid3}><Field label="Date needed"><input type="date" style={s.input} /></Field><Field label="Flexibility"><select style={s.input}><option>Exact date</option><option>± 1 day</option><option>Fri or Sat</option></select></Field><Field label="Return trip?"><select style={s.input} value={retTrip} onChange={e => setRetTrip(e.target.value)}><option value="no">One-way only</option><option value="yes">Yes, returning too</option></select></Field></div>
        {retTrip === 'yes' && <Field label="Return date"><input type="date" style={s.input} /></Field>}
        <Field label="Pickup notes"><input style={s.input} placeholder="Can meet anywhere in Gainesville — no car" /></Field>
      </Card>
      <Card>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>What you&apos;re offering</div>
        <div style={{ fontSize: 12, color: C.ink3, marginBottom: 12 }}>Drivers see this when considering your request</div>
        <div style={s.grid2}><Field label="One-way offer ($)"><input type="number" style={s.input} value={ow} onChange={e => setOw(e.target.value)} /></Field><Field label="Round trip offer ($)"><input type="number" style={s.input} value={rt} onChange={e => setRt(e.target.value)} /></Field></div>
        <div style={{ background: C.sur2, borderRadius: 9, padding: 11, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}><span>One-way</span><span>${ow}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}><span>Round trip</span><span>${rt}</span></div>
        </div>
        <div style={s.grid2}><Field label="Payment"><select style={s.input}><option>Cash</option><option>Venmo</option><option>Cash or Venmo</option><option>Any</option></select></Field><Field label="Luggage"><select style={s.input}><option>Backpack only</option><option>1 small bag</option><option>1 medium bag</option></select></Field></div>
        <Field label="Additional notes"><textarea style={{ ...s.input, minHeight: 54, resize: 'vertical' }} placeholder="Graduate student, very flexible. Happy to share snacks!" /></Field>
      </Card>
      <Alert bg={C.blueL} color={C.blueD} bdr={C.blue}>Your request will be listed publicly. Drivers on the same route can message you.</Alert>
      <button onClick={() => setSubmitted(true)} style={{ ...s.btn(C.coral), width: '100%', justifyContent: 'center' }}>Post ride request</button>
    </div>
  );
}

// ─── MY RIDES SCREEN ──────────────────────────────────────────────────────────
function MyRidesScreen({ nav }: { nav: (p: string) => void }) {
  // College trips loaded from Supabase
  const { profile } = useApp();
  const DUTIES: any[] = []; // Will be populated from Supabase schedule
  const HIST: any[] = []; // Populated from Supabase
  const dc = (d: string) => d === 'DRIVE' || d === 'DROP' ? [C.schoolL, C.schoolD] : [C.blueL, C.blueD];

  return (
    <div>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>My rides</div>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: 16 }}>School carpool schedule + college trip bookings</div>

      <SLabel>School carpool — this week</SLabel>
      <div style={{ ...s.cardSm, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const, marginBottom: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', background: C.schoolL, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600, color: C.schoolD }}>
            {profile?.school_name || 'No school set'}
          </span>
        </div>
        {DUTIES.length === 0 ? (
          <div style={{ fontSize: 13, color: C.ink3, padding: '8px 0' }}>
            No schedule yet. <button onClick={() => nav('match')} style={{ background: 'none', border: 'none', color: C.school, fontWeight: 600, cursor: 'pointer', fontSize: 13, padding: 0 }}>Find a carpool group →</button>
          </div>
        ) : DUTIES.map(d => { const [bg, color] = dc(d.role); return (
          <div key={d.date} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: `1px solid ${C.sur2}` }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.ink2, minWidth: 72 }}>{d.date}</div>
            <span style={{ background: bg, color, borderRadius: 4, fontWeight: 700, fontSize: 10, padding: '3px 5px' }}>{d.role}</span>
            <span style={{ fontSize: 12, color: C.ink3 }}>{d.note}</span>
          </div>
        ); })}
        <button onClick={() => nav('schedule')} style={{ ...s.outlineBtn(C.school), marginTop: 8, fontSize: 12, padding: '5px 10px' }}>Full schedule →</button>
      </div>

      <SLabel>College trips — upcoming</SLabel>
      <div style={{ ...s.card, textAlign: 'center', padding: 28, marginBottom: 12 }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🎓</div>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>No upcoming college rides</div>
        <div style={{ fontSize: 13, color: C.ink3, marginBottom: 14 }}>Book a seat on someone's trip or post your own ride home.</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' as const }}>
          <button onClick={() => { nav('browse'); }} style={s.btn(C.college)}>Browse rides</button>
          <button onClick={() => nav('postride')} style={s.outlineBtn(C.college)}>Post a ride</button>
        </div>
      </div>

      <SLabel>Ride history</SLabel>
      <Card>
        {HIST.length === 0
          ? <div style={{ fontSize: 13, color: C.ink3, textAlign: 'center', padding: '12px 0' }}>No ride history yet. Your completed trips will appear here.</div>
          : HIST.map((r: any, i: number) => (
            <div key={i} style={{ display: 'flex', gap: 11, paddingBottom: 14, position: 'relative' }}>
              {i < HIST.length - 1 && <div style={{ position: 'absolute', left: 12, top: 26, bottom: 0, width: 1, background: C.bdr }} />}
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: r.bg, color: r.col, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>{r.icon}</div>
              <div><div style={{ fontWeight: 600, fontSize: 13 }}>{r.hd}</div><div style={{ fontSize: 12, color: C.ink3, marginTop: 1 }}>{r.sub}</div></div>
            </div>
          ))
        }
      </Card>
    </div>
  );
}

// ─── PROFILE SCREEN ───────────────────────────────────────────────────────────
function ProfileScreen({ nav }: { nav: (p: string) => void }) {
  const { profile, session } = useApp();
  const [signingOut, setSigningOut] = useState(false);

  async function handleSignOut() {
    setSigningOut(true);
    if (supabase) await supabase.auth.signOut();
    setSigningOut(false);
  }

  return (
    <div>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 16 }}>My profile</div>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <Av name={profile?.full_name || 'User'} size={54} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{profile?.full_name || 'Complete your profile'}</div>
            <div style={{ fontSize: 12, color: C.ink3 }}>{session?.user?.email}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              {profile?.rating && <Tag bg={C.amberL} color={C.amberD}>{profile.rating}★</Tag>}
              {profile?.total_rides > 0 && <Tag bg={C.schoolL} color={C.schoolD}>{profile.total_rides} rides</Tag>}
            </div>
          </div>
        </div>
        <Divider />
        {[
          ['School', profile?.school_name],
          ['Child', profile?.child_name ? `${profile.child_name} (${profile.child_grade})` : null],
          ['University', profile?.university],
          ['Address', profile?.address],
          ['Vehicle', profile?.car_make_model ? `${profile.car_make_model} (${profile.total_seats} seats)` : null],
        ].filter(([, v]) => v).map(([l, v]) => (
          <div key={l as string} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0' }}>
            <span style={{ color: C.ink3 }}>{l}</span>
            <span style={{ fontWeight: 500 }}>{v}</span>
          </div>
        ))}
      </Card>
      <button onClick={() => nav('editprofile')} style={{ ...s.outlineBtn(C.school), width: '100%', justifyContent: 'center', marginBottom: 8 }}>✏️ Edit profile</button>
      <button onClick={() => nav('help')} style={{ ...s.outlineBtn(C.school), width: '100%', justifyContent: 'center', marginBottom: 8 }}>❓ Help & Support</button>
      <button onClick={() => nav('verify')} style={{ ...s.btn(profile?.verification_status === 'verified' ? C.schoolL : profile?.verification_status === 'pending' ? C.amberL : C.coralL, profile?.verification_status === 'verified' ? C.schoolD : profile?.verification_status === 'pending' ? C.amberD : C.coralD), width: '100%', justifyContent: 'center', marginBottom: 8, border: 'none' }}>
        {profile?.verification_status === 'verified' ? '✅ Fully verified driver' : profile?.verification_status === 'pending' ? '⏳ Verification in review' : profile?.verification_status === 'partial' ? '⚠️ Complete driver verification' : '🔒 Get driver verified'}
      </button>
      <button onClick={() => nav('admin')} style={{ ...s.btn(C.sur2, C.ink2), width: '100%', justifyContent: 'center', marginBottom: 8 }}>🔐 Admin dashboard</button>
      <button onClick={handleSignOut} disabled={signingOut} style={{ ...s.btn('#fff', '#b91c1c'), width: '100%', justifyContent: 'center', border: '1px solid #fca5a5', opacity: signingOut ? .7 : 1 }}>{signingOut ? 'Signing out…' : 'Sign out'}</button>
    </div>
  );
}

// ─── BACKGROUND CHECK SCREEN ──────────────────────────────────────────────────
function BackgroundCheckScreen({ nav }: { nav: (p: string) => void }) {
  const { profile, session, setProfile } = useApp();
  const [step, setStep] = useState<string>('loading');
  const [form, setForm] = useState({ ssn: '', dob: '', license: '', licenseState: '', consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (!supabase || !session?.user?.id) { setStep('intro'); return; }
    supabase.from('background_checks')
      .select('status, submitted_at, expires_at')
      .eq('user_id', session.user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) { setStep('intro'); return; }
        const st = data.status || 'intro';
        setStep(st === 'not_started' ? 'intro' : st);
        if (data.status !== profile?.bg_check_status) {
          setProfile({ ...profile, bg_check_status: data.status });
        }
      });
  }, [session?.user?.id]);

  async function submitCheck() {
    setError('');
    if (!form.dob) { setError('Date of birth is required.'); return; }
    if (!form.license.trim()) { setError('Driver\'s license number is required.'); return; }
    if (!form.licenseState.trim()) { setError('License state is required.'); return; }
    if (!form.ssn || form.ssn.length !== 4) { setError('Last 4 digits of SSN are required.'); return; }
    if (!form.consent) { setError('Please consent to the background check to proceed.'); return; }
    setSubmitting(true);
    try {
      if (!supabase || !session) return;
      await supabase.from('background_checks').upsert({
        user_id: session.user.id,
        status: 'pending',
        submitted_at: new Date().toISOString(),
        expires_at: new Date(Date.now() + 365*24*60*60*1000).toISOString(),
        license_number: form.license,
        license_state: form.licenseState,
      });
      await supabase.from('profiles').update({ bg_check_status: 'pending' }).eq('id', session.user.id);
      setProfile({ ...profile, bg_check_status: 'pending' });
      // Notify admin
      const { data: { user } } = await supabase.auth.getUser();
      await sendEmail('sales@parnikasystems.com', 'PoolRide Admin',
        'Background Check Submission',
        `New background check submitted.\n\nUser: ${profile?.full_name}\nEmail: ${user?.email}\nLicense: ${form.license} (${form.licenseState})\nUser ID: ${session.user.id}`
      );
      setStep('pending');
    } catch(e: any) {
      setError('Submission failed. Please try again.');
    }
    setSubmitting(false);
  }

  if (step === 'loading') return <div style={{ textAlign: 'center', padding: 40, color: C.ink3 }}>Checking status…</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <button onClick={() => nav('profile')} style={s.btnSm(C.sur2, C.ink2)}>← Back</button>
        <button onClick={() => setStep('loading')} style={{ ...s.btnSm(C.sur2, C.ink2), marginLeft: 'auto' }}>↻ Refresh</button>
      </div>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>Background check</div>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: 16 }}>Optional — helps build trust with other carpool families</div>

      {step === 'approved' && (
        <div style={{ textAlign: 'center', padding: '32px 16px' }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Background check approved!</div>
          <div style={{ fontSize: 13, color: C.ink3, marginBottom: 16 }}>Your profile shows a ✓ verified badge to other families.</div>
          <button onClick={() => nav('group')} style={s.btn(C.school)}>Back to my group</button>
        </div>
      )}

      {step === 'pending' && (
        <div style={{ textAlign: 'center', padding: '24px 16px' }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>⏳</div>
          <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Check in progress</div>
          <div style={{ fontSize: 13, color: C.ink3, marginBottom: 20 }}>We\'ll review your submission and notify you by email within 1–3 business days.</div>
          <button onClick={() => nav('dashboard')} style={s.btn(C.school)}>Go to dashboard</button>
        </div>
      )}

      {step === 'intro' && (
        <>
          <div style={{ background: C.blueL, borderRadius: 12, padding: 16, marginBottom: 14, borderLeft: `3px solid ${C.blue}` }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.blueD, marginBottom: 6 }}>Why get verified?</div>
            <div style={{ fontSize: 12, color: C.blueD }}>Verified parents get a ✓ badge and are more likely to find carpool matches. This is optional — results kept private, only a badge is shown to other families.</div>
          </div>

          {error && <Alert bg={C.coralL} color={C.coralD} bdr={C.coral}>{error}</Alert>}

          <Card>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Your information</div>
            <div style={s.grid2}>
              <div style={{ marginBottom: 10 }}>
                <label style={s.label}>Date of birth <span style={{ color: C.coral }}>*</span></label>
                <input type="date" style={{ ...s.input, borderColor: !form.dob ? C.coral : undefined }}
                  value={form.dob} onChange={e => setForm(f => ({ ...f, dob: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={s.label}>Last 4 of SSN <span style={{ color: C.coral }}>*</span></label>
                <input style={s.input} type="password" maxLength={4} placeholder="••••"
                  value={form.ssn} onChange={e => setForm(f => ({ ...f, ssn: e.target.value.replace(/\D/g, '') }))} />
              </div>
            </div>
            <div style={s.grid2}>
              <div style={{ marginBottom: 10 }}>
                <label style={s.label}>Driver\'s license # <span style={{ color: C.coral }}>*</span></label>
                <input style={{ ...s.input, borderColor: !form.license ? C.coral : undefined }}
                  placeholder="License number" value={form.license}
                  onChange={e => setForm(f => ({ ...f, license: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={s.label}>License state <span style={{ color: C.coral }}>*</span></label>
                <input style={{ ...s.input, textTransform: 'uppercase' as const }}
                  placeholder="FL" maxLength={2} value={form.licenseState}
                  onChange={e => setForm(f => ({ ...f, licenseState: e.target.value.toUpperCase() }))} />
              </div>
            </div>
            <div onClick={() => setForm(f => ({ ...f, consent: !f.consent }))}
              style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 10, background: form.consent ? C.schoolL : C.sur2, borderRadius: 9, cursor: 'pointer', marginBottom: 14, border: `1px solid ${form.consent ? C.school : C.bdr}` }}>
              <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${form.consent ? C.school : C.bdr}`, background: form.consent ? C.school : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff', fontSize: 11, fontWeight: 700 }}>
                {form.consent ? '✓' : ''}
              </div>
              <div style={{ fontSize: 12, color: form.consent ? C.schoolD : C.ink2 }}>
                I consent to a background check and authorize PoolRide to verify my information. I understand only a verified badge is shown to other families.
              </div>
            </div>
            <button onClick={submitCheck} disabled={submitting}
              style={{ ...s.btn(C.school), width: '100%', justifyContent: 'center', opacity: submitting ? .7 : 1 }}>
              {submitting ? 'Submitting…' : 'Submit for review →'}
            </button>
          </Card>
          <div style={{ fontSize: 11, color: C.ink3, textAlign: 'center', marginTop: 8 }}>
            Free · Data encrypted · Manual review by PoolRide team · Results in 1–3 business days
          </div>
        </>
      )}
    </div>
  );
}

function LiveTrackingScreen({ nav, boardRole: boardRoleProp, boardContext: boardContextProp }: { nav: (p: string) => void; boardRole?: string | null; boardContext?: { destination: string; destAddress: string; pickupAddresses: string[]; acceptedNames: string[] } | null }) {
  // Restore from sessionStorage on refresh if props are null
  const boardRole = boardRoleProp || sessionStorage.getItem('boardTrackingRole') || null;
  const boardContext = boardContextProp || (() => {
    try { const s = sessionStorage.getItem('boardRideContext'); return s ? JSON.parse(s) : null; } catch { return null; }
  })();
  // isBoardRider: true means this user is a passenger in a board ride — never show driver UI
  const { session, profile } = useApp();
  const [driveStatus, setDriveStatus] = useState<'idle'|'active'|'arrived'>('idle');
  const [watchId, setWatchId] = useState<number|null>(null);
  const [location, setLocation] = useState<{lat:number,lng:number,heading:number,speed:number}|null>(null);
  const [currentStop, setCurrentStop] = useState('');
  const isBoardRider = boardRole === 'rider';
  const isBoardDrive = boardRole === 'driver';
  const isBoardRide = !!boardContext;
  const [etaMin, setEtaMin] = useState(isBoardRide ? 0 : 8);
  const [driveId, setDriveId] = useState<string|null>(null);
  // Riders stay in rider view, drivers in driver view, carpool group users in driver view
  const [riderView, setRiderView] = useState(isBoardRider);
  const [routeCoords, setRouteCoords] = useState<[number,number][]>([]);
  const [trackedDrive, setTrackedDrive] = useState<any>(null);
  const [groupId, setGroupId] = useState<string|null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [rideJustCompleted, setRideJustCompleted] = useState(false);

  // On mount: fetch user's group_id, restore any active drive
  React.useEffect(() => {
    if (!supabase || !session) return;
    (async () => {
      // Board rider: immediately check if driver already has an active drive
      if (isBoardRider) {
        const twoHoursAgoStr = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
        const { data: activeDrives } = await supabase.from('active_drives')
          .select('*').eq('status', 'started')
          .gte('updated_at', twoHoursAgoStr)
          .order('updated_at', { ascending: false }).limit(5);
        if (activeDrives?.length) {
          setTrackedDrive(activeDrives[0]);
          setInitialLoading(false);
          return;
        }
        setInitialLoading(false);
        // Poll every 5 seconds for driver to start — only recent drives (last 2hrs)
        const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
        const pollId = setInterval(async () => {
          const { data: drives } = await supabase!.from('active_drives')
            .select('*').eq('status', 'started')
            .gte('updated_at', twoHoursAgo)
            .order('updated_at', { ascending: false }).limit(5);
          if (drives?.length) {
            setTrackedDrive(drives[0]);
            clearInterval(pollId);
          }
        }, 5000);
        // Store poll id to clear on unmount
        (window as any)._riderPollId = pollId;
        return;
      }
      // Get group_id for this user
      const { data: gm } = await supabase.from('group_members')
        .select('group_id')
        .eq('user_id', session.user.id)
        .limit(1)
        .maybeSingle();
      if (gm?.group_id) setGroupId(gm.group_id);

      // Check if I am currently driving (only restore recent drives < 4 hours old)
      const fourHoursAgo = new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString();
      const { data: myDrive } = await supabase.from('active_drives')
        .select('*')
        .eq('driver_id', session.user.id)
        .eq('status', 'started')
        .gte('updated_at', fourHoursAgo)
        .maybeSingle();

      if (myDrive) {
        // Fully restore driver's active drive state (not for board riders)
        if (!isBoardRider) {
          setDriveId(myDrive.id);
          setDriveStatus('active');
          setCurrentStop(myDrive.current_stop || 'Drive in progress');
          setEtaMin(myDrive.eta_minutes || (isBoardRide ? 0 : 8));
          setRiderView(false);
        }
        // Restore completed stops from DB field if stored, else use current_stop hint
        if (myDrive.picked_up) {
          try { setCompletedStops(JSON.parse(myDrive.picked_up)); } catch {}
        }
        console.log('✅ Restored active drive:', myDrive.id, 'stop:', myDrive.current_stop);

        // Restart GPS watch
        if (navigator.geolocation) {
          const id = navigator.geolocation.watchPosition(
            async (pos) => {
              const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude, heading: pos.coords.heading || 0, speed: pos.coords.speed ? pos.coords.speed * 2.237 : 0 };
              setLocation(loc);
              setCurrentStop(stop => {
                supabase!.from('active_drives').update({ lat: loc.lat, lng: loc.lng, heading: loc.heading, speed_mph: loc.speed, updated_at: new Date().toISOString(), current_stop: stop }).eq('id', myDrive.id).then(() => {});
                return stop;
              });
              // Notify nearby riders (throttled by Edge Function to once per 5 min per user)
              const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
              if (supabaseUrl && myDrive?.id) {
                fetch(`${supabaseUrl}/functions/v1/car-nearby`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
                  body: JSON.stringify({ drive_id: myDrive.id, driver_lat: loc.lat, driver_lng: loc.lng }),
                }).catch(() => {});
              }
            },
            (err) => console.warn('GPS error:', err.message),
            { enableHighAccuracy: true, maximumAge: 5000 }
          );
          setWatchId(id);
        }
      } else {
        // Check if someone else is driving (rider view)
        const { data: activeDrive, error: adErr } = await supabase.from('active_drives')
          .select('*')
          .eq('status', 'started')
          .neq('driver_id', session.user.id)
          .limit(1)
          .maybeSingle();

        console.log('🔍 Rider check:', { activeDrive, error: adErr?.message, userId: session.user.id });

        if (activeDrive) {
          setTrackedDrive(activeDrive);
          setRiderView(true);
        } else {
          const { data: anyDrive, error: anyErr } = await supabase.from('active_drives')
            .select('id, driver_id, status').limit(5);
          console.log('🔍 Any drives visible:', { anyDrive, error: anyErr?.message });
        }
      }
      setInitialLoading(false);
      // If no drive found for either role, ensure clean idle state
      if (!myDrive) {
        setDriveStatus('idle');
        setDriveId(null);
      }
    })();
  }, [session]);

  // Realtime: listen for new drives, location updates, and deletions
  React.useEffect(() => {
    if (!supabase || !session) return;
    const channel = supabase.channel('live-tracking-' + session.user.id)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'active_drives',
      }, (payload) => {
        console.log('🚗 active_drives change:', payload.eventType, payload.new || payload.old);
        if (payload.eventType === 'DELETE') {
          const deleted = payload.old as any;
          if (deleted?.driver_id !== session.user.id) {
            setTrackedDrive(null);
            setRiderView(true); // Stay in rider view to show completion message
            setRideJustCompleted(true);
          }
          return;
        }
        const drive = payload.new as any;
        if (!drive || drive.driver_id === session.user.id) return;
        if (drive.status === 'completed') {
          setTrackedDrive(null);
          if (!isBoardRider) setRiderView(false);
        } else {
          setTrackedDrive(drive);
          setRiderView(true);
        }
      })
      .subscribe((status) => {
        console.log('📡 Realtime subscription status:', status);
      });
    return () => {
      supabase!.removeChannel(channel);
      // Clear any board rider polling
      if ((window as any)._riderPollId) {
        clearInterval((window as any)._riderPollId);
        (window as any)._riderPollId = null;
      }
    };
  }, [session]);

  // Auto-switch to rider view when a tracked drive appears
  React.useEffect(() => {
    if (trackedDrive && driveStatus === 'idle') {
      setRiderView(true);
    }
  }, [trackedDrive]);


  // Board ride: set stop text immediately when context available (no geocoding needed)
  React.useEffect(() => {
    if (!boardContext) return;
    const firstName = boardContext.acceptedNames?.[0] || 'first pickup';
    setCurrentStop(`Heading to ${firstName}`);
  }, [boardContext?.destination]);

  async function startDrive() {
    if (!supabase || !session) return;

    setCompletedStops([]);

    // INSERT drive record first
    const { data: driveData, error: driveErr } = await supabase.from('active_drives').insert({
      driver_id: session.user.id,
      group_id: groupId,
      status: 'started',
      lat: 0, lng: 0, heading: 0, speed_mph: 0,
      eta_minutes: etaMin, current_stop: currentStop || (boardContext ? `Heading to ${boardContext.acceptedNames?.[0] || 'pickup'}` : 'Drive started'),
      started_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }).select().single();

    if (driveErr || !driveData) {
      alert('Error starting drive: ' + (driveErr?.message || 'unknown error'));
      return;
    }

    const newDriveId = driveData.id;
    setDriveId(newDriveId);
    sessionStorage.setItem('activeDriveId', newDriveId);
    sessionStorage.setItem('activeDriveId', newDriveId);
    setDriveStatus('active');
    console.log('✅ Drive started:', newDriveId);
    alert('🚗 Drive started! Riders can now see your location.');

    // Start GPS if available (optional — drive works without it)
    if (!navigator.geolocation) {
      console.warn('GPS not available — drive started without location tracking');
      return;
    }

    const gpsId = navigator.geolocation.watchPosition(
      async (pos) => {
        const loc = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          heading: pos.coords.heading || 0,
          speed: pos.coords.speed ? pos.coords.speed * 2.237 : 0,
        };
        setLocation(loc);
        // Use functional setState pattern to get latest currentStop without stale closure
        setCurrentStop(stop => {
          supabase!.from('active_drives').update({
            lat: loc.lat, lng: loc.lng, heading: loc.heading,
            speed_mph: loc.speed, updated_at: new Date().toISOString(),
            current_stop: stop,
          }).eq('id', newDriveId).then(({ error }) => {
            if (error) console.error('GPS update error:', error.message);
          });
          return stop; // don't change the value
        });
      },
      (err) => console.error('GPS error:', err),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
    setWatchId(gpsId);
  }

  async function endDrive() {
    if (watchId !== null) navigator.geolocation.clearWatch(watchId);
    setWatchId(null);
    if (supabase && driveId) {
      // Delete active drive row
      const { error } = await supabase.from('active_drives').delete().eq('id', driveId);
      if (error) {
        console.error('End drive error:', error.message);
        await supabase.from('active_drives').update({ status: 'completed', updated_at: new Date().toISOString() }).eq('id', driveId);
      }
      // Mark any matched board_posts as completed where I am the accepted responder
      const { data: myAccepted } = await supabase.from('board_responses')
        .select('post_id')
        .eq('responder_id', session!.user.id)
        .eq('status', 'accepted');
      if (myAccepted && myAccepted.length > 0) {
        const postIds = myAccepted.map((r: any) => r.post_id);
        const { error: updateErr } = await supabase.from('board_posts')
          .update({ status: 'completed' })
          .in('id', postIds)
          .eq('status', 'matched');
        if (updateErr) console.error('Board post complete error:', updateErr.message);
        else {
          console.log('✅ Board posts marked completed:', postIds);
          // Store completed post IDs so board screen knows without DB reload
          sessionStorage.setItem('completedPostIds', JSON.stringify(postIds));
        }
      }
      console.log('✅ Drive ended:', driveId);
    }
    setDriveId(null);
    sessionStorage.removeItem('activeDriveId');
    setDriveStatus('arrived');
    setTrackedDrive(null);
    if (!isBoardRider) setRiderView(false);
    setLocation(null);
    setCompletedStops([]);
  }

  const [stops, setStops] = React.useState<string[]>([]);
  const [isDrivingToday, setIsDrivingToday] = React.useState(false);
  React.useEffect(() => {
    if (!supabase || !session) return;
    (async () => {
      const todayDate = new Date().toISOString().split('T')[0];
      // Only show checklist if user has a schedule entry as driver today
      const { data: todayEntry } = await supabase.from('schedule_entries')
        .select('group_id, ride_type, duty')
        .eq('user_id', session.user.id)
        .eq('date', todayDate)
        .in('duty', ['drive', 'pickup'])
        .maybeSingle();
      if (!todayEntry?.group_id) { setIsDrivingToday(false); return; }
      setIsDrivingToday(true);
      // Load other members' children in this group
      const { data } = await supabase.from('group_members')
        .select('children_included, profiles(full_name, child_name)')
        .eq('group_id', todayEntry.group_id)
        .neq('user_id', session.user.id);
      if (data && data.length > 0) {
        const names = data.flatMap((m: any) => {
          if (m.children_included?.length > 0) return m.children_included.map((c: any) => c.name || c);
          return [m.profiles?.child_name || m.profiles?.full_name].filter(Boolean);
        });
        setStops(names.filter(Boolean));
      }
    })();
  }, [session]);
  const [completedStops, setCompletedStops] = useState<string[]>([]);

  const markPickedUp = (name: string) => {
    setCompletedStops(prev => {
      const updated = [...prev, name];
      const remaining = stops.filter(s => !updated.includes(s));
      const newStop = remaining.length > 0 ? `Heading to pick up ${remaining[0]}` : 'All picked up — heading to school';
      setCurrentStop(newStop);
      // Persist to DB so state survives navigation
      if (supabase && driveId) {
        supabase.from('active_drives').update({
          current_stop: newStop,
          picked_up: JSON.stringify(updated),
          updated_at: new Date().toISOString(),
        }).eq('id', driveId).then(({ error }) => {
          if (error) console.error('markPickedUp save error:', error.message);
        });
      }
      return updated;
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <button onClick={() => nav('schedule')} style={s.btnSm(C.sur2, C.ink2)}>← Back</button>
        <div style={{ fontSize: 19, fontWeight: 800 }}>Live tracking</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {/* Hide driver/rider toggle for board riders — they can only track */}
          {!isBoardRider && (
            <>
              <button onClick={() => setRiderView(false)} style={riderView ? s.btnSm(C.sur2, C.ink2) : s.btnSm(C.school)}>Driver view</button>
              <button onClick={() => setRiderView(true)} style={riderView ? s.btnSm(C.school) : s.btnSm(C.sur2, C.ink2)}>Rider view</button>
            </>
          )}
        </div>
      </div>

      {!riderView && !isBoardRider && (
        <>
          {initialLoading && (
            <div style={{ textAlign: 'center', padding: 32, color: C.ink3 }}>
              Checking for active drive…
            </div>
          )}
          {!initialLoading && driveStatus === 'idle' && (
            <>
              {!isBoardRide && (
                <>
                  <div style={{ background: C.schoolL, borderRadius: 12, padding: 16, marginBottom: 14, borderLeft: `3px solid ${C.school}` }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.schoolD, marginBottom: 4 }}>Ready to start today's carpool?</div>
                    <div style={{ fontSize: 12, color: C.schoolD }}>Starting a drive shares your live location with the families in your group until you mark the drive complete. No one else can see your location.</div>
                  </div>
                  <Card>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Today's pickups</div>
                    {stops.length === 0
                      ? <div style={{ fontSize: 13, color: C.ink3 }}>No group members found. Add families from the Match screen.</div>
                      : stops.map((name, i) => (
                        <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < stops.length-1 ? `1px solid ${C.sur2}` : undefined }}>
                          <div style={{ width: 24, height: 24, borderRadius: '50%', background: C.schoolL, color: C.schoolD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i+1}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{name}</div>
                            <div style={{ fontSize: 11, color: C.ink3 }}>Address from profile</div>
                          </div>
                        </div>
                      ))
                    }
                  </Card>
                </>
              )}
              <button onClick={startDrive} style={{ ...s.btn(C.school), width: '100%', justifyContent: 'center', fontSize: 15, padding: '12px 0' }}>
                🚗 {isBoardRide ? 'Start ride & share location' : 'Start drive & share location'}
              </button>
            </>
          )}

          {driveStatus === 'active' && (
            <>
              <div style={{ background: C.school, borderRadius: 12, padding: 16, marginBottom: 14, color: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#4ade80', animation: 'pulse 1.5s infinite' }} />
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Drive active — location sharing on</div>
                </div>
                <div style={{ fontSize: 13, opacity: .9 }}>{currentStop}</div>
                {location && <div style={{ fontSize: 12, opacity: .8, marginTop: 4 }}>📍 {location.lat.toFixed(5)}, {location.lng.toFixed(5)} · {location.speed.toFixed(0)} mph</div>}
                <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }`}</style>
              </div>

              {location && (
                <div style={{ marginBottom: 12 }}>
                  <MapView
                    center={[location.lat, location.lng]}
                    zoom={13}
                    height={260}
                    markers={[{ lat: location.lat, lng: location.lng, label: 'You', color: '#1a7a5e' }]}
                    routePoints={routeCoords}
                  />
                </div>
              )}

              {/* Board ride: show passengers, route, and Google Maps link */}
              {isBoardRide && boardContext && (
                <div style={{ background: C.schoolL, borderRadius: 9, padding: 12, marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.schoolD, marginBottom: 8 }}>🗺️ Route to {boardContext.destination.split(';')[0].split(',')[0]}</div>
                  {boardContext.acceptedNames.map((name: string, i: number) => (
                    <div key={i} style={{ fontSize: 12, color: C.ink2, marginBottom: 4, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      <span>📍</span>
                      <div>
                        <div style={{ fontWeight: 600 }}>Pickup {i + 1}: {name}</div>
                        <div style={{ color: C.ink3 }}>{boardContext.pickupAddresses[i] || 'Contact rider for pickup address'}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{ fontSize: 12, color: C.schoolD, marginTop: 6, fontWeight: 600 }}>🏁 Drop-off: {boardContext.destAddress || boardContext.destination}</div>
                  {/* Open in Google Maps */}
                  {location && (() => {
                    const origin = `${location.lat},${location.lng}`;
                    const waypts = boardContext.pickupAddresses.filter(Boolean).map((a: string) => encodeURIComponent(a)).join('|');
                    const dest = encodeURIComponent(boardContext.destAddress || boardContext.destination);
                    const gmUrl = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}${waypts ? `&waypoints=${waypts}` : ''}&travelmode=driving`;
                    return (
                      <a href={gmUrl} target="_blank" rel="noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, padding: '8px 12px', background: C.school, color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: 12, fontWeight: 700, justifyContent: 'center' }}>
                        🗺️ Open full route in Google Maps
                      </a>
                    );
                  })()}
                </div>
              )}

              {!isBoardRide && isDrivingToday && stops.length > 0 && <Card>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Pickup checklist</div>
                {stops.map((name, i) => {
                  const done = completedStops.includes(name);
                  return (
                    <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: i < stops.length-1 ? `1px solid ${C.sur2}` : undefined }}>
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: done ? C.school : C.sur2, color: done ? '#fff' : C.ink3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                        {done ? '✓' : i+1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, textDecoration: done ? 'line-through' : undefined, color: done ? C.ink3 : C.ink }}>{name}</div>
                      </div>
                      {!done && (
                        <button onClick={() => markPickedUp(name)} style={{ ...s.btn(C.school), fontSize: 12, padding: '6px 12px' }}>
                          Tap when picked up →
                        </button>
                      )}
                      {done && (
                        <span style={{ ...s.tag(C.schoolL, C.schoolD), fontSize: 11 }}>✓ Picked up</span>
                      )}
                    </div>
                  );
                })}
              </Card>}
              {!isBoardRide && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ ...s.label, marginBottom: 4 }}>ETA to school (min)</label>
                    <input type="number" style={s.input} value={etaMin} onChange={e => setEtaMin(Number(e.target.value))} />
                  </div>
                </div>
              )}
              <button onClick={endDrive} style={{ ...s.btn('#fff', '#b91c1c'), width: '100%', justifyContent: 'center', marginTop: 10, border: '1px solid #fca5a5' }}>
                🏁 End drive & stop sharing
              </button>
            </>
          )}

          {driveStatus === 'arrived' && (
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
              <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Drive complete!</div>
              <div style={{ fontSize: 13, color: C.ink3, marginBottom: 20 }}>
                {completedStops.length > 0
                  ? `${completedStops.length} ${completedStops.length === 1 ? 'person' : 'people'} dropped off. Location sharing has stopped.`
                  : 'Location sharing has stopped.'}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' as const }}>
                <button onClick={() => nav('schedule')} style={s.btn(C.school)}>Back to schedule</button>
                <button onClick={() => { setDriveStatus('idle'); boardTrackingRole = null; boardRideContext = null; sessionStorage.removeItem('boardTrackingRole'); sessionStorage.removeItem('boardRideContext'); nav('board'); }} style={s.outlineBtn(C.school)}>Back to board</button>
              </div>
            </div>
          )}
        </>
      )}

      {(riderView || isBoardRider) && (
        <>
          {trackedDrive ? (
            <Card border={C.school} shadow={`0 0 0 3px ${C.schoolL}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#4ade80' }} />
                <div style={{ fontWeight: 700, fontSize: 14, color: C.schoolD }}>Driver is on the way</div>
                <div style={{ marginLeft: 'auto', fontWeight: 800, fontSize: 18, color: C.school }}>{trackedDrive.eta_minutes} min</div>
              </div>
              <div style={{ fontSize: 13, color: C.ink2, marginBottom: 8 }}>📍 {trackedDrive.current_stop || 'Driver is on the way'}</div>
              <div style={{ fontSize: 12, color: C.ink3 }}>Speed: {(trackedDrive.speed_mph || 0).toFixed(0)} mph</div>
              <div style={{ marginTop: 12 }}>
                {trackedDrive.lat && trackedDrive.lat !== 0 ? (
                  <MapView
                    center={[trackedDrive.lat, trackedDrive.lng]}
                    zoom={14}
                    height={220}
                    markers={[{ lat: trackedDrive.lat, lng: trackedDrive.lng, label: 'Driver', color: '#1a7a5e' }]}
                  />
                ) : (
                  <div style={{ background: C.sur2, borderRadius: 9, padding: 14, textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: C.ink3 }}>📍 Waiting for GPS signal…</div>
                  </div>
                )}
              </div>
            </Card>
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>
                  {initialLoading ? '⏳' : rideJustCompleted ? '🎉' : '📍'}
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
                  {initialLoading ? 'Checking for active drive…' : rideJustCompleted ? 'Ride complete!' : isBoardRider ? '⏳ Waiting for driver to start...' : 'No active drive right now'}
                </div>
                <div style={{ fontSize: 13, color: C.ink3 }}>
                  {rideJustCompleted
                    ? 'Your driver has ended the ride. Thanks for using PoolRide!'
                    : isBoardRider
                      ? 'Your driver will notify you when they start the ride. This page will update automatically.'
                      : 'When your driver starts a carpool drive, their live location will appear here.'}
                </div>
                {rideJustCompleted && (
                  <button onClick={() => { setRideJustCompleted(false); setRiderView(false); boardTrackingRole = null; boardRideContext = null; sessionStorage.removeItem('boardTrackingRole'); sessionStorage.removeItem('boardRideContext'); nav('board'); }} style={{ ...s.btn(C.school), marginTop: 14, justifyContent: 'center' }}>
                    Back to board
                  </button>
                )}
              </div>
            </Card>
          )}

          {!isBoardRide && !isBoardRider && (
            <Card>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>How tracking works</div>
              {[
                ['📍', 'Driver shares GPS location', 'Only active during the drive window'],
                ['👨‍👩‍👧', 'Group members only', 'Location never shared publicly'],
                ['🔔', 'Push notifications', 'Alert when driver is 5 min away'],
                ['🏁', 'Auto-stops at school', 'Tracking ends when drive is marked complete'],
              ].map(([icon, title, sub]) => (
                <div key={title as string} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.sur2}` }}>
                  <div style={{ fontSize: 20, flexShrink: 0 }}>{icon}</div>
                  <div><div style={{ fontWeight: 600, fontSize: 13 }}>{title}</div><div style={{ fontSize: 11, color: C.ink3 }}>{sub}</div></div>
                </div>
              ))}
            </Card>
          )}
        </>
      )}
    </div>
  );
}



// ─── ACTIVITY TYPES DATA ─────────────────────────────────────────────────────
const ACTIVITY_TYPES = [
  { id: 'dance',       icon: '💃', label: 'Dance',        color: '#be185d', colorL: '#fce7f3', colorD: '#831843' },
  { id: 'swimming',    icon: '🏊', label: 'Swimming',     color: '#0369a1', colorL: '#e0f2fe', colorD: '#0c4a6e' },
  { id: 'soccer',      icon: '⚽', label: 'Soccer',       color: '#15803d', colorL: '#dcfce7', colorD: '#14532d' },
  { id: 'baseball',    icon: '⚾', label: 'Baseball',     color: '#b45309', colorL: '#fef3c7', colorD: '#78350f' },
  { id: 'basketball',  icon: '🏀', label: 'Basketball',   color: '#c2410c', colorL: '#fff7ed', colorD: '#7c2d12' },
  { id: 'gymnastics',  icon: '🤸', label: 'Gymnastics',   color: '#7c3aed', colorL: '#ede9fe', colorD: '#4c1d95' },
  { id: 'tennis',      icon: '🎾', label: 'Tennis',       color: '#0d9488', colorL: '#ccfbf1', colorD: '#134e4a' },
  { id: 'karate',      icon: '🥋', label: 'Karate',       color: '#1d4ed8', colorL: '#eff6ff', colorD: '#1e3a8a' },
  { id: 'art',         icon: '🎨', label: 'Art class',    color: '#9333ea', colorL: '#f3e8ff', colorD: '#6b21a8' },
  { id: 'music',       icon: '🎵', label: 'Music',        color: '#0e7490', colorL: '#cffafe', colorD: '#164e63' },
  { id: 'cheer',       icon: '📣', label: 'Cheerleading', color: '#db2777', colorL: '#fce7f3', colorD: '#9d174d' },
  { id: 'volleyball',  icon: '🏐', label: 'Volleyball',   color: '#d97706', colorL: '#fef3c7', colorD: '#92400e' },
  { id: 'hockey',      icon: '🏒', label: 'Hockey',       color: '#475569', colorL: '#f1f5f9', colorD: '#1e293b' },
  { id: 'football',    icon: '🏈', label: 'Football',     color: '#7c3aed', colorL: '#ede9fe', colorD: '#4c1d95' },
  { id: 'scouting',    icon: '⛺', label: 'Scouting',     color: '#15803d', colorL: '#dcfce7', colorD: '#14532d' },
  { id: 'theater',     icon: '🎭', label: 'Theater',      color: '#9333ea', colorL: '#f3e8ff', colorD: '#6b21a8' },
  { id: 'coding',      icon: '💻', label: 'Coding club',  color: '#0369a1', colorL: '#e0f2fe', colorD: '#0c4a6e' },
  { id: 'other',       icon: '🌟', label: 'Other',        color: '#0369a1', colorL: '#e0f2fe', colorD: '#0c4a6e' },
];

// ─── ACTIVITIES SCREEN ────────────────────────────────────────────────────────
function ActivitiesScreen({ nav }: { nav: (p: string) => void }) {
  const { session, profile } = useApp();
  const [tab, setTab] = useState<'my'|'find'|'post'>('my');
  const [myGroups, setMyGroups] = useState<any[]>([]);
  const [actTypes, setActTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // Find / post state
  const [findAct, setFindAct] = useState('');
  const [findCity, setFindCity] = useState(profile?.city || '');
  const [findResults, setFindResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  // New group form
  const [newGroup, setNewGroup] = useState({ actTypeId: '', name: '', locationName: '', locationAddr: '', canDrive: true, childName: profile?.child_name || '' });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  React.useEffect(() => {
    if (!supabase || !session) return;
    Promise.all([
      supabase.from('activity_members').select('*, activity_groups(*, activity_types(name,icon))').eq('user_id', session.user.id),
      supabase.from('activity_types').select('*').order('category').order('name'),
    ]).then(([{ data: grps }, { data: types }]) => {
      setMyGroups(grps || []);
      setActTypes(types || []);
      setLoading(false);
    });
  }, [session]);

  async function findGroups() {
    if (!supabase) return;
    setSearching(true);
    let q = supabase.from('activity_groups')
      .select('*, activity_types(name,icon), activity_members(count), profiles!created_by(full_name,phone)')
      .eq('status', 'active');
    if (findAct) q = q.eq('activity_types.name', findAct);
    if (findCity) q = q.ilike('location_address', `%${findCity}%`);
    const { data } = await q.order('created_at', { ascending: false }).limit(30);
    setFindResults(data || []);
    setSearching(false);
  }

  async function joinGroup(groupId: string) {
    if (!supabase || !session) return;
    await supabase.from('activity_members').insert({
      group_id: groupId, user_id: session.user.id,
      child_name: newGroup.childName || profile?.child_name,
      can_drive: newGroup.canDrive,
    });
    alert('Joined! The group organiser will be in touch.');
    setTab('my');
  }

  async function createGroup() {
    if (!supabase || !session) return;
    if (!newGroup.actTypeId || !newGroup.name) { alert('Please select an activity and enter a group name.'); return; }
    setCreating(true);
    const { data: grp } = await supabase.from('activity_groups').insert({
      activity_type_id: newGroup.actTypeId,
      name: newGroup.name,
      location_name: newGroup.locationName,
      location_address: newGroup.locationAddr,
      created_by: session.user.id,
    }).select().single();
    if (grp) {
      await supabase.from('activity_members').insert({
        group_id: grp.id, user_id: session.user.id,
        child_name: newGroup.childName, can_drive: newGroup.canDrive,
      });
    }
    setCreating(false);
    setCreated(true);
  }

  const categories = ['sports','arts','academic','other'];
  const catLabel: any = { sports:'🏅 Sports', arts:'🎨 Arts & Music', academic:'📚 Academic', other:'⭐ Other' };

  return (
    <div>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>Activities carpooling</div>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: 14 }}>Dance, swimming, soccer, scouts and more — scheduled or one-off</div>

      {/* Tabs */}
      <div style={{ display: 'flex', background: C.sur2, borderRadius: 20, padding: 3, gap: 2, marginBottom: 14 }}>
        {([['my','My groups'],['find','Find a group'],['post','Start a group']] as const).map(([t,l]) => (
          <button key={t} onClick={() => setTab(t)} style={{ flex:1, padding:'7px 0', borderRadius:18, border:'none', cursor:'pointer', fontWeight:600, fontSize:12, fontFamily:'inherit', background: tab===t ? C.sur : 'transparent', color: tab===t ? C.ink : C.ink2, boxShadow: tab===t ? '0 1px 3px rgba(0,0,0,.1)' : undefined }}>
            {l}
          </button>
        ))}
      </div>

      {/* MY GROUPS */}
      {tab === 'my' && (
        <>
          {loading && <div style={{ textAlign:'center', padding:20, color:C.ink3 }}>Loading…</div>}
          {!loading && myGroups.length === 0 && (
            <div style={{ textAlign:'center', padding:'32px 16px' }}>
              <div style={{ fontSize:44, marginBottom:12 }}>⭐</div>
              <div style={{ fontSize:16, fontWeight:700, marginBottom:8 }}>No activity groups yet</div>
              <div style={{ fontSize:13, color:C.ink3, marginBottom:16 }}>Join an existing group or start one for your child's activity.</div>
              <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                <button onClick={() => setTab('find')} style={s.btn(C.school)}>Find a group</button>
                <button onClick={() => setTab('post')} style={s.outlineBtn(C.school)}>Start a group</button>
              </div>
            </div>
          )}
          {myGroups.map((m: any) => {
            const g = m.activity_groups;
            const at = g?.activity_types;
            return (
              <div key={m.id} onClick={() => nav('actgroup')} style={{ border:`1.5px solid ${C.bdr}`, borderRadius:14, padding:14, marginBottom:10, cursor:'pointer' }}
                onMouseOver={e => (e.currentTarget.style.borderColor=C.school)} onMouseOut={e => (e.currentTarget.style.borderColor=C.bdr)}>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
                  <div style={{ width:44, height:44, borderRadius:12, background:C.schoolL, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>{at?.icon || '⭐'}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:14 }}>{g?.name}</div>
                    <div style={{ fontSize:12, color:C.ink3 }}>{at?.name} · {g?.location_name || 'Location not set'}</div>
                  </div>
                  <span style={s.tag(C.schoolL, C.schoolD)}>Active</span>
                </div>
                {g?.location_address && (
                  <div style={{ fontSize:11, color:C.ink3, marginBottom:8 }}>📍 {g.location_address}</div>
                )}
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={e => { e.stopPropagation(); nav('actgroup'); }} style={s.btnSm(C.school)}>View trips & schedule</button>
                  <button onClick={e => e.stopPropagation()} style={s.btnSm(C.sur2, C.ink2)}>+ Post a trip</button>
                </div>
              </div>
            );
          })}
          {!loading && myGroups.length > 0 && (
            <button onClick={() => setTab('post')} style={{ ...s.outlineBtn(C.school), width:'100%', justifyContent:'center', marginTop:8 }}>+ Start another activity group</button>
          )}
        </>
      )}

      {/* FIND A GROUP */}
      {tab === 'find' && (
        <>
          <div style={s.card}>
            <div style={{ fontWeight:700, fontSize:14, marginBottom:10 }}>Search for a group</div>
            <div style={s.grid2}>
              <div style={{ marginBottom:10 }}>
                <label style={s.label}>Activity</label>
                <select style={s.input} value={findAct} onChange={e => setFindAct(e.target.value)}>
                  <option value="">All activities</option>
                  {actTypes.map((a:any) => <option key={a.id} value={a.name}>{a.icon} {a.name}</option>)}
                </select>
              </div>
              <div style={{ marginBottom:10 }}>
                <label style={s.label}>City / area</label>
                <input style={s.input} value={findCity} onChange={e => setFindCity(e.target.value)} placeholder="Tampa, Wesley Chapel…"/>
              </div>
            </div>
            <button onClick={findGroups} style={s.btn(C.school)}>Search</button>
          </div>

          {searching && <div style={{ textAlign:'center', padding:20, color:C.ink3 }}>Searching…</div>}

          {!searching && findResults.length === 0 && findAct && (
            <div style={{ textAlign:'center', padding:'24px 16px' }}>
              <div style={{ fontSize:36, marginBottom:8 }}>🔍</div>
              <div style={{ fontWeight:600, fontSize:14, marginBottom:6 }}>No groups found</div>
              <div style={{ fontSize:13, color:C.ink3, marginBottom:14 }}>Be the first! Start a carpool group for this activity.</div>
              <button onClick={() => setTab('post')} style={s.btn(C.school)}>Start a group →</button>
            </div>
          )}

          {findResults.map((g: any) => (
            <div key={g.id} style={{ border:`1.5px solid ${C.bdr}`, borderRadius:14, padding:14, marginBottom:10 }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
                <div style={{ width:44, height:44, borderRadius:12, background:C.schoolL, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>{g.activity_types?.icon || '⭐'}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:14 }}>{g.name}</div>
                  <div style={{ fontSize:12, color:C.ink3 }}>{g.activity_types?.name} · Organiser: {g.profiles?.full_name}</div>
                  {g.location_name && <div style={{ fontSize:11, color:C.ink3 }}>📍 {g.location_name}, {g.location_address}</div>}
                </div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                {g.profiles?.phone && (
                  <a href={`tel:${g.profiles.phone}`} style={{ ...s.btnSm(C.schoolL, C.schoolD), textDecoration:'none' }}>📞 Call organiser</a>
                )}
                <button onClick={() => joinGroup(g.id)} style={s.btnSm(C.school)}>+ Join this group</button>
              </div>
            </div>
          ))}
        </>
      )}

      {/* START A GROUP */}
      {tab === 'post' && (
        created ? (
          <div style={{ textAlign:'center', padding:'32px 16px' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🎉</div>
            <div style={{ fontSize:19, fontWeight:800, marginBottom:8 }}>Group created!</div>
            <div style={{ fontSize:13, color:C.ink3, marginBottom:20 }}>Share with other parents at your child's activity. They can find it by searching above.</div>
            <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
              <button onClick={() => { setCreated(false); setTab('my'); }} style={s.btn(C.school)}>View my groups →</button>
            </div>
          </div>
        ) : (
          <>
            <div style={s.card}>
              <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>Activity details</div>
              <div style={{ marginBottom:10 }}>
                <label style={s.label}>Activity type</label>
                {categories.map(cat => (
                  <div key={cat}>
                    <div style={{ fontSize:11, fontWeight:700, color:C.ink3, textTransform:'uppercase', letterSpacing:'.4px', margin:'10px 0 6px' }}>{catLabel[cat]}</div>
                    <div style={{ display:'flex', flexWrap:'wrap' as const, gap:6 }}>
                      {actTypes.filter((a:any) => a.category === cat).map((a:any) => (
                        <span key={a.id} onClick={() => setNewGroup(g => ({ ...g, actTypeId: a.id, name: g.name || `${a.name} Carpool` }))}
                          style={{ padding:'6px 12px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer', userSelect:'none',
                            border:`1.5px solid ${newGroup.actTypeId === a.id ? C.school : C.bdr}`,
                            background: newGroup.actTypeId === a.id ? C.school : C.sur,
                            color: newGroup.actTypeId === a.id ? '#fff' : C.ink2 }}>
                          {a.icon} {a.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={s.card}>
              <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>Group info</div>
              <div style={{ marginBottom:10 }}>
                <label style={s.label}>Group name</label>
                <input style={s.input} value={newGroup.name} onChange={e => setNewGroup(g => ({ ...g, name: e.target.value }))} placeholder="e.g. Emma's Dance Carpool" />
              </div>
              <div style={s.grid2}>
                <div style={{ marginBottom:10 }}>
                  <label style={s.label}>Venue / Studio name</label>
                  <input style={s.input} value={newGroup.locationName} onChange={e => setNewGroup(g => ({ ...g, locationName: e.target.value }))} placeholder="Studio 5 Dance Academy" />
                </div>
                <div style={{ marginBottom:10 }}>
                  <label style={s.label}>Address</label>
                  <input style={s.input} value={newGroup.locationAddr} onChange={e => setNewGroup(g => ({ ...g, locationAddr: e.target.value }))} placeholder="123 Main St, Tampa FL" />
                </div>
              </div>
              <div style={s.grid2}>
                <div style={{ marginBottom:10 }}>
                  <label style={s.label}>Your child's name</label>
                  <input style={s.input} value={newGroup.childName} onChange={e => setNewGroup(g => ({ ...g, childName: e.target.value }))} placeholder="Emma" />
                </div>
                <div style={{ marginBottom:10 }}>
                  <label style={s.label}>Can you drive?</label>
                  <select style={s.input} value={newGroup.canDrive ? 'yes' : 'no'} onChange={e => setNewGroup(g => ({ ...g, canDrive: e.target.value === 'yes' }))}>
                    <option value="yes">Yes, I can drive</option>
                    <option value="no">No, passenger only</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={{ background: C.schoolL, borderRadius:12, padding:14, marginBottom:14, fontSize:12, color:C.schoolD }}>
              💡 After creating, share the group name with other parents. They can find it in <strong>Find a group</strong> and join with one tap.
            </div>

            <button onClick={createGroup} disabled={creating} style={{ ...s.btn(C.school), width:'100%', justifyContent:'center', opacity: creating ? .7 : 1 }}>
              {creating ? 'Creating…' : 'Create activity group →'}
            </button>
          </>
        )
      )}
    </div>
  );
}

// ─── ACTIVITY GROUP SCREEN ────────────────────────────────────────────────────
function ActivityGroupScreen({ nav }: { nav: (p: string) => void }) {
  const { session, profile } = useApp();
  const [trips, setTrips] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [group, setGroup] = useState<any>(null);
  const [loadingGroup, setLoadingGroup] = useState(true);
  const [showPostTrip, setShowPostTrip] = useState(false);
  const [tripForm, setTripForm] = useState({ date: '', time: '16:00', returnTime: '18:00', type: 'scheduled', direction: 'both', seats: '3', notes: '' });
  const [posting, setPosting] = useState(false);

  // Load the user's most recent activity group
  React.useEffect(() => {
    if (!supabase || !session) return;
    supabase.from('activity_members')
      .select('*, activity_groups(*, activity_types(name,icon))')
      .eq('user_id', session.user.id)
      .order('joined_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.activity_groups) {
          const g = data.activity_groups;
          setGroup(g);
          // Load members
          return supabase.from('activity_members')
            .select('*, profiles(full_name, phone, has_car)')
            .eq('group_id', g.id);
        }
      })
      .then((res: any) => {
        if (res?.data) setMembers(res.data);
        setLoadingGroup(false);
      });
  }, [session]);

  // Load trips for this group
  React.useEffect(() => {
    if (!supabase || !group?.id) return;
    supabase.from('activity_trips')
      .select('*, profiles(full_name)')
      .eq('group_id', group.id)
      .gte('trip_date', new Date().toISOString().split('T')[0])
      .order('trip_date', { ascending: true })
      .then(({ data }) => setTrips(data || []));
  }, [group?.id]);

  const upd = (k: string, v: string) => setTripForm(f => ({ ...f, [k]: v }));

  async function postTrip() {
    if (!tripForm.date) { alert('Please select a date.'); return; }
    setPosting(true);
    if (supabase && session && group?.id) {
      const { data } = await supabase.from('activity_trips').insert({
        group_id: group.id, driver_id: session.user.id,
        trip_date: tripForm.date, departure_time: tripForm.time,
        return_time: tripForm.returnTime || null,
        trip_type: tripForm.type, direction: tripForm.direction,
        seats_available: parseInt(tripForm.seats),
        notes: tripForm.notes || null, status: 'open',
      }).select('*, profiles(full_name)').single();
      if (data) setTrips(prev => [data, ...prev]);
    }
    setPosting(false);
    setShowPostTrip(false);
  }

  const dirLabel: any = { to: '→ Drop only', from: '← Pickup only', both: '↔ Both ways' };
  const typeLabel: any = { scheduled: '📅 Scheduled', adhoc: '⚡ Ad hoc' };

  if (loadingGroup) return (
    <div style={{ textAlign: 'center', padding: 32, color: C.ink3 }}>
      <div style={{ fontSize: 32, marginBottom: 10 }}>⭐</div>
      Loading your activity group…
    </div>
  );

  if (!group) return (
    <div style={{ textAlign: 'center', padding: '32px 16px' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>⭐</div>
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>No activity group yet</div>
      <div style={{ fontSize: 13, color: C.ink3, marginBottom: 16 }}>Join or create an activity group first.</div>
      <button onClick={() => nav('activities')} style={s.btn(C.school)}>Go to Activities →</button>
    </div>
  );

  return (
    <div>
      <button onClick={() => nav('activities')} style={{ ...s.btnSm(C.sur2, C.ink2), marginBottom:16 }}>← Back to activities</button>

      {/* Group header */}
      <div style={{ background: C.school, borderRadius:14, padding:18, marginBottom:14, color:'#fff', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', right:-16, top:-16, width:100, height:100, borderRadius:'50%', background:'rgba(255,255,255,.1)' }} />
        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
            <div style={{ fontSize:32 }}>{group.activity_types?.icon || '⭐'}</div>
            <div>
              <div style={{ fontSize:18, fontWeight:800 }}>{group.name}</div>
              <div style={{ fontSize:12, opacity:.85 }}>{group.activity_types?.name} · {members.length} {members.length === 1 ? 'family' : 'families'}</div>
            </div>
          </div>
          {group.location_name && <div style={{ fontSize:12, opacity:.8 }}>📍 {group.location_name}</div>}
          {group.location_address && <div style={{ fontSize:11, opacity:.7 }}>{group.location_address}</div>}
        </div>
      </div>

      {/* Members */}
      <div style={s.card}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:10 }}>Members ({members.length})</div>
        {members.length === 0 && <div style={{ fontSize:13, color:C.ink3 }}>No other members yet. Invite families from the Activities screen.</div>}
        {members.map((m: any, i: number) => {
          const p = m.profiles;
          const isYou = m.user_id === session?.user?.id;
          return (
            <div key={m.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom: i < members.length-1 ? `1px solid ${C.sur2}` : undefined }}>
              <Av name={p?.full_name || 'Member'} size={32} />
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:600, fontSize:13 }}>{p?.full_name || 'Member'}{isYou ? ' (You)' : ''}</div>
                <div style={{ fontSize:11, color:C.ink3 }}>{m.can_drive ? '🚗 Can drive' : '👤 Passenger'}{m.child_name ? ' · ' + m.child_name : ''}</div>
              </div>
              {isYou
                ? <span style={s.tag(C.schoolL, C.schoolD)}>You</span>
                : <div style={{ display:'flex', gap:5 }}>
                    {p?.phone && <a href={`tel:${p.phone}`} style={{ ...s.btnSm(C.schoolL, C.schoolD), textDecoration:'none' }}>📞</a>}
                    <button onClick={() => nav('messages')} style={s.btnSm(C.sur2, C.ink2)}>Chat</button>
                  </div>
              }
            </div>
          );
        })}
        <div style={{ marginTop:10 }}>
          <button style={s.outlineBtn(C.school)}>+ Invite a family</button>
        </div>
      </div>

      {/* Upcoming trips */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
        <div style={{ fontWeight:700, fontSize:16 }}>Upcoming trips</div>
        <button onClick={() => setShowPostTrip(!showPostTrip)} style={{ ...s.btnSm(C.school), marginLeft:'auto' }}>+ Post a trip</button>
      </div>

      {/* Post trip form */}
      {showPostTrip && (
        <div style={{ ...s.card, borderColor:C.school, boxShadow:`0 0 0 3px ${C.schoolL}` }}>
          <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>Post a trip</div>
          <div style={s.grid2}>
            <div style={{ marginBottom:10 }}>
              <label style={s.label}>Trip type</label>
              <select style={s.input} value={tripForm.type} onChange={e => upd('type', e.target.value)}>
                <option value="scheduled">📅 Scheduled (recurring)</option>
                <option value="adhoc">⚡ Ad hoc (one-time)</option>
              </select>
            </div>
            <div style={{ marginBottom:10 }}>
              <label style={s.label}>Direction</label>
              <select style={s.input} value={tripForm.direction} onChange={e => upd('direction', e.target.value)}>
                <option value="both">↔ Drop off + Pick up</option>
                <option value="to">→ Drop off only</option>
                <option value="from">← Pick up only</option>
              </select>
            </div>
          </div>
          <div style={s.grid3}>
            <div style={{ marginBottom:10 }}>
              <label style={s.label}>Date</label>
              <input type="date" style={s.input} value={tripForm.date} onChange={e => upd('date', e.target.value)} />
            </div>
            <div style={{ marginBottom:10 }}>
              <label style={s.label}>Departure time</label>
              <input type="time" style={s.input} value={tripForm.time} onChange={e => upd('time', e.target.value)} />
            </div>
            <div style={{ marginBottom:10 }}>
              <label style={s.label}>Return time</label>
              <input type="time" style={s.input} value={tripForm.returnTime} onChange={e => upd('returnTime', e.target.value)} />
            </div>
          </div>
          <div style={s.grid2}>
            <div style={{ marginBottom:10 }}>
              <label style={s.label}>Seats available</label>
              <select style={s.input} value={tripForm.seats} onChange={e => upd('seats', e.target.value)}>
                {['1','2','3','4','5'].map(n => <option key={n}>{n}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:10 }}>
              <label style={s.label}>Notes (optional)</label>
              <input style={s.input} value={tripForm.notes} onChange={e => upd('notes', e.target.value)} placeholder="Extra rehearsal, bring gear…" />
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => setShowPostTrip(false)} style={s.btn(C.sur2, C.ink2)}>Cancel</button>
            <button onClick={postTrip} disabled={posting} style={{ ...s.btn(C.school), flex:1, justifyContent:'center', opacity: posting ? .7 : 1 }}>
              {posting ? 'Posting…' : 'Post trip'}
            </button>
          </div>
        </div>
      )}

      {/* Trip list */}
      {trips.length === 0 ? (
        <div style={{ textAlign:'center', padding:'24px 16px' }}>
          <div style={{ fontSize:32, marginBottom:8 }}>📅</div>
          <div style={{ fontWeight:600, fontSize:14, marginBottom:6 }}>No upcoming trips</div>
          <div style={{ fontSize:13, color:C.ink3 }}>Post the first trip for this group!</div>
        </div>
      ) : (
        trips.map((t: any) => {
          const driverName = t.profiles?.full_name || 'Driver';
          const isMe = t.driver_id === session?.user?.id;
          return (
            <div key={t.id} style={{ border:`1px solid ${C.bdr}`, borderRadius:12, padding:13, marginBottom:9 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' as const, marginBottom:8 }}>
                <span style={{ fontWeight:700, fontSize:13 }}>{new Date(t.trip_date + 'T12:00:00').toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'})}</span>
                <span style={s.tag(C.blueL, C.blueD)}>{t.departure_time?.slice(0,5)}</span>
                <span style={s.tag(t.trip_type === 'scheduled' ? C.schoolL : C.amberL, t.trip_type === 'scheduled' ? C.schoolD : C.amberD)}>{typeLabel[t.trip_type]}</span>
                <span style={s.tag(C.sur2, C.ink2)}>{dirLabel[t.direction]}</span>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                <Av name={driverName} size={30} />
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:13 }}>{driverName}{isMe ? ' (You)' : ''}</div>
                  {t.return_time && <div style={{ fontSize:11, color:C.ink3 }}>Return: {t.return_time?.slice(0,5)}</div>}
                </div>
                <div style={{ fontSize:12, color:C.ink3 }}>{t.seats_available} seats open</div>
              </div>
              {t.notes && <div style={{ fontSize:11, color:C.ink3, marginBottom:6, fontStyle:'italic' }}>"{t.notes}"</div>}
              {!isMe && <button style={s.btnSm(C.school)}>+ Join this trip</button>}
              {isMe && <span style={s.tag(C.schoolL, C.schoolD)}>✓ You're driving</span>}
            </div>
          );
        })
      )}
    </div>
  );
}


// ─── ADMIN DASHBOARD ─────────────────────────────────────────────────────────
const ADMIN_EMAIL = 'admin@poolride.app'; // ← change to YOUR email

function AdminDashboard({ nav }: { nav: (p: string) => void }) {
  const { session } = useApp();
  const [tab, setTab] = useState<'checks'|'users'|'rides'>('checks');
  const [checks, setChecks] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [rides, setRides] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [noteId, setNoteId] = useState('');
  const [stats, setStats] = useState({ total:0, pending:0, approved:0, rejected:0 });

  const isAdmin = session?.user?.email === ADMIN_EMAIL ||
    session?.user?.user_metadata?.is_admin === true;

  React.useEffect(() => { if (isAdmin) loadAll(); }, [isAdmin]);

  async function loadAll() {
    if (!supabase) return;
    setLoading(true);
    const [{ data: chkData }, { data: usersData }, { data: ridesData }] = await Promise.all([
      supabase.from('background_checks').select('*, profiles(full_name,phone,address,car_make_model,license_plate)').order('submitted_at', { ascending: false }),
      supabase.from('profiles').select('*').order('created_at', { ascending: false }),
      supabase.from('college_rides').select('*, profiles(full_name,phone)').order('created_at', { ascending: false }).limit(50),
    ]);
    const all = chkData || [];
    setChecks(all); setUsers(usersData || []); setRides(ridesData || []);
    setStats({ total: all.length, pending: all.filter((c:any)=>c.status==='pending').length, approved: all.filter((c:any)=>c.status==='approved').length, rejected: all.filter((c:any)=>c.status==='rejected').length });
    setLoading(false);
  }

  async function updateCheck(userId: string, checkId: string, status: 'approved'|'rejected') {
    if (!supabase) return;
    const { error: e1 } = await supabase.from('background_checks').update({ status, completed_at: new Date().toISOString(), expires_at: status==='approved' ? new Date(Date.now()+365*24*60*60*1000).toISOString() : null }).eq('id', checkId);
    const { error: e2 } = await supabase.from('profiles').update({ bg_check_status: status }).eq('id', userId);
    if (e1||e2) { alert('Error: '+(e1?.message||e2?.message)); return; }
    setChecks(prev => prev.map((c:any) => c.id===checkId ? {...c,status} : c));
    setUsers(prev => prev.map((u:any) => u.id===userId ? {...u,bg_check_status:status} : u));
    setStats(prev => ({ ...prev, pending: Math.max(0,prev.pending-1), approved: status==='approved' ? prev.approved+1 : prev.approved, rejected: status==='rejected' ? prev.rejected+1 : prev.rejected }));
    alert(status==='approved' ? '✅ Driver approved! They will see the verified badge next time they open the app.' : '❌ Driver rejected.');
  }

  async function saveNote(checkId: string) {
    if (!supabase||!note.trim()) return;
    await supabase.from('background_checks').update({ external_id: note }).eq('id', checkId);
    setChecks(prev => prev.map((c:any) => c.id===checkId ? {...c,external_id:note} : c));
    setNoteId(''); setNote('');
  }

  const stStyle = (st: string) => ({ pending:{bg:C.amberL,color:C.amberD,label:'⏳ Pending'}, approved:{bg:C.schoolL,color:C.schoolD,label:'✅ Approved'}, rejected:{bg:C.coralL,color:C.coralD,label:'❌ Rejected'}, not_started:{bg:C.sur2,color:C.ink3,label:'— Not started'} } as any)[st] || {bg:C.sur2,color:C.ink3,label:st};

  if (!isAdmin) return (
    <div style={{ textAlign:'center', padding:'40px 16px' }}>
      <div style={{ fontSize:44, marginBottom:12 }}>🔐</div>
      <div style={{ fontSize:18, fontWeight:800, marginBottom:8 }}>Admin access only</div>
      <div style={{ fontSize:13, color:C.ink3, marginBottom:16 }}>Log in with the admin account to access this area.</div>
      <div style={{ fontSize:12, background:C.sur2, borderRadius:9, padding:12, marginBottom:16, color:C.ink3 }}>
        To grant yourself access, set <strong>ADMIN_EMAIL</strong> in App.tsx to <strong>{session?.user?.email}</strong>
      </div>
      <button onClick={() => nav('profile')} style={s.btn(C.school)}>← Back to profile</button>
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
        <button onClick={() => nav('profile')} style={s.btnSm(C.sur2, C.ink2)}>← Back</button>
        <div style={{ fontSize:19, fontWeight:800 }}>Admin dashboard</div>
        <button onClick={loadAll} style={{ ...s.btnSm(C.sur2, C.ink2), marginLeft:'auto' }}>↻ Refresh</button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:9, marginBottom:14 }}>
        {[['Total',stats.total,C.ink],['Pending',stats.pending,C.amber],['Approved',stats.approved,C.school],['Rejected',stats.rejected,C.coral]].map(([l,n,c]) => (
          <div key={l as string} style={{ background:C.sur, border:`1px solid ${C.bdr}`, borderRadius:9, padding:10, textAlign:'center' }}>
            <div style={{ fontSize:22, fontWeight:800, color:c as string }}>{n as number}</div>
            <div style={{ fontSize:11, color:C.ink3 }}>{l}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'flex', background:C.sur2, borderRadius:20, padding:3, gap:2, marginBottom:14 }}>
        {([['checks',`Checks (${stats.pending} pending)`],['users',`Users (${users.length})`],['rides','Rides']] as const).map(([t,l]) => (
          <button key={t} onClick={() => setTab(t)} style={{ flex:1, padding:'7px 0', borderRadius:18, border:'none', cursor:'pointer', fontWeight:600, fontSize:12, fontFamily:'inherit', background:tab===t?C.sur:'transparent', color:tab===t?C.ink:C.ink2, boxShadow:tab===t?'0 1px 3px rgba(0,0,0,.1)':undefined }}>{l}</button>
        ))}
      </div>

      {loading && <div style={{ textAlign:'center', padding:20, color:C.ink3 }}>Loading…</div>}

      {tab==='checks' && !loading && (
        <>
          {checks.length===0 && <div style={{ textAlign:'center', padding:32, color:C.ink3 }}><div style={{ fontSize:36, marginBottom:8 }}>🔍</div>No background checks yet.</div>}
          {checks.map((c:any) => {
            const st = stStyle(c.status); const p = c.profiles;
            return (
              <Card key={c.id} style={{ marginBottom:10 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                  <Av name={p?.full_name||'Unknown'} size={40} />
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:14 }}>{p?.full_name||'Unknown'}</div>
                    <div style={{ fontSize:11, color:C.ink3 }}>{p?.phone} · {p?.address}</div>
                  </div>
                  <span style={s.tag(st.bg, st.color)}>{st.label}</span>
                </div>
                <div style={{ background:C.sur2, borderRadius:9, padding:10, marginBottom:10, fontSize:12 }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                    <div><span style={{ color:C.ink3 }}>Vehicle: </span>{p?.car_make_model||'—'}</div>
                    <div><span style={{ color:C.ink3 }}>Plate: </span>{p?.license_plate||'—'}</div>
                    <div><span style={{ color:C.ink3 }}>Submitted: </span>{c.submitted_at ? new Date(c.submitted_at).toLocaleDateString() : '—'}</div>
                    <div><span style={{ color:C.ink3 }}>Expires: </span>{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}</div>
                  </div>
                </div>
                {noteId===c.id ? (
                  <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                    <input style={{ ...s.input, flex:1 }} placeholder="Reference # or notes…" value={note} onChange={e => setNote(e.target.value)} />
                    <button onClick={() => saveNote(c.id)} style={s.btnSm(C.school)}>Save</button>
                    <button onClick={() => { setNoteId(''); setNote(''); }} style={s.btnSm(C.sur2, C.ink2)}>Cancel</button>
                  </div>
                ) : (
                  <div style={{ fontSize:12, color:C.ink3, marginBottom:10, display:'flex', alignItems:'center', gap:8 }}>
                    <span>Ref: {c.external_id||'—'}</span>
                    <button onClick={() => { setNoteId(c.id); setNote(c.external_id||''); }} style={{ ...s.btnSm(C.sur2, C.ink2), fontSize:11 }}>Edit</button>
                  </div>
                )}
                {c.status==='pending' && (
                  <>
                    <div style={{ background:C.amberL, borderRadius:9, padding:10, marginBottom:10, fontSize:12, color:C.amberD }}>
                      <div style={{ fontWeight:700, marginBottom:4 }}>How to verify:</div>
                      <div>1. Search <strong>{p?.full_name}</strong> at <a href="https://www.beenverified.com" target="_blank" rel="noreferrer" style={{ color:C.amberD }}>beenverified.com</a> (~$5)</div>
                      <div>2. Address: <strong>{p?.address}</strong></div>
                      <div>3. Save reference number above, then Approve or Reject</div>
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={() => updateCheck(c.user_id, c.id, 'approved')} style={{ ...s.btn(C.school), flex:1, justifyContent:'center' }}>✅ Approve</button>
                      <button onClick={() => updateCheck(c.user_id, c.id, 'rejected')} style={{ ...s.btn('#fff','#b91c1c'), flex:1, justifyContent:'center', border:'1px solid #fca5a5' }}>❌ Reject</button>
                    </div>
                  </>
                )}
                {c.status==='approved' && <button onClick={() => updateCheck(c.user_id, c.id, 'rejected')} style={{ ...s.btn('#fff',C.ink3), border:`1px solid ${C.bdr}`, fontSize:12 }}>Revoke approval</button>}
                {c.status==='rejected' && <button onClick={() => updateCheck(c.user_id, c.id, 'approved')} style={{ ...s.btn(C.school), fontSize:12 }}>Re-approve</button>}
              </Card>
            );
          })}
        </>
      )}

      {tab==='users' && !loading && (
        <>
          <div style={{ fontSize:12, color:C.ink3, marginBottom:10 }}>{users.length} registered users</div>
          {users.map((u:any) => {
            const bst = stStyle(u.bg_check_status||'not_started');
            return (
              <div key={u.id} style={{ background:C.sur, border:`1px solid ${C.bdr}`, borderRadius:12, padding:12, marginBottom:8 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <Av name={u.full_name||'User'} size={36} />
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:13 }}>{u.full_name||'No name'}</div>
                    <div style={{ fontSize:11, color:C.ink3 }}>{u.phone} · {u.school_name||u.university||'—'}</div>
                    <div style={{ fontSize:11, color:C.ink3 }}>{u.address||'—'}</div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <span style={s.tag(bst.bg, bst.color)}>{bst.label}</span>
                    <div style={{ fontSize:10, color:C.ink3, marginTop:3 }}>{u.has_car?'🚗 Driver':'👤 Rider'}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}

      {tab==='rides' && !loading && (
        <>
          <div style={{ fontSize:12, color:C.ink3, marginBottom:10 }}>{rides.length} recent college rides</div>
          {rides.map((r:any) => (
            <div key={r.id} style={{ background:C.sur, border:`1px solid ${C.bdr}`, borderRadius:12, padding:12, marginBottom:8 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
                <Av name={r.profiles?.full_name||'Driver'} size={34} />
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:13 }}>{r.from_city} → {r.to_city}</div>
                  <div style={{ fontSize:11, color:C.ink3 }}>Driver: {r.profiles?.full_name} · {r.departure_date}</div>
                </div>
                <span style={s.tag(r.status==='active'?C.schoolL:C.sur2, r.status==='active'?C.schoolD:C.ink2)}>{r.status}</span>
              </div>
              <div style={{ fontSize:11, color:C.ink3 }}>${r.price_one_way} OW · {r.seats_available}/{r.total_seats} seats · {r.vehicle}</div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}


// ─── REPLY EMAIL DISPLAY ─────────────────────────────────────────────────────
function ReplyEmailDisplay() {
  const [email, setEmail] = React.useState('');
  React.useEffect(() => {
    supabase?.auth.getUser().then(({ data: { user } }) => {
      setEmail(user?.email || '');
    });
  }, []);
  if (!email) return null;
  return (
    <div style={{ fontSize: 12, color: C.ink3, marginBottom: 12 }}>
      We'll reply to: <strong style={{ color: C.ink }}>{email}</strong>
    </div>
  );
}

// ─── MISSING SCHOOL LINK ─────────────────────────────────────────────────────
function MissingSchoolLink({ state: initState, city: initCity, onSent }: { state?: string; city?: string; onSent?: () => void }) {
  const [show, setShow] = React.useState(false);
  const [form, setForm] = React.useState({
    schoolName: '', city: initCity || '', county: '', state: initState || '', zip: '', type: 'public', level: 'elementary',
  });
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const upd = (k: string, v: string) => setForm((f: any) => ({ ...f, [k]: v }));

  async function submit() {
    if (!form.schoolName.trim()) return;
    setSending(true);
    try {
      const { data: { user } } = await supabase!.auth.getUser();
      const body = `Missing School Request\n\nSchool Name: ${form.schoolName}\nCity: ${form.city}\nCounty: ${form.county}\nState: ${form.state}\nZIP: ${form.zip}\nType: ${form.type}\nLevel: ${form.level}\n\nSubmitted by: ${user?.email || 'unknown'}`;
      await sendEmail('sales@parnikasystems.com', 'PoolRide Support',
        `Missing School: ${form.schoolName} - ${form.city}, ${form.state}`, body);
      setSent(true);
      setTimeout(() => {
        setShow(false); setSent(false);
        setForm({ schoolName: '', city: initCity || '', county: '', state: initState || '', zip: '', type: 'public', level: 'elementary' });
        onSent?.();
      }, 2500);
    } catch(e) {}
    setSending(false);
  }

  if (sent) return <div style={{ fontSize: 11, color: C.schoolD, marginTop: 4 }}>✅ School request sent! We'll add it within 24 hours.</div>;

  return (
    <div style={{ marginTop: 6 }}>
      {!show ? (
        <span onClick={() => setShow(true)} style={{ fontSize: 11, color: C.school, cursor: 'pointer', textDecoration: 'underline' }}>
          🔍 Can't find your school? Report it →
        </span>
      ) : (
        <div style={{ background: C.schoolL, borderRadius: 10, padding: 12, marginTop: 6, border: `1px solid ${C.school}` }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: C.schoolD, marginBottom: 10 }}>📋 Report missing school</div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ ...s.label, fontSize: 11 }}>School name <span style={{ color: C.coral }}>*</span></label>
            <input style={{ ...s.input, fontSize: 12, borderColor: !form.schoolName ? C.coral : undefined }}
              value={form.schoolName} onChange={e => upd('schoolName', e.target.value)} placeholder="e.g. Tampa Palms Elementary School" />
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <div style={{ flex: 2 }}>
              <label style={{ ...s.label, fontSize: 11 }}>City</label>
              <input style={{ ...s.input, fontSize: 12 }} value={form.city}
                onChange={e => upd('city', e.target.value)} placeholder="Tampa" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ ...s.label, fontSize: 11 }}>State</label>
              <input style={{ ...s.input, fontSize: 12, textTransform: 'uppercase' as const }} value={form.state}
                onChange={e => upd('state', e.target.value.toUpperCase())} placeholder="FL" maxLength={2} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ ...s.label, fontSize: 11 }}>ZIP</label>
              <input style={{ ...s.input, fontSize: 12 }} value={form.zip}
                onChange={e => upd('zip', e.target.value.replace(/\D/g,'').slice(0,5))} placeholder="33647" maxLength={5} />
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ ...s.label, fontSize: 11 }}>County</label>
            <input style={{ ...s.input, fontSize: 12 }} value={form.county}
              onChange={e => upd('county', e.target.value)} placeholder="e.g. Hillsborough" />
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={{ ...s.label, fontSize: 11 }}>School type</label>
              <select style={{ ...s.input, fontSize: 12 }} value={form.type} onChange={e => upd('type', e.target.value)}>
                <option value="public">Public</option>
                <option value="private">Private</option>
                <option value="charter">Charter</option>
                <option value="magnet">Magnet</option>
                <option value="preschool">Preschool</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ ...s.label, fontSize: 11 }}>School level</label>
              <select style={{ ...s.input, fontSize: 12 }} value={form.level} onChange={e => upd('level', e.target.value)}>
                <option value="elementary">Elementary</option>
                <option value="middle">Middle</option>
                <option value="high">High School</option>
                <option value="k8">K-8</option>
                <option value="k12">K-12</option>
                <option value="preschool">Preschool</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={submit} disabled={sending || !form.schoolName.trim()}
              style={{ ...s.btn(C.school), fontSize: 12, flex: 1, justifyContent: 'center', opacity: sending ? .7 : 1 }}>
              {sending ? 'Sending…' : '📧 Submit request'}
            </button>
            <button onClick={() => setShow(false)} style={{ ...s.btn(C.sur2, C.ink3), fontSize: 12 }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── HELP & SUPPORT SCREEN ───────────────────────────────────────────────────
function HelpScreen({ nav }: { nav: (p: string) => void }) {
  const { profile } = React.useContext(AppCtx);
  const [category, setCategory] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState('');

  const categories = [
    { id: 'bug', label: '🐛 App bug or error', desc: 'Something is broken or not working' },
    { id: 'account', label: '👤 Account issue', desc: 'Login, profile, or settings problem' },
    { id: 'group', label: '👨‍👩‍👧 Group or schedule issue', desc: 'Problems with carpool groups or scheduling' },
    { id: 'notification', label: '🔔 Notification issue', desc: 'Not receiving emails or push notifications' },
    { id: 'school', label: '🏫 Missing school', desc: 'Your school is not in the dropdown' },
    { id: 'suggestion', label: '💡 Feature suggestion', desc: 'Idea to improve PoolRide' },
    { id: 'other', label: '❓ Other', desc: 'Anything else' },
  ];

  async function submit() {
    if (!category) { setError('Please select a category.'); return; }
    if (!message.trim()) { setError('Please describe the issue.'); return; }
    setSending(true); setError('');
    try {
      // Get email from Supabase session directly (most reliable source)
      const { data: { user } } = await supabase!.auth.getUser();
      const userEmail = user?.email || profile?.email || 'unknown';
      const userName = profile?.full_name || user?.email || 'Unknown';
      const subject = `PoolRide Support [${category}]: ${userName}`;
      const body = `Support request from PoolRide user.\n\nUser: ${userName}\nEmail: ${userEmail}\nPhone: ${profile?.phone || 'Not provided'}\nCategory: ${categories.find(c => c.id === category)?.label || category}\n\nMessage:\n${message}\n\n---\nUser ID: ${user?.id || 'unknown'}`;
      await sendEmail('sales@parnikasystems.com', 'PoolRide Support', subject, body);
      // Also send confirmation to user
      if (userEmail !== 'unknown') {
        await sendEmail(userEmail, userName, 'We received your PoolRide support request',
          `Hi ${userName},\n\nWe received your support request and will get back to you within 24 hours.\n\nYour message:\n${message}\n\nThanks,\nPoolRide Support Team`);
      }
      setSent(true);
    } catch(e) {
      setError('Failed to send. Please email sales@parnikasystems.com directly.');
    }
    setSending(false);
  }

  if (sent) return (
    <div style={{ padding: 24, textAlign: 'center' as const }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
      <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Message sent!</div>
      <div style={{ color: C.ink3, fontSize: 14, marginBottom: 24 }}>
        We'll get back to you at {profile?.email || 'your email'} within 24 hours.
      </div>
      <button onClick={() => nav('dashboard')} style={{ ...s.btn(C.school), justifyContent: 'center' }}>
        ← Back to home
      </button>
    </div>
  );

  return (
    <div style={{ padding: '0 0 80px' }}>
      <div style={{ padding: '16px 16px 0' }}>
        <button onClick={() => nav('dashboard')} style={s.btn(C.sur2, C.ink2)}>← Back</button>
      </div>
      <div style={{ padding: '16px 16px 0' }}>
        <div style={{ fontWeight: 800, fontSize: 22, marginBottom: 4 }}>Help & Support</div>
        <div style={{ color: C.ink3, fontSize: 13, marginBottom: 20 }}>
          We typically respond within 24 hours.
        </div>

        {/* FAQ quick links */}
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Quick answers</div>
          {[
            ['How do I create a carpool group?', 'Go to Match → find families at your school → tap Add → then go to Group to manage your group.'],
            ['How does scheduling work?', 'In Group screen → set your availability days for drop-off and pickup → tap Auto-generate schedule.'],
            ['How do I swap a drive?', 'Go to Schedule → tap Request Swap → choose drop-off only, pickup only, or full day → select a member.'],
            ['My school is missing?', 'Use the "Report missing school" link near the school field, or contact us below.'],
          ].map(([q, a]) => (
            <details key={q} style={{ marginBottom: 10, borderBottom: `1px solid ${C.sur2}`, paddingBottom: 10 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13, color: C.ink }}>{q}</summary>
              <div style={{ fontSize: 12, color: C.ink3, marginTop: 6, lineHeight: 1.5 }}>{a}</div>
            </details>
          ))}
        </Card>

        {/* Contact form */}
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Contact support</div>

          {error && <Alert bg={C.coralL} color={C.coralD} bdr={C.coral}>{error}</Alert>}

          <div style={{ marginBottom: 12 }}>
            <label style={s.label}>What do you need help with? <span style={{ color: C.coral }}>*</span></label>
            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 6 }}>
              {categories.map(cat => (
                <div key={cat.id} onClick={() => setCategory(cat.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    borderRadius: 9, cursor: 'pointer',
                    border: `1.5px solid ${category === cat.id ? C.school : C.bdr}`,
                    background: category === cat.id ? C.schoolL : C.sur }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: category === cat.id ? C.schoolD : C.ink }}>{cat.label}</div>
                    <div style={{ fontSize: 11, color: C.ink3 }}>{cat.desc}</div>
                  </div>
                  {category === cat.id && <div style={{ color: C.school, fontSize: 16 }}>✓</div>}
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={s.label}>Describe the issue <span style={{ color: C.coral }}>*</span></label>
            <textarea style={{ ...s.input, minHeight: 100, resize: 'vertical' as const }}
              value={message} onChange={e => setMessage(e.target.value)}
              placeholder="Please describe what happened, what you expected, and any error messages you saw…" />
          </div>

          <ReplyEmailDisplay />

          <button onClick={submit} disabled={sending}
            style={{ ...s.btn(C.school), width: '100%', justifyContent: 'center', opacity: sending ? .7 : 1 }}>
            {sending ? 'Sending…' : '📧 Send message'}
          </button>

          <div style={{ textAlign: 'center' as const, marginTop: 12, fontSize: 12, color: C.ink3 }}>
            Or email us directly at{' '}
            <a href="mailto:sales@parnikasystems.com" style={{ color: C.school }}>sales@parnikasystems.com</a>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─── APP SHELL ────────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {error: string|null}> {
  constructor(props: any) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e: any) { return { error: e?.message || 'Unknown error' }; }
  render() {
    if (this.state.error) return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Something went wrong</div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 16, fontFamily: 'monospace', background: '#f5f5f5', padding: 10, borderRadius: 8, textAlign: 'left' }}>
          {this.state.error}
        </div>
        <button onClick={() => { this.setState({ error: null }); window.location.reload(); }}
          style={{ padding: '10px 24px', background: '#e8650a', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>
          Reload app
        </button>
      </div>
    );
    return this.props.children;
  }
}

function AppShell() {
  const { mode, setMode, profile, session, setActiveGroupId, setActiveChildSchool, setActiveChildGrade } = useApp();
  const [page, setPage] = useState('dashboard');
  const [refreshKey, setRefreshKey] = useState(0);
  const [trackingKey, setTrackingKey] = useState(0);
  const isMobile = useWindowWidth() < 640;
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState(0);

  // Load pending invitations count
  React.useEffect(() => {
    if (!supabase || !session?.user?.id) return;
    const loadInvites = () => {
      supabase!.from('group_invitations')
        .select('id', { count: 'exact' })
        .eq('invitee_id', session.user.id)
        .eq('status', 'pending')
        .then(({ count }) => setPendingInvitations(count || 0));
    };
    loadInvites();
    // Reload when notifications change (new invite might have arrived)
    const channel = supabase.channel('invites-' + session.user.id)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'group_invitations', filter: `invitee_id=eq.${session.user.id}` },
        () => loadInvites())
      .subscribe();
    return () => { supabase!.removeChannel(channel); };
  }, [session?.user?.id]);

  // Load unread message count
  React.useEffect(() => {
    if (!supabase || !session?.user?.id) return;
    const loadUnread = async () => {
      // Get all conversations user is in
      const { data: memberships } = await supabase
        .from('conversation_members')
        .select('conversation_id, last_read_at')
        .eq('user_id', session.user.id);
      if (!memberships?.length) { setUnreadMessages(0); return; }
      // Count messages newer than last_read_at for each conversation
      let total = 0;
      for (const m of memberships) {
        const query = supabase.from('messages')
          .select('id', { count: 'exact' })
          .eq('conversation_id', m.conversation_id)
          .neq('sender_id', session.user.id); // don't count own messages
        if (m.last_read_at) query.gt('created_at', m.last_read_at);
        const { count } = await query;
        total += count || 0;
      }
      setUnreadMessages(total);
    };
    loadUnread();
    // Re-check when a new message arrives
    const channel = supabase.channel('unread-' + session.user.id)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' },
        () => loadUnread())
      .subscribe();
    return () => { supabase!.removeChannel(channel); };
  }, [session?.user?.id, page]);

  // Load and subscribe to notifications
  React.useEffect(() => {
    if (!supabase || !session?.user?.id) return;
    supabase.from('notifications')
      .select('*').eq('user_id', session.user.id).eq('read', false)
      .order('created_at', { ascending: false }).limit(20)
      .then(({ data }) => { if (data) setNotifications(data); });
    const channel = supabase.channel('notifs-' + session.user.id)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${session.user.id}` },
        (payload) => { setNotifications(prev => [payload.new, ...prev]); })
      .subscribe();
    return () => { supabase!.removeChannel(channel); };
  }, [session?.user?.id]);
  const isSchool   = mode === 'school';
  const isActivity = mode === 'activity';
  const mc = isSchool ? C.school : isActivity ? C.activity : C.college;

  // Redirect to register if profile is incomplete
  React.useEffect(() => {
    if (profile !== null && (!profile?.full_name || !profile?.school_name)) {
      setPage('register');
    }
  }, [profile]);

  const nav = (p: string) => {
    setPage(p);
    window.scrollTo(0, 0);
    if (p === 'dashboard') setRefreshKey(k => k + 1);
    if (p === 'tracking') setTrackingKey(k => k + 1);
    if (p === 'messages' && supabase && session?.user?.id) {
      // Mark all conversations as read when opening messages
      supabase.from('conversation_members')
        .update({ last_read_at: new Date().toISOString() })
        .eq('user_id', session.user.id)
        .then(() => setUnreadMessages(0));
    }
  };

  const schoolNav   = [['dashboard','Home','🏠'],['match','Match','🔍'],['group','Group','👨‍👩‍👧'],['schedule','Schedule','📅'],['board','Board','📋']];
  const collegeNav  = [['dashboard','Home','🏠'],['browse','Browse','🔍'],['postride','Post','➕'],['request','Request','🙋'],['board','Board','📋']];
  const activityNav = [['dashboard','Home','🏠'],['activities','Activities','⚽'],['actgroup','My Group','👥'],['board','Board','📋'],['myrides','My Rides','🚗']];
  const navItems = mode === 'school' ? schoolNav : mode === 'college' ? collegeNav : activityNav;

  const renderPage = () => {
    switch (page) {
      case 'dashboard': return <Dashboard nav={nav} refreshKey={refreshKey} pendingInvitations={pendingInvitations} />;
      case 'register':  return <RegisterScreen nav={nav} />;
      case 'match':     return <MatchScreen nav={nav} />;
      case 'group':     return <GroupScreen nav={nav} />;
      case 'schedule':  return <ScheduleScreen nav={nav} />;
      case 'browse':    return <BrowseScreen nav={nav} />;
      case 'postride':  return <PostRideScreen nav={nav} />;
      case 'request':   return <RequestRideScreen nav={nav} />;
      case 'myrides':   return <MyRidesScreen nav={nav} />;
      case 'profile':   return <ProfileScreen nav={nav} />;
      case 'editprofile': return <EditProfileScreen nav={nav} />;
      case 'invitations': return <InvitationsScreen nav={nav} />;
      case 'bgcheck':   return <BackgroundCheckScreen nav={nav} />;
      case 'help':      return <HelpScreen nav={nav} />;
      case 'tracking':  return <LiveTrackingScreen key={trackingKey} nav={nav} boardRole={boardTrackingRole} boardContext={boardRideContext} />;
      case 'admin':     return <AdminDashboard nav={nav} />;
      case 'board':     return <NeighborhoodBoardScreen nav={nav} />;
      case 'activities':  return <ActivitiesScreen nav={nav} />;
      case 'actgroup':    return <ActivityGroupScreen nav={nav} />;
      case 'actschedule': return <ActivityGroupScreen nav={nav} />;
      case 'messages':    return <MessagesScreen nav={nav} />;
      case 'chat':        return <ChatScreen nav={nav} />;
      case 'verify':      return <DriverVerifyScreen nav={nav} />;
      case 'autosched':   return <AutoScheduleScreen nav={nav} />;
      case 'calendar':    return <SchoolCalendarScreen nav={nav} />;
      default:            return <Dashboard nav={nav} />;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, paddingBottom: 80 }}>
      {/* TOP BAR */}
      <div style={{ background: C.sur, borderBottom: `1px solid ${C.bdr}`, display: 'flex', alignItems: 'center', padding: '0 8px', gap: 6, position: 'sticky', top: 0, zIndex: 200, minHeight: 52, overflow: 'hidden' }}>
        {/* Logo */}
        <div onClick={() => nav('dashboard')} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', flexShrink: 0 }}>
          <img src={LOGO_URL} alt="PoolRide" style={{ height: 30, objectFit: 'contain' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span style={{ fontWeight: 800, fontSize: 15, letterSpacing: -.5, whiteSpace: 'nowrap' as const }}>
            <span style={{ color: C.school }}>Pool</span><span style={{ color: C.college }}>Ride</span>
          </span>
        </div>
        {/* Mode switcher */}
        <div style={{ display: 'flex', background: C.sur2, borderRadius: 20, padding: 2, gap: 1, flexShrink: 0 }}>
          {(['school', 'college', 'activity'] as const).map(m => {
            const mc2 = m === 'school' ? C.school : m === 'college' ? C.college : C.activity;
            const emoji = m === 'school' ? '🏫' : m === 'college' ? '🎓' : '⚽';
            const fullLabel = m === 'school' ? '🏫 School' : m === 'college' ? '🎓 College' : '⚽ Activities';
            return (
              <button key={m} onClick={() => { setMode(m); nav('dashboard'); }}
                style={{ padding: '3px 7px', borderRadius: 18, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: 12, background: mode === m ? mc2 : 'transparent', color: mode === m ? '#fff' : C.ink2, whiteSpace: 'nowrap' as const }}>
                {isMobile ? emoji : fullLabel}
              </button>
            );
          })}
        </div>
        {/* Right side — push to edge */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          {pendingInvitations > 0 && (
            <button onClick={() => nav('invitations')}
              style={{ display: 'flex', alignItems: 'center', gap: 2, background: C.schoolL, border: `1.5px solid ${C.school}`, borderRadius: 20, padding: '3px 7px', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: C.schoolD, flexShrink: 0 }}>
              ✉️ {pendingInvitations}
            </button>
          )}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowNotifications(!showNotifications)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, position: 'relative' }}>
              🔔
              {notifications.length > 0 && <span style={{ position:'absolute', top:-4, right:-4, width:14, height:14, borderRadius:'50%', background:C.coral, color:'#fff', fontSize:8, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }}>{notifications.length}</span>}
            </button>
            {showNotifications && (
              <div style={{ position:'fixed', top:56, right:8, width:300, background:'#fff', borderRadius:12, boxShadow:'0 8px 24px rgba(0,0,0,.18)', border:`1px solid ${C.bdr}`, zIndex:1000, overflow:'hidden', maxHeight:'70vh', overflowY:'auto' as const }}>
                <div style={{ padding:'10px 14px', borderBottom:`1px solid ${C.bdr}`, fontWeight:700, fontSize:13, display:'flex', justifyContent:'space-between' }}>
                  Notifications
                  {notifications.length > 0 && (
                    <button onClick={async () => {
                      if (!supabase || !session) return;
                      await supabase.from('notifications').update({ read: true }).eq('user_id', session.user.id).eq('read', false);
                      setNotifications([]);
                    }} style={{ fontSize:11, color:C.school, background:'none', border:'none', cursor:'pointer' }}>Mark all read</button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <div style={{ padding:20, textAlign:'center', color:C.ink3, fontSize:13 }}>No new notifications</div>
                ) : notifications.map((n: any) => (
                  <div key={n.id} style={{ padding:'10px 14px', borderBottom:`1px solid ${C.sur2}`, cursor:'pointer' }} onClick={async () => {
                    await supabase!.from('notifications').update({ read: true }).eq('id', n.id);
                    setNotifications(prev => prev.filter((x: any) => x.id !== n.id));
                    setShowNotifications(false);
                    if (n.type === 'group_invite') nav('invitations');
                    else if (n.type === 'member_left' || n.type === 'member_joined') nav('group');
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{n.title}</div>
                    <div style={{ fontSize: 12, color: C.ink3 }}>{n.body}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => nav('messages')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, position:'relative' }}>
            💬
            {unreadMessages > 0 && <span style={{ position:'absolute', top:-4, right:-4, width:14, height:14, borderRadius:'50%', background:C.coral, color:'#fff', fontSize:8, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }}>{unreadMessages}</span>}
          </button>
          <button onClick={() => nav('help')} title="Help & Support" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>❓</button>
          <button onClick={() => nav('profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>👤</button>
        </div>
      </div>

      {/* PAGE */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '20px 14px 20px' }}>
        <ErrorBoundary>
          {renderPage()}
        </ErrorBoundary>
      </div>

      {/* COPYRIGHT BAR */}
      {/* BOTTOM NAV + COPYRIGHT */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 200, background: C.sur, borderTop: `1px solid ${C.bdr}` }}>
        {/* Nav buttons */}
        <div style={{ display: 'flex', height: 54 }}>
          {navItems.map(([id, label, icon]) => {
            const active = page === id;
            return (
              <button key={id} onClick={() => {
                if (id === 'group') {
                  setActiveChildSchool('');
                  setActiveChildGrade('');
                  setActiveGroupId(null);
                }
                nav(id);
              }} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, border: 'none', background: 'none', cursor: 'pointer', color: active ? mc : C.ink3, minWidth: 0, padding: '0 2px' }}>
                <div style={{ fontSize: 20, lineHeight: 1 }}>{icon}</div>
                <div style={{ fontSize: 9, fontWeight: active ? 700 : 500, fontFamily: 'inherit', borderBottom: active ? `2px solid ${mc}` : '2px solid transparent', paddingBottom: 1, whiteSpace: 'nowrap' as const }}>{label}</div>
              </button>
            );
          })}
        </div>
        {/* Copyright below nav */}
        <div style={{ textAlign: 'center', padding: '2px 8px 4px', fontSize: 8, color: C.ink3 }}>
          © {new Date().getFullYear()} PoolRide. All rights reserved.
        </div>
      </div>
    </div>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
function Inner() {
  const { session, loading } = useApp();

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ textAlign: 'center' }}>
        <img src={LOGO_URL} alt="PoolRide" style={{ height: 80, objectFit: 'contain', marginBottom: 14 }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        <div style={{ fontWeight: 800, fontSize: 26, marginBottom: 14 }}>
          <span style={{ color: C.school }}>Pool</span><span style={{ color: C.college }}>Ride</span>
        </div>
        <div style={{ width: 32, height: 32, borderRadius: '50%', border: `3px solid ${C.bdr}`, borderTopColor: C.school, animation: 'spin .7s linear infinite', margin: '0 auto' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  return session ? <AppShell /> : <AuthScreen />;
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);
  const [mode, setMode] = useState<Mode>('school');
  const [pendingGroupMembers, setPendingGroupMembers] = useState<any[]>([]);
  const [activeGroupId, setActiveGroupIdState] = useState<string | null>(null);

  // Wrapper that also persists to DB
  const setActiveGroupId = React.useCallback(async (id: string | null) => {
    setActiveGroupIdState(id);
    if (supabase && session?.user?.id) {
      await supabase.from('profiles').update({ last_active_group_id: id }).eq('id', session.user.id);
    }
  }, [session?.user?.id]);
  const [activeChildSchool, setActiveChildSchool] = useState<string>('');
  const [activeChildGrade, setActiveChildGrade] = useState<string>('');
  const [activeChatUser, setActiveChatUser] = useState<{ id: string; name: string } | null>(null);

  async function loadProfile(uid: string) {
    // Register push token on login
    registerPushToken(uid).catch(() => {});
    if (!supabase) return;
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', uid)
      .maybeSingle();

    if (data) {
      setProfile(data);
      // Restore last active group
      if (data.last_active_group_id) {
        setActiveGroupIdState(data.last_active_group_id);
      }
    } else {
      // No profile — set empty profile so useEffect redirects to register
      setProfile({ id: uid } as any);
    }
  }

  useEffect(() => {
    if (!supabase) { setLoading(false); return; }

    // Initial session — skip if this is a password recovery link
    const isRecovery = window.location.hash.includes('type=recovery') ||
      new URLSearchParams(window.location.search).get('type') === 'recovery';

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (isRecovery) {
        // Don't auto-login on recovery links — show reset form
        setSession(null);
        setProfile(null);
        setLoading(false);
        return;
      }
      setSession(session);
      if (session?.user) loadProfile(session.user.id);
      setLoading(false);
    });

    // Auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        // Show reset form, don't log in
        setSession(null);
        setProfile(null);
        return;
      }
      setSession(session);
      if (session?.user) loadProfile(session.user.id);
      else setProfile(null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Auto sign-out after 8 hours of inactivity
  React.useEffect(() => {
    if (!session) return;
    const TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours
    let timer = setTimeout(() => { supabase?.auth.signOut(); }, TIMEOUT);
    const reset = () => { clearTimeout(timer); timer = setTimeout(() => { supabase?.auth.signOut(); }, TIMEOUT); };
    window.addEventListener('click', reset);
    window.addEventListener('keydown', reset);
    return () => { clearTimeout(timer); window.removeEventListener('click', reset); window.removeEventListener('keydown', reset); };
  }, [session?.user?.id]);
  useEffect(() => {
    if (!supabase || !session?.user?.id) return;

    const channel = supabase
      .channel('profile-changes')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${session.user.id}`,
      }, (payload) => {
        // Merge updated fields into profile so bg_check_status etc update live
        setProfile((prev: any) => ({ ...prev, ...payload.new }));
      })
      .subscribe();

    return () => { supabase!.removeChannel(channel); };
  }, [session?.user?.id]);

  return (
    <AppCtx.Provider value={{ session, mode, setMode, loading, profile, setProfile, pendingGroupMembers, setPendingGroupMembers, activeGroupId, setActiveGroupId, activeChildSchool, setActiveChildSchool, activeChildGrade, setActiveChildGrade, activeChatUser, setActiveChatUser }}>
      <Inner />
    </AppCtx.Provider>
  );
}

// ─── MESSAGES SCREEN ──────────────────────────────────────────────────────────
function MessagesScreen({ nav }: { nav: (p: string) => void }) {
  const { session, profile } = useApp();
  const [convos, setConvos] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [activeConvo, setActiveConvo] = React.useState<any>(null);

  async function loadConvos() {
    if (!supabase || !session) return;
    const { data, error } = await supabase
      .from('conversation_members')
      .select('*, conversations(*, messages(content, created_at, sender_id), conversation_members(user_id))')
      .eq('user_id', session.user.id);
    if (error) console.error('Convos load error:', error.message);
    const processed = (data || [])
      .filter((cm: any) => cm.conversations)
      .map((cm: any) => ({
        ...cm,
        conversations: {
          ...cm.conversations,
          messages: (cm.conversations.messages || [])
            .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .slice(0, 1)
        }
      }))
      .sort((a: any, b: any) => {
        const aTime = a.conversations.messages?.[0]?.created_at || a.conversations.created_at || '';
        const bTime = b.conversations.messages?.[0]?.created_at || b.conversations.created_at || '';
        return bTime.localeCompare(aTime);
      });
    setConvos(processed);
    setLoading(false);
    return processed;
  }

  React.useEffect(() => {
    loadConvos().then(loaded => {
      // Handle pending direct chat
      if (pendingChatTarget) {
        const target = pendingChatTarget;
        pendingChatTarget = null;
        const existing = (loaded || []).find((cm: any) =>
          cm.conversations?.type === 'direct' &&
          (cm.conversations?.conversation_members || []).some((p: any) => p.user_id === target.id)
        );
        if (existing) {
          setActiveConvo({ ...existing.conversations, otherName: target.name });
        } else {
          startDirectChat(target.id, target.name);
        }
      }
      // Handle pending group chat
      if (pendingGroupChatId) {
        const gid = pendingGroupChatId;
        pendingGroupChatId = null;
        const existing = (loaded || []).find((cm: any) =>
          cm.conversations?.type === 'group' &&
          cm.conversations?.group_id === gid
        );
        if (existing) {
          setActiveConvo(existing.conversations);
        } else {
          startGroupChat(gid);
        }
      }
    });
  }, [session?.user?.id]);

  async function startDirectChat(otherUserId: string, otherName: string) {
    if (!supabase || !session) return;
    try {
      // Check once more in DB in case a convo already exists
      const { data: existingMembers } = await supabase
        .from('conversation_members')
        .select('conversation_id, conversations(id, type, title, conversation_members(user_id))')
        .eq('user_id', session.user.id);
      const alreadyExists = (existingMembers || []).find((cm: any) =>
        cm.conversations?.type === 'direct' &&
        (cm.conversations?.conversation_members || []).some((p: any) => p.user_id === otherUserId)
      );
      if (alreadyExists) {
        setActiveConvo({ ...alreadyExists.conversations, otherName });
        return;
      }
      const { data: convo, error: convoErr } = await supabase
        .from('conversations')
        .insert({ type: 'direct', title: otherName })
        .select().single();
      if (convoErr) { console.error('Chat create error:', convoErr.message); return; }
      if (convo) {
        // Insert own membership first
        const { error: myMemErr } = await supabase.from('conversation_members')
          .insert({ conversation_id: convo.id, user_id: session.user.id });
        if (myMemErr) console.error('My member insert error:', myMemErr.message);

        // Insert other person's membership separately (RLS: each user can only insert their own row,
        // so we use a service-role-bypassing approach via a stored function, or insert both if policy allows)
        const { error: otherMemErr } = await supabase.from('conversation_members')
          .insert({ conversation_id: convo.id, user_id: otherUserId });
        if (otherMemErr) {
          console.error('Other member insert error:', otherMemErr.message);
          // If RLS blocks inserting for other user, alert so we know
          alert(`Chat setup issue: could not add the other person to the conversation. Check conversation_members RLS policy. Error: ${otherMemErr.message}`);
        }

        const newConvo = { ...convo, otherName, conversation_members: [{ user_id: session.user.id }, { user_id: otherUserId }] };
        setActiveConvo(newConvo);
        setConvos(prev => [{ conversations: { ...newConvo, messages: [] } }, ...prev]);
      }
    } catch(e) { console.error('startDirectChat error:', e); }
  }

  async function startGroupChat(gid: string) {
    if (!supabase || !session) return;
    try {
      // Get group info + all member user_ids
      const { data: groupInfo } = await supabase.from('carpool_groups')
        .select('school_name, grade').eq('id', gid).single();
      const { data: members } = await supabase.from('group_members')
        .select('user_id').eq('group_id', gid);
      if (!members?.length) return;
      const title = `${groupInfo?.school_name || 'Group'} · ${groupInfo?.grade || ''} Chat`.trim();
      // Create group conversation
      const { data: convo, error: convoErr } = await supabase
        .from('conversations')
        .insert({ type: 'group', title, group_id: gid })
        .select().single();
      if (convoErr) { console.error('Group chat create error:', convoErr.message); return; }
      // Add all group members
      const memberRows = members.map((m: any) => ({ conversation_id: convo.id, user_id: m.user_id }));
      const { error: memErr } = await supabase.from('conversation_members').insert(memberRows);
      if (memErr) console.error('Group chat members error:', memErr.message);
      setActiveConvo({ ...convo, title });
      loadConvos();
    } catch(e) { console.error('startGroupChat error:', e); }
  }

  if (activeConvo) return <ChatScreen nav={nav} convo={activeConvo} onBack={() => {
    setActiveConvo(null);
    loadConvos(); // refresh convo list with latest message on back
  }} />;

  return (
    <div>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>Messages</div>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: 16 }}>Chat with your carpool group and college ride partners</div>

      {loading && <div style={{ textAlign: 'center', padding: 20, color: C.ink3 }}>Loading…</div>}

      {!loading && convos.length === 0 && (
        <div style={{ textAlign: 'center', padding: '32px 16px' }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>💬</div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>No messages yet</div>
          <div style={{ fontSize: 13, color: C.ink3, marginBottom: 16 }}>
            Start a conversation from your carpool group by tapping Chat next to a member.
          </div>
          <button onClick={() => nav('group')} style={s.btn(C.school)}>Go to my group →</button>
        </div>
      )}



      {convos.length > 0 && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: C.ink2 }}>RECENT</div>
          {convos.map((cm: any) => {
            const c = cm.conversations;
            const lastMsg = c?.messages?.[0];
            return (
              <div key={cm.id} onClick={() => setActiveConvo(c)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: C.sur, border: `1px solid ${C.bdr}`, borderRadius: 12, marginBottom: 8, cursor: 'pointer' }}>
                <Av name={c?.title || 'Chat'} size={42} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{c?.title || 'Conversation'}</div>
                  <div style={{ fontSize: 12, color: C.ink3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lastMsg?.content || 'No messages yet'}</div>
                </div>
                <div style={{ color: C.ink3, fontSize: 11, flexShrink: 0 }}>
                  {lastMsg ? new Date(lastMsg.created_at).toLocaleDateString() : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── CHAT SCREEN ──────────────────────────────────────────────────────────────
function ChatScreen({ nav, convo, onBack }: { nav: (p: string) => void; convo?: any; onBack?: () => void }) {
  const { session, profile } = useApp();
  const [messages, setMessages] = React.useState<any[]>([]);
  const [text, setText] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const bottomRef = React.useRef<HTMLDivElement>(null);
  const channelRef = React.useRef<any>(null); // prevent double-subscription in StrictMode

  function loadMessages() {
    if (!supabase || !convo?.id) return;
    console.log('💬', `Loading messages for convo ${convo.id}`);
    supabase.from('messages')
      .select('*, profiles(full_name)')
      .eq('conversation_id', convo.id)
      .order('created_at', { ascending: true })
      .then(({ data, error }) => {
        if (error) { return; }
    console.log('💬', `Loaded ${data?.length || 0} messages`);
        if (data) setMessages(data.map((m: any) => ({ ...m, sender_name: m.profiles?.full_name || 'User' })));
      });
  }

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load messages on open
  React.useEffect(() => {
    loadMessages();
  }, [convo?.id]);

  // Realtime subscription — use ref to prevent double-subscribe in React StrictMode
  React.useEffect(() => {
    if (!supabase || !convo?.id) return;
    // Cleanup any existing channel first
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    console.log('💬', `Subscribing to realtime for convo ${convo.id}`);
    const channel = supabase.channel(`chat-${convo.id}-${Date.now()}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${convo.id}` },
        (payload) => {
          const newMsg = payload.new as any;
    console.log('💬', `Realtime INSERT received: ${newMsg.id}`);
          setMessages(prev => {
            // Dedup: replace optimistic OR skip if already present
            if (prev.some(m => m.id === newMsg.id)) return prev;
            const hasOptimistic = prev.some(m => m.conversation_id === newMsg.conversation_id && m.id?.startsWith('opt-') && m.content === newMsg.content);
            if (hasOptimistic) return prev; // already shown as optimistic, will be replaced by send()
            supabase!.from('profiles').select('full_name').eq('id', newMsg.sender_id).single()
              .then(({ data }) => {
                setMessages(p => p.map(m => m.id === newMsg.id ? { ...m, sender_name: data?.full_name || 'User' } : m));
              });
            return [...prev, { ...newMsg, sender_name: '…' }];
          });
        })
      .subscribe((status) => { });
    channelRef.current = channel;
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [convo?.id]);

  async function send() {
    console.log('💬', `send() — text:"${text.trim()}" sending:${sending} supabase:${!!supabase} session:${!!session} convo.id:${convo?.id}`);
    if (!text.trim() || sending || !supabase || !session || !convo?.id) return;
    const optimisticId = `opt-${Date.now()}`;
    const optimistic = {
      id: optimisticId,
      sender_id: session.user.id,
      sender_name: profile?.full_name || 'You',
      content: text.trim(),
      created_at: new Date().toISOString(),
      conversation_id: convo.id,
    };
    setMessages(prev => [...prev, optimistic]);
    setText('');
    setSending(true);
    console.log('💬', `Inserting message to DB...`);
    const { data: inserted, error } = await supabase
      .from('messages')
      .insert({ conversation_id: convo.id, sender_id: session.user.id, content: optimistic.content })
      .select().single();
    setSending(false);
    if (error) {
    console.log('💬', `INSERT ERROR: ${error.message} (code:${error.code})`);
      setMessages(prev => prev.filter(m => m.id !== optimisticId));
      setText(optimistic.content);
    } else if (inserted) {
    console.log('💬', `Insert SUCCESS — id:${inserted.id}`);
      // Replace optimistic with real DB row (correct id prevents realtime dedup issues)
      setMessages(prev => prev.map(m => m.id === optimisticId
        ? { ...inserted, sender_name: optimistic.sender_name }
        : m));
      // Also do a full reload to ensure we have latest state
      setTimeout(() => loadMessages(), 500);
    }
  }

  const title = convo?.title || convo?.otherName || 'Chat';
  const myId = session?.user?.id || 'me';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexShrink: 0 }}>
        <button onClick={() => onBack ? onBack() : nav('messages')} style={s.btnSm(C.sur2, C.ink2)}>← Back</button>
        <Av name={title} size={36} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{title}</div>
          <div style={{ fontSize: 11, color: C.ink3 }}>convo id: {convo?.id || 'MISSING'}</div>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 2px', marginBottom: 10 }}>
        {messages.map((m: any) => {
          const isMe = m.sender_id === myId;
          return (
            <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
              {!isMe && <div style={{ fontSize: 11, color: C.ink3, marginBottom: 3, marginLeft: 4 }}>{m.sender_name}</div>}
              <div style={{
                maxWidth: '78%', padding: '9px 13px', borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                background: isMe ? C.school : C.sur, color: isMe ? '#fff' : C.ink,
                border: isMe ? 'none' : `1px solid ${C.bdr}`,
                fontSize: 14, lineHeight: 1.4
              }}>
                {m.content}
              </div>
              <div style={{ fontSize: 10, color: C.ink3, marginTop: 3, marginLeft: 4, marginRight: 4 }}>
                {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ display: 'flex', gap: 8, flexShrink: 0, padding: '8px 0' }}>
        <input
          style={{ ...s.input, flex: 1, borderRadius: 20, padding: '10px 16px' }}
          placeholder="Type a message…"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
        />
        <button onClick={send} disabled={!text.trim() || sending}
          style={{ ...s.btn(C.school), borderRadius: '50%', width: 42, height: 42, padding: 0, justifyContent: 'center', opacity: !text.trim() ? .5 : 1, flexShrink: 0 }}>
          ➤
        </button>
      </div>
    </div>
  );
}

// ─── DRIVER VERIFICATION SCREEN ───────────────────────────────────────────────
function DriverVerifyScreen({ nav }: { nav: (p: string) => void }) {
  const { session, profile, setProfile } = useApp();
  const [verif, setVerif] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({
    licenseNum: '', licenseState: '', licenseExpiry: '',
    licenseFile: null as File | null, licensePreview: '',
    insuranceProvider: '', insurancePolicy: '', insuranceExpiry: '',
    insuranceFile: null as File | null, insurancePreview: '',
    selfieFile: null as File | null, selfiePreview: '',
    bgDob: '', bgLicense: '', bgSsn: '', bgConsent: false,
  });
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  React.useEffect(() => {
    if (!supabase || !session) return;
    supabase.from('driver_verifications').select('*').eq('user_id', session.user.id).maybeSingle()
      .then(({ data }) => { setVerif(data); setLoading(false); });
  }, [session]);

  function handleFile(key: string, previewKey: string, file: File | null) {
    if (!file) return;
    set(key, file);
    const reader = new FileReader();
    reader.onload = e => set(previewKey, e.target?.result as string);
    reader.readAsDataURL(file);
  }

  async function submit() {
    if (!supabase || !session) return;
    if (!form.bgConsent) { alert('Please consent to the background check.'); return; }
    if (!form.licenseNum || !form.licenseExpiry) { alert('License number and expiry are required.'); return; }
    if (!form.insuranceProvider || !form.insuranceExpiry) { alert('Insurance details are required.'); return; }
    if (!form.selfiePreview) { alert('Please take or upload a selfie photo.'); return; }
    setSaving(true);

    // In production: upload images to Supabase Storage, get URLs
    // For now we store form data and mark as pending
    const payload = {
      user_id: session.user.id,
      bg_check_status: 'pending', bg_check_submitted_at: new Date().toISOString(),
      license_status: 'pending', license_number: form.licenseNum,
      license_state: form.licenseState, license_expiry: form.licenseExpiry || null,
      insurance_status: 'pending', insurance_provider: form.insuranceProvider,
      insurance_policy: form.insurancePolicy, insurance_expiry: form.insuranceExpiry || null,
      selfie_status: 'pending',
      updated_at: new Date().toISOString(),
    };
    await supabase.from('driver_verifications').upsert(payload);
    await supabase.from('profiles').update({ verification_status: 'pending', bg_check_status: 'pending' }).eq('id', session.user.id);
    setProfile({ ...profile, verification_status: 'pending', bg_check_status: 'pending' });
    setVerif(payload);
    setSaving(false);
  }

  const steps = [
    { key: 'bg', label: 'Background check', icon: '🔍', status: verif?.bg_check_status || 'not_started' },
    { key: 'license', label: "Driver's license", icon: '🪪', status: verif?.license_status || 'not_submitted' },
    { key: 'insurance', label: 'Auto insurance', icon: '📋', status: verif?.insurance_status || 'not_submitted' },
    { key: 'selfie', label: 'Photo / selfie', icon: '🤳', status: verif?.selfie_status || 'not_submitted' },
  ];

  const stColor = (st: string) => ({ approved: C.school, pending: C.amber, rejected: C.coral, not_started: C.ink3, not_submitted: C.ink3 } as any)[st] || C.ink3;
  const stLabel = (st: string) => ({ approved: '✅ Approved', pending: '⏳ In review', rejected: '❌ Rejected', not_started: '○ Not started', not_submitted: '○ Not submitted' } as any)[st] || st;
  const allPending = verif && ['pending','approved'].includes(verif.bg_check_status);

  return (
    <div>
      <button onClick={() => nav('profile')} style={{ ...s.btnSm(C.sur2, C.ink2), marginBottom: 16 }}>← Back to profile</button>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>Driver verification</div>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: 16 }}>All 4 steps required to drive children in school carpools</div>

      {loading && <div style={{ textAlign: 'center', padding: 20, color: C.ink3 }}>Loading…</div>}

      {/* Status overview */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 9, marginBottom: 16 }}>
        {steps.map(step => (
          <div key={step.key} style={{ background: C.sur, border: `1px solid ${C.bdr}`, borderRadius: 12, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>{step.icon}</div>
            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>{step.label}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: stColor(step.status) }}>{stLabel(step.status)}</div>
          </div>
        ))}
      </div>

      {allPending && (
        <div style={{ background: C.amberL, borderRadius: 12, padding: 16, marginBottom: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: C.amberD, marginBottom: 4 }}>Verification in review</div>
          <div style={{ fontSize: 12, color: C.amberD }}>Our team typically reviews submissions within 1–3 business days. You'll receive an email when approved.</div>
        </div>
      )}

      {verif?.fully_verified && (
        <div style={{ background: C.schoolL, borderRadius: 12, padding: 16, marginBottom: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: C.schoolD, marginBottom: 4 }}>Fully verified driver!</div>
          <div style={{ fontSize: 12, color: C.schoolD }}>Your ✓ Verified badge is now visible to all families in the Match screen.</div>
        </div>
      )}

      {!allPending && !verif?.fully_verified && !loading && (
        <>
          <div style={{ background: C.blueL, borderRadius: 12, padding: 14, marginBottom: 14, borderLeft: `3px solid ${C.blue}` }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.blueD, marginBottom: 4 }}>Why all 4 are required</div>
            <div style={{ fontSize: 12, color: C.blueD }}>Parents are trusting you with their children. A complete verification package — background check, valid license, current insurance, and photo ID — gives families full confidence.</div>
          </div>

          {/* SECTION 1: Background check */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 24 }}>🔍</div>
              <div><div style={{ fontWeight: 700, fontSize: 14 }}>Background check</div><div style={{ fontSize: 12, color: C.ink3 }}>Criminal, sex offender registry, MVR</div></div>
            </div>
            <div style={s.grid2}>
              <div style={{ marginBottom: 10 }}><label style={s.label}>Date of birth</label><input type="date" style={s.input} value={form.bgDob} onChange={e => set('bgDob', e.target.value)} /></div>
              <div style={{ marginBottom: 10 }}><label style={s.label}>Driver's license #</label><input style={s.input} value={form.bgLicense} onChange={e => set('bgLicense', e.target.value)} placeholder="License number" /></div>
            </div>
            <div style={{ marginBottom: 10 }}><label style={s.label}>Last 4 of SSN</label><input type="password" maxLength={4} style={{ ...s.input, width: 120 }} value={form.bgSsn} onChange={e => set('bgSsn', e.target.value)} placeholder="••••" /></div>
          </Card>

          {/* SECTION 2: Driver's License */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 24 }}>🪪</div>
              <div><div style={{ fontWeight: 700, fontSize: 14 }}>Driver's license</div><div style={{ fontSize: 12, color: C.ink3 }}>Upload front of license</div></div>
            </div>
            <div style={s.grid2}>
              <div style={{ marginBottom: 10 }}><label style={s.label}>License number</label><input style={s.input} value={form.licenseNum} onChange={e => set('licenseNum', e.target.value)} placeholder="A12345678" /></div>
              <div style={{ marginBottom: 10 }}><label style={s.label}>State issued</label>
                <select style={s.input} value={form.licenseState} onChange={e => set('licenseState', e.target.value)}>
                  <option value="">Select state</option>
                  {['FL','TX','OH','GA','NJ','NC','CA'].map(s2 => <option key={s2}>{s2}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 12 }}><label style={s.label}>Expiry date</label><input type="date" style={{ ...s.input, width: 180 }} value={form.licenseExpiry} onChange={e => set('licenseExpiry', e.target.value)} /></div>
            <div>
              <label style={s.label}>Upload license photo</label>
              <div style={{ marginTop: 6 }}>
                {form.licensePreview ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={form.licensePreview} alt="License" style={{ height: 100, borderRadius: 8, border: `1px solid ${C.bdr}` }} />
                    <button onClick={() => { set('licenseFile', null); set('licensePreview', ''); }} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: C.coral, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                  </div>
                ) : (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: C.sur2, border: `1.5px dashed ${C.bdr}`, borderRadius: 9, cursor: 'pointer', fontSize: 13, color: C.ink2 }}>
                    📷 Choose photo
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile('licenseFile', 'licensePreview', e.target.files?.[0] || null)} />
                  </label>
                )}
              </div>
            </div>
          </Card>

          {/* SECTION 3: Insurance */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 24 }}>📋</div>
              <div><div style={{ fontWeight: 700, fontSize: 14 }}>Auto insurance</div><div style={{ fontSize: 12, color: C.ink3 }}>Must be current and valid</div></div>
            </div>
            <div style={s.grid2}>
              <div style={{ marginBottom: 10 }}><label style={s.label}>Insurance provider</label><input style={s.input} value={form.insuranceProvider} onChange={e => set('insuranceProvider', e.target.value)} placeholder="State Farm, Geico…" /></div>
              <div style={{ marginBottom: 10 }}><label style={s.label}>Policy number</label><input style={s.input} value={form.insurancePolicy} onChange={e => set('insurancePolicy', e.target.value)} placeholder="POL-12345678" /></div>
            </div>
            <div style={{ marginBottom: 12 }}><label style={s.label}>Policy expiry date</label><input type="date" style={{ ...s.input, width: 180 }} value={form.insuranceExpiry} onChange={e => set('insuranceExpiry', e.target.value)} /></div>
            <div>
              <label style={s.label}>Upload insurance card</label>
              <div style={{ marginTop: 6 }}>
                {form.insurancePreview ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={form.insurancePreview} alt="Insurance" style={{ height: 100, borderRadius: 8, border: `1px solid ${C.bdr}` }} />
                    <button onClick={() => { set('insuranceFile', null); set('insurancePreview', ''); }} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: C.coral, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12 }}>✕</button>
                  </div>
                ) : (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: C.sur2, border: `1.5px dashed ${C.bdr}`, borderRadius: 9, cursor: 'pointer', fontSize: 13, color: C.ink2 }}>
                    📷 Choose photo
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile('insuranceFile', 'insurancePreview', e.target.files?.[0] || null)} />
                  </label>
                )}
              </div>
            </div>
          </Card>

          {/* SECTION 4: Selfie */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 24 }}>🤳</div>
              <div><div style={{ fontWeight: 700, fontSize: 14 }}>Photo verification (selfie)</div><div style={{ fontSize: 12, color: C.ink3 }}>Clear photo of your face, good lighting</div></div>
            </div>
            <div style={{ background: C.amberL, borderRadius: 9, padding: 10, marginBottom: 12, fontSize: 12, color: C.amberD }}>
              This photo helps parents visually confirm who is picking up their child. It will be shown to your carpool group members only.
            </div>
            <div>
              {form.selfiePreview ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={form.selfiePreview} alt="Selfie" style={{ height: 120, width: 120, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${C.school}` }} />
                  <button onClick={() => { set('selfieFile', null); set('selfiePreview', ''); }} style={{ position: 'absolute', top: 0, right: 0, width: 24, height: 24, borderRadius: '50%', background: C.coral, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12 }}>✕</button>
                </div>
              ) : (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: C.sur2, border: `1.5px dashed ${C.bdr}`, borderRadius: 9, cursor: 'pointer', fontSize: 13, color: C.ink2 }}>
                  🤳 Take or upload selfie
                  <input type="file" accept="image/*" capture="user" style={{ display: 'none' }} onChange={e => handleFile('selfieFile', 'selfiePreview', e.target.files?.[0] || null)} />
                </label>
              )}
            </div>
          </Card>

          {/* Consent + submit */}
          <div onClick={() => set('bgConsent', !form.bgConsent)} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 12, background: form.bgConsent ? C.schoolL : C.sur2, borderRadius: 9, cursor: 'pointer', marginBottom: 14, border: `1px solid ${form.bgConsent ? C.school : C.bdr}` }}>
            <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${form.bgConsent ? C.school : C.bdr}`, background: form.bgConsent ? C.school : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {form.bgConsent ? '✓' : ''}
            </div>
            <div style={{ fontSize: 12, color: form.bgConsent ? C.schoolD : C.ink2 }}>
              I consent to a background check and confirm all submitted documents are genuine and current. I authorize PoolRide to verify my information and understand that false submissions will result in immediate removal.
            </div>
          </div>

          <button onClick={submit} disabled={saving} style={{ ...s.btn(C.school), width: '100%', justifyContent: 'center', opacity: saving ? .7 : 1 }}>
            {saving ? 'Submitting…' : '🔒 Submit for verification'}
          </button>
          <div style={{ fontSize: 11, color: C.ink3, textAlign: 'center', marginTop: 8 }}>
            All documents are encrypted and reviewed only by authorized PoolRide admins · Reviewed in 1–3 business days
          </div>
        </>
      )}
    </div>
  );
}

// ─── AUTO SCHEDULE SCREEN ─────────────────────────────────────────────────────
function AutoScheduleScreen({ nav }: { nav: (p: string) => void }) {
  const { session, profile, activeGroupId } = useApp();
  const [step, setStep] = React.useState<'config'|'preview'|'done'>('config');
  const [startDate, setStartDate] = React.useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = React.useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 42);
    return d.toISOString().split('T')[0];
  });
  const [algo, setAlgo] = React.useState('fair_rotation');
  const [skipHolidays, setSkipHolidays] = React.useState(true);
  const [generating, setGenerating] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [schedule, setSchedule] = React.useState<any[]>([]);
  const [groupId, setGroupId] = React.useState<string|null>(null);
  const [groupName, setGroupName] = React.useState<string>('');

  const [members, setMembers] = React.useState<any[]>([]);
  React.useEffect(() => {
    if (!supabase || !session) return;
    // Use activeGroupId if set, otherwise find first group
    const findGroup = activeGroupId
      ? supabase.from('group_members').select('group_id').eq('user_id', session.user.id).eq('group_id', activeGroupId).maybeSingle()
      : supabase.from('group_members').select('group_id').eq('user_id', session.user.id).limit(1).maybeSingle();

    findGroup.then(async ({ data: myMembership }) => {
        if (!myMembership?.group_id) return;
        const gid = myMembership.group_id;
        setGroupId(gid);

        // Load group name
        const { data: grpData } = await supabase!.from('carpool_groups')
          .select('school_name, grade').eq('id', gid).maybeSingle();
        if (grpData) setGroupName(`${grpData.school_name}${grpData.grade ? ' · ' + grpData.grade : ''}`);

        // Load ALL members of this specific group with their availability from profiles
        const { data } = await supabase!.from('group_members')
          .select('*, profiles(full_name, has_car, total_seats, preferred_days, dropoff_days, pickup_days, school_name)')
          .eq('group_id', gid);

        if (data && data.length > 0) {
          // Deduplicate by user_id
          const seen = new Set<string>();
          const unique = data.filter((m: any) => {
            if (seen.has(m.user_id)) return false;
            seen.add(m.user_id); return true;
          });
          setMembers(unique.map((m: any) => {
            const p = m.profiles || {};
            // Use profile dropoff/pickup days (set during signup) — fall back to preferred_days
            const profileDropoff = Array.isArray(p.dropoff_days) && p.dropoff_days.length > 0 ? p.dropoff_days : null;
            const profilePickup = Array.isArray(p.pickup_days) && p.pickup_days.length > 0 ? p.pickup_days : null;
            const profilePreferred = Array.isArray(p.preferred_days) ? p.preferred_days : [];
            return {
              userId: m.user_id,
              name: m.user_id === session.user.id
                ? (p.full_name || 'You') + ' (You)'
                : p.full_name || 'Member',
              days: profilePreferred,
              dropoffDays: profileDropoff || profilePreferred,
              pickupDays: profilePickup || profilePreferred,
              seats: p.total_seats || 4,
              canDrive: p.has_car || false,
              groupId: gid,
              school: p.school_name || '',
              childrenIncluded: Array.isArray(m.children_included) ? m.children_included : [],
              childCount: Array.isArray(m.children_included) && m.children_included.length > 0
                ? m.children_included.length : 1,
            };
          }));

          // For each member, check if they're in OTHER groups (different school)
          // so we can mark them as "potentially double-booked"
          const allUserIds = unique.map((m: any) => m.user_id);
          if (allUserIds.length > 0) {
            supabase.from('group_members')
              .select('user_id, group_id, carpool_groups(school_name)')
              .in('user_id', allUserIds)
              .neq('group_id', gid)
              .then(({ data: otherGms }) => {
                if (!otherGms || otherGms.length === 0) return;
                // Map userId → list of other schools they drive for
                const otherSchools: Record<string, string[]> = {};
                otherGms.forEach((gm: any) => {
                  const school = (gm as any).carpool_groups?.school_name;
                  if (school) {
                    if (!otherSchools[gm.user_id]) otherSchools[gm.user_id] = [];
                    otherSchools[gm.user_id].push(school);
                  }
                });
                // Update members with their other school commitments
                setMembers(prev => prev.map(m => ({
                  ...m,
                  otherSchools: otherSchools[m.userId] || [],
                  hasConflict: (otherSchools[m.userId] || []).length > 0,
                })));
              });
          }
        }
      });
  }, [session]);

  const [holidays, setHolidays] = React.useState<string[]>([]);
  React.useEffect(() => {
    if (!supabase) return;
    const stateCode = profile?.state_code || profile?.state || 'FL';
    supabase.from('school_calendar')
      .select('event_date')
      .eq('state_code', stateCode)
      .in('event_type', ['holiday','no_school'])
      .then(({ data }) => setHolidays((data||[]).map((e:any) => e.event_date)));
  }, [profile]);

  async function generateSchedule() {
    const drivers = members.filter(m => m.canDrive);
    if (drivers.length === 0) { alert('No drivers in your group. Make sure members have "has a car" set in their profiles.'); return; }
    setGenerating(true);

    // Load cross-group assignments for all drivers in the date range
    // So we don't assign someone to drive if they're already driving for another group that day
    const driverIds = drivers.map((d: any) => d.userId);
    const { data: existingEntries } = await supabase!
      .from('schedule_entries')
      .select('user_id, date, duty, ride_type, group_id')
      .in('user_id', driverIds)
      .gte('date', startDate)
      .lte('date', endDate)
      .neq('group_id', groupId); // only OTHER groups

    // Build a map: userId → Set of dates already assigned (drive OR pickup) in other groups
    const otherGroupDropoff: Record<string, Set<string>> = {};
    const otherGroupPickup: Record<string, Set<string>> = {};
    for (const e of (existingEntries || [])) {
      if (e.duty === 'drive' || e.ride_type === 'dropoff') {
        if (!otherGroupDropoff[e.user_id]) otherGroupDropoff[e.user_id] = new Set();
        otherGroupDropoff[e.user_id].add(e.date);
      }
      if (e.duty === 'pickup' || e.ride_type === 'pickup') {
        if (!otherGroupPickup[e.user_id]) otherGroupPickup[e.user_id] = new Set();
        otherGroupPickup[e.user_id].add(e.date);
      }
    }

    const allDays: { date: string; dayName: string }[] = [];
    const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const start = new Date(startDate + 'T12:00:00');
    const end   = new Date(endDate   + 'T12:00:00');

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dow = d.getDay();
      if (dow === 0 || dow === 6) continue;
      // Use local date to avoid UTC timezone shift
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      if (skipHolidays && holidays.includes(dateStr)) {
        console.log('⛔ Skipping holiday:', dateStr, dayNames[dow]);
        continue;
      }
      allDays.push({ date: dateStr, dayName: dayNames[dow] });
    }
    console.log('📅 Total days to schedule:', allDays.length, 'Fridays:', allDays.filter(d => d.dayName === 'Fri').length);

    // ── SCHEDULING ALGORITHM ─────────────────────────────────────────────
    // Generates separate drop-off and pickup assignments per day.
    // Each driver has separate dropoffDays and pickupDays availability.
    // Target drives weighted by child count for fairness.

    const totalChildren = drivers.reduce((sum: number, d: any) => sum + (d.childCount || 1), 0);
    const totalDays = allDays.length;

    const dropoffCounts: Record<string, number> = {};
    const pickupCounts: Record<string, number> = {};
    drivers.forEach((d: any) => { dropoffCounts[d.userId] = 0; pickupCounts[d.userId] = 0; });

    const pickAvailable = (
      candidates: any[],
      counts: Record<string, number>,
      dayName: string,
      date: string,
      daysKey: 'dropoffDays' | 'pickupDays',
      conflictMap: Record<string, Set<string>>,
      excludeUserId?: string // exclude someone already doing the other ride type today
    ) => {
      // Filter out drivers already assigned in other groups on this date
      const notConflicted = candidates.filter((d: any) =>
        !conflictMap[d.userId]?.has(date)
      );
      const pool = notConflicted.length > 0 ? notConflicted : candidates;

      // Prefer drivers who have this day in their preferences
      const preferred = pool.filter((d: any) => {
        const days = d[daysKey] || [];
        return days.length === 0 || days.includes(dayName);
      });
      const dayPool = preferred.length > 0 ? preferred : pool;

      // Try to pick someone different from the other ride type today
      const different = dayPool.filter((d: any) => d.userId !== excludeUserId);
      const finalPool = different.length > 0 ? different : dayPool;

      return finalPool.sort((a: any, b: any) => (counts[a.userId] || 0) - (counts[b.userId] || 0))[0];
    };

    const assigned: any[] = [];

    for (const day of allDays) {
      const dropoffDriver = pickAvailable(drivers, dropoffCounts, day.dayName, day.date, 'dropoffDays', otherGroupDropoff);
      if (dropoffDriver) dropoffCounts[dropoffDriver.userId]++;

      // Pass dropoff driver's ID to prefer someone different for pickup
      const pickupDriver = pickAvailable(drivers, pickupCounts, day.dayName, day.date, 'pickupDays', otherGroupPickup, dropoffDriver?.userId);
      if (pickupDriver) pickupCounts[pickupDriver.userId]++;

      assigned.push({
        date: day.date,
        dayName: day.dayName,
        driver: dropoffDriver?.name || '',
        pickupDriver: pickupDriver?.name || '',
        dropoffUserId: dropoffDriver?.userId,
        pickupUserId: pickupDriver?.userId,
      });
    }

    // Group into weeks for display
    const weekMap: Record<number, any[]> = {};
    for (const day of assigned) {
      const weekNum = Math.floor(
        (new Date(day.date + 'T12:00:00').getTime() - start.getTime()) /
        (7 * 24 * 60 * 60 * 1000)
      );
      if (!weekMap[weekNum]) weekMap[weekNum] = [];
      weekMap[weekNum].push(day);
    }

    const weeks = Object.entries(weekMap)
      .sort(([a],[b]) => Number(a)-Number(b))
      .slice(0, 6)
      .map(([wn, days], i) => ({ week: i + 1, days }));

    setSchedule(weeks);
    setGenerating(false);
    setStep('preview');
  }

  async function saveSchedule() {
    if (!supabase || !session || !groupId) { setStep('done'); return; }
    setSaving(true);

    // Delete existing schedule entries for this group in this range
    await supabase.from('schedule_entries')
      .delete()
      .eq('group_id', groupId)
      .gte('date', startDate)
      .lte('date', endDate);

    // Write new entries — separate drop-off and pickup per day
    const entries: any[] = [];
    for (const week of schedule) {
      for (const day of week.days) {
        if (day.dropoffUserId) {
          entries.push({ group_id: groupId, user_id: day.dropoffUserId, date: day.date, duty: 'drive', ride_type: 'dropoff', week_number: week.week });
        }
        if (day.pickupUserId && day.pickupUserId !== day.dropoffUserId) {
          entries.push({ group_id: groupId, user_id: day.pickupUserId, date: day.date, duty: 'pickup', ride_type: 'pickup', week_number: week.week });
        } else if (day.dropoffUserId) {
          entries.push({ group_id: groupId, user_id: day.dropoffUserId, date: day.date, duty: 'pickup', ride_type: 'pickup', week_number: week.week });
        }
      }
    }

    if (entries.length === 0) {
      alert('No schedule entries to save. Make sure your group has drivers with availability days set.');
      setSaving(false);
      return;
    }

    const { error: saveError } = await supabase
      .from('schedule_entries')
      .upsert(entries, { onConflict: 'group_id,user_id,date,ride_type', ignoreDuplicates: true });

    if (saveError) {
      alert('Error saving schedule: ' + saveError.message);
      setSaving(false);
      return;
    }

    console.log(`Saved ${entries.length} schedule entries`);
    setSaving(false);
    setStep('done');
  }

  // Drive summary for preview — show dropoff and pickup separately
  const driveSummary: Record<string, { dropoff: number; pickup: number }> = {};
  schedule.forEach(week => week.days.forEach((d: any) => {
    if (!driveSummary[d.driver]) driveSummary[d.driver] = { dropoff: 0, pickup: 0 };
    driveSummary[d.driver].dropoff++;
    const pu = d.pickupDriver || d.driver;
    if (!driveSummary[pu]) driveSummary[pu] = { dropoff: 0, pickup: 0 };
    driveSummary[pu].pickup++;
  }));

  return (
    <div>
      <button onClick={() => step === 'preview' ? setStep('config') : nav('group')} style={{ ...s.btnSm(C.sur2, C.ink2), marginBottom: 16 }}>
        {step === 'preview' ? '← Back to config' : '← Back to group'}
      </button>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>Auto-generate schedule</div>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: groupName ? 4 : 16 }}>Fair rotation based on availability, skips holidays automatically</div>
      {groupName && <div style={{ fontSize: 13, fontWeight: 600, color: C.school, marginBottom: 16 }}>📍 {groupName}</div>}

      {step === 'done' && (
        <div style={{ textAlign: 'center', padding: '32px 16px' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📅</div>
          <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Schedule saved!</div>
          <div style={{ fontSize: 13, color: C.ink3, marginBottom: 20 }}>
            All {members.length} group members can now see their driving duties in the Schedule screen.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button onClick={() => nav('schedule')} style={s.btn(C.school)}>View schedule →</button>
            <button onClick={() => nav('calendar')} style={s.outlineBtn(C.school)}>View calendar</button>
          </div>
        </div>
      )}

      {step === 'config' && (
        <>
          {members.length === 0 && (
            <div style={{ ...s.card, textAlign: 'center', padding: 28, marginBottom: 12 }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>👨‍👩‍👧</div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>No carpool group yet</div>
              <div style={{ fontSize: 13, color: C.ink3, marginBottom: 14 }}>Form a group first from the Match screen, then come back here.</div>
              <button onClick={() => nav('match')} style={s.btn(C.school)}>Find families →</button>
            </div>
          )}
          {members.length > 0 && (
            <>
              <Card>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Schedule period</div>
                <div style={s.grid2}>
                  <div style={{ marginBottom: 10 }}><label style={s.label}>Start date</label><input type="date" style={s.input} value={startDate} onChange={e => setStartDate(e.target.value)} /></div>
                  <div style={{ marginBottom: 10 }}><label style={s.label}>End date</label><input type="date" style={s.input} value={endDate} onChange={e => setEndDate(e.target.value)} /></div>
                </div>
              </Card>

              <Card>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Algorithm</div>
                {[
                  { val: 'fair_rotation', label: '⚖️ Fair rotation', desc: 'Distributes drives proportionally by children per family. Preference days used as tiebreaker. Drivers only assigned on their available days.' },
                  { val: 'preference_first', label: '📅 Preference first', desc: 'Same fair rotation but gives extra priority to constrained drivers (fewer available days) to ensure they always get their days first.' },
                ].map(a => (
                  <div key={a.val} onClick={() => setAlgo(a.val)} style={{ border: `1.5px solid ${algo === a.val ? C.school : C.bdr}`, background: algo === a.val ? C.schoolL : C.sur, borderRadius: 9, padding: 12, marginBottom: 8, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${algo === a.val ? C.school : C.bdr}`, background: algo === a.val ? C.school : '#fff', flexShrink: 0 }} />
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{a.label}</span>
                    </div>
                    <div style={{ fontSize: 12, color: C.ink3, marginLeft: 22 }}>{a.desc}</div>
                  </div>
                ))}
              </Card>

              <Card>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Options</div>
                <div onClick={() => setSkipHolidays(h => !h)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${skipHolidays ? C.school : C.bdr}`, background: skipHolidays ? C.school : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                    {skipHolidays ? '✓' : ''}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>Skip school holidays & no-school days</div>
                    <div style={{ fontSize: 12, color: C.ink3 }}>
                      {holidays.length > 0 ? `${holidays.length} holidays loaded for ${profile?.state_code || 'FL'}` : 'Loading calendar…'}
                    </div>
                  </div>
                </div>
              </Card>

              <Card>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Group members ({members.length})</div>
                {members.map(m => (
                  <div key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: `1px solid ${C.sur2}` }}>
                    <Av name={m.name} size={30} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{m.name}</div>
                      <div style={{ fontSize: 11, color: C.ink3 }}>
                        {m.canDrive
                          ? `Driver · Drop-off: ${m.dropoffDays?.length > 0 ? m.dropoffDays.join(', ') : 'Any'} · Pick-up: ${m.pickupDays?.length > 0 ? m.pickupDays.join(', ') : 'Any'} · ${m.seats} seats`
                          : 'Passenger only — not assigned drives'}
                      </div>
                    </div>
                    {m.canDrive
                      ? <span style={s.tag(C.schoolL, C.schoolD)}>Driver</span>
                      : <span style={s.tag(C.sur2, C.ink2)}>Rider</span>}
                  </div>
                ))}
                {members.filter(m => m.canDrive).length === 0 && (
                  <Alert bg={C.coralL} color={C.coralD} bdr={C.coral}>
                    No drivers in group yet. Members need "has a car" set in their profile to be assigned drives.
                  </Alert>
                )}
              </Card>

              <button onClick={generateSchedule} disabled={generating || members.filter(m => m.canDrive).length === 0} style={{ ...s.btn(C.school), width: '100%', justifyContent: 'center', opacity: generating ? .7 : 1 }}>
                {generating ? '⚙️ Generating…' : '✨ Generate schedule'}
              </button>
            </>
          )}
        </>
      )}

      {step === 'preview' && (
        <>
          <div style={{ background: C.schoolL, borderRadius: 12, padding: 14, marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.schoolD, marginBottom: 8 }}>✨ Schedule generated — {schedule.reduce((t, w) => t + w.days.length, 0)} school days</div>
            <div style={{ fontSize: 11, color: C.schoolD, marginBottom: 8, opacity: .8 }}>
              Drives weighted by children per family — separate drop-off and pick-up assignments
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const }}>
              {Object.entries(driveSummary).map(([name, counts]: [string, any]) => {
                const member = members.find((m: any) => m.name === name);
                const kids = member?.childCount || 1;
                return (
                  <span key={name} style={s.tag(C.schoolL, C.schoolD)}>
                    {name.replace(' (You)','')} · 🌅{counts.dropoff} 🌆{counts.pickup}{kids > 1 ? ` (${kids} kids)` : ''}
                  </span>
                );
              })}
            </div>
          </div>

          {schedule.map(week => (
            <Card key={week.week}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: C.ink2 }}>WEEK {week.week}</div>
              {week.days.map((d: any, i: number) => {
                const isMeDropoff = d.driver.includes('(You)');
                const isMePickup = (d.pickupDriver || d.driver).includes('(You)');
                const sameDriver = !d.pickupDriver || d.driver === d.pickupDriver;
                const dateDisplay = new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                return (
                  <div key={d.date} style={{ padding: '8px 0', borderBottom: i < week.days.length-1 ? `1px solid ${C.sur2}` : undefined }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: sameDriver ? 0 : 4 }}>
                      <div style={{ minWidth: 100, fontSize: 12, color: C.ink3 }}>{dateDisplay}</div>
                      <span style={{ fontSize: 11, color: C.ink3, minWidth: 28 }}>{d.dayName}</span>
                      <span style={{ fontSize: 11, color: C.schoolD }}>🌅</span>
                      <span style={isMeDropoff ? s.tag(C.school, '#fff') : s.tag(C.schoolL, C.schoolD)}>{d.driver.replace(' (You)', isMeDropoff ? ' ✓' : '')}</span>
                      {sameDriver && <span style={{ fontSize: 11, color: C.ink3 }}>🌆 same</span>}
                      {isMeDropoff && <span style={{ fontSize: 11, color: C.schoolD, fontWeight: 600 }}>← You drive</span>}
                    </div>
                    {!sameDriver && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 136 }}>
                        <span style={{ fontSize: 11, color: C.amberD }}>🌆</span>
                        <span style={isMePickup ? s.tag(C.amber, '#fff') : s.tag(C.amberL, C.amberD)}>{(d.pickupDriver || '').replace(' (You)', isMePickup ? ' ✓' : '')}</span>
                        {isMePickup && <span style={{ fontSize: 11, color: C.amberD, fontWeight: 600 }}>← You pick up</span>}
                      </div>
                    )}
                    {d.conflict && (
                      <div style={{ fontSize: 10, color: C.coralD, marginTop: 3, paddingLeft: 136 }}>{d.conflict}</div>
                    )}
                  </div>
                );
              })}
            </Card>
          ))}

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setStep('config'); setSchedule([]); }} style={s.btn(C.sur2, C.ink2)}>← Regenerate</button>
            <button onClick={saveSchedule} disabled={saving} style={{ ...s.btn(C.school), flex: 1, justifyContent: 'center', opacity: saving ? .7 : 1 }}>
              {saving ? 'Saving…' : '✅ Save & notify group'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── SCHOOL CALENDAR SCREEN ───────────────────────────────────────────────────
function SchoolCalendarScreen({ nav }: { nav: (p: string) => void }) {
  const { session, profile } = useApp();
  const [events, setEvents] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedMonth, setSelectedMonth] = React.useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear());
  const stateCode = profile?.state_code || 'FL';

  React.useEffect(() => {
    if (!supabase) return;
    const start = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`;
    const endDay = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const end = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${endDay}`;
    supabase.from('school_calendar')
      .select('*')
      .or(`state_code.eq.${stateCode},school_name.eq.${profile?.school_name || 'none'}`)
      .gte('event_date', start).lte('event_date', end)
      .order('event_date')
      .then(({ data }) => { setEvents(data || []); setLoading(false); });
  }, [selectedMonth, selectedYear, stateCode]);

  const monthName = new Date(selectedYear, selectedMonth).toLocaleString('default', { month: 'long', year: 'numeric' });

  const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
  const firstDay = new Date(selectedYear, selectedMonth, 1).getDay();

  const eventDates = new Set(events.map(e => e.event_date));
  const eventByDate: any = {};
  events.forEach(e => { eventByDate[e.event_date] = e; });

  const typeColor = (t: string) => ({ holiday: C.coral, no_school: C.amber, early_dismissal: C.college, exam: C.blue, special: C.school } as any)[t] || C.ink3;
  const typeLabel = (t: string) => ({ holiday: 'Holiday', no_school: 'No School', early_dismissal: 'Early Dismissal', exam: 'Exams', special: 'Special Event' } as any)[t] || t;

  return (
    <div>
      <button onClick={() => nav('schedule')} style={{ ...s.btnSm(C.sur2, C.ink2), marginBottom: 16 }}>← Back to schedule</button>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>School calendar</div>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: 16 }}>
        {profile?.school_name || 'Your school'} · {stateCode} · Carpool days auto-skip no-school days
      </div>

      {/* Month nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <button onClick={() => { if(selectedMonth === 0){setSelectedMonth(11);setSelectedYear(y=>y-1);}else setSelectedMonth(m=>m-1); }} style={s.btnSm(C.sur2, C.ink2)}>←</button>
        <div style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 16 }}>{monthName}</div>
        <button onClick={() => { if(selectedMonth === 11){setSelectedMonth(0);setSelectedYear(y=>y+1);}else setSelectedMonth(m=>m+1); }} style={s.btnSm(C.sur2, C.ink2)}>→</button>
      </div>

      {/* Calendar grid */}
      <Card padding="sm">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 4 }}>
          {['S','M','T','W','T','F','S'].map((d,i) => <div key={i} style={{ textAlign:'center', fontSize:11, fontWeight:700, color:C.ink3, padding:'4px 0' }}>{d}</div>)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
          {Array.from({ length: firstDay }, (_, i) => <div key={'e'+i} />)}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const dateStr = `${selectedYear}-${String(selectedMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
            const event = eventByDate[dateStr];
            const today = new Date().toISOString().split('T')[0];
            const isToday = dateStr === today;
            const dayOfWeek = new Date(dateStr + 'T12:00:00').getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
            return (
              <div key={day} style={{
                textAlign: 'center', padding: '6px 2px', borderRadius: 6, fontSize: 12,
                background: event ? typeColor(event.event_type) + '25' : isToday ? C.schoolL : 'transparent',
                color: isWeekend ? C.ink3 : C.ink,
                border: isToday ? `1.5px solid ${C.school}` : '1.5px solid transparent',
                fontWeight: isToday ? 700 : 400,
              }}>
                {day}
                {event && <div style={{ width: 5, height: 5, borderRadius: '50%', background: typeColor(event.event_type), margin: '1px auto 0' }} />}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const, marginBottom: 14 }}>
        {['holiday','no_school','early_dismissal','exam'].map(t => (
          <span key={t} style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11 }}>
            <span style={{ width:8, height:8, borderRadius:'50%', background:typeColor(t), display:'inline-block' }} />
            <span style={{ color:C.ink3 }}>{typeLabel(t)}</span>
          </span>
        ))}
      </div>

      {/* Events list */}
      {loading && <div style={{ textAlign:'center', padding:20, color:C.ink3 }}>Loading…</div>}
      {!loading && events.length === 0 && (
        <div style={{ textAlign:'center', padding:'20px 16px', color:C.ink3 }}>
          <div style={{ fontSize:32, marginBottom:8 }}>🗓️</div>
          No events this month. Enjoy uninterrupted carpool days!
        </div>
      )}
      {events.map(e => (
        <div key={e.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', background:C.sur, border:`1px solid ${C.bdr}`, borderRadius:12, marginBottom:8, borderLeft:`4px solid ${typeColor(e.event_type)}` }}>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:600, fontSize:14 }}>{e.title}</div>
            <div style={{ fontSize:12, color:C.ink3 }}>{new Date(e.event_date+'T12:00:00').toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}</div>
          </div>
          <span style={s.tag(typeColor(e.event_type)+'25', typeColor(e.event_type))}>{typeLabel(e.event_type)}</span>
        </div>
      ))}

      <Card>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>Add a custom event</div>
        <div style={{ fontSize:12, color:C.ink3, marginBottom:10 }}>Add your school's specific events that aren't in the system</div>
        <div style={s.grid2}>
          <div style={{ marginBottom:10 }}><label style={s.label}>Date</label><input type="date" style={s.input} /></div>
          <div style={{ marginBottom:10 }}>
            <label style={s.label}>Type</label>
            <select style={s.input}><option value="no_school">No School</option><option value="early_dismissal">Early Dismissal</option><option value="holiday">Holiday</option><option value="special">Special Event</option></select>
          </div>
        </div>
        <div style={{ marginBottom:10 }}><label style={s.label}>Event name</label><input style={s.input} placeholder="Teacher planning day, field trip…" /></div>
        <button style={s.btn(C.school)}>Add to calendar</button>
      </Card>
    </div>
  );
}

// ─── NEIGHBORHOOD BOARD SCREEN ────────────────────────────────────────────────
function NeighborhoodBoardScreen({ nav }: { nav: (p: string) => void }) {
  const { session, profile } = useApp();
  const [tab, setTab] = useState<'browse'|'post'|'mine'>('browse');
  const [filter, setFilter] = useState<'all'|'need_ride'|'offering_ride'>('all');
  const [search, setSearch] = useState('');
  const [posts, setPosts] = useState<any[]>([]);
  const [responses, setResponses] = useState<any[]>([]); // all responses for posts I own
  const [myResponses, setMyResponses] = useState<any[]>([]);
  const [seatsWanted, setSeatsWanted] = useState<Record<string,number>>({}); // postId -> seats wanted
  const [pickupInputs, setPickupInputs] = useState<Record<string,string>>({}); // postId -> pickup address
  const [pickupErrors, setPickupErrors] = useState<Record<string,string>>({}); // postId -> validation error
  const [pickupValidating, setPickupValidating] = useState<Record<string,boolean>>({});
  const [driverStarted, setDriverStarted] = useState<Record<string,boolean>>({}); // driverId -> has active drive
  const [driverCompleted, setDriverCompleted] = useState<Record<string,boolean>>({}); // driverId -> completed drive today
  const [myDriveStatus, setMyDriveStatus] = useState<string|null>(null); // status of my own active_drives row
  const [editingPostId, setEditingPostId] = useState<string|null>(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState<string | null>(null);
  const [responseMsg, setResponseMsg] = useState('');
  const [responseSent, setResponseSent] = useState<string[]>([]);
  const [posted, setPosted] = useState(false);

  // New post form
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  })();
  const defaultTime = (() => {
    const d = new Date(); d.setMinutes(d.getMinutes() + 30);
    return d.toTimeString().substring(0,5);
  })();
  const [form, setForm] = useState({
    type: 'need_ride' as 'need_ride' | 'offering_ride',
    destination: '',
    destStreet: '', destCity: '', destState: '',
    pickupStreet: profile?.address?.split(',')[0] || '',
    pickupCity: profile?.address?.split(',')[1]?.trim() || '',
    pickupState: profile?.address?.split(',')[2]?.trim()?.substring(0,2) || '',
    startStreet: '', startCity: '', startState: '',
    date: todayStr,
    time: defaultTime,
    returnNeeded: false,
    returnTime: '15:00',
    seatsOffered: '3',
    seatsNeeded: '1',
    notes: '',
  });
  const [addrError, setAddrError] = useState('');
  const [pickupError, setPickupError] = useState('');
  const [validatingAddr, setValidatingAddr] = useState(false);
  const [locating, setLocating] = useState(false);
  const [userLatLon, setUserLatLon] = useState<{lat: number; lon: number} | null>(null);
  const userLatLonRef = React.useRef<{lat: number; lon: number} | null>(null); // always-current ref for async callbacks
  const [destQuery, setDestQuery] = useState('');
  const [destSuggestions, setDestSuggestions] = useState<any[]>([]);
  const [searchingDest, setSearchingDest] = useState(false);
  const destSearchTimer = React.useRef<any>(null);
  const [startQuery, setStartQuery] = React.useState('');
  const [startSuggestions, setStartSuggestions] = React.useState<any[]>([]);
  const [searchingStart, setSearchingStart] = React.useState(false);
  const startSearchTimer = React.useRef<any>(null);

  function onStartQueryChange(val: string) {
    setStartQuery(val);
    upd('startStreet', ''); upd('startCity', ''); upd('startState', '');
    setStartSuggestions([]);
    if (startSearchTimer.current) clearTimeout(startSearchTimer.current);
    if (val.trim().length < 3) return;
    startSearchTimer.current = setTimeout(async () => {
      setSearchingStart(true);
      const loc = userLatLonRef.current;
      const results = await searchPlaces(val, loc?.lat, loc?.lon);
      setStartSuggestions(results);
      setSearchingStart(false);
    }, 600);
  }

  function selectStartSuggestion(s: any) {
    setStartQuery(s.name);
    upd('startStreet', s.street);
    upd('startCity', s.city);
    upd('startState', s.state);
    setStartSuggestions([]);
    setPickupError('');
  }

  // Auto-detect location on mount
  React.useEffect(() => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lon } = pos.coords;
      setUserLatLon({ lat, lon });
      userLatLonRef.current = { lat, lon };
      const addr = await reverseGeocode(lat, lon);
      if (addr) {
        upd('pickupStreet', addr.street);
        upd('pickupCity', addr.city);
        upd('pickupState', addr.state);
      }
      setLocating(false);
    }, () => setLocating(false), { timeout: 8000 });
  }, []);

  // Debounced destination search
  function onDestQueryChange(val: string) {
    setDestQuery(val);
    upd('destination', val);
    upd('destStreet', ''); upd('destCity', ''); upd('destState', '');
    setDestSuggestions([]);
    if (destSearchTimer.current) clearTimeout(destSearchTimer.current);
    if (val.trim().length < 3) return;
    destSearchTimer.current = setTimeout(async () => {
      setSearchingDest(true);
      const results = await searchPlaces(val, userLatLon?.lat, userLatLon?.lon);
      setDestSuggestions(results);
      setSearchingDest(false);
    }, 600);
  }

  function selectDestSuggestion(s: any) {
    upd('destination', s.name);
    upd('destStreet', s.street);
    upd('destCity', s.city);
    upd('destState', s.state);
    setDestQuery(s.name);
    setDestSuggestions([]);
    setAddrError('');
  }
  const upd = (k: string, v: any) => {
    if (k === 'type') {
      // Clear location fields when switching type to avoid cross-contamination
      setStartQuery(''); setStartSuggestions([]);
      setDestQuery(''); setDestSuggestions([]);
    }
    setForm(f => ({ ...f, [k]: v }));
  };

  React.useEffect(() => { loadPosts(); }, [filter, session]);

  // Check own drive status by ID (most reliable — no driver_id confusion)
  React.useEffect(() => {
    if (!supabase || !session) return;
    const checkMyDrive = async () => {
      const driveId = sessionStorage.getItem('activeDriveId');
      if (driveId) {
        const { data } = await supabase.from('active_drives').select('status').eq('id', driveId).maybeSingle();
        setMyDriveStatus(data?.status || null);
      } else {
        // Also check if I have any drive started in last 4 hours
        const fourHoursAgo = new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString();
        const { data } = await supabase.from('active_drives')
          .select('id, status').eq('driver_id', session.user.id)
          .gte('updated_at', fourHoursAgo)
          .order('updated_at', { ascending: false }).limit(1).maybeSingle();
        if (data) {
          if (data.status === 'started') sessionStorage.setItem('activeDriveId', data.id);
          setMyDriveStatus(data.status);
        }
      }
    };
    checkMyDrive();
    const interval = setInterval(checkMyDrive, 10000);
    return () => clearInterval(interval);
  }, [session?.user?.id]);

  // Pre-fill form when editing an existing post
  React.useEffect(() => {
    if (tab !== 'post') return;
    const ep = (window as any)._editPost;
    if (!ep) return;
    (window as any)._editPost = null;
    setEditingPostId(ep.id);
    setDestQuery(ep.destination || '');
    setStartQuery(ep.type === 'offering_ride' ? (ep.address_from?.split(',')[0]?.trim() || '') : '');
    setForm((f: any) => ({
      ...f,
      type: ep.type,
      destination: ep.destination || '',
      destStreet: ep.destination_address?.split(',')[0]?.trim() || '',
      destCity: ep.destination_address?.split(',')[1]?.trim() || '',
      destState: ep.destination_address?.split(',')[2]?.trim() || '',
      pickupStreet: ep.type === 'need_ride' ? (ep.address_from?.split(',')[0]?.trim() || '') : '',
      pickupCity: ep.type === 'need_ride' ? (ep.address_from?.split(',')[1]?.trim() || '') : '',
      pickupState: ep.type === 'need_ride' ? (ep.address_from?.split(',')[2]?.trim() || '') : '',
      startStreet: ep.type === 'offering_ride' ? (ep.address_from?.split(',')[0]?.trim() || '') : '',
      startCity: ep.type === 'offering_ride' ? (ep.address_from?.split(',')[1]?.trim() || '') : '',
      startState: ep.type === 'offering_ride' ? (ep.address_from?.split(',')[2]?.trim() || '') : '',
      date: ep.trip_date || f.date,
      time: ep.trip_time?.substring(0,5) || f.time,
      seatsOffered: String(ep.seats_offered || 3),
      seatsNeeded: String(ep.seats_needed || 1),
      notes: ep.notes || '',
      returnNeeded: ep.return_needed || false,
      returnTime: ep.return_time?.substring(0,5) || '15:00',
    }));
  }, [tab]);


  // Pre-fill form when editing an existing post
  React.useEffect(() => {
    if (tab !== 'post') return;
    const ep = (window as any)._editPost;
    if (!ep) return;
    (window as any)._editPost = null;
    setEditingPostId(ep.id);
    setDestQuery(ep.destination || '');
    setForm((f: any) => ({
      ...f,
      type: ep.type,
      destination: ep.destination || '',
      destStreet: ep.destination_address?.split(',')[0]?.trim() || '',
      destCity: ep.destination_address?.split(',')[1]?.trim() || '',
      destState: ep.destination_address?.split(',')[2]?.trim() || '',
      pickupStreet: ep.address_from?.split(',')[0]?.trim() || '',
      pickupCity: ep.address_from?.split(',')[1]?.trim() || '',
      pickupState: ep.address_from?.split(',')[2]?.trim() || '',
      startStreet: ep.type === 'offering_ride' ? (ep.address_from?.split(',')[0]?.trim() || '') : '',
      startCity: ep.type === 'offering_ride' ? (ep.address_from?.split(',')[1]?.trim() || '') : '',
      startState: ep.type === 'offering_ride' ? (ep.address_from?.split(',')[2]?.trim() || '') : '',
      date: ep.trip_date || f.date,
      time: ep.trip_time?.substring(0,5) || f.time,
      seatsOffered: String(ep.seats_offered || 3),
      seatsNeeded: String(ep.seats_needed || 1),
      notes: ep.notes || '',
      returnNeeded: ep.return_needed || false,
      returnTime: ep.return_time?.substring(0,5) || '15:00',
    }));
  }, [tab]);

  // When switching to mine tab, always reload fresh from DB
  // Track which accepted drivers have started their ride (for rider tracking button)
  const boardDriveChannelRef = React.useRef<any>(null);
  React.useEffect(() => {
    if (!supabase || !session) return;
    const checkDrivers = async () => {
      // For need_ride posts: responder_id is the driver
      // For offering_ride posts: the post owner (bp.user_id) is the driver
      const acceptedDriverIds = responses
        .filter((r: any) => r.status === 'accepted')
        .map((r: any) => r.responder_id).filter(Boolean);
      // My accepted responses to offering_ride posts — driver is the post owner
      const offeringRideDriverIds = myResponses
        .filter((r: any) => r.status === 'accepted' && r.board_posts?.type === 'offering_ride')
        .map((r: any) => r.board_posts?.user_id).filter(Boolean);
      const allDriverIds = Array.from(new Set([...acceptedDriverIds, ...offeringRideDriverIds]));
      if (allDriverIds.length === 0) return;
      const fourHoursAgo = new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString();
      const { data: drives } = await supabase!.from('active_drives')
        .select('driver_id').in('driver_id', allDriverIds).eq('status', 'started')
        .gte('updated_at', fourHoursAgo);
      const started: Record<string,boolean> = {};
      (drives || []).forEach((d: any) => { started[d.driver_id] = true; });
      setDriverStarted(started);
      // Also check for completed drives today
      const todayStart = new Date(); todayStart.setHours(0,0,0,0);
      const { data: completedDrives } = await supabase!.from('active_drives')
        .select('driver_id').in('driver_id', allDriverIds)
        .eq('status', 'completed')
        .gte('updated_at', todayStart.toISOString());
      const completed: Record<string,boolean> = {};
      (completedDrives || []).forEach((d: any) => { completed[d.driver_id] = true; });
      setDriverCompleted(completed);
    };
    checkDrivers();
    const pollId = setInterval(checkDrivers, 10000);
    // Clean up existing channel first (handles React StrictMode double-invoke)
    if (boardDriveChannelRef.current) {
      supabase.removeChannel(boardDriveChannelRef.current);
      boardDriveChannelRef.current = null;
    }
    const ch = supabase.channel('board-active-drives-' + Date.now())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'active_drives' }, checkDrivers)
      .subscribe();
    boardDriveChannelRef.current = ch;
    return () => {
      clearInterval(pollId);
      if (boardDriveChannelRef.current) {
        supabase.removeChannel(boardDriveChannelRef.current);
        boardDriveChannelRef.current = null;
      }
    };
  }, [responses.length, myResponses.length]);

  React.useEffect(() => {
    if (tab !== 'mine' || !supabase || !session) return;
    // Always clear and reload myResponses so status changes (completed) show immediately
    setMyResponses([]);

    // Load ALL my posts (open + matched, any type, no date filter)
    supabase.from('board_posts')
      .select('*, profiles(full_name, phone, address, has_car, bg_check_status)')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
      .then(async ({ data: myPosts }) => {
        if (!myPosts || myPosts.length === 0) return;
        // Always replace with fresh data (so status changes like 'completed' show up)
        setPosts(prev => {
          const myIds = new Set(myPosts.map((p: any) => p.id));
          // Replace existing my posts with fresh ones, keep others
          const others = prev.filter((p: any) => !myIds.has(p.id));
          return [...myPosts, ...others];
        });
        const myIds = myPosts.map((p: any) => p.id);
        const { data: respData } = await supabase!.from('board_responses')
          .select('*, profiles(full_name, phone, address), pickup_address')
          .in('post_id', myIds)
          .order('created_at', { ascending: false });
        if (respData) setResponses(respData);
      });

    // Also reload MY sent responses using direct queries (no join RLS issues)
    (async () => {
      const { data: myRespData } = await supabase!.from('board_responses')
        .select('*')
        .eq('responder_id', session.user.id)
        .order('created_at', { ascending: false });

      if (myRespData && myRespData.length > 0) {
        const pIds = Array.from(new Set(myRespData.map((r: any) => r.post_id)));
        const { data: pData } = await supabase!.from('board_posts')
          .select('id, destination, trip_date, trip_time, status, user_id, type')
          .in('id', pIds);
        const uIds = Array.from(new Set((pData || []).map((p: any) => p.user_id)));
        const { data: profData } = await supabase!.from('profiles')
          .select('id, full_name, phone').in('id', uIds);
        const pMap: any = {}; (pData || []).forEach((p: any) => { pMap[p.id] = p; });
        const profMap: any = {}; (profData || []).forEach((p: any) => { profMap[p.id] = p; });
        setMyResponses(myRespData.map((r: any) => {
          const post = pMap[r.post_id];
          return { ...r, board_posts: post ? { ...post, profiles: profMap[post.user_id] || null } : null };
        }));
      }
    })();
  }, [tab, session]);

  // Load MY sent responses on mount
  React.useEffect(() => {
    if (!supabase || !session) return;
    (async () => {
      // Step 1: get my responses
      const { data: respData } = await supabase!.from('board_responses')
        .select('*')
        .eq('responder_id', session.user.id)
        .order('created_at', { ascending: false });

      if (!respData || respData.length === 0) return;

      // Step 2: get the posts separately (no RLS join issues)
      const postIds = Array.from(new Set(respData.map((r: any) => r.post_id)));
      const { data: postsData } = await supabase!.from('board_posts')
        .select('id, destination, trip_date, trip_time, status, user_id, type')
        .in('id', postIds);

      // Step 3: get poster profiles
      const posterIds = Array.from(new Set((postsData || []).map((p: any) => p.user_id)));
      const { data: profilesData } = await supabase!.from('profiles')
        .select('id, full_name, phone')
        .in('id', posterIds);

      // Step 4: merge everything
      const postsMap: any = {};
      (postsData || []).forEach((p: any) => { postsMap[p.id] = p; });
      const profilesMap: any = {};
      (profilesData || []).forEach((p: any) => { profilesMap[p.id] = p; });

      const enriched = respData.map((r: any) => {
        const post = postsMap[r.post_id];
        const poster = post ? profilesMap[post.user_id] : null;
        return { ...r, board_posts: post ? { ...post, profiles: poster } : null };
      });
      setMyResponses(enriched);
    })();
  }, [session]);

  // Realtime new responses
  React.useEffect(() => {
    if (!supabase || !session) return;
    const ch = supabase.channel('board-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'board_posts' }, (payload) => {
        setPosts(prev => [payload.new, ...prev]);
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'board_responses' }, async (payload) => {
        // Fetch the full response with profile info
        const { data } = await supabase!.from('board_responses')
          .select('*, profiles(full_name, phone)')
          .eq('id', payload.new.id)
          .single();
        if (data) {
          setResponses(prev => [...prev, data]);
          setMyResponses(prev => data.responder_id === session.user.id ? [...prev, data] : prev);
        }
      })
      .subscribe();
    return () => { supabase!.removeChannel(ch); };
  }, [session]);

  async function loadPosts() {
    if (!supabase || !session) { setPosts([]); setLoading(false); return; }
    setLoading(true);
    let q = supabase.from('board_posts')
      .select('*')
      .eq('status', 'open')
      .order('created_at', { ascending: false });
    if (filter !== 'all') q = q.eq('type', filter);
    const { data: postsData, error } = await q.limit(50);
    if (error) { console.error('loadPosts error:', error.message); setLoading(false); return; }
    if (!postsData || postsData.length === 0) {
      setLoading(false); return; // no open posts found
    }
    // Fetch poster profiles separately (avoids RLS join blocking)
    const userIds = Array.from(new Set(postsData.map((p: any) => p.user_id)));
    const { data: profilesData } = await supabase.from('profiles')
      .select('id, full_name, phone, address, has_car, bg_check_status')
      .in('id', userIds);
    const profileMap: any = {};
    (profilesData || []).forEach((p: any) => { profileMap[p.id] = p; });
    const enriched = postsData.map((p: any) => ({ ...p, profiles: profileMap[p.user_id] || null }));
    setPosts(enriched);
    // Pre-fill pickup address from profile for accept flow
    if (profile?.address) {
      const prefills: Record<string,string> = {};
      enriched.filter((p: any) => p.type === 'offering_ride' && p.user_id !== session.user.id)
        .forEach((p: any) => { prefills[p.id] = profile.address; });
      setPickupInputs(prev => ({ ...prefills, ...prev })); // don't override manually entered
    }
    setLoading(false);

    // Load responses for my own posts so Accept offer shows on Browse immediately
    const myPostIds = enriched.filter((p: any) => p.user_id === session.user.id).map((p: any) => p.id);
    if (myPostIds.length > 0) {
      const { data: respData } = await supabase.from('board_responses')
        .select('*, profiles(full_name, phone)')
        .in('post_id', myPostIds)
        .order('created_at', { ascending: false });
      if (respData) setResponses(respData);
    }
  }

  async function submitPost() {
    setAddrError(''); setPickupError('');
    if (!form.destination.trim()) { alert('Please enter a destination name.'); return; }
    if (!form.destStreet.trim() || !form.destCity.trim() || !form.destState.trim()) { alert('Please enter the full destination address.'); return; }
    if (form.type === 'need_ride' && (!form.pickupStreet.trim() || !form.pickupCity.trim() || !form.pickupState.trim())) { alert('Please enter your full pickup address.'); return; }
    if (form.type === 'offering_ride' && !form.startStreet.trim()) { alert('Please enter your starting location.'); return; }
    if (!form.date) { alert('Please select a date.'); return; }
    // Past date/time check
    const selectedDT = new Date(`${form.date}T${form.time || '00:00'}`);
    const now = new Date();
    if (selectedDT < now) {
      if (form.date === todayStr) {
        alert('The selected time has already passed today. Please choose a future time.');
      } else {
        alert('Date and time cannot be in the past.');
      }
      return;
    }
    // Validate addresses
    setValidatingAddr(true);
    const validations = await Promise.all([
      validateAddress(form.destStreet, form.destCity, form.destState),
      // Only validate pickup as full address for need_ride; offering_ride starting location is a landmark
      form.type === 'need_ride'
        ? validateAddress(form.pickupStreet, form.pickupCity, form.pickupState)
        : Promise.resolve({ valid: true, message: undefined } as any),
    ]);
    setValidatingAddr(false);
    const [destCheck, pickupCheck] = validations as any[];
    if (!destCheck.valid) {
      if ((destCheck as any).warn) {
        const go = window.confirm(`⚠️ ${destCheck.message}\n\nClick OK to post anyway, or Cancel to re-enter.`);
        if (!go) { setAddrError('Please verify the destination address.'); return; }
      } else { setAddrError(destCheck.message || 'Destination address not found.'); return; }
    }
    if (!pickupCheck.valid) {
      if ((pickupCheck as any).warn) {
        const go = window.confirm(`⚠️ ${pickupCheck.message}\n\nClick OK to post anyway, or Cancel to re-enter.`);
        if (!go) { setPickupError('Please verify your pickup address.'); return; }
      } else { setPickupError(pickupCheck.message || 'Pickup address not found.'); return; }
    }
    const destFull = [form.destStreet, form.destCity, form.destState].join(', ');
    const pickupFull = form.type === 'offering_ride'
      ? [form.startStreet, form.startCity, form.startState].filter(Boolean).join(', ')
      : [form.pickupStreet, form.pickupCity, form.pickupState].join(', ');
    const newPost: any = {
      type: form.type,
      destination: form.destination,
      destination_address: destFull,
      address_from: pickupFull,
      trip_date: form.date, trip_time: form.time || null,
      return_needed: form.returnNeeded, return_time: form.returnNeeded ? form.returnTime : null,
      seats_offered: form.type === 'offering_ride' ? parseInt(form.seatsOffered) : null,
      seats_remaining: form.type === 'offering_ride' ? parseInt(form.seatsOffered) : null,
      seats_needed: form.type === 'need_ride' ? parseInt(form.seatsNeeded) : null,
      notes: form.notes, status: 'open',
    };
    if (supabase && session) {
      if (editingPostId) {
        // Update existing post
        const { error } = await supabase.from('board_posts').update(newPost).eq('id', editingPostId);
        if (error) { alert('Error updating: ' + error.message); return; }
        setEditingPostId(null);
      } else {
        newPost.user_id = session.user.id;
        const { data, error } = await supabase.from('board_posts').insert(newPost).select().single();
        if (error) { alert('Error posting: ' + error.message); return; }
        if (data) newPost.id = data.id;
      }
    }
    setPosted(true);
    setTab('browse');
    loadPosts();
  }

  async function sendResponse(postId: string) {
    if (!responseMsg.trim()) { alert('Please write a message first.'); return; }
    if (!supabase || !session) return;

    // Check if already responded — update message instead of inserting new
    const { data: existing } = await supabase.from('board_responses')
      .select('id')
      .eq('post_id', postId)
      .eq('responder_id', session.user.id)
      .maybeSingle();

    let data, error;

    if (existing) {
      // Already responded — update the existing message
      ({ data, error } = await supabase.from('board_responses')
        .update({ message: responseMsg.trim(), status: 'pending' })
        .eq('id', existing.id)
        .select('*, profiles(full_name, phone)')
        .single());
    } else {
      // First response — insert new
      ({ data, error } = await supabase.from('board_responses')
        .insert({ post_id: postId, responder_id: session.user.id, message: responseMsg.trim(), status: 'pending' })
        .select('*, profiles(full_name, phone)')
        .single());
    }

    if (error) {
      alert('Error sending response: ' + error.message);
      return;
    }

    setResponseSent(s => [...s, postId]);
    if (data) setMyResponses(prev => [data, ...prev.filter((r: any) => r.post_id !== postId)]);
    setResponding(null);
    setResponseMsg('');

    // Notify the post owner — in-app + push + email
    const sentPost = posts.find((p: any) => p.id === postId);
    if (sentPost && sentPost.user_id !== session.user.id) {
      const responderName = profile?.full_name || 'A neighbor';
      const isOffer = sentPost.type === 'need_ride'; // they need ride, we offered
      const notifTitle = isOffer
        ? `🚗 ${responderName} offered you a ride!`
        : `🙋 ${responderName} wants to join your ride`;
      const notifBody = isOffer
        ? `${responderName} can give you a ride to ${sentPost.destination}. Open PoolRide to accept.`
        : `${responderName} is interested in your ride offer to ${sentPost.destination}.`;
      await notifyUser(sentPost.user_id, 'board_response', notifTitle, notifBody, { post_id: postId });
    }

    if (sentPost?.type !== 'offering_ride') {
      alert(existing ? '✅ Message updated!' : '✅ Message sent! The poster will see your response.');
    }
  }

  const todayForFilter = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  })();
  const filtered = posts.filter(p => {
    if (filter !== 'all' && p.type !== filter) return false;
    if (search && !p.destination.toLowerCase().includes(search.toLowerCase()) && !p.address_from?.toLowerCase().includes(search.toLowerCase())) return false;
    // Hide matched/completed posts from browse
    if (p.status === 'matched' || p.status === 'completed') return false;
    // Hide past trips (trip date before today)
    if (p.trip_date && p.trip_date < todayForFilter) return false;
    return true;
  });

  const typeColor = (t: string) => t === 'need_ride' ? [C.amberL, C.amberD, C.amber] : [C.schoolL, C.schoolD, C.school];
  const typeLabel = (t: string) => t === 'need_ride' ? '🙋 Need a ride' : '🚗 Offering a ride';
  const fmtDate = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const fmtTime = (t: string) => { if (!t) return ''; const [h, m] = t.split(':'); const hr = parseInt(h); return `${hr > 12 ? hr - 12 : hr}:${m} ${hr >= 12 ? 'PM' : 'AM'}`; };

  return (
    <div>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>Neighborhood board</div>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: 14 }}>
        Ad hoc one-off ride requests and offers — school runs, errands, activities
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', background: C.sur2, borderRadius: 20, padding: 3, gap: 2, marginBottom: 14 }}>
        {([['browse', '📋 Browse'], ['post', '+ Post'], ['mine', 'My posts']] as const).map(([t, l]) => (
          <button key={t} onClick={() => { setTab(t); if (t === 'browse') setPosted(false); }} style={{ flex: 1, padding: '7px 0', borderRadius: 18, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12, fontFamily: 'inherit', background: tab === t ? C.sur : 'transparent', color: tab === t ? C.ink : C.ink2, boxShadow: tab === t ? '0 1px 3px rgba(0,0,0,.1)' : undefined }}>
            {l}
          </button>
        ))}
      </div>

      {/* ── BROWSE ── */}
      {tab === 'browse' && (
        <>
          {posted && (
            <Alert bg={C.schoolL} color={C.schoolD} bdr={C.school}>
              ✅ Your post is live! Neighbors will see it and can reach out directly.
            </Alert>
          )}

          {/* Filters */}
          <div style={{ ...s.cardSm, marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, marginBottom: 8 }}>
              {[['all', 'All posts'], ['need_ride', '🙋 Need rides'], ['offering_ride', '🚗 Offering rides']].map(([k, l]) => (
                <span key={k} onClick={() => setFilter(k as any)} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: filter === k ? C.school : C.sur2, color: filter === k ? '#fff' : C.ink2 }}>{l}</span>
              ))}
            </div>
            <input style={s.input} placeholder="Search destination or area…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          {loading && <div style={{ textAlign: 'center', padding: 20, color: C.ink3 }}>Loading…</div>}

          {!loading && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '28px 16px' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>No posts yet in your area</div>
              <div style={{ fontSize: 13, color: C.ink3, marginBottom: 16 }}>Be the first! Post a request or offer to help a neighbor.</div>
              <button onClick={() => setTab('post')} style={s.btn(C.school)}>Post now →</button>
            </div>
          )}

          {filtered.filter((p: any) => {
            // Only show open posts in browse, hide matched ones
            if (p.status === 'matched') return false;
            return true;
          }).map(p => {
            const [bgL, clr, bdr] = typeColor(p.type);
            // Check both local state AND loaded responses from DB
            const alreadyResponded = responseSent.includes(p.id) ||
              myResponses.some((r: any) => r.post_id === p.id);
            const myExistingResponse = myResponses.find((r: any) => r.post_id === p.id);
            const isOwn = p.user_id === session?.user?.id;
            const isVerified = p.profiles?.bg_check_status === 'approved';
            const pendingResps = isOwn && p.type === 'need_ride'
              ? responses.filter((r: any) => r.post_id === p.id && r.status === 'pending')
              : [];
            return (
              <div key={p.id} style={{ border: `1.5px solid ${C.bdr}`, borderRadius: 14, padding: 14, marginBottom: 10, background: C.sur }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' as const }}>
                  <span style={s.tag(bgL, clr)}>{typeLabel(p.type)}</span>
                  <span style={s.tag(C.blueL, C.blueD)}>{fmtDate(p.trip_date)}</span>
                  {p.trip_time && <span style={s.tag(C.sur2, C.ink2)}>{fmtTime(p.trip_time)}</span>}
                  {p.return_needed && <span style={s.tag(C.collegeL, C.collegeD)}>↔ Return too</span>}
                  {isVerified && <span style={s.tag(C.schoolL, C.schoolD)}>✓ Verified</span>}
                </div>

                {/* Destination */}
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>📍 {p.destination}</div>
                {p.address_from && <div style={{ fontSize: 12, color: C.ink3, marginBottom: 8 }}>From: {p.address_from}</div>}

                {/* Details row */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, marginBottom: 10 }}>

                  {p.type === 'offering_ride' && p.seats_offered && (() => {
                    // Use seats_remaining from DB if available, otherwise fall back to seats_offered
                    const remaining = p.seats_remaining !== undefined && p.seats_remaining !== null
                      ? p.seats_remaining
                      : (p.seats_offered || 1);
                    return <span style={s.tag(remaining > 0 ? C.schoolL : C.coralL, remaining > 0 ? C.schoolD : C.coralD)}>
                      {remaining > 0 ? `${remaining} of ${p.seats_offered} seat${p.seats_offered > 1 ? 's' : ''} available` : 'Full'}
                    </span>;
                  })()}
                  {p.type === 'need_ride' && p.seats_needed && <span style={s.tag(C.amberL, C.amberD)}>{p.seats_needed} seat{p.seats_needed > 1 ? 's' : ''} needed</span>}
                  {p.return_needed && p.return_time && <span style={s.tag(C.sur2, C.ink2)}>Return {fmtTime(p.return_time)}</span>}
                  {p.school_name && <span style={s.tag(C.sur2, C.ink2)}>🏫 {p.school_name}</span>}
                </div>

                {/* Poster info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: `1px solid ${C.sur2}`, borderBottom: responding === p.id ? undefined : `1px solid ${C.sur2}`, marginBottom: responding === p.id ? 0 : 8 }}>
                  <Av name={p.profiles?.full_name || 'Neighbor'} size={32} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{p.profiles?.full_name || 'Neighbor'}</div>
                    {p.notes && <div style={{ fontSize: 11, color: C.ink3, fontStyle: 'italic' }}>"{p.notes}"</div>}
                  </div>
                </div>

                {/* Response panel */}
                {responding === p.id && (
                  <div style={{ background: C.schoolL, borderRadius: 9, padding: 12, marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.schoolD, marginBottom: 8 }}>
                      {p.type === 'need_ride' ? `Offer ${p.profiles?.full_name?.split(' ')[0]} a ride` : `Ask about this ride offer`}
                    </div>
                    <textarea style={{ ...s.input, minHeight: 60, resize: 'vertical' as const, marginBottom: 8 }}
                      placeholder={p.type === 'need_ride' ? `Hi! I can give you a ride to ${p.destination}. I pass by your area and have ${form.seatsOffered} seats available…` : `Hi! I'd love a ride if you have space. I'm coming from ${form.pickupStreet}…`}
                      value={responseMsg} onChange={e => setResponseMsg(e.target.value)} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => { setResponding(null); setResponseMsg(''); }} style={s.btnSm(C.sur2, C.ink2)}>Cancel</button>
                      <button onClick={() => sendResponse(p.id)} style={{ ...s.btnSm(C.school), flex: 1, justifyContent: 'center' }}>
                        {myExistingResponse ? 'Update message' : 'Send message'}
                      </button>
                      {p.profiles?.phone && (
                        <a href={`tel:${p.profiles.phone}`} style={{ ...s.btnSm(C.schoolL, C.schoolD), textDecoration: 'none' }}>📞 Call</a>
                      )}
                    </div>
                  </div>
                )}

                {/* Action buttons — only for non-owners not currently in respond mode */}
                {!isOwn && responding !== p.id && !alreadyResponded && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                    {p.type === 'offering_ride' && (
                      <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8, width: '100%' }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: C.ink3, whiteSpace: 'nowrap' as const }}>Seats needed:</span>
                          <select
                            value={seatsWanted[p.id] || 1}
                            onChange={e => setSeatsWanted(prev => ({ ...prev, [p.id]: parseInt(e.target.value) }))}
                            style={{ ...s.input, width: 80, fontSize: 12, padding: '4px 6px' }}>
                            {Array.from({
                              length: p.seats_remaining !== undefined && p.seats_remaining !== null
                                ? p.seats_remaining
                                : (p.seats_offered || 1)
                            }, (_, i) => i + 1).map(n => (
                              <option key={n} value={n}>{n}</option>
                            ))}
                          </select>
                        </div>
                        <input
                          style={{ ...s.input, fontSize: 12, borderColor: pickupErrors[p.id] ? C.coral : undefined }}
                          placeholder="Street, City, State e.g. 123 Hyde Manor Dr, Tampa, FL"
                          value={pickupInputs[p.id] || ''}
                          onChange={e => {
                            setPickupInputs(prev => ({ ...prev, [p.id]: e.target.value }));
                            setPickupErrors(prev => ({ ...prev, [p.id]: '' }));
                          }}
                        />
                        {pickupErrors[p.id] && <div style={{ color: C.coral, fontSize: 12 }}>{pickupErrors[p.id]}</div>}
                        <button onClick={async () => {
                          if (!supabase || !session) return;
                          const myPickup = pickupInputs[p.id]?.trim();
                          if (!myPickup) { setPickupErrors(prev => ({ ...prev, [p.id]: 'Please enter your pickup address.' })); return; }
                          // Parse street/city/state from the entered address
                          // Light validation — just ensure it has some content that looks like an address
                          const parts = myPickup.split(',').map((s: string) => s.trim());
                          if (parts.length < 2 || !parts[0] || parts[0].length < 5) {
                            setPickupErrors(prev => ({ ...prev, [p.id]: 'Please enter a more complete address e.g. 123 Main St, Tampa, FL' }));
                            return;
                          }
                          const seats = seatsWanted[p.id] || 1;
                          const msg = `Hi! I'd like to accept your ride offer to ${p.destination}. I need ${seats} seat${seats > 1 ? 's' : ''}. My pickup address is: ${myPickup}. Please confirm the details.`;
                          const { data: inserted, error } = await supabase.from('board_responses')
                            .insert({ post_id: p.id, responder_id: session.user.id, message: msg, status: 'pending', seats_requested: seats, pickup_address: myPickup })
                            .select().single();
                          if (error) { alert('Error: ' + error.message); return; }
                          if (inserted) setMyResponses((prev: any[]) => [{ ...inserted }, ...prev.filter((r: any) => r.post_id !== p.id)]);
                          setResponseSent((prev: any[]) => [...prev, p.id]);
                          await notifyUser(p.user_id, 'board_response',
                            '🚗 Someone wants to join your ride!',
                            `${profile?.full_name} wants ${seats} seat${seats > 1 ? 's' : ''} on your ride to ${p.destination}. Pickup: ${myPickup}`,
                            { post_id: p.id });
                        }} disabled={pickupValidating[p.id]} style={{ ...s.btn(C.school), fontSize: 13, width: '100%', justifyContent: 'center', opacity: pickupValidating[p.id] ? 0.7 : 1 }}>
                          {pickupValidating[p.id] ? '⏳ Validating address…' : '✅ Accept this ride'}
                        </button>
                      </div>
                    )}
                    {p.type === 'offering_ride' && (
                      <button onClick={() => { setResponding(p.id); setResponseMsg(''); }} style={{ ...s.btn(C.sur2, C.ink2), fontSize: 13 }}>
                        💬 Message
                      </button>
                    )}
                    {p.type === 'need_ride' && (
                      <button onClick={() => { setResponding(p.id); setResponseMsg(''); }} style={{ ...s.btn(C.school), fontSize: 13 }}>
                        🚗 Offer a ride
                      </button>
                    )}
                  </div>
                )}
                {!isOwn && responding !== p.id && alreadyResponded && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
                    <span style={s.tag(C.schoolL, C.schoolD)}>{p.type === 'offering_ride' ? '✅ Ride accepted' : '✓ Message sent'}</span>
                    {myExistingResponse && p.type === 'need_ride' && (
                      <span style={{ fontSize: 11, color: C.ink3, fontStyle: 'italic' }}>"{myExistingResponse.message?.slice(0, 40)}{myExistingResponse.message?.length > 40 ? '…' : ''}"</span>
                    )}
                    {p.type === 'need_ride' && (
                      <button onClick={() => { setResponding(p.id); setResponseMsg(myExistingResponse?.message || ''); }} style={s.btnSm(C.sur2, C.ink2)}>Edit message</button>
                    )}
                    {p.profiles?.phone && (
                      <a href={`tel:${p.profiles.phone}`} style={{ ...s.btnSm(C.schoolL, C.schoolD), textDecoration: 'none' }}>📞 Call</a>
                    )}
                  </div>
                )}
                {isOwn && (
                  <span style={s.tag(C.collegeL, C.collegeD)}>Your post</span>
                )}
                {pendingResps.length > 0 && (
                  <div style={{ marginTop: 10, borderTop: `1px solid ${C.bdr}`, paddingTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.ink3, marginBottom: 6 }}>
                      {pendingResps.length} offer{pendingResps.length > 1 ? 's' : ''} received:
                    </div>
                    {pendingResps.map((r: any) => (
                      <div key={r.id} style={{ border: `1px solid ${C.bdr}`, borderRadius: 9, padding: 10, marginBottom: 6, background: C.sur2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <Av name={r.profiles?.full_name || 'User'} size={26} />
                          <div style={{ flex: 1, fontWeight: 600, fontSize: 13 }}>{r.profiles?.full_name || 'Neighbor'}</div>
                          {r.profiles?.phone && (
                            <a href={`tel:${r.profiles.phone}`} style={{ ...s.btnSm(C.sur2, C.ink2), textDecoration: 'none' }}>📞 Call</a>
                          )}
                        </div>
                        <div style={{ fontSize: 13, color: C.ink, paddingLeft: 34, marginBottom: 6 }}>"{r.message}"</div>
                        <div style={{ display: 'flex', gap: 6, paddingLeft: 34 }}>
                          <button onClick={async () => {
                            if (!supabase) return;
                            const { error } = await supabase.from('board_responses').update({ status: 'accepted' }).eq('id', r.id);
                            if (error) { alert('Error: ' + error.message); return; }
                            // Check if all seats filled
                            const { data: allAccepted } = await supabase.from('board_responses')
                              .select('seats_requested').eq('post_id', p.id).eq('status', 'accepted');
                            const seatsTaken = (allAccepted || []).reduce((sum: number, x: any) => sum + (x.seats_requested || 1), 0);
                            const seatsOffered = p.seats_offered || 1;
                            const seatsRemaining = seatsOffered - seatsTaken;
                            const newStatus = seatsRemaining <= 0 ? 'matched' : 'open';
                            await supabase.from('board_posts').update({ status: newStatus, seats_remaining: Math.max(0, seatsRemaining) }).eq('id', p.id);
                            setResponses((prev: any[]) => prev.map((resp: any) => resp.id === r.id ? { ...resp, status: 'accepted' } : resp));
                            setPosts((prev: any[]) => prev.map((post: any) => post.id === p.id ? { ...post, status: newStatus, seats_remaining: Math.max(0, seatsRemaining) } : post));
                            await notifyUser(r.responder_id, 'board_accepted',
                              '✅ Your ride request was accepted!',
                              `${profile?.full_name || 'The poster'} confirmed your seat on the ride to ${p.destination}.`,
                              { post_id: p.id });
                            alert(`✅ Accepted! ${r.profiles?.full_name} has a seat.${seatsRemaining > 0 ? ` ${seatsRemaining} seat${seatsRemaining > 1 ? 's' : ''} still available.` : ' Ride is now full.'}`);
                          }} style={s.btnSm(C.school)}>✅ Accept offer</button>
                          <button onClick={async () => {
                            if (!supabase) return;
                            await supabase.from('board_responses').update({ status: 'declined' }).eq('id', r.id);
                            setResponses((prev: any[]) => prev.map((resp: any) => resp.id === r.id ? { ...resp, status: 'declined' } : resp));
                          }} style={{ ...s.btnSm('#fff', '#b91c1c'), border: '1px solid #fca5a5' }}>❌ Decline</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          </>
      )}

      {/* ── POST ── */}
      {tab === 'post' && (
        <>
          <div style={{ background: C.blueL, borderRadius: 12, padding: 14, marginBottom: 14, borderLeft: `3px solid ${C.blue}` }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.blueD, marginBottom: 4 }}>Quick and easy</div>
            <div style={{ fontSize: 12, color: C.blueD }}>No group required. Post a one-off request or offer — school runs, errands, activities, anywhere. Your neighbors will see it immediately.</div>
          </div>

          <Card>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Post type</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
              {([['need_ride', '🙋', 'I need a ride', 'Looking for someone to take my kid(s) or myself'], ['offering_ride', '🚗', 'I\'m offering a ride', 'I have space and can help a neighbor']] as const).map(([val, icon, label, desc]) => (
                <div key={val} onClick={() => upd('type', val)} style={{ flex: 1, border: `1.5px solid ${form.type === val ? C.school : C.bdr}`, background: form.type === val ? C.schoolL : C.sur, borderRadius: 12, padding: 12, cursor: 'pointer', textAlign: 'center' as const }}>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>{icon}</div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: form.type === val ? C.schoolD : C.ink }}>{label}</div>
                  <div style={{ fontSize: 11, color: C.ink3, marginTop: 3 }}>{desc}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Trip details</div>

            {/* OFFERING RIDE: Starting location with search + GPS */}
            {form.type === 'offering_ride' && (
              <div style={{ marginBottom: 10, position: 'relative' as const }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                  <label style={{ ...s.label, margin: 0 }}>Starting location</label>
                  <button onClick={async () => {
                    if (!navigator.geolocation) return;
                    setLocating(true);
                    navigator.geolocation.getCurrentPosition(async (pos) => {
                      const { latitude: lat, longitude: lon } = pos.coords;
                      setUserLatLon({ lat, lon });
                      userLatLonRef.current = { lat, lon };
                      const addr = await reverseGeocode(lat, lon);
                      if (addr) {
                        upd('startStreet', addr.street); upd('startCity', addr.city); upd('startState', addr.state);
                        setStartQuery(addr.street);
                      }
                      setLocating(false);
                    }, () => setLocating(false), { timeout: 8000 });
                  }} style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px', borderRadius: 6, border: `1px solid ${C.bdr}`, background: C.sur2, color: C.school, cursor: 'pointer', fontWeight: 600 }}>
                    {locating ? '📍 Locating…' : '📍 Use my location'}
                  </button>
                </div>
                <div style={{ position: 'relative' as const }}>
                  <input style={s.input} value={startQuery}
                    onChange={e => onStartQueryChange(e.target.value)}
                    placeholder={locating ? 'Detecting your location…' : 'Type a landmark, street or area…'} />
                  {searchingStart && <div style={{ position: 'absolute', right: 10, top: 10, fontSize: 12, color: C.ink3 }}>🔍</div>}
                </div>
                {startSuggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: `1px solid ${C.bdr}`, borderRadius: 9, zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto' as const }}>
                    {startSuggestions.map((sg: any, i: number) => (
                      <div key={i} onClick={() => selectStartSuggestion(sg)}
                        style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: i < startSuggestions.length - 1 ? `1px solid ${C.bdr}` : 'none', fontSize: 13 }}
                        onMouseEnter={e => (e.currentTarget.style.background = C.schoolL)}
                        onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                        <div style={{ fontWeight: 600 }}>{sg.name}</div>
                        <div style={{ fontSize: 11, color: C.ink3 }}>{[sg.street, sg.city, sg.state].filter(Boolean).join(', ')}</div>
                      </div>
                    ))}
                  </div>
                )}
                {(form.startStreet || (startQuery.length > 2 && startSuggestions.length === 0 && !searchingStart)) && (
                  <div style={{ marginTop: 6 }}>
                    <input style={{ ...s.input, marginBottom: 6 }} value={form.startStreet}
                      onChange={e => upd('startStreet', e.target.value)} placeholder="Street address" />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input style={{ ...s.input, flex: 2 }} value={form.startCity} onChange={e => upd('startCity', e.target.value)} placeholder="City" />
                      <input style={{ ...s.input, width: 55, textTransform: 'uppercase' as const }} value={form.startState} onChange={e => upd('startState', e.target.value.toUpperCase())} placeholder="ST" maxLength={2} />
                    </div>
                  </div>
                )}
                {pickupError && <div style={{ color: C.coral, fontSize: 12, marginTop: 4 }}>{pickupError}</div>}
              </div>
            )}

            {/* Destination — search box with suggestions */}
            <div style={{ marginBottom: 10, position: 'relative' as const }}>
              <label style={s.label}>Destination</label>
              <div style={{ position: 'relative' as const }}>
                <input style={s.input} value={destQuery}
                  onChange={e => onDestQueryChange(e.target.value)}
                  placeholder="Type Costco, Walmart, school name…" />
                {searchingDest && <div style={{ position: 'absolute', right: 10, top: 10, fontSize: 12, color: C.ink3 }}>🔍</div>}
              </div>
              {destSuggestions.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: `1px solid ${C.bdr}`, borderRadius: 9, zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto' as const }}>
                  {destSuggestions.map((s: any, i: number) => (
                    <div key={i} onClick={() => selectDestSuggestion(s)}
                      style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: i < destSuggestions.length - 1 ? `1px solid ${C.bdr}` : 'none', fontSize: 13 }}
                      onMouseEnter={e => (e.currentTarget.style.background = C.schoolL)}
                      onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                      <div style={{ fontWeight: 600 }}>{s.name}</div>
                      <div style={{ fontSize: 11, color: C.ink3 }}>{[s.street, s.city, s.state].filter(Boolean).join(', ')}</div>
                    </div>
                  ))}
                </div>
              )}
              {(form.destStreet || (destQuery.length > 2 && destSuggestions.length === 0 && !searchingDest)) && (
                <div style={{ marginTop: 6 }}>
                  <input style={{ ...s.input, marginBottom: 6 }} value={form.destStreet}
                    onChange={e => upd('destStreet', e.target.value)} placeholder="Street address" />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input style={{ ...s.input, flex: 2 }} value={form.destCity} onChange={e => upd('destCity', e.target.value)} placeholder="City" />
                    <input style={{ ...s.input, width: 55, textTransform: 'uppercase' as const }} value={form.destState} onChange={e => upd('destState', e.target.value.toUpperCase())} placeholder="ST" maxLength={2} />
                  </div>
                </div>
              )}
              {addrError && <div style={{ color: C.coral, fontSize: 12, marginTop: 4 }}>{addrError}</div>}
            </div>

            {/* NEED RIDE: Pickup address after destination */}
            {form.type === 'need_ride' && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                  <label style={{ ...s.label, margin: 0 }}>Your pickup address</label>
                  <button onClick={async () => {
                    if (!navigator.geolocation) return;
                    setLocating(true);
                    navigator.geolocation.getCurrentPosition(async (pos) => {
                      const { latitude: lat, longitude: lon } = pos.coords;
                      setUserLatLon({ lat, lon });
                      userLatLonRef.current = { lat, lon };
                      const addr = await reverseGeocode(lat, lon);
                      if (addr) { upd('pickupStreet', addr.street); upd('pickupCity', addr.city); upd('pickupState', addr.state); }
                      setLocating(false);
                    }, () => setLocating(false), { timeout: 8000 });
                  }} style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px', borderRadius: 6, border: `1px solid ${C.bdr}`, background: C.sur2, color: C.school, cursor: 'pointer', fontWeight: 600 }}>
                    {locating ? '📍 Locating…' : '📍 Use my location'}
                  </button>
                </div>
                <input style={{ ...s.input, marginBottom: 6, borderColor: locating ? C.school : undefined }}
                  value={form.pickupStreet} onChange={e => upd('pickupStreet', e.target.value)}
                  placeholder={locating ? 'Detecting your location…' : 'Street address e.g. 123 Maple Grove Dr'} />
                <div style={{ display: 'flex', gap: 6 }}>
                  <input style={{ ...s.input, flex: 2 }} value={form.pickupCity} onChange={e => upd('pickupCity', e.target.value)} placeholder="City" />
                  <input style={{ ...s.input, width: 55, textTransform: 'uppercase' as const }} value={form.pickupState} onChange={e => upd('pickupState', e.target.value.toUpperCase())} placeholder="ST" maxLength={2} />
                </div>
                {pickupError && <div style={{ color: C.coral, fontSize: 12, marginTop: 4 }}>{pickupError}</div>}
              </div>
            )}

            {/* Date / Time / Seats */}
            <div style={s.grid3}>
              <div style={{ marginBottom: 10 }}>
                <label style={s.label}>Date</label>
                <input type="date" style={s.input} min={todayStr} value={form.date} onChange={e => upd('date', e.target.value)} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={s.label}>{form.type === 'offering_ride' ? 'Departure time' : 'Time'}</label>
                <input type="time" style={s.input}
                  min={form.date === todayStr ? new Date().toTimeString().substring(0,5) : undefined}
                  value={form.time} onChange={e => upd('time', e.target.value)} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={s.label}>{form.type === 'offering_ride' ? 'Available seats' : 'Seats needed'}</label>
                <select style={s.input} value={form.type === 'offering_ride' ? form.seatsOffered : form.seatsNeeded} onChange={e => upd(form.type === 'offering_ride' ? 'seatsOffered' : 'seatsNeeded', e.target.value)}>
                  {['1','2','3','4','5'].map(n => <option key={n}>{n}</option>)}
                </select>
              </div>
            </div>

            {/* Return trip toggle — need_ride only */}
            {form.type === 'need_ride' && (
              <div>
                <div onClick={() => upd('returnNeeded', !form.returnNeeded)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, background: form.returnNeeded ? C.schoolL : C.sur2, borderRadius: 9, cursor: 'pointer', marginBottom: form.returnNeeded ? 10 : 0, border: `1px solid ${form.returnNeeded ? C.school : C.bdr}` }}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${form.returnNeeded ? C.school : C.bdr}`, background: form.returnNeeded ? C.school : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                    {form.returnNeeded ? '✓' : ''}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: form.returnNeeded ? C.schoolD : C.ink2 }}>Also need return trip</span>
                </div>
                {form.returnNeeded && (
                  <div style={{ marginBottom: 10 }}>
                    <label style={s.label}>Return time</label>
                    <input type="time" style={{ ...s.input, width: 160 }} value={form.returnTime} onChange={e => upd('returnTime', e.target.value)} />
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            <div style={{ marginTop: 10 }}>
              <label style={s.label}>Notes (optional)</label>
              <textarea style={{ ...s.input, minHeight: 56, resize: 'vertical' as const }} value={form.notes} onChange={e => upd('notes', e.target.value)} placeholder="Any relevant details about the trip…" />
            </div>
          </Card>

          <Alert bg={C.amberL} color={C.amberD} bdr={C.amber}>
            Your phone number from your profile will be visible to neighbors who respond, so they can contact you directly.
          </Alert>

          <button onClick={submitPost} disabled={validatingAddr} style={{ ...s.btn(C.school), width: '100%', justifyContent: 'center', fontSize: 14, padding: '12px 0', opacity: validatingAddr ? 0.7 : 1 }}>
            {validatingAddr ? '⏳ Validating addresses…' : editingPostId ? '✏️ Update post' : '📋 Post to neighborhood board'}
          </button>
          {editingPostId && (
            <button onClick={() => { setEditingPostId(null); setTab('mine'); }} style={{ ...s.outlineBtn(C.ink3), width: '100%', justifyContent: 'center', marginTop: 6 }}>
              Cancel editing
            </button>
          )}
        </>
      )}

      {/* ── MY POSTS ── */}
      {tab === 'mine' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: C.ink3 }}>Your active posts and their responses</div>
            <button onClick={async () => {
              if (!supabase || !session) return;
              // Reload all my posts regardless of status
              const { data: mine } = await supabase.from('board_posts')
                .select('*, profiles(full_name, phone, address, has_car, bg_check_status)')
                .eq('user_id', session.user.id)
                .order('created_at', { ascending: false });
              if (mine?.length) {
                setPosts(prev => {
                  const otherPosts = prev.filter((p: any) => p.user_id !== session.user.id);
                  return [...mine, ...otherPosts];
                });
                const myIds = mine.map((p: any) => p.id);
                const { data: respData } = await supabase.from('board_responses')
                  .select('*, profiles(full_name, phone)')
                  .in('post_id', myIds).order('created_at', { ascending: false });
                if (respData) setResponses(respData);
              }
              // Also reload my sent responses
              const { data: myRespData } = await supabase.from('board_responses')
                .select('*, board_posts(*, profiles(full_name, phone))')
                .eq('responder_id', session.user.id).order('created_at', { ascending: false });
              if (myRespData) setMyResponses(myRespData);
            }} style={{ ...s.btnSm(C.sur2, C.ink2), marginLeft: 'auto' }}>↻ Refresh</button>
          </div>

          {/* MY POSTS (X's view — shows responses received) */}
          {(() => {
            const d = new Date();
            const todayForMine = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
            const myOwnPosts = posts.filter(p => p.user_id === session?.user?.id);
            if (myOwnPosts.length === 0) return null;
            return (
              <>
                <SLabel>Posts you created</SLabel>
                {myOwnPosts.map(p => {
                  const [bgL, clr] = typeColor(p.type);
                  const postResponses = responses.filter((r: any) => r.post_id === p.id);
                  const acceptedResp = postResponses.find((r: any) => r.status === 'accepted');
                  return (
                    <div key={p.id} style={{ border: `1.5px solid ${acceptedResp ? C.school : C.bdr}`, borderRadius: 14, padding: 14, marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={s.tag(bgL, clr)}>{typeLabel(p.type)}</span>
                        <span style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>{p.destination}</span>
                        <span style={s.tag(p.status === 'open' ? C.schoolL : p.status === 'matched' ? C.school : C.sur2, p.status === 'open' ? C.schoolD : p.status === 'matched' ? '#fff' : C.ink3)}>{p.status}</span>
                      </div>
                      <div style={{ fontSize: 12, color: C.ink3, marginBottom: p.status === 'open' ? 8 : 0 }}>{fmtDate(p.trip_date)}{p.trip_time ? ' · ' + fmtTime(p.trip_time) : ''}</div>
                      {p.status === 'open' && (
                        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                          <button onClick={() => {
                            // Pre-fill post form for editing
                            setTab('post');
                            setTimeout(() => {
                              // Signal to post form to load this post for editing
                              (window as any)._editPost = p;
                            }, 50);
                          }} style={s.btnSm(C.sur2, C.ink2)}>✏️ Edit</button>
                          <button onClick={async () => {
                            if (!supabase) return;
                            if (!window.confirm('Cancel this post? Neighbors who responded will no longer see it.')) return;
                            const { error } = await supabase.from('board_posts').update({ status: 'cancelled' }).eq('id', p.id);
                            if (error) { alert('Error: ' + error.message); return; }
                            setPosts(prev => prev.map(post => post.id === p.id ? { ...post, status: 'cancelled' } : post));
                          }} style={{ ...s.btnSm('#fff', C.coralD), border: `1px solid ${C.coral}` }}>🗑 Cancel post</button>
                        </div>
                      )}

                      {/* Responses section */}
                      <div style={{ marginTop: 10 }}>
                        {/* Ride confirmed banner for poster */}
                        {acceptedResp && (() => {
                          const completedIds = JSON.parse(sessionStorage.getItem('completedPostIds') || '[]');
                          const isDone = p.status === 'completed' || completedIds.includes(p.id) ||
                            (p.trip_date ? new Date(p.trip_date + 'T23:59:59') < new Date() && p.status === 'matched' : false);
                          return isDone ? (
                            <div style={{ background: C.sur2, borderRadius: 10, padding: 14, marginBottom: 10 }}>
                              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>✅ Ride completed!</div>
                              <div style={{ fontSize: 13, color: C.ink3 }}>
                                {acceptedResp.profiles?.full_name} gave you a ride to {p.destination}. Thanks for using PoolRide!
                              </div>
                            </div>
                          ) : p.type === 'offering_ride' ? (
                            // I posted an offering_ride — I AM the driver, someone accepted a seat
                            <div style={{ background: C.school, borderRadius: 10, padding: 14, marginBottom: 10, color: '#fff' }}>
                              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>🎉 Passenger confirmed!</div>
                              <div style={{ fontSize: 13, opacity: .9, marginBottom: 10 }}>
                                {acceptedResp.profiles?.full_name} will ride with you to {p.destination} on {fmtDate(p.trip_date)}.
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                                {acceptedResp.profiles?.phone && (
                                  <a href={`tel:${acceptedResp.profiles.phone}`} style={{ ...s.btnSm('rgba(255,255,255,.2)', '#fff'), textDecoration: 'none', border: '1px solid rgba(255,255,255,.3)' }}>
                                    📞 Call passenger
                                  </a>
                                )}
                                <button onClick={async () => {
                                  if (supabase) {
                                    // Notify all accepted riders
                                    const acceptedRiders = postResponses.filter((r: any) => r.status === 'accepted');
                                    for (const rider of acceptedRiders) {
                                      await notifyUser(rider.responder_id, 'ride_starting',
                                        '🚗 Your driver is on the way!',
                                        `${profile?.full_name} has started the ride to ${p.destination}. Open PoolRide to track.`,
                                        { post_id: p.id });
                                    }
                                  }
                                  const acceptedForCtx = postResponses.filter((r: any) => r.status === 'accepted');
                                  boardRideContext = {
                                    destination: p.destination,
                                    destAddress: p.destination_address || '',
                                    // Use pickup_address entered at acceptance, fallback to profile address
                                    pickupAddresses: acceptedForCtx.map((r: any) => r.pickup_address || r.profiles?.address || ''),
                                    acceptedNames: acceptedForCtx.map((r: any) => r.profiles?.full_name || 'Passenger'),
                                  };
                                  boardTrackingRole = 'driver';
                                  sessionStorage.setItem('boardTrackingRole', 'driver');
                                  sessionStorage.setItem('boardRideContext', JSON.stringify(boardRideContext));
                                  nav('tracking');
                                }} disabled={myDriveStatus === 'completed'} style={{ ...s.btnSm('rgba(255,255,255,.2)', '#fff'), border: '1px solid rgba(255,255,255,.3)', opacity: myDriveStatus === 'completed' ? 0.6 : 1 }}>
                                  {myDriveStatus === 'completed' ? '✅ Ride completed' : myDriveStatus === 'started' ? '🚗 Continue ride →' : '🚗 Start ride'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            // I posted a need_ride — someone offered to drive me
                            <div style={{ background: C.school, borderRadius: 10, padding: 14, marginBottom: 10, color: '#fff' }}>
                              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>🎉 Ride confirmed!</div>
                              <div style={{ fontSize: 13, opacity: .9, marginBottom: 10 }}>
                                {acceptedResp.profiles?.full_name} is giving you a ride to {p.destination} on {fmtDate(p.trip_date)}.
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                                {acceptedResp.profiles?.phone && (
                                  <a href={`tel:${acceptedResp.profiles.phone}`} style={{ ...s.btnSm('rgba(255,255,255,.2)', '#fff'), textDecoration: 'none', border: '1px solid rgba(255,255,255,.3)' }}>
                                    📞 Call driver
                                  </a>
                                )}
                                <button onClick={() => { boardTrackingRole = 'rider'; sessionStorage.setItem('boardTrackingRole', 'rider'); sessionStorage.removeItem('boardRideContext'); nav('tracking'); }}
                                  style={{ ...s.btnSm('rgba(255,255,255,.2)', '#fff'), border: '1px solid rgba(255,255,255,.3)', opacity: driverStarted[acceptedResp.responder_id] ? 1 : 0.5 }}>
                                  {driverStarted[acceptedResp.responder_id] ? '📍 Track driver' : '⏳ Waiting for driver...'}
                                </button>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Pending responses */}
                        {postResponses.length === 0 ? (
                          <div style={{ background: C.sur2, borderRadius: 9, padding: 10, fontSize: 12, color: C.ink3 }}>
                            No responses yet — share with your neighborhood to get more visibility!
                          </div>
                        ) : (
                          <>
                            <div style={{ fontSize: 12, fontWeight: 700, color: C.ink2, marginBottom: 6 }}>
                              {postResponses.length} response{postResponses.length > 1 ? 's' : ''} received:
                            </div>
                            {postResponses.map((r: any) => (
                              <div key={r.id} style={{ borderRadius: 9, padding: 10, marginBottom: 6, border: `1px solid ${r.status === 'accepted' ? C.school : r.status === 'declined' ? C.coral : C.bdr}`, background: r.status === 'accepted' ? C.schoolL : r.status === 'declined' ? '#fff5f5' : C.sur2 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                  <Av name={r.profiles?.full_name || 'User'} size={28} />
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, fontSize: 13 }}>{r.profiles?.full_name || 'Neighbor'}</div>
                                    <div style={{ fontSize: 11, color: C.ink3 }}>{new Date(r.created_at).toLocaleDateString()}</div>
                                  </div>
                                  <span style={s.tag(
                                    r.status === 'accepted' ? C.schoolL : r.status === 'declined' ? C.coralL : C.amberL,
                                    r.status === 'accepted' ? C.schoolD : r.status === 'declined' ? C.coralD : C.amberD
                                  )}>
                                    {r.status === 'accepted' ? '✅ Accepted' : r.status === 'declined' ? '❌ Declined' : '⏳ Pending'}
                                  </span>
                                </div>
                                <div style={{ fontSize: 13, color: C.ink, paddingLeft: 36, marginBottom: 6 }}>"{r.message}"</div>
                                <div style={{ display: 'flex', gap: 6, paddingLeft: 36, flexWrap: 'wrap' as const }}>
                                  {r.profiles?.phone && (
                                    <a href={`tel:${r.profiles.phone}`} style={{ ...s.btnSm(C.sur2, C.ink2), textDecoration: 'none' }}>📞 Call</a>
                                  )}
                                  {r.status === 'pending' && (
                                    <>
                                      <button onClick={async () => {
                                        if (!supabase) return;
                                        const { error } = await supabase.from('board_responses').update({ status: 'accepted' }).eq('id', r.id);
                                        if (error) { alert('Error: ' + error.message); return; }
                                        // Check if all seats filled
                                        const { data: allAccepted } = await supabase.from('board_responses')
                                          .select('seats_requested').eq('post_id', p.id).eq('status', 'accepted');
                                        const seatsTaken = (allAccepted || []).reduce((sum: number, x: any) => sum + (x.seats_requested || 1), 0);
                                        const seatsOffered = p.seats_offered || 1;
                                        const seatsRemaining = seatsOffered - seatsTaken;
                                        const newStatus = seatsRemaining <= 0 ? 'matched' : 'open';
                                        await supabase.from('board_posts').update({ status: newStatus, seats_remaining: Math.max(0, seatsRemaining) }).eq('id', p.id);
                                        setResponses((prev: any[]) => prev.map((resp: any) => resp.id === r.id ? { ...resp, status: 'accepted' } : resp));
                                        setPosts((prev: any[]) => prev.map((post: any) => post.id === p.id ? { ...post, status: newStatus, seats_remaining: Math.max(0, seatsRemaining) } : post));
                                        await notifyUser(r.responder_id, 'board_accepted',
                                          '✅ Your ride request was accepted!',
                                          `${profile?.full_name || 'The poster'} confirmed your seat on the ride to ${p.destination}.`,
                                          { post_id: p.id });
                                        alert(`✅ Accepted! ${r.profiles?.full_name} has a seat.${seatsRemaining > 0 ? ` ${seatsRemaining} seat${seatsRemaining > 1 ? 's' : ''} still available — post stays open.` : ' Ride is now full.'}`);
                                      }} style={s.btnSm(C.school)}>✅ Accept offer</button>
                                      <button onClick={async () => {
                                        if (!supabase) return;
                                        await supabase.from('board_responses').update({ status: 'declined' }).eq('id', r.id);
                                        setResponses((prev: any[]) => prev.map((resp: any) => resp.id === r.id ? { ...resp, status: 'declined' } : resp));
                                      }} style={{ ...s.btnSm('#fff', '#b91c1c'), border: '1px solid #fca5a5' }}>❌ Decline</button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}

          {/* MY RESPONSES (Y's view — shows messages Y sent) */}
          {myResponses.length > 0 && (
            <>
              <SLabel>Responses you sent</SLabel>
              {myResponses.map((r: any) => {
                const bp = r.board_posts;
                const isAccepted = r.status === 'accepted';
                const isDeclined = r.status === 'declined';
                const posterName = bp?.profiles?.full_name || 'Neighbor';
                const destination = bp?.destination || 'destination not found';
                const tripDate = bp?.trip_date ? fmtDate(bp.trip_date) : '';
                const posterPhone = bp?.profiles?.phone;
                return (
                  <div key={r.id} style={{ border: `1.5px solid ${isAccepted ? C.school : isDeclined ? C.coral : C.bdr}`, borderRadius: 12, padding: 12, marginBottom: 8, background: isAccepted ? C.schoolL : isDeclined ? '#fff5f5' : C.sur }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>📍 {destination}</div>
                        <div style={{ fontSize: 11, color: C.ink3 }}>
                          Posted by {posterName}{tripDate ? ' · ' + tripDate : ''}
                        </div>
                      </div>
                      <span style={s.tag(
                        isAccepted ? C.schoolL : isDeclined ? C.coralL : C.amberL,
                        isAccepted ? C.schoolD : isDeclined ? C.coralD : C.amberD
                      )}>
                        {isAccepted ? '✅ Accepted!' : isDeclined ? '❌ Declined' : '⏳ Pending'}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: C.ink3, fontStyle: 'italic', marginBottom: isAccepted ? 10 : 0 }}>
                      Your message: "{r.message}"
                    </div>
                    {isAccepted && (() => {
                      const postStatus = bp?.status;
                      // Check sessionStorage for posts marked completed by endDrive
                      const completedIds = JSON.parse(sessionStorage.getItem('completedPostIds') || '[]');
                      const isSessionCompleted = bp?.id && completedIds.includes(bp.id);
                      const tripDatePast = bp?.trip_date ? new Date(bp.trip_date + 'T23:59:59') < new Date() : false;
                      const isDone = postStatus === 'completed' || postStatus === 'cancelled' || isSessionCompleted || (postStatus === 'matched' && tripDatePast);
                      return (
                        <div style={{ background: isDone ? C.sur2 : C.school, borderRadius: 9, padding: 12, color: isDone ? C.ink : '#fff' }}>
                          {isDone ? (
                            <>
                              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>✅ Ride completed!</div>
                              <div style={{ fontSize: 12, color: C.ink3 }}>You gave {posterName.split(' ')[0]} a ride to {destination}. Thanks for helping a neighbor!</div>
                            </>
                          ) : bp?.type === 'need_ride' ? (
                            // I responded to a "need ride" post — I AM the driver
                            <>
                              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>🎉 Your offer was accepted!</div>
                              <div style={{ fontSize: 12, opacity: .9, marginBottom: 10 }}>
                                You're giving {posterName.split(' ')[0]} a ride to <strong>{destination}</strong>{tripDate ? ' on ' + tripDate : ''}.
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                                {posterPhone && (
                                  <a href={`tel:${posterPhone}`} style={{ ...s.btnSm('rgba(255,255,255,.2)', '#fff'), textDecoration: 'none', border: '1px solid rgba(255,255,255,.3)' }}>
                                    📞 Call {posterName.split(' ')[0]}
                                  </a>
                                )}
                                <button onClick={async () => {
                                  if (supabase && bp?.id) {
                                    // Notify the poster (rider) that driver is on the way
                                    if (bp.user_id) {
                                      await notifyUser(bp.user_id, 'ride_starting',
                                        '🚗 Your driver is on the way!',
                                        `${profile?.full_name} has started the ride to ${destination}. Open PoolRide to track.`,
                                        { post_id: bp.id });
                                    }
                                  }
                                  // For need_ride: pickup is what poster entered as their pickup address
                                  boardRideContext = {
                                    destination,
                                    destAddress: bp?.destination_address || '',
                                    pickupAddresses: [bp?.address_from || ''],
                                    acceptedNames: [posterName],
                                  };
                                  boardTrackingRole = 'driver';
                                  sessionStorage.setItem('boardTrackingRole', 'driver');
                                  sessionStorage.setItem('boardRideContext', JSON.stringify(boardRideContext));
                                  nav('tracking');
                                }} style={{ ...s.btnSm('rgba(255,255,255,.2)', '#fff'), border: '1px solid rgba(255,255,255,.3)' }}>
                                  🚗 {driverStarted[session?.user?.id || ''] ? 'Continue ride →' : 'Start ride'}
                                </button>
                              </div>
                            </>
                          ) : (
                            // I accepted an "offering ride" post — I AM a rider, the post owner is the driver
                            <>
                              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>🎉 Your seat is confirmed!</div>
                              <div style={{ fontSize: 12, opacity: .9, marginBottom: 10 }}>
                                {posterName.split(' ')[0]} is driving you to <strong>{destination}</strong>{tripDate ? ' on ' + tripDate : ''}.
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                                {posterPhone && (
                                  <a href={`tel:${posterPhone}`} style={{ ...s.btnSm('rgba(255,255,255,.2)', '#fff'), textDecoration: 'none', border: '1px solid rgba(255,255,255,.3)' }}>
                                    📞 Call driver
                                  </a>
                                )}
                                <button
                                  onClick={() => { boardTrackingRole = 'rider'; sessionStorage.setItem('boardTrackingRole', 'rider'); sessionStorage.removeItem('boardRideContext'); nav('tracking'); }}
                                  disabled={!driverStarted[bp?.user_id]}
                                  style={{ ...s.btnSm('rgba(255,255,255,.2)', '#fff'), border: '1px solid rgba(255,255,255,.3)', opacity: driverStarted[bp?.user_id] ? 1 : 0.5 }}>
                                  {driverStarted[bp?.user_id] ? '📍 Track driver' : '⏳ Waiting for driver...'}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}
                    {isDeclined && (
                      <div style={{ fontSize: 12, color: C.coralD, marginTop: 4 }}>
                        Your offer was declined. You can browse other posts to help someone else.
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {posts.filter(p => p.user_id === session?.user?.id).length === 0 && myResponses.length === 0 && (
            <div style={{ textAlign: 'center', padding: '28px 16px' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>Nothing here yet</div>
              <div style={{ fontSize: 13, color: C.ink3, marginBottom: 14 }}>Post a ride request or respond to someone else's post.</div>
              <button onClick={() => setTab('post')} style={s.btn(C.school)}>Create a post →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── CHILD CARD COMPONENT (reusable cascading dropdowns) ─────────────────────
function ChildCard({ child, index, onUpdate, onRemove }: {
  child: any; index: number;
  onUpdate: (k: string, v: string) => void;
  onRemove?: () => void;
}) {
  const [counties, setCounties] = React.useState<any[]>([]);
  const [cities, setCities] = React.useState<any[]>([]);
  const [schools, setSchools] = React.useState<any[]>([]);
  const states = [
    {code:'FL',name:'Florida'},{code:'TX',name:'Texas'},{code:'OH',name:'Ohio'},
    {code:'GA',name:'Georgia'},{code:'NJ',name:'New Jersey'},
    {code:'NC',name:'North Carolina'},{code:'CA',name:'California'},
  ];

  async function onStateChange(stateCode: string) {
    onUpdate('state', stateCode);
    onUpdate('county', ''); onUpdate('city', ''); onUpdate('school', '');
    setCounties([]); setCities([]); setSchools([]);
    if (!supabase || !stateCode) return;
    const { data } = await supabase.from('counties').select('*').eq('state_code', stateCode).order('name');
    setCounties(data || []);
  }

  async function onCountyChange(countyId: string) {
    onUpdate('county', countyId);
    onUpdate('city', ''); onUpdate('school', '');
    setCities([]); setSchools([]);
    if (!supabase || !countyId) return;
    const { data } = await supabase.from('cities').select('*').eq('county_id', countyId).order('name');
    setCities(data || []);
  }

  async function onCityChange(cityName: string) {
    onUpdate('city', cityName);
    onUpdate('school', '');
    setSchools([]);
    if (!supabase || !child.state) return;
    let q = supabase.from('schools').select('*').eq('active', true).eq('state_code', child.state);
    // Always load all schools in the county — covers cities like New Tampa, Temple Terrace
    // that share schools with their main city
    const countyId = child.county;
    if (countyId) {
      const { data: countyCities } = await supabase.from('cities')
        .select('name').eq('county_id', countyId);
      const cityNames = (countyCities || []).map((c: any) => c.name);
      if (cityNames.length > 0) {
        q = q.in('city', cityNames);
      }
    } else if (cityName) {
      q = q.eq('city', cityName);
    }
    const { data } = await q.order('name');
    setSchools(data || []);
  }

  // Load dropdowns if child already has state set (for edit mode)
  React.useEffect(() => {
    if (!supabase) return;
    if (child.state) {
      onStateChange(child.state).then(() => {
        if (child.county) onCountyChange(child.county).then(() => {
          if (child.city) onCityChange(child.city);
        });
      });
      return;
    }
    // Auto-detect state/county/city from school name
    if (child.school && !child.state) {
      supabase.from('schools')
        .select('name, city, state_code, county_id')
        .ilike('name', child.school)
        .limit(1)
        .maybeSingle()
        .then(async ({ data: school }) => {
          if (!school) return;
          const stateCode = school.state_code;
          const countyId = school.county_id;
          const cityName = school.city;
          if (!stateCode) return;
          // Load counties for state
          const { data: countiesData } = await supabase!.from('counties')
            .select('*').eq('state_code', stateCode).order('name');
          setCounties(countiesData || []);
          onUpdate('state', stateCode);
          if (!countyId) return;
          // Load cities for county
          const { data: citiesData } = await supabase!.from('cities')
            .select('*').eq('county_id', countyId).order('name');
          setCities(citiesData || []);
          onUpdate('county', String(countyId));
          if (!cityName) return;
          onUpdate('city', cityName);
          // Load ALL schools in this county (not just the city)
          const { data: countyCities } = await supabase!.from('cities')
            .select('name').eq('county_id', countyId).order('name');
          const cityNames = (countyCities || []).map((c: any) => c.name);
          const { data: schoolsData } = await supabase!.from('schools')
            .select('*').eq('active', true).eq('state_code', stateCode)
            .in('city', cityNames.length > 0 ? cityNames : [cityName]).order('name');
          setSchools(schoolsData || []);
        });
    }
  }, []);

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>Child {index + 1}</div>
        {onRemove && (
          <button onClick={onRemove} style={{ ...s.btnSm(C.coralL, C.coralD), marginLeft: 'auto', fontSize: 11 }}>🗑️ Remove child</button>
        )}
      </div>
      <div style={s.grid2}>
        <div style={{ marginBottom: 10 }}>
          <label style={s.label}>Child's name <span style={{ color: C.coral }}>*</span></label>
          <input id={`child-name-${index}`} style={{ ...s.input, borderColor: !child.name ? C.coral : undefined }}
            value={child.name} onChange={e => onUpdate('name', e.target.value)} placeholder="Emma" />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={s.label}>Grade <span style={{ color: C.coral }}>*</span></label>
          <select id={`child-grade-${index}`} style={{ ...s.input, borderColor: !child.grade ? C.coral : undefined }}
            value={child.grade} onChange={e => onUpdate('grade', e.target.value)}>
            {['Pre-K','Kindergarten','Grade 1','Grade 2','Grade 3','Grade 4','Grade 5','Grade 6','Grade 7','Grade 8','Grade 9','Grade 10','Grade 11','Grade 12'].map(g => <option key={g}>{g}</option>)}
          </select>
        </div>
      </div>
      <div style={s.grid2}>
        <div style={{ marginBottom: 10 }}>
          <label style={s.label}>State <span style={{ color: C.coral }}>*</span></label>
          <select style={{ ...s.input, borderColor: !child.state ? C.coral : undefined }}
            value={child.state || ''} onChange={e => onStateChange(e.target.value)}>
            <option value="">Select state</option>
            {states.map(st => <option key={st.code} value={st.code}>{st.name}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={s.label}>County</label>
          <select style={s.input} value={child.county || ''} onChange={e => onCountyChange(e.target.value)} disabled={counties.length === 0}>
            <option value="">Select county</option>
            {counties.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>
      <div style={s.grid2}>
        <div style={{ marginBottom: 10 }}>
          <label style={s.label}>City</label>
          <select style={s.input} value={child.city || ''} onChange={e => onCityChange(e.target.value)} disabled={cities.length === 0}>
            <option value="">Select city</option>
            {cities.map((c: any) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={s.label}>School</label>
          <select style={s.input} value={child.school || ''} onChange={e => onUpdate('school', e.target.value)} disabled={schools.length === 0}>
            <option value="">Select school</option>
            {schools.map((sc: any) => <option key={sc.id} value={sc.name}>{sc.name}</option>)}
          </select>
        </div>
      </div>
      {/* School name text input - always visible */}
      <div style={{ marginBottom: 10 }}>
        <label style={s.label}>School name <span style={{ color: C.coral }}>*</span></label>
        <input id={`child-school-${index}`} style={{ ...s.input, borderColor: !child.school ? C.coral : undefined }}
          value={child.school || ''} onChange={e => onUpdate('school', e.target.value)}
          placeholder="e.g. Lawton Chiles Elementary" />
        {!child.school && <div style={{ fontSize: 11, color: C.coralD, marginTop: 3 }}>School is required</div>}
        <MissingSchoolLink state={child.state} city={child.city} />
      </div>
    </Card>
  );
}

// ─── EDIT PROFILE SCREEN ──────────────────────────────────────────────────────
function EditProfileScreen({ nav }: { nav: (p: string) => void }) {
  const { profile, session, setProfile } = useApp();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Parse existing address into parts (format: "Street, City, ST, ZIP")
  const parseAddr = (addr: string) => {
    const parts = (addr || '').split(',').map(s => s.trim());
    return {
      street: parts[0] || '',
      city: parts[1] || '',
      state: parts[2] || '',
      zip: parts[3] || '',
    };
  };
  const existingAddr = parseAddr(profile?.address || '');

  // Pre-fill from existing profile
  const [f, setF] = useState({
    name: profile?.full_name || '',
    phone: profile?.phone || '',
    address: profile?.address || '',
    addrStreet: existingAddr.street,
    addrCity: existingAddr.city,
    addrState: existingAddr.state,
    addrZip: existingAddr.zip,
    make: profile?.car_make_model || '',
    color: profile?.car_color || '',
    plate: profile?.license_plate || '',
    seats: String(profile?.total_seats || 5),
    hasCar: profile?.has_car ? 'yes' : 'no',
    days: profile?.preferred_days || ['Mon','Tue','Wed','Thu','Fri'],
    dropoffDays: (profile as any)?.dropoff_days || profile?.preferred_days || ['Mon','Tue','Wed','Thu','Fri'],
    pickupDays: (profile as any)?.pickup_days || profile?.preferred_days || ['Mon','Tue','Wed','Thu','Fri'],
    notes: profile?.notes || '',
    uni: profile?.university || '',
    hometown: profile?.hometown || '',
    smsNotif: (profile as any)?.sms_notifications || false,
    emailNotif: (profile as any)?.email_notifications !== false,
    pushNotif: (profile as any)?.notif_push !== false,
    notifDriveReminder: (profile as any)?.notif_drive_reminder !== false,
    notifCarNearby: (profile as any)?.notif_car_nearby !== false,
  });
  const upd = (k: string, v: any) => setF(x => ({ ...x, [k]: v }));
  const toggleDay = (d: string) => upd('days', f.days.includes(d) ? f.days.filter((x: string) => x !== d) : [...f.days, d]);

  // Children — always start with profile data (guaranteed source of truth)
  const makeChild = (name: string, grade: string, school: string) => 
    ({ name, grade: grade || 'Grade 3', school: school || '', state: '', county: '', city: '' });

  const [children, setChildren] = useState<any[]>([
    makeChild(profile?.child_name || '', profile?.child_grade || '', profile?.school_name || '')
  ]);
  const addChild = () => setChildren(c => [...c, makeChild('', 'Grade 3', '')]);
  const removeChild = (i: number) => setChildren(c => c.filter((_: any, idx: number) => idx !== i));
  const updChild = (i: number, k: string, v: string) => setChildren(c => c.map((ch: any, idx: number) => idx === i ? { ...ch, [k]: v } : ch));

  // Load additional children from children table
  React.useEffect(() => {
    if (!supabase || !session) return;
    supabase.from('children').select('*').eq('parent_id', session.user.id)
      .then(({ data }) => {
        if (!data || data.length === 0) return;
        const extras = data
          .filter((ch: any) => ch.name !== profile?.child_name)
          .map((ch: any) => makeChild(ch.name, ch.grade, ch.school_name));
        setChildren(prev => [prev[0], ...extras]);
      });
  }, [session]);

  async function save() {
    if (!session || !supabase) return;
    setError('');

    // Validation
    if (!f.name.trim()) { scrollToField('edit-name', 'Full name is required.', setError); return; }
    if (!f.phone.trim()) { scrollToField('edit-phone', 'Phone number is required.', setError); return; }
    if (!f.addrStreet.trim()) { scrollToField('edit-addrStreet', 'Street address is required.', setError); return; }
    if (f.addrStreet.trim().length < 5) { scrollToField('edit-addrStreet', 'Please enter a complete street address (e.g. 123 Maple Grove Dr).', setError); return; }
    if (!/\d/.test(f.addrStreet)) { scrollToField('edit-addrStreet', 'Street address must include a house number (e.g. 123 Maple Grove Dr).', setError); return; }
    if (!f.addrCity.trim()) { scrollToField('edit-addrCity', 'City is required.', setError); return; }
    if (!f.addrState.trim()) { scrollToField('edit-addrState', 'State is required.', setError); return; }
    if (!f.addrZip.trim()) { scrollToField('edit-addrZip', 'ZIP code is required.', setError); return; }
    for (let i = 0; i < children.length; i++) {
      const ch = children[i];
      if (!ch.name?.trim()) { scrollToField(`child-name-${i}`, `Child ${i + 1}: name is required.`, setError); return; }
      if (!ch.school?.trim()) { scrollToField(`child-school-${i}`, `Child ${i + 1} (${ch.name}): school is required.`, setError); return; }
    }

    // Vehicle validation if driver
    if (f.hasCar === 'yes') {
      if (!f.make.trim()) { scrollToField('edit-make', 'Car make & model is required for drivers.', setError); return; }
      if (!f.color.trim()) { scrollToField('edit-color', 'Car color is required for drivers.', setError); return; }
      if (!f.plate.trim()) { scrollToField('edit-plate', 'License plate is required for drivers.', setError); return; }
      const plateClean = f.plate.replace(/[-\s]/g, '');
      if (!/^[A-Z0-9]{2,8}$/.test(plateClean)) {
        scrollToField('edit-plate', 'Invalid license plate format. Use letters and numbers only (e.g. ABC-1234).', setError); return;
      }
    }

    // Validate address via OpenStreetMap
    setSaving(true);
    setError('Validating address…');
    const addrCheck = await validateAddress(f.addrStreet, f.addrCity, f.addrState);
    if (!addrCheck.valid) {
      if ((addrCheck as any).warn) {
        const proceed = window.confirm(`⚠️ ${addrCheck.message}\n\nClick OK to save anyway, or Cancel to re-enter your address.`);
        if (!proceed) { setError('Please verify your street address.'); setSaving(false); return; }
      } else {
        scrollToField('edit-addrStreet', addrCheck.message || 'Address not found. Please check your street address.', setError);
        setSaving(false);
        return;
      }
    }
    setError('');
    const primary = children[0];
    const { data, error: err } = await supabase.from('profiles').upsert({
      id: session.user.id,
      full_name: f.name,
      phone: f.phone,
      address: [f.addrStreet, f.addrCity, f.addrState, f.addrZip].filter(Boolean).join(', '),
      avatar_initials: f.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2),
      school_name: primary?.school || profile?.school_name,
      child_name: primary?.name || profile?.child_name,
      child_grade: primary?.grade || profile?.child_grade,
      university: f.uni,
      hometown: f.hometown,
      has_car: f.hasCar !== 'no',
      car_make_model: f.make,
      car_color: f.color,
      license_plate: f.plate,
      total_seats: parseInt(f.seats),
      preferred_days: Array.from(new Set([...f.dropoffDays, ...f.pickupDays])),
      dropoff_days: f.dropoffDays,
      pickup_days: f.pickupDays,
      notes: f.notes,
      email_notifications: f.emailNotif,
      notif_push: f.pushNotif,
      notif_drive_reminder: f.notifDriveReminder,
      notif_car_nearby: f.notifCarNearby,
    }).select().single();

    if (err) { setError(err.message); setSaving(false); return; }
    setProfile(data);

    // Save additional children
    await supabase.from('children').delete().eq('parent_id', session.user.id);
    for (let i = 1; i < children.length; i++) {
      const ch = children[i];
      if (!ch.name) continue;
      try {
        await supabase.from('children').insert({ parent_id: session.user.id, name: ch.name, grade: ch.grade, school_name: ch.school });
      } catch (_) {}
    }

    setSaving(false);
    setSaved(true);
    setTimeout(() => { setSaved(false); nav('profile'); }, 1500);
  }

  return (
    <div>
      <button onClick={() => nav('profile')} style={{ ...s.btnSm(C.sur2, C.ink2), marginBottom: 16 }}>← Back</button>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>Edit profile</div>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: 16 }}>Changes save to all your carpools</div>

      {saved && <Alert bg={C.schoolL} color={C.schoolD} bdr={C.school}>✅ Profile saved!</Alert>}
      {error && <Alert bg={C.coralL} color={C.coralD} bdr={C.coral}>{error}</Alert>}

      {/* Basic info */}
      <Card>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Basic info</div>
        <div style={{ marginBottom: 10 }}>
          <label style={s.label}>Email (cannot change)</label>
          <div style={{ ...s.input, background: C.sur2, color: C.ink3 }}>{session?.user?.email}</div>
        </div>
        <div style={s.grid2}>
          <div style={{ marginBottom: 10 }}>
            <label style={s.label}>Full name <span style={{ color: C.coral }}>*</span></label>
            <input id="edit-name" style={{ ...s.input, borderColor: !f.name ? C.coral : undefined }}
              value={f.name} onChange={e => upd('name', e.target.value)} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={s.label}>Phone <span style={{ color: C.coral }}>*</span></label>
            <input id="edit-phone" style={{ ...s.input, borderColor: !f.phone ? C.coral : undefined }}
              value={f.phone} onChange={e => upd('phone', e.target.value)} type="tel" />
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={s.label}>Notification preferences</label>
          {[
            { key: 'emailNotif', label: '📧 Email notifications', desc: 'Group invites, swap requests, schedule updates' },
            { key: 'pushNotif', label: '🔔 Push notifications', desc: 'In-app alerts when app is open or in background' },
            { key: 'notifDriveReminder', label: '🚗 Drive day reminder', desc: 'Reminder the night before your scheduled drive' },
            { key: 'notifCarNearby', label: '📍 Car nearby alert', desc: 'Alert when your driver is less than 0.5 miles away' },
          ].map(({ key, label, desc }) => (
            <div key={key} onClick={() => upd(key, !(f as any)[key])}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: C.sur2, borderRadius: 9, marginBottom: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={(f as any)[key] !== false} onChange={() => {}} style={{ width: 18, height: 18, accentColor: C.school, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 11, color: C.ink3 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={s.label}>Home address <span style={{ color: C.coral }}>*</span></label>
          <input id="edit-addrStreet" style={{ ...s.input, marginBottom: 6, borderColor: !f.addrStreet ? C.coral : undefined }}
            value={f.addrStreet} onChange={e => upd('addrStreet', e.target.value)} placeholder="Street address e.g. 123 Maple Grove Dr" />
          <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
            <input id="edit-addrZip" style={{ ...s.input, width: 90, borderColor: !f.addrZip ? C.coral : undefined }}
              value={f.addrZip} onChange={e => {
                const zip = e.target.value.replace(/\D/g,'').slice(0,5);
                upd('addrZip', zip);
                if (zip.length === 5) {
                  upd('addrCity', '⏳'); upd('addrState', '');
                  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
                  fetch(`${supabaseUrl}/functions/v1/zip-lookup`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
                    body: JSON.stringify({ zip }),
                  }).then(r => r.ok ? r.json() : null).then(data => {
                    if (data?.city) { upd('addrCity', data.city); upd('addrState', data.state); }
                    else { upd('addrCity', ''); }
                  }).catch(() => { upd('addrCity', ''); });
                }
              }} placeholder="ZIP" maxLength={5} />
            <input id="edit-addrCity" style={{ ...s.input, flex: 2, borderColor: !f.addrCity ? C.coral : undefined }}
              value={f.addrCity} onChange={e => upd('addrCity', e.target.value)} placeholder="City" />
            <input id="edit-addrState" style={{ ...s.input, width: 55, borderColor: !f.addrState ? C.coral : undefined, textTransform: 'uppercase' as const }}
              value={f.addrState} onChange={e => upd('addrState', e.target.value.toUpperCase())} placeholder="ST" maxLength={2} />
          </div>
          <div style={{ fontSize: 11, color: C.ink3 }}>Enter ZIP and tab out — city and state auto-fill</div>
        </div>
      </Card>

      {/* Children */}
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Children</div>
      {children.map((child: any, i: number) => (
        <ChildCard
          key={i}
          child={child}
          index={i}
          onUpdate={(k, v) => updChild(i, k, v)}
          onRemove={children.length > 1 ? () => removeChild(i) : undefined}
        />
      ))}
      <button onClick={addChild} style={{ ...s.outlineBtn(C.school), width: '100%', justifyContent: 'center', marginBottom: 12 }}>
        + Add another child
      </button>

      {/* Vehicle */}
      <Card>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Vehicle</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {[['yes','🚗 I have a car'],['no','👤 No car']].map(([v,l]) => (
            <div key={v} onClick={() => upd('hasCar', v)} style={{ flex: 1, padding: 10, borderRadius: 9, border: `1.5px solid ${f.hasCar === v ? C.school : C.bdr}`, background: f.hasCar === v ? C.schoolL : C.sur, cursor: 'pointer', textAlign: 'center' as const, fontSize: 13, fontWeight: 600 }}>{l}</div>
          ))}
        </div>
        {f.hasCar === 'yes' && (
          <>
            <div style={s.grid2}>
              <div style={{ marginBottom: 10 }}>
                <label style={s.label}>Make & model <span style={{ color: C.coral }}>*</span></label>
                <input id="edit-make" style={{ ...s.input, borderColor: !f.make ? C.coral : undefined }}
                  value={f.make} onChange={e => upd('make', e.target.value)} placeholder="Toyota Sienna" />
              </div>
              <div style={{ marginBottom: 10 }}><label style={s.label}>Seats</label><select style={s.input} value={f.seats} onChange={e => upd('seats', e.target.value)}>{['2','3','4','5','6','7','8'].map(n => <option key={n}>{n}</option>)}</select></div>
            </div>
            <div style={s.grid2}>
              <div style={{ marginBottom: 10 }}><label style={s.label}>Color <span style={{ color: C.coral }}>*</span></label><input id="edit-color" style={{ ...s.input, borderColor: !f.color ? C.coral : undefined }} value={f.color} onChange={e => upd('color', e.target.value)} /></div>
              <div style={{ marginBottom: 10 }}>
                <label style={s.label}>License plate <span style={{ color: C.coral }}>*</span></label>
                <input id="edit-plate" style={{ ...s.input, borderColor: !f.plate ? C.coral : undefined, textTransform: 'uppercase' as const }}
                  value={f.plate} onChange={e => upd('plate', e.target.value.toUpperCase())} placeholder="ABC-1234" />
                <div style={{ fontSize: 11, color: C.ink3, marginTop: 3 }}>Format: ABC-1234 or ABC1234</div>
              </div>
            </div>
            <div>
              <label style={s.label}>Driving availability</label>
              <div style={{ fontSize: 11, color: C.ink3, marginBottom: 8 }}>Set separately for drop-off and pick-up</div>
              {(['dropoffDays', 'pickupDays'] as const).map(key => (
                <div key={key} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 5, color: key === 'dropoffDays' ? C.schoolD : C.amberD }}>
                    {key === 'dropoffDays' ? '🌅 Drop-off (morning)' : '🌆 Pick-up (afternoon)'}
                  </div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' as const }}>
                    {['Mon','Tue','Wed','Thu','Fri'].map(d => {
                      const arr: string[] = f[key];
                      const sel = arr.includes(d);
                      return (
                        <span key={d} onClick={() => upd(key, sel ? arr.filter((x: string) => x !== d) : [...arr, d])}
                          style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', userSelect: 'none' as const,
                            border: `1.5px solid ${sel ? (key === 'dropoffDays' ? C.school : C.amber) : C.bdr}`,
                            background: sel ? (key === 'dropoffDays' ? C.school : C.amber) : C.sur,
                            color: sel ? '#fff' : C.ink2 }}>{d}</span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {/* Background check — show if not yet approved */}
        {profile?.bg_check_status !== 'approved' && (
          <div style={{ background: C.amberL, borderRadius: 10, padding: 14, marginTop: 8, border: `1px solid ${C.amber}` }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ fontSize: 22 }}>🛡️</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.amberD, marginBottom: 4 }}>
                  Background verification
                  {profile?.bg_check_status === 'pending' && <span style={{ marginLeft: 8, fontSize: 11, background: C.amber, color: '#fff', padding: '2px 8px', borderRadius: 20 }}>Pending</span>}
                </div>
                {profile?.bg_check_status === 'pending' ? (
                  <div style={{ fontSize: 12, color: C.amberD }}>Your verification is in progress. We'll notify you when complete.</div>
                ) : (
                  <>
                    <div style={{ fontSize: 12, color: C.amberD, lineHeight: 1.5, marginBottom: 10 }}>
                      Verified parents get a <strong>✓ badge</strong> and are <strong>2x more likely</strong> to find carpool matches.
                    </div>
                    <button onClick={async () => {
                      if (!supabase || !session) return;
                      await supabase.from('profiles').update({ bg_check_status: 'pending' }).eq('id', session.user.id);
                      setProfile({ ...profile, bg_check_status: 'pending' } as any);
                      alert('✅ Background check requested! We\'ll be in touch shortly.');
                    }} style={{ ...s.btn(C.amber), fontSize: 12, padding: '8px 16px' }}>
                      ✅ Request background check
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      <button onClick={save} disabled={saving} style={{ ...s.btn(C.school), width: '100%', justifyContent: 'center', fontSize: 15, padding: '12px 0', opacity: saving ? .7 : 1 }}>
        {saving ? 'Saving…' : '✅ Save changes'}
      </button>
    </div>
  );
}

// ─── INVITATIONS SCREEN ───────────────────────────────────────────────────────
function InvitationsScreen({ nav }: { nav: (p: string) => void }) {
  const { session, profile } = useApp();
  const [invitations, setInvitations] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!supabase || !session) return;
    supabase.from('group_invitations')
      .select('*, carpool_groups(id, school_name), profiles!group_invitations_inviter_id_fkey(id, full_name, phone)')
      .eq('invitee_id', session.user.id)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) console.error('Load invitations error:', error.message);
        console.log('Invitations loaded:', data);
        setInvitations(data || []);
        setLoading(false);
      });
  }, [session]);

  async function respond(inv: any, accept: boolean) {
    if (!supabase || !session) return;

    console.log('Responding to invitation:', inv.id, 'accept:', accept, 'group:', inv.group_id);

    // Update invitation status
    const { error: invErr } = await supabase.from('group_invitations')
      .update({ status: accept ? 'accepted' : 'declined', responded_at: new Date().toISOString() })
      .eq('id', inv.id);
    if (invErr) console.error('Invitation update error:', invErr.message);

    if (accept) {
      // Check if already a member (from previous accept attempts)
      const { data: existing } = await supabase.from('group_members')
        .select('group_id').eq('group_id', inv.group_id).eq('user_id', session.user.id).maybeSingle();

      let targetGroupId = inv.group_id; // declared here so accessible after if/else

      // Get group school upfront (used in both branches)
      const groupSchool = (inv.carpool_groups?.school_name || '').toLowerCase().trim();
      console.log('🏫 Accept invite: groupSchool=', groupSchool, 'inviter_id=', inv.inviter_id);

      if (existing) {
        // Tejaswini is already in this group — this is a join request from Rama
        // Add the inviter (Rama) to Tejaswini's group
        console.log('Already a member — adding inviter to this group. inviter_id:', inv.inviter_id);
        if (inv.inviter_id) {
          // Check inviter's children for this school
          const groupSchool = (inv.carpool_groups?.school_name || '').toLowerCase().trim();
          const { data: inviterProfile } = await supabase.from('profiles')
            .select('child_name, school_name, has_car, preferred_days').eq('id', inv.inviter_id).maybeSingle();
          const { data: inviterKids } = await supabase.from('children')
            .select('name, school_name').eq('parent_id', inv.inviter_id);
          const inviterChildren: string[] = [];
          if (inviterProfile?.child_name && (inviterProfile?.school_name || '').toLowerCase().trim() === groupSchool) {
            inviterChildren.push(inviterProfile.child_name);
          }
          for (const kid of (inviterKids || [])) {
            if ((kid.school_name || '').toLowerCase().trim() === groupSchool && !inviterChildren.includes(kid.name)) {
              inviterChildren.push(kid.name);
            }
          }
          const { error: addErr } = await supabase.rpc('add_member_to_group', {
            p_group_id: inv.group_id,
            p_user_id: inv.inviter_id,
            p_role: inviterProfile?.has_car ? 'driver' : 'passenger',
            p_preferred_days: inviterProfile?.preferred_days || [],
            p_children_included: inviterChildren,
          });
          if (addErr) console.error('Error adding inviter:', addErr.message);
          else console.log('✅ Added inviter to group');
        }
      } else {
        console.log('🏫 Accept invite: groupSchool=', groupSchool);

        // Check if acceptor already has a group for this school
        const { data: myExistingGms } = await supabase.from('group_members')
          .select('group_id, carpool_groups(id, school_name)')
          .eq('user_id', session.user.id);

        const myGroupForSchool = (myExistingGms || []).find((gm: any) => {
          const gs = (gm.carpool_groups?.school_name || '').toLowerCase().trim();
          return gs && groupSchool && gs === groupSchool;
        });

        // Use outer targetGroupId (declared above if/else)
        targetGroupId = inv.group_id;

        if (myGroupForSchool && myGroupForSchool.group_id !== inv.group_id) {
          // Acceptor already has a group — only option is add inviter to existing or decline
          const addToMine = window.confirm(
            `You already have a carpool group for ${inv.carpool_groups?.school_name || groupSchool}.\n\nEach child can only be in one carpool group.\n\nWould you like to add ${inv.profiles?.full_name || 'this family'} to your existing group?\n\nOK = Add them to your group\nCancel = Decline invitation`
          );
          if (addToMine) {
            targetGroupId = myGroupForSchool.group_id;
            const { error: addErr } = await supabase.rpc('add_member_to_group', {
              p_group_id: targetGroupId,
              p_user_id: inv.inviter_id,
              p_role: 'driver',
              p_preferred_days: [],
              p_children_included: [],
            });
            if (addErr) console.error('Error adding inviter to my group:', addErr.message);
            await supabase.from('group_invitations')
              .update({ group_id: targetGroupId }).eq('id', inv.id);
          } else {
            // Decline — notify inviter
            const inviterId = inv.inviter_id || inv.profiles?.id;
            if (inviterId) {
              await notifyUser(inviterId, 'invite_declined', '❌ Invitation declined',
                `${profile?.full_name} already has a group for ${inv.carpool_groups?.school_name || groupSchool} and declined your invitation.`,
                { group_id: inv.group_id });
            }
            setInvitations(prev => prev.filter((i: any) => i.id !== inv.id));
            return;
          }
        }

        // Get user's children and filter to matching school
        const { data: myKids } = await supabase.from('children')
          .select('name, school_name').eq('parent_id', session.user.id);
        console.log('🧒 myKids:', myKids, 'profile child:', profile?.child_name, profile?.school_name);

        const matchingChildren: string[] = [];
        const gs = groupSchool;
        // Include primary child if school matches exactly
        if (profile?.child_name && profile?.school_name) {
          const ps = profile.school_name.toLowerCase().trim();
          console.log('🧒 primary match?', ps, '===', gs, '->', ps === gs);
          if (ps && gs && ps === gs) {
            matchingChildren.push(profile.child_name);
          }
        }
        // Include additional children if school matches exactly
        for (const kid of (myKids || [])) {
          const ks = (kid.school_name || '').toLowerCase().trim();
          console.log('🧒 child match?', kid.name, ks, '===', gs, '->', ks === gs);
          if (ks && gs && ks === gs) {
            if (!matchingChildren.includes(kid.name)) matchingChildren.push(kid.name);
          }
        }
        console.log('🧒 matchingChildren:', matchingChildren);

        if (matchingChildren.length === 0) {
          alert(`No children found for this group's school (${inv.carpool_groups?.school_name || groupSchool}). You cannot join this group.`);
          return;
        }

        const { error: insertErr } = await supabase.from('group_members').upsert({
          group_id: targetGroupId,
          user_id: session.user.id,
          role: profile?.has_car ? 'driver' : 'passenger',
          preferred_days: profile?.preferred_days || [],
          children_included: matchingChildren,
        }, { onConflict: 'group_id,user_id', ignoreDuplicates: false });
        if (insertErr) {
          alert('Error joining group: ' + insertErr.message);
          console.error('Insert error:', insertErr);
          return;
        }
      }
      console.log('✅ Joined group successfully');

      // Notify inviter — get inviter_id from inv or from invitations query
      const inviterId = inv.inviter_id || inv.profiles?.id;
      if (inviterId) {
        await notifyUser(inviterId, 'member_joined', '🎉 Invitation accepted!',
          `${profile?.full_name} accepted your carpool group invitation and joined the group.`,
          { group_id: targetGroupId, joiner_name: profile?.full_name });
        console.log('✅ Notified inviter:', inviterId);
      } else {
        console.warn('No inviter_id found on invitation:', inv);
      }
    } else {
      const inviterId = inv.inviter_id || inv.profiles?.id;
      if (inviterId) {
        await notifyUser(inviterId, 'invite_declined', '❌ Invitation declined',
          `${profile?.full_name} declined your carpool group invitation.`,
          { group_id: inv.group_id });
      }
    }

    setInvitations(prev => prev.filter((i: any) => i.id !== inv.id));
    if (accept) { 
      const isJoinRequest = inv.message?.includes('wants to join');
      alert(isJoinRequest ? `✅ ${inv.profiles?.full_name} has been added to your group!` : '✅ You joined the group!'); 
      nav('group'); 
    }
    else alert('Invitation declined.');
  }

  return (
    <div>
      <button onClick={() => nav('dashboard')} style={{ ...s.btnSm(C.sur2, C.ink2), marginBottom: 16 }}>← Back</button>
      <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 3 }}>Group invitations</div>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: 16 }}>Accept or decline carpool group invitations</div>

      {loading && <div style={{ textAlign: 'center', padding: 32, color: C.ink3 }}>Loading…</div>}

      {!loading && invitations.length === 0 && (
        <Card>
          <div style={{ textAlign: 'center', padding: 24 }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>No pending invitations</div>
            <div style={{ fontSize: 13, color: C.ink3 }}>When someone invites you to their carpool group it'll appear here.</div>
          </div>
        </Card>
      )}

      {invitations.map((inv: any) => (
        <Card key={inv.id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <Av name={inv.profiles?.full_name || 'Parent'} size={44} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{inv.profiles?.full_name}</div>
              <div style={{ fontSize: 12, color: C.ink3 }}>
                {inv.message?.includes('wants to join') ? 'Wants to join your carpool group' : 'Invited you to their carpool group'}
              </div>
              <div style={{ fontSize: 12, color: C.schoolD, fontWeight: 600 }}>📍 {inv.carpool_groups?.school_name}</div>
            </div>
          </div>
          {inv.message && (
            <div style={{ fontSize: 13, color: C.ink, background: C.sur2, borderRadius: 8, padding: 10, marginBottom: 12, fontStyle: 'italic' }}>
              "{inv.message}"
            </div>
          )}
          {inv.profiles?.phone && (
            <div style={{ fontSize: 12, color: C.ink3, marginBottom: 12 }}>
              📞 <a href={`tel:${inv.profiles.phone}`} style={{ color: C.school }}>{inv.profiles.phone}</a>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => respond(inv, true)} style={{ ...s.btn(C.school), flex: 1, justifyContent: 'center' }}>
              {inv.message?.includes('wants to join') ? '✅ Accept & add to group' : '✅ Accept & join group'}
            </button>
            <button onClick={() => respond(inv, false)} style={{ ...s.btnSm('#fff5f5', '#b91c1c'), border: '1px solid #fca5a5', flex: 1, justifyContent: 'center' }}>
              ❌ Decline
            </button>
          </div>
        </Card>
      ))}
    </div>
  );
}
